/**
 * character/composer.ts
 *
 * The layer composition engine — where a person becomes a person.
 *
 * Every character in Hearth is assembled from discrete layers rendered onto
 * an offscreen canvas and cached until their outfit changes. No sprite atlas.
 * No external assets. Just math, fillRect, and a little love.
 *
 * Architecture note: this module knows about characters and pixels. It does
 * NOT know about rooms, tiles, or anything beyond "draw this person here."
 */

import type { WeaponVisual, ShieldVisual } from "../engine/state.js";

// ─── Direction & Animation Types ────────────────────────────────────────────

export type Direction = "n" | "ne" | "e" | "se" | "s" | "sw" | "w" | "nw";
export type AnimState = "idle" | "walk" | "sit" | "interact" | "emote" | "dance" | "device" | "device_walk" | "device_sit" | "combat_idle" | "combat_attack";

// Direction index for sprite data lookup (0=S, 1=SE, 2=E, 3=NE, 4=N, 5=NW, 6=W, 7=SW)
const DIR_INDEX: Record<Direction, number> = {
  s: 0, se: 1, e: 2, ne: 3, n: 4, nw: 5, w: 6, sw: 7,
};

// ─── Palette System ──────────────────────────────────────────────────────────

export type Palette = ReadonlyArray<string>; // CSS hex strings

/**
 * The master skin tone palette — 12 shades, all warm.
 * From porcelain to deep brown, every tone is beautiful.
 */
export const SKIN_TONES: Palette = [
  "#FDDBB4", "#F5C6A0", "#EBB98C", "#D4956A",
  "#C07B52", "#A8623E", "#8B4A2C", "#73361E",
  "#5C2713", "#9B7060", "#7A5245", "#4A2E22",
];

/**
 * Hair colors — from natural to vivid. Life is too short for boring hair.
 */
export const HAIR_COLORS: Palette = [
  "#1C0A00", "#2C1A0E", "#3D2314", "#5C3317",
  "#7A4A2A", "#A0522D", "#C68B4E", "#D4A853",
  "#E8C97A", "#F5E5B0", "#F5F5DC", "#F0F0F0",
  "#CC3333", "#993366", "#6633CC", "#3366CC",
  "#33AACC", "#33CC66", "#CCAA33", "#CC6633",
];

/**
 * Eye colors — a window into who they are.
 */
export const EYE_COLORS: Palette = [
  "#2C1810", "#3D2A1A", "#5C4030", "#3D5C2A",
  "#2A5C3D", "#2A3D5C", "#5C2A3D", "#7A7A7A",
  "#1A1A2E", "#4A3728", "#6B8E4E", "#4E8E6B",
];

/**
 * Lip colors — from subtle nudes to bold statements.
 * Organized: nudes, pinks, reds, berries, unconventional.
 */
export const LIP_COLORS: Palette = [
  "#C4877A", "#D4A090", "#BF8070", "#A06050",  // nudes: dusty rose, peach nude, mauve nude, brown nude
  "#E8909A", "#F0A0A8", "#D07080", "#CC6688",  // pinks: soft pink, bubblegum, rose, hot pink
  "#CC3030", "#AA2020", "#DD4444", "#990000",  // reds: classic red, deep red, cherry, oxblood
  "#882244", "#663355", "#993366", "#551133",  // berries: plum, grape, magenta, dark berry
  "#FF6040", "#884400", "#222222", "#6633AA",  // bold: coral, chocolate, black, violet
];

/**
 * Makeup accent colors — eyeshadow, liner, blush tones.
 */
export const MAKEUP_COLORS: Palette = [
  "#C4A090", "#E0B8A0", "#D4967A", "#A07060",  // neutrals: taupe, champagne, bronzed, mocha
  "#D090A0", "#E8A0B0", "#CC7088", "#AA5070",  // warm: dusty rose, petal, berry, cranberry
  "#6070A0", "#8090B0", "#404880", "#303060",  // cool: slate, periwinkle, navy, midnight
  "#608050", "#90A070", "#CC9930", "#E0C060",  // earth: olive, sage, amber, gold
  "#8050A0", "#A070C0", "#40A0A0", "#20C0C0",  // vivid: plum, lavender, teal, electric cyan
];

/** Generic item colors — clothing, hats, shoes, etc. */
export const ITEM_COLORS: Palette = [
  "#FFFFFF", "#F0F0F0", "#C0C0C0", "#808080", "#404040", "#101010",
  "#CC2222", "#CC6622", "#CCCC22", "#22CC22", "#2222CC", "#CC22CC",
  "#AA1111", "#AA5511", "#AAAA11", "#11AA11", "#1111AA", "#AA11AA",
  "#FF6666", "#FF9966", "#FFFF66", "#66FF66", "#6666FF", "#FF66FF",
  "#FFDDDD", "#FFE8CC", "#FFFFCC", "#DDFFDD", "#DDDDFF", "#FFDDFF",
  "#8B4513", "#A0522D", "#CD853F", "#F4A460", "#DEB887", "#FFDEAD",
];

// ─── Core Interfaces ─────────────────────────────────────────────────────────

export type Expression = "neutral" | "smile" | "frown" | "surprise" | "laugh" | "sleepy";

export interface CharacterAppearance {
  bodyType: 0 | 1 | 2 | 3;   // 0=slim, 1=average, 2=broad, 3=petite
  skinTone: number;            // index into SKIN_TONES
  hairStyle: number;           // 0-15
  hairColor: number;           // index into HAIR_COLORS
  eyeStyle: number;            // 0-7
  eyeColor: number;            // index into EYE_COLORS
  topStyle: number;            // shirt/top variant
  topColor: number;            // index into ITEM_COLORS
  bottomStyle: number;         // pants/skirt variant
  bottomColor: number;
  shoeStyle: number;
  shoeColor: number;
  hatStyle: number;            // -1 = no hat
  hatColor: number;
  heldItem: number;            // -1 = nothing held
  jacketStyle: number;         // -1 = no jacket
  jacketColor: number;
  skinDetail: number;          // -1 = none, else freckle/mark pattern
  accessoryStyle: number;      // -1 = none, 0=scarf, 1=glasses, 2=earrings, 3=necklace, 4=bandana
  accessoryColor: number;      // index into ITEM_COLORS
  auraStyle: number;           // -1 = none, 0=ember, 1=bloom, 2=frost, 3=pulse
  wingsStyle: number;          // -1 = none, 0=angel, 1=butterfly, 2=bat, 3=fairy
  wingsColor: number;
  capeStyle: number;           // -1 = none, 0=short, 1=long, 2=hooded
  capeColor: number;
  backpackStyle: number;       // -1 = none, 0=small, 1=hiking, 2=satchel
  backpackColor: number;
  facialHairStyle: number;     // -1 = none, 0=stubble, 1=goatee, 2=full beard, 3=mustache, 4=soul patch, 5=mutton chops
  facialHairColor: number;     // index into HAIR_COLORS (shares with head hair)
  makeupStyle: number;         // -1 = none, 0=natural, 1=glam, 2=smoky eye, 3=cat eye, 4=blush glow, 5=avant-garde
  makeupColor: number;         // index into MAKEUP_COLORS
  lipStyle: number;            // -1 = none (skin-tone mouth), 0=subtle tint, 1=matte, 2=gloss, 3=bold, 4=ombre
  lipColor: number;            // index into LIP_COLORS
  expression: Expression;      // facial expression
  // ── Body shape — archetype blending ──
  // Two archetypes lerped by blend ratio. Produces anatomically coherent
  // proportions — shoulder/waist/hip ratios maintain human constraints.
  bodyArchetype1: BodyArchetypeId;  // primary build
  bodyArchetype2: BodyArchetypeId;  // secondary build (blended toward)
  archetypeBlend: number;      // 0.0 = pure archetype1, 1.0 = pure archetype2
  bodyScale: number;           // 0-4, overall height/mass scalar, default 2
  // ── Fine-tuning — individual region adjustment on top of archetype ──
  shoulderAdjust: number;      // 0-4: narrow(0) → wide(4), default 2 = no change
  waistAdjust: number;         // 0-4: cinched(0) → wide(4), default 2 = no change
  hipAdjust: number;           // 0-4: narrow(0) → wide(4), default 2 = no change
  legGap: number;              // 0-4: thighs touching(0) → wide gap(4), default 2 = gender-natural
  // ── Nudity / anatomy ──
  nude: boolean;               // true = strip all clothing, show full anatomy
  bodyGender: BodyGender;      // anatomical presentation for nude rendering
  breastSize: number;          // 0-4: flat → small → medium → large → very large (feminine/andro)
  buttSize: number;            // 0-4: flat → toned → round → full → voluptuous
  genitalSize: number;         // 0-4: masculine genital size (small → large)
}

export type BodyArchetypeId = "athletic" | "soft" | "lithe" | "powerful" | "petite";
export type BodyGender = "feminine" | "masculine" | "androgynous";

export interface CharacterLayer {
  id: string;
  zIndex: number;
  /** Draw this layer onto the composite canvas context */
  draw: (ctx: OffscreenCanvasRenderingContext2D, dir: Direction, frame: number) => void;
  visible: boolean;
}

export interface CharacterState {
  id: string;
  appearance: CharacterAppearance;
  /** Cached composite canvases keyed by "dir:frame" */
  cache: Map<string, ImageData>;
  /** Marks cache as stale — rebuild on next render */
  dirty: boolean;
  /** Current animation state */
  animState: AnimState;
  /** Current facing direction */
  direction: Direction;
  /** Animation frame index */
  animFrame: number;
  /** Timer for animation advances (seconds) */
  animTimer: number;
  /** Vertical bob offset for walking (pixels) */
  bobOffset: number;
  /** The offscreen composite canvas, reused across frames */
  offscreen: OffscreenCanvas;
}

// ─── Canvas size constants ────────────────────────────────────────────────────

export const CHAR_W = 96;
export const CHAR_H = 144;

/** Legacy dimensions — old pixel-art functions draw at this size, scaled 2× */
const LEGACY_W = 48;
const LEGACY_H = 72;

// ─── Pixel-art drawing helpers ────────────────────────────────────────────────

/**
 * Draw a single pixel at (x, y) with CSS color on an ImageData buffer.
 * This is the atomic unit of all our character art.
 */
function setPixel(data: ImageData, x: number, y: number, color: string): void {
  if (x < 0 || y < 0 || x >= data.width || y >= data.height) return;
  const r = parseInt(color.slice(1, 3), 16);
  const g = parseInt(color.slice(3, 5), 16);
  const b = parseInt(color.slice(5, 7), 16);
  const idx = (y * data.width + x) * 4;
  data.data[idx]     = r;
  data.data[idx + 1] = g;
  data.data[idx + 2] = b;
  data.data[idx + 3] = 255;
}

/**
 * Fill a rectangle of pixels. Our fillRect, hand-rolled for ImageData.
 */
function fillRect(
  data: ImageData,
  x: number, y: number,
  w: number, h: number,
  color: string
): void {
  for (let dy = 0; dy < h; dy++) {
    for (let dx = 0; dx < w; dx++) {
      setPixel(data, x + dx, y + dy, color);
    }
  }
}

/**
 * Darken a hex color by a factor (0–1). Used for shading.
 */
function darken(hex: string, factor: number): string {
  const r = Math.floor(parseInt(hex.slice(1, 3), 16) * factor);
  const g = Math.floor(parseInt(hex.slice(3, 5), 16) * factor);
  const b = Math.floor(parseInt(hex.slice(5, 7), 16) * factor);
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

/**
 * Lighten a hex color by a factor (0–1).
 */
function lighten(hex: string, factor: number): string {
  const r = Math.min(255, Math.floor(parseInt(hex.slice(1, 3), 16) + (255 - parseInt(hex.slice(1, 3), 16)) * factor));
  const g = Math.min(255, Math.floor(parseInt(hex.slice(3, 5), 16) + (255 - parseInt(hex.slice(3, 5), 16)) * factor));
  const b = Math.min(255, Math.floor(parseInt(hex.slice(5, 7), 16) + (255 - parseInt(hex.slice(5, 7), 16)) * factor));
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
}

/**
 * Blend two ImageData buffers — composites src ON TOP of dst.
 * Only copies non-transparent pixels.
 */
function blendOver(dst: ImageData, src: ImageData, offsetX = 0, offsetY = 0): void {
  for (let y = 0; y < src.height; y++) {
    for (let x = 0; x < src.width; x++) {
      const si = (y * src.width + x) * 4;
      if (src.data[si + 3] < 128) continue; // skip transparent
      const dx = x + offsetX;
      const dy = y + offsetY;
      if (dx < 0 || dy < 0 || dx >= dst.width || dy >= dst.height) continue;
      const di = (dy * dst.width + dx) * 4;
      dst.data[di]     = src.data[si];
      dst.data[di + 1] = src.data[si + 1];
      dst.data[di + 2] = src.data[si + 2];
      dst.data[di + 3] = 255;
    }
  }
}

// ─── Body type configuration ──────────────────────────────────────────────────

interface BodyConfig {
  /** Width of the torso in pixels */
  torsoW: number;
  /** Height of the torso */
  torsoH: number;
  /** Width of head */
  headW: number;
  /** Height of head */
  headH: number;
  /** X offset for the whole character within the 48×72 canvas */
  originX: number;
  /** Y offset (feet at bottom) */
  originY: number;
}

const BODY_CONFIGS: Record<0 | 1 | 2 | 3, BodyConfig> = {
  0: { torsoW: 10, torsoH: 14, headW: 9,  headH: 9,  originX: 19, originY: 57 }, // slim
  1: { torsoW: 12, torsoH: 14, headW: 10, headH: 10, originX: 18, originY: 58 }, // average
  2: { torsoW: 14, torsoH: 15, headW: 11, headH: 10, originX: 17, originY: 58 }, // broad
  3: { torsoW: 9,  torsoH: 12, headW: 8,  headH: 8,  originX: 20, originY: 55 }, // petite
};

// ─── Canvas2D drawing system ──────────────────────────────────────────────────
//
// New body anchor system for canvas2D drawing at 96×144 resolution.
// Each anchor set describes joint positions for a body type facing south.
// Direction transforms and mirroring derive all 8 orientations.

type DrawCtx = OffscreenCanvasRenderingContext2D;
type DirCat = "front" | "qfront" | "side" | "qback" | "back";

interface BodyAnchors {
  cx: number;     // center x
  headY: number;  headRx: number;  headRy: number;
  neckY: number;  shY: number;     shW: number;     armW: number;
  elbowY: number; wristY: number;  handY: number;
  waistY: number; waistW: number;  hipY: number;    hipW: number;
  legGap: number; kneeY: number;   ankleY: number;  footY: number;
  eyeY: number;
}

//
// Proportions guide (stylized ~5-head figure, optimized for isometric social game):
//   Head ~20%, Torso ~28%, Legs ~40%, Feet/Neck ~12%
//   Longer legs = elegant. Slightly oversized head = expressive + readable at 48px on screen.
//   Waist sits high (golden ratio split), hips slightly wider than waist.
//
const BA: Record<0 | 1 | 2 | 3, BodyAnchors> = {
  // slim — narrow shoulders, defined waist, long limbs
  0: { cx:48, headY:18, headRx:11, headRy:13, neckY:32, shY:37, shW:20, armW:26,
       elbowY:56, wristY:72, handY:76,
       waistY:62, waistW:13, hipY:74, hipW:15, legGap:4, kneeY:102, ankleY:130, footY:136, eyeY:19 },
  // average — balanced, classic proportions
  1: { cx:48, headY:18, headRx:12, headRy:14, neckY:33, shY:38, shW:24, armW:30,
       elbowY:58, wristY:74, handY:78,
       waistY:64, waistW:16, hipY:76, hipW:18, legGap:5, kneeY:104, ankleY:130, footY:136, eyeY:19 },
  // broad — wide shoulders, strong build, same height
  2: { cx:48, headY:18, headRx:13, headRy:14, neckY:33, shY:38, shW:26, armW:32,
       elbowY:58, wristY:74, handY:78,
       waistY:64, waistW:18, hipY:76, hipW:20, legGap:5, kneeY:104, ankleY:130, footY:136, eyeY:19 },
  // petite — smaller frame, proportionally larger head for cute factor
  // Shifted +18 so footY=136 aligns with canvas ground anchor (CHAR_H - 8)
  3: { cx:48, headY:34, headRx:11, headRy:13, neckY:48, shY:52, shW:20, armW:25,
       elbowY:68, wristY:82, handY:86,
       waistY:74, waistW:13, hipY:84, hipW:15, legGap:4, kneeY:108, ankleY:130, footY:136, eyeY:35 },
};

function dirCat(dir: Direction): [DirCat, boolean] {
  switch (dir) {
    case "s":  return ["front",  false];
    case "se": return ["qfront", false];
    case "sw": return ["qfront", true ];
    case "e":  return ["side",   true ];
    case "w":  return ["side",   false];
    case "ne": return ["qback",  false];
    case "nw": return ["qback",  true ];
    default:   return ["back",   false];
  }
}

function withFlip(ctx: DrawCtx, dir: Direction, fn: (cat: DirCat) => void): void {
  const [cat, flip] = dirCat(dir);
  if (flip) { ctx.save(); ctx.translate(CHAR_W, 0); ctx.scale(-1, 1); }
  fn(cat);
  if (flip) ctx.restore();
}

function shade(hex: string, f: number): string {
  const n = parseInt(hex.replace("#", ""), 16);
  const cl = (v: number) => Math.min(255, Math.max(0, Math.round(v)));
  return "#" + [cl(((n >> 16) & 255) * f), cl(((n >> 8) & 255) * f), cl((n & 255) * f)]
    .map(v => v.toString(16).padStart(2, "0")).join("");
}

function limbStroke(
  ctx: DrawCtx, x0: number, y0: number, x1: number, y1: number,
  w: number, color: string
): void {
  ctx.strokeStyle = color;
  ctx.lineWidth = w;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(x0, y0);
  ctx.quadraticCurveTo((x0 + x1) / 2 + (x1 - x0) * 0.08, (y0 + y1) / 2, x1, y1);
  ctx.stroke();
}

function ctxRoundRect(ctx: DrawCtx, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
  ctx.fill();
}

// ─── Unified Animation Pose ──────────────────────────────────────────────────
//
// Single source of truth for ALL body offsets — walk, idle breathing, sit, dance.
// Every draw function reads _animPose. Computed once before compositing.

interface AnimPose {
  // ── Arm offsets ──
  nearArmDY: number;      // near arm vertical swing
  farArmDY: number;       // far arm vertical swing
  nearArmDX: number;      // near arm horizontal reach (positive = forward/inward)
  farArmDX: number;       // far arm horizontal reach
  nearElbowBend: number;  // extra elbow bend (0=natural, positive=more bent)
  farElbowBend: number;
  // ── Leg offsets ──
  nearLegDX: number;      // near leg horizontal stride
  nearLegDY: number;      // near leg lift (negative = up)
  farLegDX: number;
  farLegDY: number;
  // ── Torso ──
  torsoSwayX: number;     // lateral sway
  shoulderTilt: number;   // rotation in radians
  breatheY: number;       // chest expansion (positive = shoulders rise)
  torsoDY: number;        // whole-body vertical shift (sitting lowers torso)
  // ── Sitting ──
  isSitting: boolean;
  kneeAngle: number;      // leg bend for sitting (0 = straight, 1 = 90°)
  // ── Cloth physics ──
  clothPhase: number;     // secondary motion phase for fabric sway
  clothAmplitude: number; // how much fabric moves (0 idle, higher when walking)
  // ── Expression ──
  expression: Expression;
  // ── Gaze & Attention ──
  gazeX: number;          // pupil horizontal offset (-2..2 px). Eyes track targets.
  gazeY: number;          // pupil vertical offset (-1..1 px)
  headTiltX: number;      // subtle head horizontal shift (-1..1 px) following gaze
  headTiltY: number;      // subtle head vertical nod (-0.5..0.5 px)
  // ── Continuous head orientation (72+ orientations) ──
  headYaw: number;        // head horizontal rotation (-1..1). -1=full left, 0=facing camera, 1=full right
  headPitch: number;      // head vertical tilt (-0.5..0.5). Negative=looking up, positive=down
  // ── Blink phase ──
  blinkPhase: number;     // 0=open, 0→0.5=closing, 0.5=closed, 0.5→1=opening
  // ── Hair dynamics ──
  hairSwingX: number;     // horizontal swing from velocity/turning (-3..3 px)
  hairSettlePhase: number;// damped oscillation phase for settling (0=rest)
  // ── Secondary motion ──
  secondaryLagX: number;  // accessory/earring horizontal lag (-2..2 px)
  secondaryLagY: number;  // accessory vertical lag (-1..1 px)
  // ── Body response to attention (NEW) ──
  shoulderTurnX: number;  // subtle torso/shoulder orientation toward gaze (-0.25..0.25)
  breathIntensity: number; // breathing amplitude multiplier (0.6=calm, 1.4=alert)
  interestLevel: number;  // 0-1 engagement with current target
  // ── Deep eye life ──
  pupilDilation: number;   // 0.6=contracted, 1=normal, 1.6=dilated (interest-driven)
  lidFollowY: number;      // upper eyelid droop/lift from vertical gaze (-0.15..0.35)
  microTremorX: number;    // physiological nystagmus X offset
  microTremorY: number;    // physiological nystagmus Y offset
  browEngagement: number;  // attention-driven brow response (-0.3..0.4)
  // ── Idle micro-fidgets ──
  fidgetArmDY: number;     // subtle arm drift from idle micro-movements
  fidgetTorsoX: number;    // micro weight shift
  weightShiftPhase: number; // continuous slow weight oscillation phase
  // ── Emotion posture ──
  emotionTension: number;   // 0=relaxed, 1=tense — raises shoulders, tightens limbs
  emotionWithdraw: number;  // 0=open, 1=withdrawn — curves spine inward, drops head
  shoulderGirdleRot: number; // ±1 shoulder girdle rotation from arm swing (torso twist)
  // ── Body physics (secondary motion) ──
  breastBounceY: number;    // vertical breast offset from step impact (-2..2 px)
  breastBounceVel: number;  // breast velocity for spring simulation
  buttJiggleL: number;      // left cheek lateral shift from alternating step
  buttJiggleR: number;      // right cheek lateral shift
  genitalSwayX: number;     // pendular horizontal sway for masculine anatomy
  genitalSwayY: number;     // vertical bounce component
}

const ZERO_POSE: AnimPose = {
  nearArmDY: 0, farArmDY: 0,
  nearArmDX: 0, farArmDX: 0,
  nearElbowBend: 0, farElbowBend: 0,
  nearLegDX: 0, nearLegDY: 0, farLegDX: 0, farLegDY: 0,
  torsoSwayX: 0, shoulderTilt: 0, breatheY: 0, torsoDY: 0,
  isSitting: false, kneeAngle: 0,
  clothPhase: 0, clothAmplitude: 0,
  expression: "neutral",
  gazeX: 0, gazeY: 0, headTiltX: 0, headTiltY: 0, headYaw: 0, headPitch: 0,
  blinkPhase: 0,
  hairSwingX: 0, hairSettlePhase: 0,
  secondaryLagX: 0, secondaryLagY: 0,
  shoulderTurnX: 0, breathIntensity: 1.0, interestLevel: 0,
  pupilDilation: 1.0, lidFollowY: 0, microTremorX: 0, microTremorY: 0,
  browEngagement: 0,
  fidgetArmDY: 0, fidgetTorsoX: 0, weightShiftPhase: 0,
  emotionTension: 0, emotionWithdraw: 0, shoulderGirdleRot: 0,
  breastBounceY: 0, breastBounceVel: 0,
  buttJiggleL: 0, buttJiggleR: 0,
  genitalSwayX: 0, genitalSwayY: 0,
};

function computeAnimPose(state: AnimState, frame: number, expression: Expression): AnimPose {
  const pose: AnimPose = { ...ZERO_POSE, expression };

  if (state === "walk") {
    const t = (frame % 8) / 8;
    const phase = t * Math.PI * 2;
    // Character sprite is 96×144 rendered at 0.5× on screen.
    // All offsets here are in sprite pixels — halved on screen.
    // Need large values to read clearly as walking at final display size.
    const armSwing = Math.sin(phase) * 5;        // ±5px sprite = ±2.5px screen
    const legSwingX = Math.sin(phase) * 7;       // ±7px sprite = ±3.5px screen — visible stride
    const legLiftAmt = Math.abs(Math.sin(phase)) * 6; // 6px sprite = 3px screen lift
    pose.nearArmDY = armSwing;
    pose.farArmDY = -armSwing;
    pose.nearElbowBend = 0.18;    // natural bend while walking
    pose.farElbowBend = 0.18;
    // Both legs swing and lift — the "contact" leg still gets a small lift
    // to keep both feet visibly moving, not planted.
    pose.nearLegDX = legSwingX;
    pose.nearLegDY = -(Math.sin(phase) > 0 ? legLiftAmt : legLiftAmt * 0.3);
    pose.farLegDX = -legSwingX;
    pose.farLegDY = -(Math.sin(phase) < 0 ? legLiftAmt : legLiftAmt * 0.3);
    pose.torsoSwayX = Math.sin(phase) * 1.0;
    pose.shoulderTilt = Math.sin(phase) * 0.02;
    pose.breatheY = Math.sin(phase * 2) * 0.6;
    pose.clothPhase = phase;
    pose.clothAmplitude = 1.0;
    // ── Body physics — breast bounce from step impact, butt jiggle from alternating weight shift ──
    // Breast bounce: phase-delayed from footfall (double frequency since each step impacts)
    // Spring-like: sharp downward on impact, oscillating recovery
    const stepImpact = Math.abs(Math.sin(phase)); // peaks at each footfall
    const bouncePhase = phase * 2 + Math.PI * 0.25; // quarter-phase delay from step
    pose.breastBounceY = Math.sin(bouncePhase) * 1.8 * stepImpact;
    pose.breastBounceVel = Math.cos(bouncePhase) * 2.0;
    // Butt jiggle: alternating cheeks shift with each step (opposite phase per side)
    pose.buttJiggleL = Math.sin(phase) * 1.2;
    pose.buttJiggleR = Math.sin(phase + Math.PI) * 1.2; // opposite phase
    // Genital sway: pendular, lags behind body (lower frequency)
    pose.genitalSwayX = Math.sin(phase * 0.5 + Math.PI * 0.3) * 1.5;
    pose.genitalSwayY = Math.abs(Math.sin(phase)) * 0.8;
  } else if (state === "interact") {
    // Interact: arms reach forward — typing, pressing buttons, using objects
    // Slow 4-frame cycle: subtle hand movement at the interaction point
    const breathPhase = (frame / 4) * Math.PI * 2;
    pose.breatheY = Math.sin(breathPhase) * 0.6;
    // Both arms extend forward and inward, elbows bent (typing posture)
    pose.nearArmDX = 6;          // arms reach inward
    pose.farArmDX = 6;
    pose.nearArmDY = 8;          // hands lower (at desk/object height)
    pose.farArmDY = 8;
    pose.nearElbowBend = 0.5;    // elbows bent more
    pose.farElbowBend = 0.5;
    // Subtle finger-motion oscillation
    const fingerPhase = (frame / 4) * Math.PI * 2;
    pose.nearArmDY += Math.sin(fingerPhase) * 1.5;
    pose.farArmDY += Math.sin(fingerPhase + Math.PI) * 1.5;
    pose.clothPhase = breathPhase * 0.2;
    pose.clothAmplitude = 0.1;
  } else if (state === "emote") {
    // Per-emote pose variations — each emote gets unique body language
    const breathPhase = (frame / 6) * Math.PI * 2;
    const phase = (frame / 6) * Math.PI * 2;

    if (_activeEmoteId === "surprised" || _activeEmoteId === "shocked") {
      // Shocked: lean back, both arms up, wide stance
      pose.breatheY = -3 + Math.sin(phase * 3) * 0.5; // quick startled bob
      pose.nearArmDY = -14 + Math.sin(phase) * 2;  // arms up in alarm
      pose.farArmDY = -14 + Math.sin(phase + 0.5) * 2;
      pose.nearArmDX = -4;  // spread wide
      pose.farArmDX = 4;
      pose.nearElbowBend = -0.2;
      pose.farElbowBend = -0.2;
      pose.torsoSwayX = Math.sin(phase * 4) * 1.5; // trembling
      pose.torsoDY = -2; // lean back
      pose.clothPhase = phase;
      pose.clothAmplitude = 0.6;
    } else if (_activeEmoteId === "laugh") {
      // Laugh: hunched forward, bouncing, arms on belly
      pose.breatheY = Math.abs(Math.sin(phase * 2)) * 3; // bouncing
      pose.nearArmDY = 6 + Math.sin(phase * 3) * 2;  // clutching belly
      pose.farArmDY = 6 + Math.sin(phase * 3 + 1) * 2;
      pose.nearArmDX = 3;
      pose.farArmDX = -3;
      pose.nearElbowBend = 0.7;
      pose.farElbowBend = 0.7;
      pose.torsoSwayX = Math.sin(phase * 1.5) * 1;
      pose.torsoDY = 4; // hunched forward
      pose.clothPhase = phase * 2;
      pose.clothAmplitude = 0.8;
    } else if (_activeEmoteId === "cry") {
      // Cry: hunched, one hand to face, slow sway
      pose.breatheY = Math.sin(phase * 0.5) * 2;
      pose.nearArmDY = -8;  // hand to face
      pose.nearArmDX = 2;
      pose.nearElbowBend = 0.8;
      pose.farArmDY = 4; // limp at side
      pose.farArmDX = 0;
      pose.torsoSwayX = Math.sin(phase * 0.3) * 0.8;
      pose.torsoDY = 3;
      pose.clothPhase = phase * 0.2;
      pose.clothAmplitude = 0.15;
    } else if (_activeEmoteId === "bow") {
      // Bow: deep forward lean, arms at sides or crossed
      const bowT = Math.min(1, frame / 4); // lean into bow over first frames
      pose.torsoDY = 10 * bowT;
      pose.breatheY = 2 * bowT;
      pose.nearArmDY = 6 * bowT;
      pose.farArmDY = 6 * bowT;
      pose.nearArmDX = 1;
      pose.farArmDX = -1;
      pose.clothPhase = phase * 0.1;
      pose.clothAmplitude = 0.1;
    } else if (_activeEmoteId === "salute") {
      // Salute: near arm snaps to forehead, straight posture
      pose.breatheY = Math.sin(breathPhase) * 0.3;
      pose.nearArmDY = -14; // hand at forehead
      pose.nearArmDX = 1;
      pose.nearElbowBend = 0.9; // sharp bend
      pose.farArmDY = 3; // at side, rigid
      pose.farArmDX = -1;
      pose.torsoDY = -1; // stand straight
      pose.clothAmplitude = 0.05;
    } else if (_activeEmoteId === "clap") {
      // Clap: both arms swing together rhythmically
      const clapPhase = (frame / 3) * Math.PI * 2; // faster cycle
      pose.breatheY = Math.sin(clapPhase) * 0.5;
      pose.nearArmDY = -4 + Math.sin(clapPhase) * 3;
      pose.farArmDY = -4 + Math.sin(clapPhase) * 3;
      pose.nearArmDX = 3 - Math.abs(Math.sin(clapPhase)) * 5; // hands meet
      pose.farArmDX = -3 + Math.abs(Math.sin(clapPhase)) * 5;
      pose.nearElbowBend = 0.4;
      pose.farElbowBend = 0.4;
      pose.clothPhase = clapPhase;
      pose.clothAmplitude = 0.3;
    } else if (_activeEmoteId === "think") {
      // Think: hand on chin, slight head tilt
      pose.breatheY = Math.sin(breathPhase) * 0.6;
      pose.nearArmDY = -10; // hand near chin
      pose.nearArmDX = 2;
      pose.nearElbowBend = 0.8;
      pose.farArmDY = 4; // arm crosses body
      pose.farArmDX = 3;
      pose.farElbowBend = 0.5;
      pose.torsoSwayX = 0.3;
      pose.clothAmplitude = 0.08;
    } else if (_activeEmoteId === "shrug") {
      // Shrug: shoulders up, palms out
      const shrugT = Math.min(1, frame / 3);
      pose.breatheY = -2 * shrugT;
      pose.nearArmDY = -6 * shrugT;
      pose.farArmDY = -6 * shrugT;
      pose.nearArmDX = -5 * shrugT;
      pose.farArmDX = 5 * shrugT;
      pose.nearElbowBend = -0.3;
      pose.farElbowBend = -0.3;
      pose.shoulderTilt = 0.02 * Math.sin(phase);
      pose.clothAmplitude = 0.2;
    } else if (_activeEmoteId === "flex") {
      // Flex: both arms up in strongman pose, pulsing
      const flexPhase = (frame / 4) * Math.PI * 2;
      pose.breatheY = -1 + Math.sin(flexPhase) * 0.5;
      pose.nearArmDY = -14;
      pose.farArmDY = -14;
      pose.nearArmDX = -6; // spread wide
      pose.farArmDX = 6;
      pose.nearElbowBend = 0.9; // bicep curl
      pose.farElbowBend = 0.9;
      pose.torsoSwayX = Math.sin(flexPhase * 0.5) * 0.5;
      pose.clothAmplitude = 0.4;
    } else {
      // Default wave (wave, generic emotes)
      const wavePhase = phase;
      pose.breatheY = Math.sin(breathPhase) * 0.8;
      pose.nearArmDY = -12 + Math.sin(wavePhase) * 4;
      pose.nearArmDX = -3 + Math.sin(wavePhase * 2) * 2;
      pose.nearElbowBend = -0.3;
      pose.farArmDY = 2;
      pose.farArmDX = 0;
      pose.torsoSwayX = Math.sin(wavePhase) * 0.5;
      pose.clothPhase = wavePhase * 0.3;
      pose.clothAmplitude = 0.3;
    }
  } else if (state === "idle") {
    // Breathing: 4 frames = one full cycle at 0.5 FPS = 8 second breath
    // Natural breathing has slight irregularity — add harmonics for organic feel
    const breathPhase = (frame / 4) * Math.PI * 2;
    const breathIrregular = Math.sin(breathPhase * 0.37) * 0.15; // slow drift
    pose.breatheY = Math.sin(breathPhase) * (1.2 + breathIrregular);
    // Weight shift — subtle lateral sway with longer period than breathing
    pose.torsoSwayX = Math.sin(breathPhase * 0.5) * 0.3 + Math.sin(breathPhase * 0.17) * 0.15;
    // Shoulders micro-settle — very slight asymmetric droop
    pose.shoulderTilt = Math.sin(breathPhase * 0.23) * 0.008;
    pose.clothPhase = breathPhase * 0.3;
    pose.clothAmplitude = 0.15;
  } else if (state === "sit") {
    pose.isSitting = true;
    pose.torsoDY = 12;
    pose.kneeAngle = 0.85;
    pose.nearArmDY = 4;
    pose.farArmDY = 4;
    // Seated breathing: relaxed, slightly slower, with drift (4 frames per cycle)
    const breathPhase = (frame / 4) * Math.PI * 2;
    pose.breatheY = Math.sin(breathPhase) * 0.8 + Math.sin(breathPhase * 0.31) * 0.1;
    // Subtle seated sway — shifting weight
    pose.torsoSwayX = Math.sin(breathPhase * 0.25) * 0.2;
    // Arms drift slightly with breathing
    pose.nearArmDY = 4 + Math.sin(breathPhase * 0.5) * 0.3;
    pose.clothPhase = breathPhase * 0.2;
    pose.clothAmplitude = 0.05;
  } else if (state === "dance") {
    const t = (frame % 8) / 8;
    const phase = t * Math.PI * 2;
    pose.nearArmDY = Math.sin(phase) * 10;
    pose.farArmDY = Math.sin(phase + Math.PI * 0.7) * 10;
    pose.nearLegDX = Math.sin(phase) * 4;
    pose.nearLegDY = -Math.abs(Math.sin(phase)) * 5;
    pose.farLegDX = -Math.sin(phase) * 4;
    pose.farLegDY = -Math.abs(Math.cos(phase)) * 5;
    pose.torsoSwayX = Math.sin(phase) * 2;
    pose.breatheY = Math.sin(phase * 2) * 1;
    pose.clothPhase = phase;
    pose.clothAmplitude = 1.5;
    // Dance physics — exaggerated bounce, stronger than walk
    const danceImpact = Math.abs(Math.sin(phase * 2));
    pose.breastBounceY = Math.sin(phase * 2 + 0.3) * 2.5 * danceImpact;
    pose.breastBounceVel = Math.cos(phase * 2 + 0.3) * 3.0;
    pose.buttJiggleL = Math.sin(phase) * 1.8;
    pose.buttJiggleR = Math.sin(phase + Math.PI) * 1.8;
    pose.genitalSwayX = Math.sin(phase * 0.7) * 2.0;
    pose.genitalSwayY = danceImpact * 1.2;
  } else if (state === "device") {
    // Device: character holds up their phone or pad, absorbed in the screen.
    // Pose should read instantly as "using a phone/tablet" from any angle.
    const breathPhase = (frame / 4) * Math.PI * 2;
    const tapPhase = (frame / 4) * Math.PI * 2;

    if (_activeDeviceType === "pad") {
      // hPad: both arms cradle landscape tablet in front of chest.
      // Arms forward and bent — elbows at sides, hands meeting in the middle.
      pose.nearArmDY = -6;
      pose.farArmDY = -6;
      pose.nearArmDX = 4;
      pose.farArmDX = -4;
      pose.nearElbowBend = 0.6;
      pose.farElbowBend = 0.6;
      pose.breatheY = Math.sin(breathPhase) * 0.5 + 1.5; // head tilts down to look at pad
    } else {
      // hPhone: near arm raised high, elbow tight to body, phone at face level.
      // Far arm either crosses loosely (supporting elbow) or rests at hip.
      pose.nearArmDY = -10 + Math.sin(tapPhase) * 0.6;  // high — phone near face
      pose.nearArmDX = 2;                                 // close to body, not sticking out
      pose.nearElbowBend = 0.85;                           // very bent — forearm vertical
      // Far arm: slightly raised (hand on hip or supporting elbow)
      pose.farArmDY = 0;
      pose.farArmDX = 1;
      pose.farElbowBend = 0.25;
      pose.breatheY = Math.sin(breathPhase) * 0.5 + 2;   // head tilts down more to see phone
    }

    // Slight weight shift — standing naturally, not at attention
    pose.torsoSwayX = Math.sin(breathPhase * 0.5) * 0.25;
    pose.clothPhase = breathPhase * 0.15;
    pose.clothAmplitude = 0;
  } else if (state === "device_walk") {
    // Walking while looking at phone — distracted shuffle.
    // Phone arm stays high and steady, free arm swings loosely, legs take
    // shorter steps than a normal walk. Think "texting while walking."
    const phase = (frame / 8) * Math.PI * 2;
    const breathPhase = (frame / 8) * Math.PI * 2;
    const tapPhase = (frame / 4) * Math.PI * 2;

    // Shorter, shuffling steps — clearly walking but distracted (smaller than full walk)
    pose.nearLegDX = Math.sin(phase) * 5;
    pose.nearLegDY = -Math.abs(Math.sin(phase)) * 4;
    pose.farLegDX = -Math.sin(phase) * 5;
    pose.farLegDY = -Math.abs(Math.cos(phase)) * 4;

    // Phone arm — high and steady, barely affected by walk bounce
    pose.nearArmDY = -8 + Math.sin(tapPhase) * 0.3 + Math.sin(phase) * 0.4;
    pose.nearArmDX = 2;
    pose.nearElbowBend = 0.8;

    // Free arm swings naturally with gait (counterbalance)
    pose.farArmDY = Math.sin(phase) * 2;
    pose.farArmDX = -Math.sin(phase) * 0.6;
    pose.farElbowBend = 0.1;

    // Body sway from walking + head tilt down for screen
    pose.torsoSwayX = Math.sin(phase) * 0.5;
    pose.breatheY = Math.sin(breathPhase) * 0.4 + 1.5;
    pose.clothPhase = phase;
    pose.clothAmplitude = 0.5;
  } else if (state === "device_sit") {
    // Sitting while holding device — relaxed seated posture, absorbed in screen.
    const breathPhase = (frame / 4) * Math.PI * 2;
    const tapPhase = (frame / 4) * Math.PI * 2;

    // Seated legs
    pose.isSitting = true;
    pose.torsoDY = 12;
    pose.kneeAngle = 0.85;
    pose.nearLegDX = 2;
    pose.nearLegDY = 4;
    pose.farLegDX = -1;
    pose.farLegDY = 3;

    if (_activeDeviceType === "pad") {
      // Pad propped on lap/thighs — both arms cradle it, elbows on armrests
      pose.nearArmDY = -3;
      pose.farArmDY = -3;
      pose.nearArmDX = 3;
      pose.farArmDX = -3;
      pose.nearElbowBend = 0.6;
      pose.farElbowBend = 0.6;
      pose.breatheY = Math.sin(breathPhase) * 0.3 + 1.5;
    } else {
      // Phone in one hand — elbow on armrest/knee, phone up near face
      pose.nearArmDY = -6 + Math.sin(tapPhase) * 0.3;
      pose.nearArmDX = 2;
      pose.nearElbowBend = 0.85;
      // Other arm resting on lap/armrest
      pose.farArmDY = 4;
      pose.farArmDX = -1;
      pose.breatheY = Math.sin(breathPhase) * 0.3 + 2;
    }

    pose.torsoSwayX = 0;
    pose.clothPhase = breathPhase * 0.1;
    pose.clothAmplitude = 0;
  } else if (state === "combat_idle") {
    // Combat ready stance — weapon class shapes the guard posture.
    const phase = (frame / 4) * Math.PI * 2;
    const breathPhase = (frame / 4) * Math.PI * 2;
    const wc = _weaponVisual?.class ?? null;

    // Base bounce — all fighters bob on balls of feet
    pose.breatheY = Math.sin(phase) * 1.8 + 0.5;
    pose.torsoSwayX = Math.sin(phase * 0.5) * 0.4;
    pose.shoulderTilt = Math.sin(breathPhase * 0.3) * 0.01;
    pose.clothPhase = phase * 0.3;
    pose.clothAmplitude = 0.2;
    pose.nearLegDX = 2 + Math.sin(phase * 0.5) * 0.5;
    pose.farLegDX = -2 + Math.sin(phase * 0.5) * 0.5;
    pose.nearLegDY = -Math.abs(Math.sin(phase)) * 1.0;
    pose.farLegDY = -Math.abs(Math.cos(phase)) * 0.5;

    if (wc === "dagger") {
      // Dagger: low guard, hands close, quick reflexes
      pose.nearArmDY = -3 + Math.sin(phase) * 1.5;
      pose.farArmDY = -2 + Math.sin(phase + 0.5) * 1.0;
      pose.nearArmDX = 4; pose.farArmDX = 3;
      pose.nearElbowBend = 0.5; pose.farElbowBend = 0.4;
    } else if (wc === "sword_2h") {
      // Greatsword: both hands grip, high guard, wider stance
      pose.nearArmDY = -8 + Math.sin(phase) * 1.0;
      pose.farArmDY = -6 + Math.sin(phase + 0.3) * 0.8;
      pose.nearArmDX = 2; pose.farArmDX = 1;
      pose.nearElbowBend = 0.7; pose.farElbowBend = 0.65;
      pose.nearLegDX += 1; pose.farLegDX -= 1;
    } else if (wc === "staff") {
      // Staff: vertical hold, both hands on shaft
      pose.nearArmDY = -7 + Math.sin(phase) * 0.8;
      pose.farArmDY = -2 + Math.sin(phase + 0.5) * 0.6;
      pose.nearArmDX = 1; pose.farArmDX = 0;
      pose.nearElbowBend = 0.6; pose.farElbowBend = 0.3;
    } else if (wc === "axe") {
      // Axe: one-hand low grip, weight pulls weapon arm down, off-hand loose
      pose.nearArmDY = -4 + Math.sin(phase) * 1.5;
      pose.farArmDY = -1 + Math.sin(phase + 0.7) * 0.8;
      pose.nearArmDX = 2; pose.farArmDX = 1;
      pose.nearElbowBend = 0.45; pose.farElbowBend = 0.3;
    } else if (wc === "mace") {
      // Mace: compact guard, weapon at shoulder height, off-hand braced forward
      pose.nearArmDY = -5 + Math.sin(phase) * 1.3;
      pose.farArmDY = -4 + Math.sin(phase + 0.3) * 0.6;
      pose.nearArmDX = 3; pose.farArmDX = 3;
      pose.nearElbowBend = 0.6; pose.farElbowBend = 0.55;
    } else if (wc === "sword_1h") {
      // Sword: mid guard, blade forward, elbow bent ready to parry
      pose.nearArmDY = -5 + Math.sin(phase) * 1.0;
      pose.farArmDY = -4 + Math.sin(phase + 0.4) * 0.8;
      pose.nearArmDX = 5; pose.farArmDX = 2;
      pose.nearElbowBend = 0.55; pose.farElbowBend = 0.5;
    } else {
      // Default (fists): classic boxing guard
      pose.nearArmDY = -6 + Math.sin(phase) * 1.2;
      pose.farArmDY = -4 + Math.sin(phase + 0.5) * 1.0;
      pose.nearArmDX = 3; pose.farArmDX = 2;
      pose.nearElbowBend = 0.65; pose.farElbowBend = 0.55;
    }

    // Shield arm override — far arm raises to present shield
    if (_shieldVisual) {
      pose.farArmDY = -6 + Math.sin(phase) * 0.8;
      pose.farArmDX = 4; pose.farElbowBend = 0.7;
    }

  } else if (state === "combat_attack") {
    // Attack swing — weapon class determines arc, speed, and weight.
    // 3 frames at 10 FPS = 0.3s total.
    const wc = _weaponVisual?.class ?? null;

    if (wc === "dagger") {
      // Dagger: fast jab — minimal wind-up, straight thrust
      if (frame === 0) {
        pose.breatheY = 0.3;
        pose.nearArmDY = -2; pose.nearArmDX = -1; pose.nearElbowBend = 0.6;
        pose.farArmDY = -3; pose.farArmDX = 2; pose.farElbowBend = 0.4;
        pose.torsoSwayX = -0.5; pose.shoulderTilt = -0.015; pose.clothAmplitude = 0.3;
      } else if (frame === 1) {
        pose.breatheY = -0.3;
        pose.nearArmDY = -5; pose.nearArmDX = 10; pose.nearElbowBend = -0.3;
        pose.farArmDY = -2; pose.farArmDX = 1; pose.farElbowBend = 0.3;
        pose.torsoSwayX = 1.5; pose.torsoDY = -1;
        pose.nearLegDX = 2; pose.nearLegDY = -1;
        pose.shoulderTilt = 0.025; pose.clothAmplitude = 1.2;
      } else {
        pose.nearArmDY = -3; pose.nearArmDX = 4; pose.nearElbowBend = 0.2;
        pose.farArmDY = -2; pose.farArmDX = 2; pose.farElbowBend = 0.4;
        pose.torsoSwayX = 0.3; pose.clothAmplitude = 0.5;
      }
    } else if (wc === "sword_2h") {
      // Greatsword: heavy sweep — big wind-up, both arms swing
      if (frame === 0) {
        pose.breatheY = 1.0;
        pose.nearArmDY = -10; pose.nearArmDX = -4; pose.nearElbowBend = 0.9;
        pose.farArmDY = -8; pose.farArmDX = -3; pose.farElbowBend = 0.85;
        pose.torsoSwayX = -2.0; pose.nearLegDX = -2; pose.farLegDX = 3;
        pose.nearLegDY = -1; pose.shoulderTilt = -0.05; pose.clothAmplitude = 0.8;
      } else if (frame === 1) {
        pose.breatheY = -1.0;
        pose.nearArmDY = -6; pose.nearArmDX = 10; pose.nearElbowBend = -0.15;
        pose.farArmDY = -4; pose.farArmDX = 8; pose.farElbowBend = 0.1;
        pose.torsoSwayX = 3.0; pose.torsoDY = -3;
        pose.nearLegDX = 4; pose.farLegDX = -3; pose.nearLegDY = -3; pose.farLegDY = -1;
        pose.shoulderTilt = 0.06; pose.clothAmplitude = 3.0;
      } else {
        pose.nearArmDY = -4; pose.nearArmDX = 6; pose.nearElbowBend = 0.35;
        pose.farArmDY = -3; pose.farArmDX = 4; pose.farElbowBend = 0.5;
        pose.torsoSwayX = 1.5; pose.torsoDY = -1;
        pose.shoulderTilt = 0.03; pose.clothAmplitude = 1.8;
      }
    } else if (wc === "staff") {
      // Staff: linear thrust — long reach forward push
      if (frame === 0) {
        pose.breatheY = 0.4;
        pose.nearArmDY = -5; pose.nearArmDX = -3; pose.nearElbowBend = 0.85;
        pose.farArmDY = -3; pose.farArmDX = -2; pose.farElbowBend = 0.6;
        pose.torsoSwayX = -0.8; pose.shoulderTilt = -0.02; pose.clothAmplitude = 0.4;
      } else if (frame === 1) {
        pose.breatheY = -0.5;
        pose.nearArmDY = -4; pose.nearArmDX = 12; pose.nearElbowBend = -0.3;
        pose.farArmDY = -2; pose.farArmDX = 6; pose.farElbowBend = 0.15;
        pose.torsoSwayX = 2.5; pose.torsoDY = -2;
        pose.nearLegDX = 3; pose.nearLegDY = -2;
        pose.shoulderTilt = 0.03; pose.clothAmplitude = 1.5;
      } else {
        pose.nearArmDY = -3; pose.nearArmDX = 5; pose.nearElbowBend = 0.3;
        pose.farArmDY = -2; pose.farArmDX = 3; pose.farElbowBend = 0.4;
        pose.torsoSwayX = 0.5; pose.clothAmplitude = 0.8;
      }
    } else if (wc === "axe") {
      // Axe: overhead chop — arm goes HIGH, crashes down
      if (frame === 0) {
        pose.breatheY = 0.8;
        pose.nearArmDY = -14; pose.nearArmDX = -1; pose.nearElbowBend = 0.9;
        pose.farArmDY = -4; pose.farArmDX = 2; pose.farElbowBend = 0.5;
        pose.torsoSwayX = -0.5; pose.farLegDX = 2;
        pose.nearLegDY = -2; pose.shoulderTilt = -0.04; pose.clothAmplitude = 0.6;
      } else if (frame === 1) {
        pose.breatheY = -1.0;
        pose.nearArmDY = 2; pose.nearArmDX = 7; pose.nearElbowBend = -0.1;
        pose.farArmDY = -1; pose.farArmDX = 1; pose.farElbowBend = 0.3;
        pose.torsoSwayX = 1.5; pose.torsoDY = -3;
        pose.nearLegDX = 3; pose.nearLegDY = -2;
        pose.shoulderTilt = 0.05; pose.clothAmplitude = 2.5;
      } else {
        pose.nearArmDY = -2; pose.nearArmDX = 4; pose.nearElbowBend = 0.3;
        pose.farArmDY = -3; pose.farArmDX = 2; pose.farElbowBend = 0.5;
        pose.torsoSwayX = 0.5; pose.clothAmplitude = 1.2;
      }
    } else if (wc === "mace") {
      // Mace: compact overhead smash
      if (frame === 0) {
        pose.breatheY = 0.6;
        pose.nearArmDY = -10; pose.nearArmDX = -1; pose.nearElbowBend = 0.85;
        pose.farArmDY = -4; pose.farArmDX = 3; pose.farElbowBend = 0.5;
        pose.torsoSwayX = -0.8; pose.shoulderTilt = -0.03; pose.clothAmplitude = 0.5;
      } else if (frame === 1) {
        pose.breatheY = -0.8;
        pose.nearArmDY = 0; pose.nearArmDX = 8; pose.nearElbowBend = -0.15;
        pose.farArmDY = -1; pose.farArmDX = 1; pose.farElbowBend = 0.35;
        pose.torsoSwayX = 2.0; pose.torsoDY = -2;
        pose.nearLegDX = 3; pose.nearLegDY = -2;
        pose.shoulderTilt = 0.045; pose.clothAmplitude = 2.2;
      } else {
        pose.nearArmDY = -3; pose.nearArmDX = 4; pose.nearElbowBend = 0.3;
        pose.farArmDY = -3; pose.farArmDX = 2; pose.farElbowBend = 0.5;
        pose.torsoSwayX = 0.6; pose.clothAmplitude = 1.0;
      }
    } else if (wc === "sword_1h") {
      // Sword: diagonal slash — elbow-led arc, blade extends at impact
      if (frame === 0) {
        pose.breatheY = 0.4;
        pose.nearArmDY = -7; pose.nearArmDX = -2; pose.nearElbowBend = 0.75;
        pose.farArmDY = -3; pose.farArmDX = 2; pose.farElbowBend = 0.5;
        pose.torsoSwayX = -1.2; pose.nearLegDX = -1; pose.farLegDX = 2;
        pose.nearLegDY = -1; pose.shoulderTilt = -0.035;
        pose.clothPhase = 0; pose.clothAmplitude = 0.6;
      } else if (frame === 1) {
        pose.breatheY = -0.6;
        pose.nearArmDY = -6; pose.nearArmDX = 9; pose.nearElbowBend = -0.25;
        pose.farArmDY = -2; pose.farArmDX = 1; pose.farElbowBend = 0.35;
        pose.torsoSwayX = 2.2; pose.torsoDY = -2;
        pose.nearLegDX = 3; pose.farLegDX = -2; pose.nearLegDY = -2; pose.farLegDY = -1;
        pose.shoulderTilt = 0.045; pose.clothPhase = Math.PI; pose.clothAmplitude = 2.2;
      } else {
        pose.nearArmDY = -4; pose.nearArmDX = 5; pose.nearElbowBend = 0.3;
        pose.farArmDY = -3; pose.farArmDX = 2; pose.farElbowBend = 0.5;
        pose.torsoSwayX = 0.8; pose.torsoDY = -1; pose.nearLegDX = 2;
        pose.shoulderTilt = 0.02; pose.clothPhase = Math.PI * 1.5; pose.clothAmplitude = 1.0;
      }
    } else {
      // Default (fists): bare-knuckle swing
      if (frame === 0) {
        pose.breatheY = 0.5;
        pose.nearArmDY = -3; pose.nearArmDX = -2; pose.nearElbowBend = 0.8;
        pose.farArmDY = -4; pose.farArmDX = 3; pose.farElbowBend = 0.6;
        pose.torsoSwayX = -1.0; pose.nearLegDX = -1; pose.farLegDX = 2;
        pose.nearLegDY = -1; pose.shoulderTilt = -0.03;
        pose.clothPhase = 0; pose.clothAmplitude = 0.5;
      } else if (frame === 1) {
        pose.breatheY = -0.5;
        pose.nearArmDY = -8; pose.nearArmDX = 8; pose.nearElbowBend = -0.2;
        pose.farArmDY = -2; pose.farArmDX = 1; pose.farElbowBend = 0.4;
        pose.torsoSwayX = 2.0; pose.torsoDY = -2;
        pose.nearLegDX = 3; pose.farLegDX = -2; pose.nearLegDY = -2; pose.farLegDY = -1;
        pose.shoulderTilt = 0.04; pose.clothPhase = Math.PI; pose.clothAmplitude = 2.0;
      } else {
        pose.nearArmDY = -5; pose.nearArmDX = 5; pose.nearElbowBend = 0.3;
        pose.farArmDY = -3; pose.farArmDX = 2; pose.farElbowBend = 0.5;
        pose.torsoSwayX = 0.8; pose.torsoDY = -1; pose.nearLegDX = 2;
        pose.shoulderTilt = 0.02; pose.clothPhase = Math.PI * 1.5; pose.clothAmplitude = 1.0;
      }
    }

    // Shield arm brace during attack — shield stays raised through ALL frames
    if (_shieldVisual) {
      if (frame === 1) {
        // Impact frame: shield braces tighter, absorbing the swing's counter-force
        pose.farArmDY = -7; pose.farArmDX = 5; pose.farElbowBend = 0.8;
      } else {
        pose.farArmDY = -5; pose.farArmDX = 3; pose.farElbowBend = 0.65;
      }
    }
  }

  // ── Stagger overlay — flinch reaction when hit ──
  if (_staggerTimer > 0) {
    // t goes 1→0 as timer decays. We want full flinch at impact (t=1), fade to zero.
    const t = Math.min(_staggerTimer / 0.25, 1.0);
    const flinch = Math.sin(t * Math.PI * 0.5);
    pose.torsoSwayX -= flinch * 3;
    pose.nearArmDY += flinch * 5;
    pose.farArmDY += flinch * 4;
    pose.breatheY += flinch * 2;
    pose.torsoDY += flinch * 1;
  }

  // ── Emotion posture — expression drives subtle body language ──
  // Tension raises shoulders (~8%), tightens arms, narrows stance.
  // Withdrawal curves spine inward, drops head, closes chest.
  switch (expression) {
    case "frown":
      pose.emotionTension = 0.5;
      pose.emotionWithdraw = 0.3;
      pose.breathIntensity *= 0.85;
      break;
    case "surprise":
      pose.emotionTension = 0.3;
      pose.breathIntensity *= 1.15;
      break;
    case "smile":
      pose.emotionTension = -0.15; // relaxed, open posture
      break;
    case "laugh":
      pose.emotionTension = -0.2;
      pose.breathIntensity *= 1.1;
      break;
    case "sleepy":
      pose.emotionWithdraw = 0.4;
      pose.breathIntensity *= 0.7;
      break;
  }

  // Apply tension → shoulder raise + arm tightening
  if (pose.emotionTension > 0) {
    pose.breatheY += pose.emotionTension * 1.5; // shoulders rise
    pose.nearArmDX += pose.emotionTension * 0.8; // arms pull in
    pose.farArmDX += pose.emotionTension * 0.8;
  }

  // Apply withdrawal → spine curve inward, head drop
  if (pose.emotionWithdraw > 0) {
    pose.torsoDY += pose.emotionWithdraw * 1.5;  // slump
    pose.headTiltY += pose.emotionWithdraw * 0.4; // head drops
    pose.torsoSwayX += pose.emotionWithdraw * 0.3; // slight asymmetry
  }

  // ── Shoulder girdle rotation — tied to arm swing differential ──
  // When near arm swings forward (positive nearArmDY), shoulders rotate
  // toward that side. Effect ~±12% of arm swing magnitude.
  const armDiff = pose.nearArmDY - pose.farArmDY;
  pose.shoulderGirdleRot = armDiff * 0.12;

  return pose;
}

// ─── Sprite adapter (legacy bridge) ───────────────────────────────────────────
//
// Wraps old ImageData-returning pixel-art functions so they work with the
// new CharacterLayer.draw() interface.  Scales 48×72 → 96×144 with
// nearest-neighbor interpolation (crisp pixel art at double resolution).

let _adapterCanvas: OffscreenCanvas | null = null;

function spriteAdapter(
  fn: (dir: Direction, frame: number) => ImageData
): CharacterLayer["draw"] {
  return (ctx: DrawCtx, dir: Direction, frame: number) => {
    if (!_adapterCanvas) _adapterCanvas = new OffscreenCanvas(LEGACY_W, LEGACY_H);
    const lctx = _adapterCanvas.getContext("2d")!;
    lctx.clearRect(0, 0, LEGACY_W, LEGACY_H);
    lctx.putImageData(fn(dir, frame), 0, 0);
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(_adapterCanvas, 0, 0, LEGACY_W, LEGACY_H, 0, 0, CHAR_W, CHAR_H);
    ctx.imageSmoothingEnabled = true;
  };
}

// ─── Sprite generation functions ──────────────────────────────────────────────
//
// Each function returns an ImageData (48×72) for one layer.
// The coordinate system: originX/originY is the center-bottom of the torso.
// Head sits on top of torso. Legs extend below.
//
// For side-facing directions we apply a simple horizontal flip.
// For N-facing we show the back of the head and narrower body.
// This gives us convincing 8-direction sprites from minimal code.

function makeImageData(): ImageData {
  return new ImageData(LEGACY_W, LEGACY_H);
}

/** Is this direction facing leftward? We mirror E sprites to get W. */
function isMirrored(dir: Direction): boolean {
  return dir === "w" || dir === "sw" || dir === "nw";
}

/** Is this direction facing fully backward (away from camera)? */
function isFacingAway(dir: Direction): boolean {
  return dir === "n";
}

/** Slight angle toward camera (NE/NW = partial side) */
function isQuarterAngle(dir: Direction): boolean {
  return dir === "ne" || dir === "nw";
}

/** Flip an ImageData horizontally (for mirroring left/right directions) */
function flipH(src: ImageData): ImageData {
  const dst = new ImageData(src.width, src.height);
  for (let y = 0; y < src.height; y++) {
    for (let x = 0; x < src.width; x++) {
      const si = (y * src.width + (src.width - 1 - x)) * 4;
      const di = (y * src.width + x) * 4;
      dst.data[di]     = src.data[si];
      dst.data[di + 1] = src.data[si + 1];
      dst.data[di + 2] = src.data[si + 2];
      dst.data[di + 3] = src.data[si + 3];
    }
  }
  return dst;
}

// ─── Shadow layer ─────────────────────────────────────────────────────────────

function drawShadow(_dir: Direction, _frame: number): ImageData {
  const data = makeImageData();
  // Soft elliptical shadow on the ground plane
  const cx = CHAR_W / 2;
  const cy = CHAR_H - 4;
  const rx = 10;
  const ry = 4;
  for (let y = cy - ry; y <= cy + ry; y++) {
    for (let x = cx - rx; x <= cx + rx; x++) {
      const nx = (x - cx) / rx;
      const ny = (y - cy) / ry;
      const dist = nx * nx + ny * ny;
      if (dist <= 1.0) {
        const alpha = Math.floor((1.0 - dist) * 80);
        const idx = (Math.round(y) * CHAR_W + Math.round(x)) * 4;
        if (idx >= 0 && idx < data.data.length - 3) {
          data.data[idx]     = 0;
          data.data[idx + 1] = 0;
          data.data[idx + 2] = 0;
          data.data[idx + 3] = alpha;
        }
      }
    }
  }
  return data;
}

// ─── Body layer ───────────────────────────────────────────────────────────────

/**
 * Draw the base body shape. This is the skin-colored silhouette:
 * head, neck, torso, arms, legs. Everything else layers on top.
 */
function drawBody(
  dir: Direction,
  _frame: number,
  skinColor: string,
  bodyType: 0 | 1 | 2 | 3
): ImageData {
  const shouldMirror = isMirrored(dir);
  const baseDir = shouldMirror
    ? (dir === "w" ? "e" : dir === "sw" ? "se" : "ne")
    : dir;
  const away = isFacingAway(baseDir);
  const quarter = isQuarterAngle(baseDir);
  const side = baseDir === "e" || baseDir === "se" || baseDir === "sw";

  const cfg = BODY_CONFIGS[bodyType];
  const data = makeImageData();

  const ox = cfg.originX;
  const oy = cfg.originY;

  // Legs: two rectangles below torso
  const legW = Math.max(3, Math.floor(cfg.torsoW * 0.38));
  const legH = 10;
  const legSpread = side ? 1 : 2;

  if (side) {
    // Side view: one leg in front, one behind (narrower)
    fillRect(data, ox + 1, oy, legW + 1, legH, darken(skinColor, 0.82)); // back leg
    fillRect(data, ox + 2, oy, legW, legH, skinColor);                    // front leg
  } else if (away) {
    fillRect(data, ox + legSpread, oy, legW, legH, darken(skinColor, 0.88));
    fillRect(data, ox + cfg.torsoW - legW - legSpread, oy, legW, legH, darken(skinColor, 0.88));
  } else {
    // Front-facing: two legs, slight shading
    fillRect(data, ox + legSpread, oy, legW, legH, skinColor);
    fillRect(data, ox + cfg.torsoW - legW - legSpread, oy, legW, legH, darken(skinColor, 0.92));
  }

  // Torso
  const torsoX = ox;
  const torsoY = oy - cfg.torsoH;
  const torsoShade = away ? darken(skinColor, 0.88) : skinColor;
  const torsoSide  = side ? darken(skinColor, 0.94) : skinColor;
  fillRect(data, torsoX, torsoY, cfg.torsoW, cfg.torsoH, side ? torsoSide : torsoShade);

  // Arms
  const armW = side ? 2 : 2;
  const armH = cfg.torsoH - 2;
  if (side) {
    // Near arm in front of torso
    fillRect(data, ox - armW, torsoY + 1, armW, armH, skinColor);
  } else if (quarter) {
    // One arm visible at side
    fillRect(data, ox - armW, torsoY + 1, armW, armH, skinColor);
    fillRect(data, ox + cfg.torsoW, torsoY + 1, 1, armH - 2, darken(skinColor, 0.9));
  } else {
    // Front/back: both arms at sides
    fillRect(data, ox - armW, torsoY + 1, armW, armH, skinColor);
    fillRect(data, ox + cfg.torsoW, torsoY + 1, armW, armH, darken(skinColor, 0.9));
  }

  // Neck
  const neckW = side ? 2 : 3;
  const neckH = 2;
  const neckX = ox + Math.floor(cfg.torsoW / 2) - Math.floor(neckW / 2);
  const neckY = torsoY - neckH;
  fillRect(data, neckX, neckY, neckW, neckH, skinColor);

  // Head
  const headX = ox + Math.floor(cfg.torsoW / 2) - Math.floor(cfg.headW / 2);
  const headY = neckY - cfg.headH;
  const headShade = away ? darken(skinColor, 0.88) : skinColor;
  fillRect(data, headX, headY, cfg.headW, cfg.headH, headShade);

  // Subtle cheek highlight (front-facing only)
  if (!away && !side) {
    setPixel(data, headX + 1, headY + cfg.headH - 3, lighten(skinColor, 0.12));
    setPixel(data, headX + cfg.headW - 2, headY + cfg.headH - 3, lighten(skinColor, 0.12));
  }

  // Ear dots for side views
  if (side) {
    setPixel(data, headX + cfg.headW - 1, headY + 4, darken(skinColor, 0.85));
  }

  return shouldMirror ? flipH(data) : data;
}

// ─── Skin detail layer ────────────────────────────────────────────────────────

/**
 * Optional freckles, birthmarks, cheek blush — the little things that make
 * a character feel like they've lived a life.
 */
function drawSkinDetail(
  dir: Direction,
  _frame: number,
  skinColor: string,
  detailIndex: number,
  bodyType: 0 | 1 | 2 | 3
): ImageData {
  if (detailIndex < 0) return makeImageData();

  const shouldMirror = isMirrored(dir);
  const away = isFacingAway(dir) || isFacingAway(shouldMirror ? (dir === "w" ? "e" : "ne") : dir);
  const data = makeImageData();

  if (away) return data; // no face detail visible from behind

  const cfg = BODY_CONFIGS[bodyType];
  const headX = cfg.originX + Math.floor(cfg.torsoW / 2) - Math.floor(cfg.headW / 2);
  const headY = cfg.originY - cfg.torsoH - 2 - cfg.headH;

  const freckleColor = darken(skinColor, 0.78);
  const blushColor = "#FFAAAA";

  switch (detailIndex % 4) {
    case 0: // Classic freckles
      setPixel(data, headX + 2, headY + 5, freckleColor);
      setPixel(data, headX + 4, headY + 4, freckleColor);
      setPixel(data, headX + cfg.headW - 3, headY + 5, freckleColor);
      setPixel(data, headX + cfg.headW - 5, headY + 4, freckleColor);
      setPixel(data, headX + 3, headY + 6, freckleColor);
      break;
    case 1: // Blush cheeks
      setPixel(data, headX + 1, headY + cfg.headH - 3, blushColor);
      setPixel(data, headX + 2, headY + cfg.headH - 3, blushColor);
      setPixel(data, headX + cfg.headW - 2, headY + cfg.headH - 3, blushColor);
      setPixel(data, headX + cfg.headW - 3, headY + cfg.headH - 3, blushColor);
      break;
    case 2: // Single beauty mark
      setPixel(data, headX + cfg.headW - 2, headY + 3, freckleColor);
      break;
    case 3: // Nose freckle bridge
      setPixel(data, headX + Math.floor(cfg.headW / 2) - 1, headY + 4, freckleColor);
      setPixel(data, headX + Math.floor(cfg.headW / 2) + 1, headY + 4, freckleColor);
      setPixel(data, headX + Math.floor(cfg.headW / 2), headY + 3, freckleColor);
      break;
  }

  return shouldMirror ? flipH(data) : data;
}

// ─── Eyes + expression layer ──────────────────────────────────────────────────

/**
 * Eyes are the soul of the character. Eight styles, expression state,
 * and blink frames. They look at you. They look away. They spark.
 */
function drawEyes(
  dir: Direction,
  frame: number,
  eyeColor: string,
  eyeStyle: number,
  bodyType: 0 | 1 | 2 | 3,
  isBlinking: boolean
): ImageData {
  const shouldMirror = isMirrored(dir);
  const baseDir = shouldMirror
    ? (dir === "w" ? "e" : dir === "sw" ? "se" : "ne")
    : dir;
  const away = isFacingAway(baseDir);
  const side = baseDir === "e" || baseDir === "se";

  if (away) return makeImageData(); // eyes hidden from behind

  const cfg = BODY_CONFIGS[bodyType];
  const headX = cfg.originX + Math.floor(cfg.torsoW / 2) - Math.floor(cfg.headW / 2);
  const headY = cfg.originY - cfg.torsoH - 2 - cfg.headH;
  const data = makeImageData();

  const pupilDark  = "#101018";
  const white      = "#FFFFFF";
  const lashColor  = "#1C1C1C";
  const highlight  = "#FFFFFF";

  // Eye position relative to head
  const eyeY = headY + 3;
  const eyeStyle4 = eyeStyle % 8;

  if (isBlinking) {
    // Blink: just a horizontal line
    if (side) {
      setPixel(data, headX + 1, eyeY + 2, lashColor);
      setPixel(data, headX + 2, eyeY + 2, lashColor);
    } else {
      const lEyeX = headX + 1;
      const rEyeX = headX + cfg.headW - 3;
      fillRect(data, lEyeX, eyeY + 2, 2, 1, lashColor);
      fillRect(data, rEyeX, eyeY + 2, 2, 1, lashColor);
    }
    return shouldMirror ? flipH(data) : data;
  }

  if (side) {
    // Single eye visible from side
    const eyeX = headX + 1;
    setPixel(data, eyeX, eyeY + 1, lashColor);
    setPixel(data, eyeX + 1, eyeY + 1, lashColor);
    setPixel(data, eyeX, eyeY + 2, eyeColor);
    setPixel(data, eyeX + 1, eyeY + 2, pupilDark);
    return shouldMirror ? flipH(data) : data;
  }

  // Front/quarter: two eyes
  const spacing = Math.floor(cfg.headW * 0.28);
  const lEyeX = headX + spacing;
  const rEyeX = headX + cfg.headW - spacing - 2;
  const eyeRow = eyeY + 2;

  switch (eyeStyle4) {
    case 0: // Round bright eyes
      setPixel(data, lEyeX,     eyeRow - 1, lashColor);
      setPixel(data, lEyeX + 1, eyeRow - 1, lashColor);
      setPixel(data, lEyeX,     eyeRow,     eyeColor);
      setPixel(data, lEyeX + 1, eyeRow,     eyeColor);
      setPixel(data, lEyeX,     eyeRow + 1, pupilDark);
      setPixel(data, lEyeX + 1, eyeRow + 1, white);

      setPixel(data, rEyeX,     eyeRow - 1, lashColor);
      setPixel(data, rEyeX + 1, eyeRow - 1, lashColor);
      setPixel(data, rEyeX,     eyeRow,     eyeColor);
      setPixel(data, rEyeX + 1, eyeRow,     eyeColor);
      setPixel(data, rEyeX,     eyeRow + 1, pupilDark);
      setPixel(data, rEyeX + 1, eyeRow + 1, white);
      break;

    case 1: // Almond / narrow eyes
      setPixel(data, lEyeX - 1, eyeRow, lashColor);
      setPixel(data, lEyeX,     eyeRow, eyeColor);
      setPixel(data, lEyeX + 1, eyeRow, eyeColor);
      setPixel(data, lEyeX + 2, eyeRow, lashColor);
      setPixel(data, lEyeX,     eyeRow - 1, lashColor);
      setPixel(data, lEyeX + 1, eyeRow - 1, lashColor);

      setPixel(data, rEyeX - 1, eyeRow, lashColor);
      setPixel(data, rEyeX,     eyeRow, eyeColor);
      setPixel(data, rEyeX + 1, eyeRow, eyeColor);
      setPixel(data, rEyeX + 2, eyeRow, lashColor);
      setPixel(data, rEyeX,     eyeRow - 1, lashColor);
      setPixel(data, rEyeX + 1, eyeRow - 1, lashColor);
      break;

    case 2: // Wide innocent eyes
      fillRect(data, lEyeX - 1, eyeRow - 1, 4, 4, white);
      setPixel(data, lEyeX,     eyeRow,     eyeColor);
      setPixel(data, lEyeX + 1, eyeRow,     eyeColor);
      setPixel(data, lEyeX,     eyeRow + 1, pupilDark);
      setPixel(data, lEyeX + 2, eyeRow - 1, highlight);

      fillRect(data, rEyeX - 1, eyeRow - 1, 4, 4, white);
      setPixel(data, rEyeX,     eyeRow,     eyeColor);
      setPixel(data, rEyeX + 1, eyeRow,     eyeColor);
      setPixel(data, rEyeX,     eyeRow + 1, pupilDark);
      setPixel(data, rEyeX + 2, eyeRow - 1, highlight);
      break;

    case 3: // Half-lidded cool eyes
      setPixel(data, lEyeX,     eyeRow,     eyeColor);
      setPixel(data, lEyeX + 1, eyeRow,     eyeColor);
      setPixel(data, lEyeX - 1, eyeRow,     lashColor);
      setPixel(data, lEyeX + 2, eyeRow,     lashColor);
      setPixel(data, lEyeX,     eyeRow - 1, lashColor);
      setPixel(data, lEyeX + 1, eyeRow - 1, lashColor);

      setPixel(data, rEyeX,     eyeRow,     eyeColor);
      setPixel(data, rEyeX + 1, eyeRow,     eyeColor);
      setPixel(data, rEyeX - 1, eyeRow,     lashColor);
      setPixel(data, rEyeX + 2, eyeRow,     lashColor);
      setPixel(data, rEyeX,     eyeRow - 1, lashColor);
      setPixel(data, rEyeX + 1, eyeRow - 1, lashColor);
      break;

    case 4: // Sparkle eyes
      setPixel(data, lEyeX,     eyeRow,     eyeColor);
      setPixel(data, lEyeX + 1, eyeRow,     eyeColor);
      setPixel(data, lEyeX,     eyeRow + 1, pupilDark);
      setPixel(data, lEyeX + 1, eyeRow - 1, "#FFFFFF");
      setPixel(data, lEyeX + 2, eyeRow,     lashColor);
      setPixel(data, lEyeX - 1, eyeRow,     lashColor);

      setPixel(data, rEyeX,     eyeRow,     eyeColor);
      setPixel(data, rEyeX + 1, eyeRow,     eyeColor);
      setPixel(data, rEyeX,     eyeRow + 1, pupilDark);
      setPixel(data, rEyeX + 1, eyeRow - 1, "#FFFFFF");
      setPixel(data, rEyeX + 2, eyeRow,     lashColor);
      setPixel(data, rEyeX - 1, eyeRow,     lashColor);
      break;

    case 5: // Tired/gentle droopy eyes
      setPixel(data, lEyeX,     eyeRow,     eyeColor);
      setPixel(data, lEyeX + 1, eyeRow,     eyeColor);
      setPixel(data, lEyeX - 1, eyeRow + 1, lashColor);
      setPixel(data, lEyeX + 2, eyeRow - 1, lashColor);

      setPixel(data, rEyeX,     eyeRow,     eyeColor);
      setPixel(data, rEyeX + 1, eyeRow,     eyeColor);
      setPixel(data, rEyeX - 1, eyeRow + 1, lashColor);
      setPixel(data, rEyeX + 2, eyeRow - 1, lashColor);
      break;

    case 6: // Dot eyes (cute/chibi)
      setPixel(data, lEyeX + 1, eyeRow + 1, pupilDark);
      setPixel(data, rEyeX + 1, eyeRow + 1, pupilDark);
      break;

    case 7: // Heart-shaped happy eyes (closed smile)
      fillRect(data, lEyeX, eyeRow + 1, 2, 1, lashColor);
      setPixel(data, lEyeX - 1, eyeRow, lashColor);
      setPixel(data, lEyeX + 2, eyeRow, lashColor);

      fillRect(data, rEyeX, eyeRow + 1, 2, 1, lashColor);
      setPixel(data, rEyeX - 1, eyeRow, lashColor);
      setPixel(data, rEyeX + 2, eyeRow, lashColor);
      break;
  }

  // Nose — tiny implied shape between the eyes
  if (!side) {
    const noseX = headX + Math.floor(cfg.headW / 2);
    const noseY = eyeRow + 3;
    setPixel(data, noseX, noseY, darken(eyeColor === "#101018" ? "#C07B52" : eyeColor, 0.7));
  }

  void frame; // frame used by caller for blink timing
  return shouldMirror ? flipH(data) : data;
}

// ─── Clothing layers ──────────────────────────────────────────────────────────

function drawBottom(
  dir: Direction,
  _frame: number,
  color: string,
  style: number,
  bodyType: 0 | 1 | 2 | 3
): ImageData {
  const shouldMirror = isMirrored(dir);
  const baseDir = shouldMirror
    ? (dir === "w" ? "e" : dir === "sw" ? "se" : "ne")
    : dir;
  const away = isFacingAway(baseDir);
  const side = baseDir === "e" || baseDir === "se";

  const cfg = BODY_CONFIGS[bodyType];
  const data = makeImageData();
  const ox = cfg.originX;
  const oy = cfg.originY;

  const shade = darken(color, 0.80);
  const legW = Math.max(3, Math.floor(cfg.torsoW * 0.38));
  const legSpread = side ? 1 : 2;

  switch (style % 4) {
    case 0: // Straight leg pants
      if (side) {
        fillRect(data, ox + 2, oy, legW, 8, color);
        fillRect(data, ox + 1, oy, legW + 1, 8, shade); // back leg darker
        fillRect(data, ox + 2, oy, legW, 8, color);
      } else {
        fillRect(data, ox + legSpread, oy, legW, 8, color);
        fillRect(data, ox + cfg.torsoW - legW - legSpread, oy, legW, 8, shade);
        // Belt line
        fillRect(data, ox, oy - cfg.torsoH, cfg.torsoW, 2, shade);
      }
      break;

    case 1: // Shorts / above-knee
      if (side) {
        fillRect(data, ox + 2, oy, legW, 5, color);
        fillRect(data, ox + 1, oy, legW + 1, 5, shade);
        fillRect(data, ox + 2, oy, legW, 5, color);
      } else {
        fillRect(data, ox + legSpread, oy, legW, 5, color);
        fillRect(data, ox + cfg.torsoW - legW - legSpread, oy, legW, 5, shade);
        fillRect(data, ox, oy - cfg.torsoH, cfg.torsoW, 2, shade);
      }
      break;

    case 2: // Skirt / flared
      {
        const skirtY = oy - Math.floor(cfg.torsoH * 0.5);
        const skirtH = Math.floor(cfg.torsoH * 0.5) + 5;
        const skirtW = cfg.torsoW + (side ? 0 : 3);
        const skirtX = side ? ox : ox - 1;
        fillRect(data, skirtX, skirtY, skirtW, skirtH, color);
        // Hem line
        fillRect(data, skirtX, skirtY + skirtH - 1, skirtW, 1, shade);
        // Legs peek below
        if (!away) {
          fillRect(data, ox + legSpread, oy + 3, legW - 1, 4, "#00000000");
        }
      }
      break;

    case 3: // Wide/baggy pants with cuffs
      if (side) {
        fillRect(data, ox + 1, oy, legW + 2, 9, color);
        fillRect(data, ox + 2, oy, legW, 9, darken(color, 0.9));
        fillRect(data, ox + 1, oy + 7, legW + 2, 2, shade);
      } else {
        const wideW = legW + 1;
        fillRect(data, ox + legSpread - 1, oy, wideW, 9, color);
        fillRect(data, ox + cfg.torsoW - wideW - legSpread + 1, oy, wideW, 9, shade);
        fillRect(data, ox + legSpread - 1, oy + 7, wideW, 2, darken(color, 0.7));
        fillRect(data, ox + cfg.torsoW - wideW - legSpread + 1, oy + 7, wideW, 2, darken(shade, 0.7));
      }
      break;
  }

  return shouldMirror ? flipH(data) : data;
}

function drawShoes(
  dir: Direction,
  _frame: number,
  color: string,
  style: number,
  bodyType: 0 | 1 | 2 | 3
): ImageData {
  const shouldMirror = isMirrored(dir);
  const baseDir = shouldMirror
    ? (dir === "w" ? "e" : dir === "sw" ? "se" : "ne")
    : dir;
  const side = baseDir === "e" || baseDir === "se";

  const cfg = BODY_CONFIGS[bodyType];
  const data = makeImageData();
  const ox = cfg.originX;
  const oy = cfg.originY;
  const legW = Math.max(3, Math.floor(cfg.torsoW * 0.38));
  const shade = darken(color, 0.72);
  const legSpread = side ? 1 : 2;

  // Shoes sit at the foot of the legs
  const shoeY = oy + 8;

  switch (style % 4) {
    case 0: // Sneakers / simple shoes
      if (side) {
        fillRect(data, ox, shoeY, legW + 3, 2, color);
        setPixel(data, ox + legW + 3, shoeY + 1, shade);
      } else {
        fillRect(data, ox + legSpread - 1, shoeY, legW + 1, 2, color);
        fillRect(data, ox + cfg.torsoW - legW - legSpread - 1, shoeY, legW + 1, 2, shade);
      }
      break;

    case 1: // Boots / taller
      if (side) {
        fillRect(data, ox, shoeY - 2, legW + 2, 4, color);
        setPixel(data, ox + legW + 2, shoeY, shade);
      } else {
        fillRect(data, ox + legSpread - 1, shoeY - 2, legW + 1, 4, color);
        fillRect(data, ox + cfg.torsoW - legW - legSpread - 1, shoeY - 2, legW + 1, 4, shade);
      }
      break;

    case 2: // Heels / fancy
      if (side) {
        fillRect(data, ox + 1, shoeY, legW + 2, 2, color);
        setPixel(data, ox, shoeY + 1, shade);
        setPixel(data, ox + 1, shoeY + 2, shade);
      } else {
        fillRect(data, ox + legSpread, shoeY, legW, 2, color);
        fillRect(data, ox + cfg.torsoW - legW - legSpread, shoeY, legW, 2, shade);
      }
      break;

    case 3: // Sandals / open
      if (side) {
        setPixel(data, ox + 1, shoeY + 1, color);
        setPixel(data, ox + 2, shoeY + 1, color);
        setPixel(data, ox + 3, shoeY + 1, color);
        setPixel(data, ox + 1, shoeY, shade);
      } else {
        for (let i = 0; i < legW; i++) {
          if (i % 2 === 0) setPixel(data, ox + legSpread + i, shoeY + 1, color);
        }
        for (let i = 0; i < legW; i++) {
          if (i % 2 === 0) setPixel(data, ox + cfg.torsoW - legSpread - legW + i, shoeY + 1, shade);
        }
      }
      break;
  }

  return shouldMirror ? flipH(data) : data;
}

function drawTop(
  dir: Direction,
  _frame: number,
  color: string,
  style: number,
  bodyType: 0 | 1 | 2 | 3
): ImageData {
  const shouldMirror = isMirrored(dir);
  const baseDir = shouldMirror
    ? (dir === "w" ? "e" : dir === "sw" ? "se" : "ne")
    : dir;
  const away = isFacingAway(baseDir);
  const side = baseDir === "e" || baseDir === "se";

  const cfg = BODY_CONFIGS[bodyType];
  const data = makeImageData();
  const ox = cfg.originX;
  const torsoY = cfg.originY - cfg.torsoH;
  const shade = darken(color, 0.80);
  const highlight = lighten(color, 0.12);

  const torsoColor = away ? darken(color, 0.85) : color;
  const torsoSide  = side ? darken(color, 0.88) : color;

  // Main torso coverage
  fillRect(data, ox, torsoY, cfg.torsoW, cfg.torsoH - 1, side ? torsoSide : torsoColor);

  // Arm coverage
  const armW = 2;
  const armH = cfg.torsoH - 2;
  if (side) {
    fillRect(data, ox - armW, torsoY + 1, armW, armH, shade);
  } else if (isQuarterAngle(baseDir)) {
    fillRect(data, ox - armW, torsoY + 1, armW, armH, shade);
    fillRect(data, ox + cfg.torsoW, torsoY + 1, 1, armH - 2, darken(shade, 0.9));
  } else {
    fillRect(data, ox - armW, torsoY + 1, armW, armH, color);
    fillRect(data, ox + cfg.torsoW, torsoY + 1, armW, armH, shade);
  }

  switch (style % 5) {
    case 0: // Plain tee — collar and bottom hem
      fillRect(data, ox + 2, torsoY, cfg.torsoW - 4, 2, shade); // collar
      fillRect(data, ox, torsoY + cfg.torsoH - 2, cfg.torsoW, 1, shade); // hem
      break;

    case 1: // Striped shirt — horizontal stripes
      for (let row = 0; row < cfg.torsoH; row += 3) {
        fillRect(data, ox, torsoY + row, cfg.torsoW, 1, shade);
      }
      break;

    case 2: // Hoodie — front pocket rectangle, hood ridge
      fillRect(data, ox + 2, torsoY + 4, cfg.torsoW - 4, cfg.torsoH - 6, darken(color, 0.92));
      fillRect(data, ox + 3, torsoY + 5, cfg.torsoW - 6, cfg.torsoH - 8, shade);
      fillRect(data, ox + 2, torsoY, cfg.torsoW - 4, 3, highlight); // hood front
      break;

    case 3: // Dress / tunic — extends lower
      {
        const tunicH = cfg.torsoH + 4;
        fillRect(data, ox - 1, torsoY, cfg.torsoW + 2, tunicH, side ? torsoSide : torsoColor);
        fillRect(data, ox - 1, torsoY + tunicH - 1, cfg.torsoW + 2, 1, shade);
      }
      break;

    case 4: // Button-up shirt — center placket
      {
        const midX = ox + Math.floor(cfg.torsoW / 2);
        fillRect(data, midX - 1, torsoY, 2, cfg.torsoH, shade);
        for (let b = 0; b < cfg.torsoH - 2; b += 3) {
          setPixel(data, midX, torsoY + b + 1, "#FFFFFF");
        }
        fillRect(data, ox + 2, torsoY, cfg.torsoW - 4, 2, shade); // collar
      }
      break;
  }

  return shouldMirror ? flipH(data) : data;
}

function drawJacket(
  dir: Direction,
  _frame: number,
  color: string,
  style: number,
  bodyType: 0 | 1 | 2 | 3
): ImageData {
  if (style < 0) return makeImageData();

  const shouldMirror = isMirrored(dir);
  const baseDir = shouldMirror
    ? (dir === "w" ? "e" : dir === "sw" ? "se" : "ne")
    : dir;
  const side = baseDir === "e" || baseDir === "se";

  const cfg = BODY_CONFIGS[bodyType];
  const data = makeImageData();
  const ox = cfg.originX;
  const torsoY = cfg.originY - cfg.torsoH;
  const shade = darken(color, 0.75);
  const lining = lighten(color, 0.20);

  const armW = 2;
  const armH = cfg.torsoH - 2;

  switch (style % 3) {
    case 0: // Open jacket / cardigan — lapels open
      fillRect(data, ox, torsoY, cfg.torsoW, cfg.torsoH, side ? darken(color, 0.9) : color);
      // Open front V
      for (let y = 0; y < cfg.torsoH - 3; y++) {
        const spread = Math.floor(y * 0.4);
        setPixel(data, ox + 2 + spread, torsoY + y, lining);
        setPixel(data, ox + cfg.torsoW - 3 - spread, torsoY + y, lining);
      }
      fillRect(data, ox - armW, torsoY + 1, armW, armH, shade);
      fillRect(data, ox + cfg.torsoW, torsoY + 1, armW, armH, shade);
      break;

    case 1: // Bomber / zip-up
      fillRect(data, ox, torsoY, cfg.torsoW, cfg.torsoH, side ? darken(color, 0.9) : color);
      fillRect(data, ox + Math.floor(cfg.torsoW / 2) - 1, torsoY + 1, 2, cfg.torsoH - 2, shade);
      fillRect(data, ox - armW, torsoY + 1, armW, armH, darken(color, 0.88));
      fillRect(data, ox + cfg.torsoW, torsoY + 1, armW, armH, darken(color, 0.88));
      // Collar band
      fillRect(data, ox + 1, torsoY, cfg.torsoW - 2, 2, shade);
      break;

    case 2: // Long coat / trench
      {
        const coatH = cfg.torsoH + 5;
        fillRect(data, ox, torsoY, cfg.torsoW, coatH, side ? darken(color, 0.9) : color);
        // Belt
        fillRect(data, ox, torsoY + cfg.torsoH - 2, cfg.torsoW, 2, shade);
        fillRect(data, ox - armW, torsoY + 1, armW, armH + 3, darken(color, 0.88));
        fillRect(data, ox + cfg.torsoW, torsoY + 1, armW, armH + 3, darken(shade, 0.9));
        // Front panel line
        const midX = ox + Math.floor(cfg.torsoW / 2);
        fillRect(data, midX - 1, torsoY + 2, 2, coatH - 3, shade);
      }
      break;
  }

  return shouldMirror ? flipH(data) : data;
}

// ─── Hair layers ──────────────────────────────────────────────────────────────

/**
 * Hair is personality made visible. Sixteen styles, front and back passes,
 * with the body layer sandwiched between them for proper depth.
 */
function drawHairBack(
  dir: Direction,
  _frame: number,
  color: string,
  style: number,
  bodyType: 0 | 1 | 2 | 3
): ImageData {
  const shouldMirror = isMirrored(dir);
  const baseDir = shouldMirror
    ? (dir === "w" ? "e" : dir === "sw" ? "se" : "ne")
    : dir;

  const cfg = BODY_CONFIGS[bodyType];
  const data = makeImageData();
  const hx = cfg.originX + Math.floor(cfg.torsoW / 2) - Math.floor(cfg.headW / 2);
  const hy = cfg.originY - cfg.torsoH - 2 - cfg.headH;
  const hw = cfg.headW;
  const hh = cfg.headH;
  const dk = darken(color, 0.78);
  const dk2 = darken(color, 0.65);
  const isBack = baseDir === "n";

  const hairStyle = style % 16;

  switch (hairStyle) {
    case 0: // Crew cut — minimal back
      if (isBack) fillRect(data, hx, hy, hw, hh, dk);
      break;
    case 1: // Side part — slight coverage
      if (isBack) {
        fillRect(data, hx, hy, hw, hh, color);
        setPixel(data, hx + 2, hy, dk);
      }
      break;
    case 2: // Afro — big round back
      fillRect(data, hx - 2, hy - 1, hw + 4, hh + 3, dk);
      fillRect(data, hx - 1, hy - 2, hw + 2, hh + 4, dk);
      break;
    case 3: // Long straight — curtains
      fillRect(data, hx - 1, hy, hw + 2, hh + 10, dk);
      fillRect(data, hx, hy, hw, hh + 12, dk2);
      // Strand highlights
      for (let y = hh; y < hh + 11; y += 3) setPixel(data, hx + Math.floor(hw / 2), hy + y, color);
      break;
    case 4: // Messy/bedhead — tufts behind
      if (isBack) {
        fillRect(data, hx - 1, hy - 1, hw + 2, hh + 2, dk);
        setPixel(data, hx - 1, hy + 2, color);
        setPixel(data, hx + hw, hy + 3, color);
      }
      break;
    case 5: // Wavy long — flowing waves
      fillRect(data, hx - 1, hy, hw + 2, hh + 11, dk);
      for (let y = 0; y < 12; y++) {
        const wave = Math.sin(y * 0.9) > 0 ? 1 : 0;
        setPixel(data, hx - 1 + wave, hy + hh + y - 1, color);
        setPixel(data, hx + hw - wave, hy + hh + y - 1, color);
      }
      break;
    case 6: // Undercut — buzzed sides, no back layer
      if (isBack) fillRect(data, hx, hy + 3, hw, hh - 3, dk2);
      break;
    case 7: // Ponytail — tail behind
      {
        if (isBack) fillRect(data, hx, hy, hw, hh, dk);
        // The actual tail
        const tx = hx + Math.floor(hw / 2) - 1;
        fillRect(data, tx, hy + hh - 2, 3, 10, dk);
        fillRect(data, tx + 1, hy + hh, 1, 10, color); // center highlight
        // Tie
        fillRect(data, tx - 1, hy + hh - 1, 5, 1, dk2);
      }
      break;
    case 8: // Bob — jaw-length back
      fillRect(data, hx - 1, hy, hw + 2, hh + 3, dk);
      // Curved bottom
      setPixel(data, hx - 1, hy + hh + 2, dk2);
      setPixel(data, hx + hw, hy + hh + 2, dk2);
      break;
    case 9: // Long curly — voluminous back
      fillRect(data, hx - 2, hy, hw + 4, hh + 12, dk);
      for (let y = 0; y < 13; y += 2) {
        setPixel(data, hx - 2 + (y % 3), hy + hh + y - 1, color);
        setPixel(data, hx + hw + 1 - (y % 3), hy + hh + y - 1, color);
      }
      break;
    case 10: // Spiky — no back layer
      if (isBack) fillRect(data, hx, hy, hw, hh, dk);
      break;
    case 11: // Side sweep — asymmetric drape
      fillRect(data, hx - 1, hy, hw + 2, hh + 5, dk);
      // Longer on one side
      fillRect(data, hx - 2, hy + 3, 2, hh + 4, dk2);
      break;
    case 12: // Mohawk — strip on back
      if (isBack) {
        fillRect(data, hx + Math.floor(hw / 2) - 1, hy - 2, 3, hh + 2, dk);
      }
      break;
    case 13: // Braids — two rope braids
      {
        if (isBack) fillRect(data, hx, hy, hw, hh, dk);
        const bx1 = hx + 1, bx2 = hx + hw - 2;
        for (let y = 0; y < 12; y++) {
          const c = y % 3 === 0 ? color : dk;
          setPixel(data, bx1 + (y % 2), hy + hh + y - 2, c);
          setPixel(data, bx2 - (y % 2), hy + hh + y - 2, c);
        }
        // Ties at ends
        setPixel(data, bx1, hy + hh + 9, dk2);
        setPixel(data, bx2, hy + hh + 9, dk2);
      }
      break;
    case 14: // Top knot — bun sits on front layer
      if (isBack) fillRect(data, hx, hy, hw, hh, dk);
      break;
    case 15: // Long layered — tiered lengths
      fillRect(data, hx - 2, hy, hw + 4, hh + 13, dk);
      // Layered step highlights
      for (let step = 0; step < 3; step++) {
        const ly = hy + hh + step * 4;
        fillRect(data, hx - 1, ly, hw + 2, 1, color);
      }
      break;
    default:
      if (isBack) fillRect(data, hx, hy, hw, hh, dk);
      break;
  }

  return shouldMirror ? flipH(data) : data;
}

function drawHairFront(
  dir: Direction,
  _frame: number,
  color: string,
  style: number,
  bodyType: 0 | 1 | 2 | 3
): ImageData {
  const shouldMirror = isMirrored(dir);
  const baseDir = shouldMirror
    ? (dir === "w" ? "e" : dir === "sw" ? "se" : "ne")
    : dir;
  const away = isFacingAway(baseDir);
  const side = baseDir === "e" || baseDir === "se";

  const cfg = BODY_CONFIGS[bodyType];
  const data = makeImageData();
  const hx = cfg.originX + Math.floor(cfg.torsoW / 2) - Math.floor(cfg.headW / 2);
  const hy = cfg.originY - cfg.torsoH - 2 - cfg.headH;
  const hw = cfg.headW;
  const hh = cfg.headH;
  const dk = darken(color, 0.78);
  const dk2 = darken(color, 0.65);
  const hi = lighten(color, 0.20);
  const hi2 = lighten(color, 0.35);

  const hairStyle = style % 16;

  if (away) {
    // Back of head — style-specific coverage
    fillRect(data, hx, hy, hw, Math.floor(hh * 0.65), color);
    // Add crown highlight
    setPixel(data, hx + Math.floor(hw / 2), hy + 1, hi);
    return shouldMirror ? flipH(data) : data;
  }

  switch (hairStyle) {
    case 0: // Crew cut — tight, clean buzz
      fillRect(data, hx - 1, hy, hw + 2, 3, color);
      fillRect(data, hx, hy, hw, 4, dk);
      // Subtle fade on sides
      setPixel(data, hx - 1, hy + 2, dk);
      setPixel(data, hx + hw, hy + 2, dk);
      // Crown shine
      setPixel(data, hx + Math.floor(hw / 2), hy, hi2);
      setPixel(data, hx + Math.floor(hw / 2) + 1, hy, hi);
      break;

    case 1: // Side part — elegant sweep
      fillRect(data, hx - 1, hy, hw + 2, 4, color);
      // Part line — distinct gap
      fillRect(data, hx + 3, hy, 1, 2, dk2);
      // Sweep left side heavier
      fillRect(data, hx - 1, hy, 4, 5, color);
      setPixel(data, hx - 1, hy + 4, dk);
      // Right side lighter
      fillRect(data, hx + 4, hy + 1, hw - 3, 2, dk);
      // Highlight
      setPixel(data, hx + 1, hy, hi);
      break;

    case 2: // Afro — full, proud volume
      {
        // Large rounded shape
        for (let dy = -3; dy <= 3; dy++) {
          const rowW = dy <= -2 ? hw : dy <= 0 ? hw + 4 : dy <= 2 ? hw + 3 : hw;
          const ox = hx - Math.floor((rowW - hw) / 2);
          for (let dx = 0; dx < rowW; dx++) {
            const c = ((dx + dy) % 3 === 0) ? dk : ((dx + dy) % 5 === 0) ? hi : color;
            setPixel(data, ox + dx, hy + dy + 1, c);
          }
        }
        // Rounded top highlight
        setPixel(data, hx + Math.floor(hw / 2), hy - 2, hi2);
        setPixel(data, hx + Math.floor(hw / 2) - 1, hy - 2, hi);
      }
      break;

    case 3: // Long straight — sleek face-framing curtains
      fillRect(data, hx - 1, hy, hw + 2, 4, color);
      // Center part
      setPixel(data, hx + Math.floor(hw / 2), hy + 1, dk2);
      // Left curtain
      fillRect(data, hx - 1, hy + 2, 2, hh + 1, dk);
      setPixel(data, hx - 1, hy + 3, color);
      // Right curtain
      fillRect(data, hx + hw - 1, hy + 2, 2, hh + 1, dk);
      setPixel(data, hx + hw, hy + 3, color);
      // Crown highlight
      setPixel(data, hx + 2, hy, hi2);
      setPixel(data, hx + 3, hy, hi);
      break;

    case 4: // Messy / bedhead — charming chaos
      {
        // Base layer
        fillRect(data, hx - 1, hy, hw + 2, 3, color);
        // Individual tufts sticking up at different heights
        const tufts = [
          { x: 0, h: 3 }, { x: 1, h: 2 }, { x: 2, h: 4 }, { x: 3, h: 1 },
          { x: 4, h: 3 }, { x: 5, h: 2 }, { x: 6, h: 4 }, { x: 7, h: 2 },
          { x: 8, h: 3 }, { x: 9, h: 1 },
        ];
        for (const t of tufts) {
          if (t.x >= hw + 2) continue;
          const tx = hx - 1 + t.x;
          const th = t.h;
          const c = t.x % 3 === 0 ? hi : t.x % 2 === 0 ? color : dk;
          fillRect(data, tx, hy - th + 2, 1, th, c);
        }
        // Stray strand
        setPixel(data, hx + hw, hy + 2, dk);
      }
      break;

    case 5: // Wavy long — romantic flowing waves
      fillRect(data, hx - 1, hy, hw + 2, 4, color);
      // Wavy side strands
      for (let y = 0; y < hh + 2; y++) {
        const waveL = Math.sin(y * 0.7) > 0.2 ? 1 : 0;
        const waveR = Math.sin(y * 0.7 + 1.5) > 0.2 ? 1 : 0;
        setPixel(data, hx - 1 - waveL, hy + 2 + y, y % 3 === 0 ? color : dk);
        setPixel(data, hx + hw + waveR, hy + 2 + y, y % 3 === 0 ? color : dk);
      }
      // Crown highlight
      setPixel(data, hx + 2, hy, hi2);
      setPixel(data, hx + 3, hy + 1, hi);
      break;

    case 6: // Undercut — shaved sides, long top flopping forward
      // Short buzz on sides
      if (side) {
        setPixel(data, hx - 1, hy + 4, dk2);
        setPixel(data, hx - 1, hy + 5, dk2);
      } else {
        setPixel(data, hx - 1, hy + 4, dk2);
        setPixel(data, hx + hw, hy + 4, dk2);
      }
      // Long top — thick swooping volume
      fillRect(data, hx, hy - 1, hw, 5, color);
      fillRect(data, hx - 1, hy, hw + 2, 3, color);
      // Directional swoop
      if (side) {
        fillRect(data, hx + hw - 2, hy - 1, 4, 3, color);
      } else {
        fillRect(data, hx - 2, hy, 3, 3, color);
      }
      setPixel(data, hx + Math.floor(hw / 2), hy - 1, hi2);
      break;

    case 7: // Ponytail — pulled back, clean top with visible tail
      fillRect(data, hx - 1, hy, hw + 2, 3, color);
      // Smoothed-back look — less volume than loose styles
      fillRect(data, hx, hy + 2, hw, 1, dk);
      // Highlight stripe
      setPixel(data, hx + Math.floor(hw / 2), hy, hi2);
      setPixel(data, hx + Math.floor(hw / 2) + 1, hy, hi);
      // Hair tie visible from side
      if (side) {
        setPixel(data, hx + hw, hy + 2, dk2);
      }
      break;

    case 8: // Bob — clean jaw-length frame
      fillRect(data, hx - 1, hy, hw + 2, 4, color);
      // Side panels — thick and geometric
      fillRect(data, hx - 1, hy + 2, 2, hh - 1, dk);
      fillRect(data, hx + hw - 1, hy + 2, 2, hh - 1, dk);
      // Inward curve at jaw
      setPixel(data, hx, hy + hh, color);
      setPixel(data, hx + hw - 1, hy + hh, color);
      // Bangs
      fillRect(data, hx + 1, hy + 2, hw - 2, 1, dk);
      // Crown shine
      setPixel(data, hx + Math.floor(hw / 2), hy, hi2);
      break;

    case 9: // Long curly — voluminous ringlets
      {
        fillRect(data, hx - 1, hy, hw + 2, 3, color);
        // Curly fringe
        for (let i = 0; i < hw; i++) {
          const curl = Math.abs(Math.sin(i * 1.5)) * 2;
          fillRect(data, hx + i, hy, 1, 3 + Math.floor(curl), i % 3 === 0 ? hi : color);
        }
        // Wide curly sides
        for (let y = 0; y < hh + 2; y++) {
          const bulge = Math.sin(y * 0.8);
          if (bulge > 0) {
            setPixel(data, hx - 2, hy + 2 + y, y % 2 === 0 ? color : dk);
            setPixel(data, hx + hw + 1, hy + 2 + y, y % 2 === 0 ? color : dk);
          }
          setPixel(data, hx - 1, hy + 2 + y, dk);
          setPixel(data, hx + hw, hy + 2 + y, dk);
        }
        setPixel(data, hx + 2, hy, hi2);
      }
      break;

    case 10: // Spiky / punk — dramatic upward spikes
      {
        // Base
        fillRect(data, hx - 1, hy, hw + 2, 2, color);
        // Spikes — varied heights and widths
        const spikes = [
          { x: 0, h: 4, w: 2 }, { x: 2, h: 6, w: 1 },
          { x: 4, h: 5, w: 2 }, { x: 6, h: 7, w: 1 },
          { x: 8, h: 4, w: 2 },
        ];
        for (const s of spikes) {
          if (s.x >= hw + 2) continue;
          const sx = hx - 1 + s.x;
          for (let y = 0; y < s.h; y++) {
            const w = Math.max(1, s.w - Math.floor(y / 3));
            fillRect(data, sx, hy - y, w, 1, y === s.h - 1 ? hi2 : y % 2 === 0 ? color : hi);
          }
        }
      }
      break;

    case 11: // Side sweep — dramatic asymmetric fringe
      fillRect(data, hx - 1, hy, hw + 3, 4, color);
      // Heavy sweep to one side — covers part of face
      fillRect(data, hx - 2, hy + 1, 4, 4, color);
      fillRect(data, hx - 2, hy + 4, 3, 2, dk);
      // Thinning on other side
      setPixel(data, hx + hw + 1, hy + 2, dk);
      // Highlight on the sweep
      setPixel(data, hx, hy, hi2);
      setPixel(data, hx - 1, hy + 1, hi);
      break;

    case 12: // Mohawk — bold central strip
      {
        const mx = hx + Math.floor(hw / 2);
        // Tall central strip
        fillRect(data, mx - 1, hy - 5, 3, 8, color);
        // Wider at base
        fillRect(data, mx - 2, hy, 5, 3, dk);
        // Tip highlight
        setPixel(data, mx, hy - 5, hi2);
        setPixel(data, mx, hy - 4, hi);
        // Shaved sides
        setPixel(data, hx, hy + 3, dk2);
        setPixel(data, hx + hw - 1, hy + 3, dk2);
      }
      break;

    case 13: // Braids — two visible braids with crown
      fillRect(data, hx - 1, hy, hw + 2, 4, color);
      // Part in center
      setPixel(data, hx + Math.floor(hw / 2), hy + 1, dk2);
      // Braid strands down sides
      for (let y = 3; y < hh + 4; y++) {
        const braid = y % 3 === 0 ? color : y % 3 === 1 ? dk : dk2;
        setPixel(data, hx - 1, hy + y, braid);
        setPixel(data, hx + hw, hy + y, braid);
      }
      // Crown highlight
      setPixel(data, hx + 2, hy, hi);
      break;

    case 14: // Top knot / bun — sleek with round bun
      fillRect(data, hx, hy, hw, 3, color);
      // Smoothed back — minimal fringe
      setPixel(data, hx + Math.floor(hw / 2), hy, hi);
      // Bun on top — round and defined
      {
        const bx = hx + Math.floor(hw / 2) - 2;
        const by = hy - 4;
        fillRect(data, bx, by, 5, 4, dk);
        fillRect(data, bx + 1, by, 3, 5, color);
        // Bun highlight
        setPixel(data, bx + 2, by, hi2);
        setPixel(data, bx + 1, by + 1, hi);
      }
      break;

    case 15: // Long layered — feathered, flowing tiers
      fillRect(data, hx - 1, hy, hw + 2, 4, color);
      // Face-framing layers at different lengths
      // Inner layer (shorter)
      fillRect(data, hx - 1, hy + 3, 2, Math.floor(hh * 0.6), dk);
      fillRect(data, hx + hw - 1, hy + 3, 2, Math.floor(hh * 0.6), dk);
      // Outer layer (longer)
      fillRect(data, hx - 2, hy + 4, 1, hh, dk2);
      fillRect(data, hx + hw + 1, hy + 4, 1, hh, dk2);
      // Wispy ends
      setPixel(data, hx - 2, hy + 4 + hh - 1, color);
      setPixel(data, hx + hw + 1, hy + 4 + hh - 1, color);
      // Crown highlight
      setPixel(data, hx + 2, hy, hi2);
      setPixel(data, hx + 3, hy, hi);
      break;
  }

  return shouldMirror ? flipH(data) : data;
}

// ─── Hat / headwear layer ─────────────────────────────────────────────────────

function drawHat(
  dir: Direction,
  _frame: number,
  color: string,
  style: number,
  bodyType: 0 | 1 | 2 | 3
): ImageData {
  if (style < 0) return makeImageData();

  const shouldMirror = isMirrored(dir);
  const baseDir = shouldMirror
    ? (dir === "w" ? "e" : dir === "sw" ? "se" : "ne")
    : dir;
  const side = baseDir === "e" || baseDir === "se";

  const cfg = BODY_CONFIGS[bodyType];
  const data = makeImageData();
  const headX = cfg.originX + Math.floor(cfg.torsoW / 2) - Math.floor(cfg.headW / 2);
  const headY = cfg.originY - cfg.torsoH - 2 - cfg.headH;
  const shade = darken(color, 0.75);
  const highlight = lighten(color, 0.15);

  switch (style % 8) {
    case 0: // Beanie / knit cap
      fillRect(data, headX, headY - 2, cfg.headW, 5, color);
      // Ribbing
      for (let x = 0; x < cfg.headW; x += 2) {
        setPixel(data, headX + x, headY + 2, shade);
      }
      fillRect(data, headX, headY + 3, cfg.headW, 2, shade); // brim roll
      break;

    case 1: // Baseball cap
      fillRect(data, headX, headY - 1, cfg.headW, 4, color);
      if (side) {
        fillRect(data, headX + cfg.headW, headY + 2, 5, 2, shade); // brim
      } else {
        fillRect(data, headX - 2, headY + 2, cfg.headW + 4, 2, shade); // brim
      }
      setPixel(data, headX + 2, headY, highlight);
      break;

    case 2: // Top hat
      fillRect(data, headX + 1, headY - 6, cfg.headW - 2, 6, color);
      fillRect(data, headX - 1, headY - 1, cfg.headW + 2, 2, shade); // brim
      fillRect(data, headX + 1, headY - 5, 2, 4, shade); // shadow stripe
      break;

    case 3: // Crown
      for (let i = 0; i < cfg.headW; i += 3) {
        fillRect(data, headX + i, headY - 4, 2, 4, color);
      }
      fillRect(data, headX, headY - 1, cfg.headW, 2, shade);
      // Gems
      setPixel(data, headX + 1, headY - 3, "#FF6666");
      setPixel(data, headX + 4, headY - 4, "#66FFFF");
      setPixel(data, headX + 7, headY - 3, "#FFFF66");
      break;

    case 4: // Witch hat
      {
        const tipX = headX + Math.floor(cfg.headW / 2);
        for (let y = 0; y < 8; y++) {
          const w = 1 + Math.floor(y * 0.7);
          fillRect(data, tipX - Math.floor(w / 2), headY - 8 + y, w, 1, color);
        }
        fillRect(data, headX - 2, headY - 1, cfg.headW + 4, 2, shade); // brim
      }
      break;

    case 5: // Beret
      {
        const bcx = headX + Math.floor(cfg.headW / 2);
        const bcy = headY;
        for (let dy = -3; dy <= 1; dy++) {
          for (let dx = -Math.floor(cfg.headW * 0.6); dx <= Math.floor(cfg.headW * 0.6); dx++) {
            if (dx * dx / (cfg.headW * cfg.headW * 0.36) + dy * dy / 9 <= 1) {
              setPixel(data, bcx + dx, bcy + dy, dy === 0 ? shade : color);
            }
          }
        }
        setPixel(data, bcx - 2, bcy - 2, highlight);
      }
      break;

    case 6: // Halo (effect more than hat)
      {
        const hcx = headX + Math.floor(cfg.headW / 2);
        const hy = headY - 4;
        for (let dx = -cfg.headW / 2 - 1; dx <= cfg.headW / 2 + 1; dx++) {
          const px = Math.round(hcx + dx);
          const dist = Math.abs(dx) / (cfg.headW / 2 + 1);
          if (dist > 0.7 && dist < 1.0) {
            setPixel(data, px, hy, "#FFE070");
            setPixel(data, px, hy + 1, "#FFD040");
          }
        }
      }
      break;

    case 7: // Bucket hat
      fillRect(data, headX + 1, headY - 3, cfg.headW - 2, 4, color);
      fillRect(data, headX - 1, headY, cfg.headW + 2, 2, shade);
      break;
  }

  void side;
  return shouldMirror ? flipH(data) : data;
}

// ─── Held item layer ──────────────────────────────────────────────────────────

function drawHeldItem(
  dir: Direction,
  _frame: number,
  _color: string,
  style: number,
  bodyType: 0 | 1 | 2 | 3
): ImageData {
  if (style < 0) return makeImageData();

  const shouldMirror = isMirrored(dir);
  const baseDir = shouldMirror
    ? (dir === "w" ? "e" : dir === "sw" ? "se" : "ne")
    : dir;
  const side = baseDir === "e" || baseDir === "se";

  const cfg = BODY_CONFIGS[bodyType];
  const data = makeImageData();
  const ox = cfg.originX;
  const armY = cfg.originY - cfg.torsoH + Math.floor(cfg.torsoH * 0.6);

  // Items appear in the "hand" position — to the side of the torso
  const itemX = side ? ox + cfg.torsoW + 1 : ox + cfg.torsoW + 1;
  const itemY = armY;

  switch (style % 6) {
    case 0: // Coffee cup / drink
      fillRect(data, itemX, itemY, 4, 5, "#FFFFFF");
      fillRect(data, itemX + 1, itemY + 1, 2, 3, "#C07840");
      setPixel(data, itemX + 1, itemY - 1, "#CCCCCC"); // steam
      break;

    case 1: // Phone / device
      fillRect(data, itemX, itemY, 3, 5, "#222222");
      setPixel(data, itemX + 1, itemY + 1, "#4488FF");
      setPixel(data, itemX + 1, itemY + 2, "#4488FF");
      break;

    case 2: // Book
      fillRect(data, itemX - 1, itemY, 5, 4, "#8B4513");
      fillRect(data, itemX, itemY + 1, 3, 2, "#F5DEB3");
      setPixel(data, itemX, itemY, "#5C2A0A");
      break;

    case 3: // Flower
      setPixel(data, itemX + 1, itemY + 4, "#3A8C3A"); // stem
      setPixel(data, itemX + 1, itemY + 3, "#3A8C3A");
      setPixel(data, itemX + 1, itemY + 2, "#3A8C3A");
      setPixel(data, itemX + 1, itemY + 1, "#FF9999"); // petals
      setPixel(data, itemX,     itemY + 1, "#FF9999");
      setPixel(data, itemX + 2, itemY + 1, "#FF9999");
      setPixel(data, itemX + 1, itemY,     "#FF9999");
      setPixel(data, itemX + 1, itemY + 1, "#FFE4E1"); // center
      break;

    case 4: // Lantern / light
      fillRect(data, itemX, itemY + 1, 4, 4, "#888800");
      fillRect(data, itemX + 1, itemY + 2, 2, 2, "#FFFF80");
      setPixel(data, itemX + 1, itemY, "#888800"); // handle
      break;

    case 5: // Gift box
      fillRect(data, itemX, itemY + 1, 5, 4, "#CC2222");
      fillRect(data, itemX + 2, itemY, 1, 5, "#FFD700"); // ribbon
      fillRect(data, itemX, itemY + 2, 5, 1, "#FFD700"); // ribbon
      break;
  }

  void side;
  return shouldMirror ? flipH(data) : data;
}

// ─── Aura/effect layer ────────────────────────────────────────────────────────

function drawAura(
  _dir: Direction,
  frame: number,
  _color: string,
  _style: number
): ImageData {
  // Aura effects are subtle — a gentle glow or sparkle around the character.
  // This is a placeholder for future VFX expansion.
  void frame;
  return makeImageData();
}

// ─── Native canvas2D drawing ──────────────────────────────────────────────────
//
// These draw directly onto the composite canvas at full 96×144 resolution.
// Bezier curves, gradients, smooth shading — characters are people, not grids.

function drawShadowV2(ctx: DrawCtx, dir: Direction, _frame: number, bodyType: 0 | 1 | 2 | 3): void {
  const a = BA[bodyType];
  const [cat] = dirCat(dir);
  // Shadow foreshortens with viewing angle — narrower from side, wider front/back
  const n = getNarrow(cat);
  const baseRx = a.shW * 0.9;
  ctx.fillStyle = "rgba(0,0,0,0.16)";
  ctx.beginPath();
  ctx.ellipse(a.cx, a.footY + 5, baseRx * n, 7, 0, 0, Math.PI * 2);
  ctx.fill();
}

/** Draw expression-aware mouth. `scale` shrinks for side views (0.6) vs front (1.0). */
function drawMouth(
  ctx: DrawCtx, cx: number, mouthY: number,
  skinColor: string, expr: Expression, scale: number
): void {
  if (expr === "smile" || expr === "laugh") {
    const mw = (expr === "laugh" ? 4.5 : 3.5) * scale;
    const lift = expr === "laugh" ? 1.5 : 0.8;
    ctx.strokeStyle = shade(skinColor, 0.70);
    ctx.lineWidth = expr === "laugh" ? 1.0 : 0.8;
    ctx.beginPath();
    ctx.moveTo(cx - mw, mouthY);
    ctx.quadraticCurveTo(cx, mouthY + lift + 1.5 * scale, cx + mw, mouthY);
    ctx.stroke();
    if (expr === "laugh") {
      ctx.fillStyle = "#FFFFFF";
      ctx.beginPath();
      ctx.ellipse(cx, mouthY + 0.8, 2.8 * scale, 1.4, 0, 0, Math.PI);
      ctx.fill();
      ctx.fillStyle = shade(skinColor, 0.45);
      ctx.beginPath();
      ctx.ellipse(cx, mouthY + 1.5, 2 * scale, 1, 0, 0, Math.PI);
      ctx.fill();
    }
  } else if (expr === "frown") {
    ctx.strokeStyle = shade(skinColor, 0.72);
    ctx.lineWidth = 0.9;
    ctx.beginPath();
    ctx.moveTo(cx - 3 * scale, mouthY + 1.5);
    ctx.quadraticCurveTo(cx, mouthY - 0.5, cx + 3 * scale, mouthY + 1.5);
    ctx.stroke();
  } else if (expr === "surprise") {
    ctx.fillStyle = shade(skinColor, 0.45);
    ctx.beginPath();
    ctx.ellipse(cx, mouthY + 0.5, 2.5 * scale, 3 * scale, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = shade(skinColor, 0.65);
    ctx.lineWidth = 0.7;
    ctx.beginPath();
    ctx.ellipse(cx, mouthY + 0.5, 2.5 * scale, 3 * scale, 0, 0, Math.PI * 2);
    ctx.stroke();
  } else if (expr === "sleepy") {
    ctx.strokeStyle = shade(skinColor, 0.78);
    ctx.lineWidth = 0.7;
    ctx.beginPath();
    ctx.moveTo(cx - 2 * scale, mouthY + 0.5);
    ctx.lineTo(cx + 2 * scale, mouthY + 0.5);
    ctx.stroke();
  } else {
    ctx.strokeStyle = shade(skinColor, 0.75);
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.moveTo(cx - 3 * scale, mouthY);
    ctx.quadraticCurveTo(cx, mouthY + 1 * scale, cx + 3 * scale, mouthY);
    ctx.stroke();
  }
}

function drawBodyV2(ctx: DrawCtx, dir: Direction, _frame: number, skinColor: string, bodyType: 0 | 1 | 2 | 3): void {
  const a = BA[bodyType];
  const dk = shade(skinColor, 0.82);
  const hi = shade(skinColor, 1.08);
  const warm = shade(skinColor, 0.92);

  withFlip(ctx, dir, (cat) => {
    const isFront = cat === "front" || cat === "qfront";
    const isBack  = cat === "back"  || cat === "qback";
    const n       = getNarrow(cat);
    const sk      = isBack ? dk : skinColor;
    const sk2     = isBack ? shade(dk, 0.92) : dk;

    // ── Shape-aware proportions (archetype-resolved, per-region projection) ──
    const bs = _bodyShape;
    const sw = projectW(cat, bs.shoulderW, bs.shoulderD);
    const ww = projectW(cat, bs.waistW, bs.waistD);
    const hw = projectW(cat, bs.hipW, bs.hipD);
    // Leg gap: gender provides natural baseline, slider fine-tunes
    // Slider 0-4 (2=neutral): each unit = ±15% gap change
    // Gender: feminine wider pelvis, masculine narrower
    const genderGap = _anatomy.gender === "feminine" ? 1.3
                     : _anatomy.gender === "masculine" ? 0.7 : 1.0;
    const lgSlider = 1.0 + ((_legGapSlider - 2) * 0.15);
    const lg = a.legGap * n * genderGap * lgSlider;
    // Side/quarter views: reduce limb widths to prevent blob overlap
    const isSide = cat === "side";
    const isNarrow = n < 0.9;
    const dirScale = isSide ? 0.7 : isNarrow ? 0.85 : 1.0;
    const limbScale = dirScale * bs.legMass;
    const thighW = 9.5 * limbScale;   // was 7.5 — thicker, realistic thighs
    const calfW = 7 * limbScale;      // was 6 — fuller calves
    const armScale = dirScale * bs.armMass;
    const upperArmW = 6.5 * armScale;   // was 5.5 — fuller upper arms
    const forearmW = 5 * armScale;       // was 4.5 — slightly thicker forearms
    const handR = 3.5 * bs.scale;
    const footRx = 5.5 * bs.scale;  // was 4.5 — bigger, more natural feet

    // ── Animation offsets (walk, breathe, sit) ──
    const wp = _animPose;
    const bY = -wp.breatheY;  // breathing lift (negative Y = up)

    // ── Organic arm helper — filled bezier with deltoid, bicep, elbow, forearm taper ──
    const drawOrganicArm = (
      startX: number, startY: number,
      elbX: number, elbY: number,
      wrstX: number, wrstY: number,
      handY: number, color: string
    ) => {
      const uaHW = upperArmW * 0.5;
      const deltW = uaHW * 1.1;       // deltoid cap wider than shaft
      const bicepW = uaHW * 1.05;     // bicep peak
      const elbHW = uaHW * 0.82;      // elbow narrows
      const faHW = forearmW * 0.5;
      const brHW = faHW * 1.06;       // brachioradialis swell
      const wrstHW = faHW * 0.7;      // wrist tapers
      // Key mid-points along arm centerline
      const bicepX = startX + (elbX - startX) * 0.4;
      const bicepY = startY + (elbY - startY) * 0.4;
      const brX = elbX + (wrstX - elbX) * 0.3;
      const brY = elbY + (wrstY - elbY) * 0.3;

      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.moveTo(startX + deltW, startY);
      // Right: deltoid → bicep → elbow
      ctx.bezierCurveTo(
        bicepX + bicepW + 0.3, bicepY - 2,
        elbX + elbHW + 0.5, elbY - 5,
        elbX + elbHW, elbY);
      // Right: elbow → brachioradialis → wrist
      ctx.bezierCurveTo(
        elbX + elbHW + 0.2, elbY + 3,
        brX + brHW, brY,
        wrstX + wrstHW, wrstY);
      ctx.lineTo(wrstX - wrstHW, wrstY);
      // Left: wrist → forearm → elbow
      ctx.bezierCurveTo(
        brX - brHW, brY,
        elbX - elbHW - 0.2, elbY + 3,
        elbX - elbHW, elbY);
      // Left: elbow → bicep → shoulder
      ctx.bezierCurveTo(
        elbX - elbHW - 0.5, elbY - 5,
        bicepX - bicepW - 0.3, bicepY - 2,
        startX - deltW, startY);
      ctx.closePath();
      ctx.fill();
      // Elbow crease
      ctx.strokeStyle = "rgba(0,0,0,0.035)";
      ctx.lineWidth = 0.4;
      ctx.beginPath();
      ctx.moveTo(elbX - elbHW + 0.5, elbY);
      ctx.quadraticCurveTo(elbX, elbY + 0.8, elbX + elbHW - 0.5, elbY);
      ctx.stroke();
      // Hand
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.ellipse(wrstX, handY, handR * dirScale, 3 * bs.scale, 0, 0, Math.PI * 2);
      ctx.fill();
    };

    // ── Far arm (behind torso): organic shape ──
    const armHang = bs.shoulderW + 5 * bs.armMass;
    const farArmX = a.cx + armHang * n;
    const farArmDY = wp.farArmDY;
    const farArmDX = wp.farArmDX;
    const farBend = wp.farElbowBend;
    const farElbowX = a.cx + (armHang - 2) * n - farArmDX * 0.4;
    const farWristX = a.cx + (armHang - 4) * n - farArmDX;
    const farElbowY = a.elbowY + farArmDY * 0.5 + farBend * 6;
    drawOrganicArm(farArmX, a.shY + 2, farElbowX, farElbowY, farWristX, a.wristY + farArmDY, a.handY + farArmDY, sk2);
    // Shoulder cap — fills gap between torso edge and arm deltoid (far side)
    {
      const shEdge = a.cx + sw;
      const deltEdge = farArmX - upperArmW * 0.5 * 1.1;
      if (deltEdge > shEdge + 0.5) {
        ctx.fillStyle = sk2;
        ctx.fillRect(shEdge, a.shY, deltEdge - shEdge, 4);
      }
    }

    // ── Legs — organic bezier shapes that flare from hip and taper naturally ──
    // No more limbStroke sticks — legs are filled paths with:
    // - Upper thigh flaring from hip width (gluteal fold)
    // - Mid-thigh volume (quadriceps/hamstring mass)
    // - Knee narrowing
    // - Calf muscle swell
    // - Ankle taper
    const farLegOff = isSide ? 1 : 0;
    // Thigh should start from near the hip contour width, not from legGap
    // This creates the natural transition from wide hips to thigh
    // Thigh outer at hip = torso hip width — seamless torso→leg transition (no gap/oval)
    const hipAttachW = Math.max(thighW * 0.52, hw - (lg + 2));
    const innerThighW = lg + 1;       // inner thigh edge stays near legGap
    const kneeHW = thighW * 0.45;  // knee is narrower than mid-thigh
    const calfPeak = 0.35;         // calf muscle peaks at 35% of shin length
    const calfHW = calfW * 0.48;   // calf half-width
    const ankleHW = calfW * 0.32;  // ankle is narrower than calf

    /** Draw one organic leg — filled bezier shape from hip to ankle */
    const drawOrganicLeg = (side: number, color: string, dx: number, dy: number, legOff: number) => {
      const sgn = side;  // -1 = near (left), +1 = far (right)
      const cx = a.cx + sgn * (lg + 2) - (sgn > 0 ? legOff : 0);
      // Walk offsets applied progressively down the leg
      const kneeDX = dx * 0.3;
      const kneeDY = dy * 0.4;
      const ankDX = dx;
      const ankDY = dy;
      // Key points
      const hipX = cx;
      const hipOuter = cx + sgn * hipAttachW;
      const hipInner = a.cx + sgn * innerThighW;
      const kneeX = cx + kneeDX;
      const kneeY = a.kneeY + kneeDY;
      const ankX = cx + ankDX;
      const ankY = a.ankleY + ankDY;
      // Mid-thigh swell (quadriceps volume)
      const midThighY = a.hipY + (kneeY - a.hipY) * 0.45;
      const midThighOuter = kneeX + sgn * (thighW * 0.52) + kneeDX * 0.15;
      const midThighInner = kneeX - sgn * (thighW * 0.35) + kneeDX * 0.1;
      // Calf muscle swell
      const calfSwellY = kneeY + (ankY - kneeY) * calfPeak;
      const calfSwellOuter = ankX + sgn * calfHW + ankDX * 0.15;

      ctx.fillStyle = color;
      ctx.beginPath();
      // Start at hip outer edge — transitions from torso hip contour
      ctx.moveTo(hipOuter, a.hipY);
      // Outer edge: hip → mid-thigh swell → knee
      ctx.bezierCurveTo(
        hipOuter + sgn * 0.5, a.hipY + 6,                // upper thigh — barely narrows from hip
        midThighOuter + sgn * 0.5, midThighY - 4,         // quad volume
        kneeX + sgn * kneeHW, kneeY);                     // knee narrows
      // Outer calf: knee → calf swell → ankle
      ctx.bezierCurveTo(
        kneeX + sgn * (kneeHW + 0.5), kneeY + 4,         // just below knee
        calfSwellOuter, calfSwellY,                        // gastrocnemius peak
        ankX + sgn * ankleHW, ankY);                       // ankle taper
      // Ankle bottom
      ctx.lineTo(ankX - sgn * ankleHW, ankY);
      // Inner calf: ankle → calf inner → knee
      ctx.bezierCurveTo(
        ankX - sgn * (ankleHW - 0.5), calfSwellY + 2,     // inner calf
        kneeX - sgn * (kneeHW - 1), kneeY + 3,            // inner knee
        kneeX - sgn * kneeHW, kneeY);                     // knee inner
      // Inner thigh: knee → inner thigh → hip
      ctx.bezierCurveTo(
        midThighInner, midThighY,                          // inner thigh
        hipInner + sgn * 0.5, a.hipY + 5,                 // adductor taper
        hipInner, a.hipY);                                 // hip inner
      ctx.closePath();
      ctx.fill();

      // Knee definition — subtle horizontal shadow
      ctx.strokeStyle = "rgba(0,0,0,0.04)";
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(kneeX - sgn * (kneeHW - 1), kneeY - 1);
      ctx.quadraticCurveTo(kneeX, kneeY + 0.5, kneeX + sgn * (kneeHW - 1), kneeY - 1);
      ctx.stroke();

      // Inner thigh shadow — adductor line
      if (!isSide) {
        ctx.strokeStyle = "rgba(0,0,0,0.03)";
        ctx.lineWidth = 0.4;
        ctx.beginPath();
        ctx.moveTo(hipInner + sgn * 1, a.hipY + 3);
        ctx.quadraticCurveTo(midThighInner + sgn * 0.5, midThighY, kneeX - sgn * (kneeHW - 2), kneeY - 2);
        ctx.stroke();
      }

      // Foot
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.ellipse(ankX, a.footY - 1 + ankDY - dy + dy, footRx * dirScale, 2.5 * bs.scale, 0, 0, Math.PI * 2);
      ctx.fill();
    };

    if (wp.isSitting) {
      // Seated legs: organic bezier shapes — thighs project forward, calves hang
      const sitHipY = a.hipY + wp.torsoDY * 0.5;
      const thighLen = a.kneeY - a.hipY;
      const thighFwd = thighLen * 0.6;
      const thighDrop = thighLen * 0.35;
      const calfLen = a.ankleY - a.kneeY;
      const sitThighHW = thighW * 0.55; // half-width, wider when foreshortened
      const sitKneeHW = sitThighHW * 0.85;
      const sitCalfHW = calfW * 0.48;
      const sitAnkleHW = calfW * 0.32;

      /** Draw one organic seated leg — filled bezier thigh + hanging calf */
      const drawSeatedLeg = (hipStartX: number, kneeX: number, kneeY: number, color: string) => {
        const calfEndY = kneeY + calfLen * 0.7;
        const midThighY = sitHipY + (kneeY - sitHipY) * 0.45;
        const calfSwellY = kneeY + (calfEndY - kneeY) * 0.35;
        // Thigh — foreshortened, projecting forward from hip
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.moveTo(hipStartX - sitThighHW, sitHipY);
        ctx.bezierCurveTo(
          hipStartX - sitThighHW + 0.5, midThighY - 2,
          kneeX - sitKneeHW - 0.3, kneeY - 4,
          kneeX - sitKneeHW, kneeY);
        ctx.lineTo(kneeX + sitKneeHW, kneeY);
        ctx.bezierCurveTo(
          kneeX + sitKneeHW + 0.3, kneeY - 4,
          hipStartX + sitThighHW - 0.5, midThighY - 2,
          hipStartX + sitThighHW, sitHipY);
        ctx.closePath();
        ctx.fill();
        // Calf — hanging vertically from knee
        ctx.beginPath();
        ctx.moveTo(kneeX - sitKneeHW, kneeY);
        ctx.bezierCurveTo(
          kneeX - sitKneeHW - 0.3, calfSwellY,
          kneeX - sitCalfHW, calfSwellY + 2,
          kneeX - sitAnkleHW + 1, calfEndY);
        ctx.lineTo(kneeX + sitAnkleHW + 1, calfEndY);
        ctx.bezierCurveTo(
          kneeX + sitCalfHW, calfSwellY + 2,
          kneeX + sitKneeHW + 0.3, calfSwellY,
          kneeX + sitKneeHW, kneeY);
        ctx.closePath();
        ctx.fill();
        // Knee crease
        ctx.strokeStyle = "rgba(0,0,0,0.04)";
        ctx.lineWidth = 0.4;
        ctx.beginPath();
        ctx.moveTo(kneeX - sitKneeHW + 1, kneeY);
        ctx.quadraticCurveTo(kneeX, kneeY + 1, kneeX + sitKneeHW - 1, kneeY);
        ctx.stroke();
        // Foot
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.ellipse(kneeX + 1, calfEndY + 2, footRx * dirScale, 2.5 * bs.scale, 0, 0, Math.PI * 2);
        ctx.fill();
      };

      // Far leg (behind torso)
      const fKneeX = a.cx + lg + 2 - farLegOff + thighFwd * 0.4;
      const fKneeY = sitHipY + thighDrop;
      drawSeatedLeg(a.cx + lg + 3 - farLegOff, fKneeX, fKneeY, sk2);
      // Near leg (in front)
      const nKneeX = a.cx - lg - 2 + thighFwd * 0.4;
      const nKneeY = sitHipY + thighDrop;
      drawSeatedLeg(a.cx - lg - 3, nKneeX, nKneeY, sk);
    } else {
      // Standing/walking — organic bezier legs
      // Far leg (behind torso)
      drawOrganicLeg(1, sk2, wp.farLegDX, wp.farLegDY, farLegOff);
      // Near leg (in front)
      drawOrganicLeg(-1, sk, wp.nearLegDX, wp.nearLegDY, 0);
    }

    // ── Torso — shape-aware bezier silhouette ──
    // Every direction gets explicit handling: front, qfront, side, qback, back.
    // Anatomy-driven curves: breastSize/buttSize/gender physically change the silhouette.
    // Breast bounce from walk physics shifts chest contour vertically.
    const hasBreasts = (_anatomy.gender === "feminine" || _anatomy.gender === "androgynous") && _anatomy.breastSize > 0;
    const isFem = _anatomy.gender === "feminine";
    const brstNorm = _anatomy.breastSize / 4; // 0-1
    const buttNorm = _anatomy.buttSize / 4;   // 0-1
    const breastExtra = hasBreasts ? brstNorm * 3.0 : 0;
    const buttExtra = buttNorm * 2.0;
    // Breast bounce offset — applied to chest Y position
    const bbY = hasBreasts ? wp.breastBounceY * (0.4 + brstNorm * 0.6) : 0;
    // Feminine waist cinch: subtle inward pull at waist (additive to resolveBodyShape)
    const waistCinch = isFem ? (0.8 + brstNorm * 0.3 + buttNorm * 0.3) * n : 0;

    // Direction-specific projection amounts
    let chestB = 0;  // frontal chest widening
    let buttB = 0;   // rear hip widening
    let sideBreastFwd = 0; // side-view forward breast protrusion
    let sideButtBack = 0;  // side-view backward butt protrusion

    if (cat === "front") {
      chestB = ((bs.chestProjection - 1.0) * 4.0 + breastExtra) * n;
    } else if (cat === "qfront") {
      // 3/4 front: chest swell visible but foreshortened, slight butt peek on far side
      chestB = ((bs.chestProjection - 1.0) * 3.0 + breastExtra * 0.7) * n;
      sideBreastFwd = hasBreasts ? brstNorm * 1.8 : 0;
      sideButtBack = buttNorm * 1.0;
    } else if (cat === "side") {
      // Pure side: full S-curve profile — breasts forward, butt backward
      sideBreastFwd = hasBreasts ? brstNorm * 3.5 : (bs.chestProjection - 0.8) * 1.5;
      sideButtBack = buttNorm * 3.0 + (bs.buttProjection - 0.8) * 1.5;
    } else if (cat === "qback") {
      // 3/4 rear: butt prominent, breast side-peek visible
      buttB = ((bs.buttProjection - 1.0) * 3.0 + buttExtra * 0.7) * n;
      sideBreastFwd = hasBreasts ? brstNorm * 1.2 : 0; // breast side-peek
      sideButtBack = buttNorm * 1.8;
    } else { // "back"
      buttB = ((bs.buttProjection - 1.0) * 3.5 + buttExtra) * n;
    }

    const chestY = a.shY + (a.waistY - a.shY) * (0.36 + (hasBreasts ? brstNorm * 0.01 : 0)) + bbY;
    const cw = sw + chestB;   // chest half-width at peak
    // Cinched waist width
    const wwCinch = ww - waistCinch;

    // Waist→hip bezier control ratios — anatomically driven
    // Masculine: nearly straight oblique line (minimal curve departure/arrival)
    // Feminine: obliques straighter at waist, iliac crest angular at hip
    // The key: real hips are NOT circular. The iliac crest is angular.
    const wDepart = isFem ? 0.15 : _anatomy.gender === "masculine" ? 0.10 : 0.12;  // vertical departure tightness at waist
    const hArrive = isFem ? 0.50 : _anatomy.gender === "masculine" ? 0.55 : 0.50;  // horizontal approach at hip

    // Shoulder girdle rotation — near shoulder drops, far rises during walk
    const sgr = wp.shoulderGirdleRot;
    const nearShDY = sgr * 2;   // ±2px vertical offset
    const farShDY  = -sgr * 2;
    const nearShDX = sgr * 1;   // ±1px horizontal shift
    const farShDX  = -sgr * 1;

    const tGrad = ctx.createLinearGradient(a.cx - sw, a.shY, a.cx + sw, a.hipY);
    tGrad.addColorStop(0, shade(sk, 0.96));
    tGrad.addColorStop(0.25, sk);
    tGrad.addColorStop(0.6, sk);
    tGrad.addColorStop(0.85, warm);
    tGrad.addColorStop(1, sk2);
    ctx.fillStyle = tGrad;

    // Build torso path per direction category
    ctx.beginPath();
    if (cat === "side") {
      // ── Side view: full S-curve silhouette ──
      // Near edge = character's back, far edge = character's front
      const backProj = sideButtBack;
      const frontProj = sideBreastFwd;
      // Back edge: shoulder → waist concavity → butt projection → hip
      ctx.moveTo(a.cx - sw - backProj * 0.15 + nearShDX, a.shY + nearShDY);
      ctx.bezierCurveTo(
        a.cx - sw - 0.5, a.shY + (a.waistY - a.shY) * 0.4,  // upper back straight
        a.cx - wwCinch - 0.5, a.waistY - 3,                    // cinch inward at waist
        a.cx - wwCinch, a.waistY);                              // waist midpoint
      ctx.bezierCurveTo(
        a.cx - wwCinch - backProj * 0.4, a.waistY + 5,         // flare out to butt
        a.cx - hw - backProj * 0.8, a.hipY - 3,                // butt peak
        a.cx - hw - backProj * 0.5, a.hipY);                   // hip end
      // Bottom hip line
      ctx.lineTo(a.cx + hw + frontProj * 0.3, a.hipY);
      // Front edge: hip → waist → chest projection → shoulder
      ctx.bezierCurveTo(
        a.cx + wwCinch + frontProj * 0.2, a.waistY + 3,        // hip flares in
        a.cx + wwCinch, a.waistY + 1,                           // waist cinch
        a.cx + wwCinch, a.waistY);                              // waist
      ctx.bezierCurveTo(
        a.cx + wwCinch + frontProj * 0.35, chestY + 4,         // chest swells out
        a.cx + sw + frontProj * 0.7, chestY,                    // breast peak
        a.cx + sw + frontProj * 0.3 + farShDX, a.shY + farShDY); // shoulder
    } else if (cat === "qfront") {
      // ── Quarter-front: waist as path point ──
      const shToW = a.waistY - a.shY;
      const wToH = a.hipY - a.waistY;
      ctx.moveTo(a.cx - sw - sideButtBack * 0.1 + nearShDX, a.shY + nearShDY);
      // Near side: shoulder → waist → hip
      ctx.bezierCurveTo(a.cx - sw, a.shY + shToW * 0.5, a.cx - wwCinch, a.waistY - shToW * 0.3, a.cx - wwCinch, a.waistY);
      ctx.bezierCurveTo(a.cx - wwCinch, a.waistY + wToH * wDepart, a.cx - hw - sideButtBack * 0.3, a.hipY - wToH * hArrive, a.cx - hw - sideButtBack * 0.3, a.hipY);
      ctx.lineTo(a.cx + hw + sideBreastFwd * 0.15, a.hipY);
      // Far side: hip → waist → shoulder
      if (chestB > 0.5) {
        ctx.bezierCurveTo(a.cx + hw, a.hipY - wToH * hArrive, a.cx + wwCinch, a.waistY + wToH * wDepart, a.cx + wwCinch, a.waistY);
        ctx.bezierCurveTo(a.cx + wwCinch, a.waistY - shToW * 0.25, a.cx + cw, chestY + shToW * 0.4, a.cx + cw, chestY);
        ctx.quadraticCurveTo(a.cx + cw + 0.5, chestY - 2, a.cx + sw + farShDX, a.shY + farShDY);
      } else {
        ctx.bezierCurveTo(a.cx + hw, a.hipY - wToH * hArrive, a.cx + wwCinch, a.waistY + wToH * wDepart, a.cx + wwCinch, a.waistY);
        ctx.bezierCurveTo(a.cx + wwCinch, a.waistY - shToW * 0.3, a.cx + sw, a.shY + shToW * 0.5, a.cx + sw + farShDX, a.shY + farShDY);
      }
    } else if (cat === "qback") {
      // ── Quarter-back: waist as path point ──
      const shToW = a.waistY - a.shY;
      const wToH = a.hipY - a.waistY;
      ctx.moveTo(a.cx - sw - sideBreastFwd * 0.1 + nearShDX, a.shY + nearShDY);
      // Near side: shoulder → waist → hip
      if (sideBreastFwd > 0.5) {
        ctx.bezierCurveTo(a.cx - sw - sideBreastFwd * 0.15, chestY + 2, a.cx - wwCinch, a.waistY - shToW * 0.2, a.cx - wwCinch, a.waistY);
      } else {
        ctx.bezierCurveTo(a.cx - sw, a.shY + shToW * 0.5, a.cx - wwCinch, a.waistY - shToW * 0.3, a.cx - wwCinch, a.waistY);
      }
      ctx.bezierCurveTo(a.cx - wwCinch, a.waistY + wToH * wDepart, a.cx - hw, a.hipY - wToH * hArrive, a.cx - hw, a.hipY);
      ctx.lineTo(a.cx + hw + buttB + sideButtBack * 0.2, a.hipY);
      // Far side: hip → waist → shoulder
      const farHipXQ = a.cx + hw + buttB + sideButtBack * 0.2;
      ctx.bezierCurveTo(farHipXQ, a.hipY - wToH * hArrive, a.cx + wwCinch, a.waistY + wToH * wDepart, a.cx + wwCinch, a.waistY);
      ctx.bezierCurveTo(a.cx + wwCinch, a.waistY - shToW * 0.3, a.cx + sw, a.shY + shToW * 0.5, a.cx + sw + farShDX, a.shY + farShDY);
    } else if (cat === "front") {
      // ── Front view: waist is a PATH POINT, not a control point ──
      // Two segments: shoulder→waist and waist→hip with vertical tangents at join
      const shToW = a.waistY - a.shY;
      const wToH = a.hipY - a.waistY;
      ctx.moveTo(a.cx - sw + nearShDX, a.shY + nearShDY);
      if (chestB > 0.5) {
        ctx.quadraticCurveTo(a.cx - cw - 0.5, chestY - 2, a.cx - cw, chestY);
        // Chest → waist: vertical tangent at waist
        ctx.bezierCurveTo(a.cx - cw, chestY + shToW * 0.4, a.cx - wwCinch, a.waistY - shToW * 0.25, a.cx - wwCinch, a.waistY);
      } else {
        // Shoulder → waist: vertical tangent at both ends
        ctx.bezierCurveTo(a.cx - sw, a.shY + shToW * 0.5, a.cx - wwCinch, a.waistY - shToW * 0.3, a.cx - wwCinch, a.waistY);
      }
      // Waist → hip: tight departure at waist (obliques), angular arrival at hip (iliac crest)
      ctx.bezierCurveTo(a.cx - wwCinch, a.waistY + wToH * wDepart, a.cx - hw, a.hipY - wToH * hArrive, a.cx - hw, a.hipY);
      ctx.lineTo(a.cx + hw, a.hipY);
      // Right side: hip → waist → shoulder (mirror)
      ctx.bezierCurveTo(a.cx + hw, a.hipY - wToH * hArrive, a.cx + wwCinch, a.waistY + wToH * wDepart, a.cx + wwCinch, a.waistY);
      if (chestB > 0.5) {
        ctx.bezierCurveTo(a.cx + wwCinch, a.waistY - shToW * 0.25, a.cx + cw, chestY + shToW * 0.4, a.cx + cw, chestY);
        ctx.quadraticCurveTo(a.cx + cw + 0.5, chestY - 2, a.cx + sw + farShDX, a.shY + farShDY);
      } else {
        ctx.bezierCurveTo(a.cx + wwCinch, a.waistY - shToW * 0.3, a.cx + sw, a.shY + shToW * 0.5, a.cx + sw + farShDX, a.shY + farShDY);
      }
    } else {
      // ── Back view: waist as path point ──
      const shToW = a.waistY - a.shY;
      const wToH = a.hipY - a.waistY;
      ctx.moveTo(a.cx - sw + nearShDX, a.shY + nearShDY);
      ctx.bezierCurveTo(a.cx - sw, a.shY + shToW * 0.5, a.cx - wwCinch, a.waistY - shToW * 0.3, a.cx - wwCinch, a.waistY);
      ctx.bezierCurveTo(a.cx - wwCinch, a.waistY + wToH * wDepart, a.cx - hw - buttB, a.hipY - wToH * hArrive, a.cx - hw - buttB, a.hipY);
      ctx.lineTo(a.cx + hw + buttB, a.hipY);
      ctx.bezierCurveTo(a.cx + hw + buttB, a.hipY - wToH * hArrive, a.cx + wwCinch, a.waistY + wToH * wDepart, a.cx + wwCinch, a.waistY);
      ctx.bezierCurveTo(a.cx + wwCinch, a.waistY - shToW * 0.3, a.cx + sw, a.shY + shToW * 0.5, a.cx + sw + farShDX, a.shY + farShDY);
    }
    ctx.closePath();
    ctx.fill();

    // ── Direction-specific silhouette details ──

    if (isFront) {
      // Waist definition — inner shadow line (stronger for feminine cinch)
      const waistAlpha = isFem ? 0.09 : 0.06;
      ctx.strokeStyle = `rgba(0,0,0,${waistAlpha})`;
      ctx.lineWidth = isFem ? 1.2 : 1;
      ctx.beginPath();
      ctx.moveTo(a.cx - wwCinch + 2, a.waistY);
      ctx.quadraticCurveTo(a.cx, a.waistY + (isFem ? 1.5 : 1), a.cx + wwCinch - 2, a.waistY);
      ctx.stroke();

      // Chest contour shadow — underboob line, with bounce offset
      if (chestB > 1.2) {
        ctx.strokeStyle = `rgba(0,0,0,${0.05 + brstNorm * 0.02})`;
        ctx.lineWidth = 0.8 + brstNorm * 0.3;
        ctx.beginPath();
        ctx.moveTo(a.cx - cw + 3, chestY + 3);
        ctx.quadraticCurveTo(a.cx, chestY + 4.5 + bbY * 0.3, a.cx + cw - 3, chestY + 3);
        ctx.stroke();
      }

      // Feminine hip curves — iliac crest highlight + hip flare lines
      if (isFem && buttNorm > 0.2) {
        ctx.strokeStyle = `rgba(0,0,0,${0.03 + buttNorm * 0.02})`;
        ctx.lineWidth = 0.6;
        // Hip flare crease — from waist to hip, emphasizing hourglass
        ctx.beginPath();
        ctx.moveTo(a.cx - wwCinch - 1, a.waistY + 2);
        ctx.quadraticCurveTo(a.cx - (wwCinch + hw) * 0.5 - 1, (a.waistY + a.hipY) * 0.5, a.cx - hw, a.hipY - 1);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(a.cx + wwCinch + 1, a.waistY + 2);
        ctx.quadraticCurveTo(a.cx + (wwCinch + hw) * 0.5 + 1, (a.waistY + a.hipY) * 0.5, a.cx + hw, a.hipY - 1);
        ctx.stroke();
      }

      // Shoulder slope — trapezius line from neck to shoulder edge
      const slopeY = bs.shoulderSlope * 3; // 0-3px drop
      ctx.strokeStyle = "rgba(0,0,0,0.04)";
      ctx.lineWidth = 0.6;
      ctx.beginPath();
      ctx.moveTo(a.cx - bs.neckW * 0.4, a.shY - 1);
      ctx.quadraticCurveTo(a.cx - sw * 0.5, a.shY + slopeY * 0.5, a.cx - sw, a.shY + slopeY);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(a.cx + bs.neckW * 0.4, a.shY - 1);
      ctx.quadraticCurveTo(a.cx + sw * 0.5, a.shY + slopeY * 0.5, a.cx + sw, a.shY + slopeY);
      ctx.stroke();

    } else if (isBack) {
      // Trapezius — wider shadow triangle from neck to shoulders
      const slopeY = bs.shoulderSlope * 4;
      ctx.fillStyle = "rgba(0,0,0,0.04)";
      ctx.beginPath();
      ctx.moveTo(a.cx - bs.neckW * 0.3, a.shY - 2);
      ctx.lineTo(a.cx - sw, a.shY + slopeY);
      ctx.lineTo(a.cx - sw + 3, a.shY + slopeY - 1);
      ctx.closePath();
      ctx.fill();
      ctx.beginPath();
      ctx.moveTo(a.cx + bs.neckW * 0.3, a.shY - 2);
      ctx.lineTo(a.cx + sw, a.shY + slopeY);
      ctx.lineTo(a.cx + sw - 3, a.shY + slopeY - 1);
      ctx.closePath();
      ctx.fill();

      // Spine indent — subtle vertical shadow from neck to lower back
      // Feminine: deeper lumbar curve (lordosis) creating sexy lower-back arch
      const spineDepth = isFem ? 0.06 : 0.04;
      const lumbarCurve = isFem ? 1.5 + buttNorm * 0.8 : 0.3;
      ctx.strokeStyle = `rgba(0,0,0,${spineDepth})`;
      ctx.lineWidth = 0.7;
      ctx.beginPath();
      ctx.moveTo(a.cx, a.shY + 2);
      ctx.bezierCurveTo(a.cx - 0.3, a.waistY - 4, a.cx + lumbarCurve, a.waistY + 4, a.cx, a.hipY - 2);
      ctx.stroke();

      // Scapulae hints — two oval shadows mid-back
      const scapY = a.shY + (a.waistY - a.shY) * 0.3;
      const scapX = sw * 0.45;
      ctx.fillStyle = "rgba(0,0,0,0.03)";
      ctx.beginPath(); ctx.ellipse(a.cx - scapX, scapY, 4, 6, -0.15, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.ellipse(a.cx + scapX, scapY, 4, 6, 0.15, 0, Math.PI * 2); ctx.fill();

      // Waist cinch shadow (back view) — deepens the hourglass from behind
      if (waistCinch > 0.5) {
        ctx.strokeStyle = `rgba(0,0,0,${0.04 + waistCinch * 0.01})`;
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.moveTo(a.cx - wwCinch + 2, a.waistY);
        ctx.quadraticCurveTo(a.cx, a.waistY + 1, a.cx + wwCinch - 2, a.waistY);
        ctx.stroke();
      }

      // Butt dimple/crease line — stronger with size
      if (buttB > 0.5 || buttNorm > 0.3) {
        const dimpleAlpha = 0.04 + buttNorm * 0.02;
        ctx.strokeStyle = `rgba(0,0,0,${dimpleAlpha})`;
        ctx.lineWidth = 0.6 + buttNorm * 0.3;
        ctx.beginPath();
        ctx.moveTo(a.cx - hw * 0.4, a.hipY - 2);
        ctx.quadraticCurveTo(a.cx, a.hipY + buttNorm * 1.5, a.cx + hw * 0.4, a.hipY - 2);
        ctx.stroke();
      }

      // Lower back dimples (Venus dimples) — feminine beauty mark
      if (isFem) {
        const dimpleX = hw * 0.28;
        ctx.fillStyle = `rgba(0,0,0,${0.03 + buttNorm * 0.02})`;
        ctx.beginPath(); ctx.ellipse(a.cx - dimpleX, a.hipY - 5, 1.8, 1.8, 0, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.ellipse(a.cx + dimpleX, a.hipY - 5, 1.8, 1.8, 0, 0, Math.PI * 2); ctx.fill();
      }

    } else if (isSide) {
      // S-curve profile — chest projects forward, butt projects backward
      // Subtle highlight on chest, shadow in waist concavity
      const chestFwd = sideBreastFwd + (bs.chestProjection - 0.8) * 2.0;
      const buttFwd  = sideButtBack + (bs.buttProjection - 0.8) * 1.5;

      if (chestFwd > 0.3) {
        // Chest highlight — forward projection with bounce offset
        ctx.fillStyle = `rgba(255,255,255,${0.03 + brstNorm * 0.02})`;
        ctx.beginPath();
        ctx.ellipse(a.cx + sw * 0.3, chestY + bbY * 0.5, chestFwd * 0.8, 5 + brstNorm * 2, 0.05, 0, Math.PI * 2);
        ctx.fill();
      }

      // Waist concavity shadow — deeper for feminine curves
      const waistShadowAlpha = isFem ? 0.08 : 0.05;
      ctx.strokeStyle = `rgba(0,0,0,${waistShadowAlpha})`;
      ctx.lineWidth = isFem ? 1.0 : 0.7;
      ctx.beginPath();
      ctx.moveTo(a.cx + sw * 0.1, a.waistY - 3);
      ctx.quadraticCurveTo(a.cx, a.waistY, a.cx - hw * 0.1, a.waistY + 3);
      ctx.stroke();

      // Feminine S-curve: lumbar arch highlight (the sexy lower-back curve)
      if (isFem) {
        ctx.strokeStyle = `rgba(255,255,255,${0.02 + buttNorm * 0.015})`;
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        ctx.moveTo(a.cx - sw * 0.05, a.waistY - 2);
        ctx.quadraticCurveTo(a.cx - wwCinch * 0.3 - 1, a.waistY + 3, a.cx - hw * 0.2, a.hipY - 4);
        ctx.stroke();
      }

      if (buttFwd > 0.3) {
        // Glute projection — backward, with jiggle offset
        const jiggle = (wp.buttJiggleL + wp.buttJiggleR) * 0.25 * buttNorm;
        ctx.fillStyle = `rgba(0,0,0,${0.03 + buttNorm * 0.015})`;
        ctx.beginPath();
        ctx.ellipse(a.cx - hw * 0.2, a.hipY - 1 + jiggle, buttFwd * 0.7, 4 + buttNorm * 2, -0.1, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // ── Near arm (in front of torso) — with walk/interact/emote swing ──
    // Mirrors far arm: full hang width, elbows/wrists tuck inward naturally.
    const nearArmX = a.cx - armHang * n;
    const nearArmDY = wp.nearArmDY;
    const nearArmDX = wp.nearArmDX;
    const nearBend = wp.nearElbowBend;
    // Elbow hangs ~2px inside arm edge, wrist ~4px inside
    const nearElbowX = a.cx - (armHang - 2) * n + nearArmDX * 0.4;
    const nearWristX = a.cx - (armHang - 4) * n + nearArmDX;
    const nearElbowY = a.elbowY + nearArmDY * 0.5 + nearBend * 6;
    drawOrganicArm(nearArmX, a.shY + 2, nearElbowX, nearElbowY, nearWristX, a.wristY + nearArmDY, a.handY + nearArmDY, sk);
    // Shoulder cap — fills gap between torso edge and arm deltoid (near side)
    {
      const shEdge = a.cx - sw;
      const deltEdge = nearArmX + upperArmW * 0.5 * 1.1;
      if (deltEdge < shEdge - 0.5) {
        ctx.fillStyle = sk;
        ctx.fillRect(deltEdge, a.shY, shEdge - deltEdge, 4);
      }
    }

    // ── Neck & Head — breathing + attention head shift ──
    // The head physically displaces toward the gaze target (65% of headTilt).
    // Eyes do the remaining 35% independently — creating the natural
    // "head turns, eyes lead slightly" effect that makes characters feel 3D.
    const headShiftX = wp.headTiltX * 0.65;
    const headShiftY = wp.headTiltY * 0.5;
    const neckW = cat === "side" ? bs.neckW * 0.6 : bs.neckW;
    const headY = a.headY + bY + headShiftY;
    const neckY = a.neckY + bY * 0.8;
    const eyeY = a.eyeY + bY + headShiftY;
    const headX = a.cx + headShiftX;
    ctx.fillStyle = sk;
    // Neck follows head slightly (30% of shift — it's anchored to shoulders)
    ctxRoundRect(ctx, a.cx + headShiftX * 0.3 - neckW / 2, neckY, neckW, a.shY - neckY + 1, 2);

    // ── Head — follows breathing, shifts toward gaze, and deforms with yaw ──
    // headYaw drives perspective foreshortening: when turning right, the head
    // narrows horizontally (we see more profile). This creates the illusion of
    // a 3D head turn without sprite-sheet directional frames.
    const yaw = wp.headYaw; // -1..1
    const yawAbs = Math.abs(yaw);
    const yawFactor = 1.0 - yawAbs * 0.15; // narrow up to 15% at full turn
    const baseHeadRx = a.headRx * (cat === "side" ? 0.82 : 0.95);
    const headRx = baseHeadRx * yawFactor;
    // Slight vertical stretch when turned (viewing more of the side = taller silhouette)
    const headRy = a.headRy * (1.0 + yawAbs * 0.04);
    ctx.fillStyle = sk;
    ctx.beginPath();
    ctx.ellipse(headX, headY, headRx, headRy, 0, 0, Math.PI * 2);
    ctx.fill();

    // ── Face features — direction-aware ──
    // Front/qfront: full face. Side: profile ear, nose bump, mouth. Back: nothing.
    const expr = wp.expression;
    const mouthY = eyeY + 10;

    if (isFront) {
      // Face shading — forehead coolness + cheek warmth
      const fg = ctx.createRadialGradient(headX, headY - 2, 1, headX, headY + 3, a.headRy);
      fg.addColorStop(0, "rgba(255,200,130,0.05)");
      fg.addColorStop(0.5, "rgba(0,0,0,0)");
      fg.addColorStop(1, "rgba(0,0,0,0.06)");
      ctx.fillStyle = fg;
      ctx.beginPath();
      ctx.ellipse(headX, headY, headRx, a.headRy, 0, 0, Math.PI * 2);
      ctx.fill();

      // Ears — both visible from front, with yaw-driven parallax.
      // headYaw drives stronger 3D parallax than headShiftX alone.
      // Near-side ear pushes outward, far-side tucks in behind the head.
      const earParallax = yaw * 2.5; // stronger effect from yaw (-1..1 → -2.5..2.5px)
      ctx.fillStyle = dk;
      // Left ear: comes forward when head turns right, tucks when turns left
      const leftEarSize = Math.max(0.5, 2.5 + earParallax * 0.3);
      ctx.beginPath(); ctx.ellipse(headX - headRx + 1 - earParallax, headY + 2, leftEarSize, 3.5, 0, 0, Math.PI * 2); ctx.fill();
      // Right ear: opposite parallax — shrinks/grows inversely
      const rightEarSize = Math.max(0.5, 2.5 - earParallax * 0.3);
      ctx.beginPath(); ctx.ellipse(headX + headRx - 1 - earParallax, headY + 2, rightEarSize, 3.5, 0, 0, Math.PI * 2); ctx.fill();

      // Nose — soft frontal shadow triangle, shifts with yaw for 3D depth
      const noseShift = yaw * 1.5; // nose protrudes toward turn direction
      ctx.fillStyle = shade(skinColor, 0.88);
      ctx.beginPath();
      ctx.moveTo(headX + noseShift - 1.5, eyeY + 5);
      ctx.lineTo(headX + noseShift + 1.5, eyeY + 5);
      ctx.lineTo(headX + noseShift, eyeY + 7.5);
      ctx.closePath();
      ctx.fill();

      // ── Mouth — expression-aware (front) ──
      drawMouth(ctx, headX, mouthY, skinColor, expr, 1.0);

    } else if (isSide) {
      // ── Side view face: single ear, nose profile, mouth profile ──

      // Ear — visible on the far side of the head, slightly behind center
      ctx.fillStyle = dk;
      ctx.beginPath();
      ctx.ellipse(headX + headRx - 1, headY + 2, 2, 3.5, 0, 0, Math.PI * 2);
      ctx.fill();
      // Inner ear detail
      ctx.fillStyle = warm;
      ctx.beginPath();
      ctx.ellipse(headX + headRx - 0.5, headY + 2, 1, 2, 0, 0, Math.PI * 2);
      ctx.fill();

      // Nose — profile bump projecting from the face edge.
      // Protrusion varies with head turn: turning toward viewer extends nose further.
      const noseX = headX - headRx + 1;  // near side of head
      const noseProt = 4 + headShiftX * -0.5; // head turning near = nose extends
      ctx.fillStyle = sk;
      ctx.beginPath();
      ctx.moveTo(noseX, eyeY + 2);
      ctx.quadraticCurveTo(noseX - noseProt, eyeY + 4, noseX - noseProt + 1, eyeY + 6);
      ctx.lineTo(noseX, eyeY + 6.5);
      ctx.closePath();
      ctx.fill();
      // Nostril hint
      ctx.fillStyle = shade(skinColor, 0.82);
      ctx.beginPath();
      ctx.arc(noseX - 1.5, eyeY + 5.8, 0.7, 0, Math.PI * 2);
      ctx.fill();

      // Mouth — side profile (shorter, shifted to face edge)
      drawMouth(ctx, headX - headRx * 0.2, mouthY, skinColor, expr, 0.6);

      // Chin contour — subtle jaw line
      ctx.strokeStyle = shade(skinColor, 0.88);
      ctx.lineWidth = 0.6;
      ctx.beginPath();
      ctx.moveTo(headX - headRx * 0.3, mouthY + 3);
      ctx.quadraticCurveTo(headX - headRx * 0.1, headY + a.headRy - 1, headX + headRx * 0.3, headY + a.headRy);
      ctx.stroke();

    } else if (cat === "qback") {
      // Quarter-back: one ear visible, no face features
      ctx.fillStyle = dk;
      ctx.beginPath();
      ctx.ellipse(headX - headRx + 2, headY + 2, 2, 3, 0, 0, Math.PI * 2);
      ctx.fill();
    }

    void hi;
  });
}

function drawEyesV2(ctx: DrawCtx, dir: Direction, _frame: number, eyeColor: string, eyeStyle: number, bodyType: 0 | 1 | 2 | 3, isBlinking: boolean): void {
  const a = BA[bodyType];
  const [cat, flip] = dirCat(dir);
  if (cat === "back" || cat === "qback") return;
  if (flip) { ctx.save(); ctx.translate(CHAR_W, 0); ctx.scale(-1, 1); }

  const bY = -_animPose.breatheY;
  // ── Head micro-tilt from gaze attention ──
  // The head shifts subtly toward where the eyes are looking.
  // X values are pre-negated for flipped directions in renderCharacter,
  // so withFlip's scale(-1,1) produces the correct screen-space result.
  const htX = _animPose.headTiltX;
  const htY = _animPose.headTiltY;
  const ey = a.eyeY + bY + htY;
  const expr = _animPose.expression;
  const yaw = _animPose.headYaw;
  const yawAbs = Math.abs(yaw);

  // Eye spread compresses with head yaw — far eye tucks inward, near eye widens.
  // At full turn the far eye nearly touches center (3/4 view effect).
  const baseSpread = a.headRx * 0.42;
  const spread = cat === "side" ? baseSpread : baseSpread * (1.0 - yawAbs * 0.1);
  const baseEW = cat === "side" ? 2.4 : 2.8 * (1.0 - yawAbs * 0.08); // far eye slightly smaller
  const baseEH = 3;

  // Expression modifies eye shape
  let ew = baseEW;
  let eh = baseEH;
  let eyeOffY = 0;
  let lidDroop = 0;
  if (expr === "surprise") {
    ew = baseEW * 1.2;
    eh = baseEH * 1.25;
  } else if (expr === "sleepy") {
    eh = baseEH * 0.65;
    lidDroop = 0.4;
  } else if (expr === "smile" || expr === "laugh") {
    eh = baseEH * 0.8;
    eyeOffY = 0.5;
  } else if (expr === "frown") {
    eyeOffY = 0.3;
  }

  const adjEY = ey + eyeOffY;

  // ── Multi-stage blink ──
  // blinkPhase 0→0.25: eyelids closing (lid descends)
  // blinkPhase 0.25→0.5: fully closed
  // blinkPhase 0.5→0.75: eyelids opening (lid ascends)
  // blinkPhase 0.75→1.0: fully open (reset)
  // When isBlinking is true (legacy path), treat as phase=0.4 (closed).
  const bp = _animPose.blinkPhase;
  const effectiveBlink = isBlinking ? 0.4 : bp;
  const isEyeClosed = effectiveBlink > 0.15 && effectiveBlink < 0.75;
  // Lid coverage: 0=fully open, 1=fully closed
  let lidCover = 0;
  if (effectiveBlink <= 0.25) {
    lidCover = effectiveBlink / 0.25;           // closing
  } else if (effectiveBlink <= 0.5) {
    lidCover = 1.0;                              // held closed
  } else if (effectiveBlink <= 0.75) {
    lidCover = 1 - (effectiveBlink - 0.5) / 0.25; // opening
  }

  // Sleepy expression adds permanent partial droop
  if (expr === "sleepy") lidCover = Math.max(lidCover, 0.45);

  // ── Fully or mostly closed — draw lash line + optional slit ──
  if (isEyeClosed || (expr === "sleepy" && !isBlinking && bp < 0.01)) {
    const lineY = adjEY + 0.5;
    const lashColor = shade(eyeColor, 0.5);
    ctx.strokeStyle = lashColor;
    ctx.lineWidth = 0.9 + lidCover * 0.3;

    const drawClosedEyeAt = (ex: number, w: number) => {
      ctx.beginPath(); ctx.moveTo(ex - w, lineY); ctx.lineTo(ex + w, lineY); ctx.stroke();
      // Partial-open slit showing iris underneath (during close/open transition)
      const slitOpen = 1 - lidCover;
      if (slitOpen > 0.1) {
        ctx.fillStyle = eyeColor;
        ctx.globalAlpha = slitOpen;
        ctx.beginPath();
        ctx.ellipse(ex, lineY + 0.3, w * 0.5, slitOpen * 1.2, 0, 0, Math.PI);
        ctx.fill();
        ctx.globalAlpha = 1.0;
      }
    };

    if (cat === "side") {
      drawClosedEyeAt(a.cx - spread * 0.55 + htX, ew);
    } else if (cat === "qfront") {
      drawClosedEyeAt(a.cx - spread * 0.9 + htX, ew * 0.82);
      drawClosedEyeAt(a.cx + spread + htX, ew * 1.04);
    } else {
      drawClosedEyeAt(a.cx - spread + htX, ew);
      drawClosedEyeAt(a.cx + spread + htX, ew);
    }
    drawEyebrows(ctx, a.cx + htX, ey, spread, ew, cat, expr);
    if (flip) ctx.restore();
    return;
  }

  // ── Gaze attention — pupil offset with micro-tremor ──
  // gazeX/gazeY shift the iris AND pupil within the sclera.
  // Micro-tremor (physiological nystagmus) adds constant sub-pixel jitter —
  // this is what separates "alive" from "painted."
  const gx = (_animPose.gazeX + _animPose.microTremorX) * (cat === "side" ? 0.6 : 1.0);
  const gy = _animPose.gazeY + _animPose.microTremorY;

  // Pupil dilation from attention engine — interest dilates, boredom contracts
  const dilation = _animPose.pupilDilation;
  // Eyelid vertical tracking — lid follows iris when looking up/down
  const lidGazeFollow = _animPose.lidFollowY;

  const drawSingleEye = (ex: number) => {
    // Sclera (shifted by headTilt for subtle head turn)
    const eyeX = ex + htX;
    ctx.fillStyle = "#FFFFFF";
    ctx.beginPath();
    ctx.ellipse(eyeX, adjEY, ew, eh, 0, 0, Math.PI * 2);
    ctx.fill();

    // Iris — offset by gaze direction, with micro-tremor baked in
    const irisX = eyeX + gx;
    const irisY = adjEY + 0.3 + gy;
    ctx.fillStyle = eyeColor;
    ctx.beginPath();
    ctx.arc(irisX, irisY, ew * 0.68, 0, Math.PI * 2);
    ctx.fill();

    // Iris depth ring — darker rim for dimensional depth
    ctx.strokeStyle = shade(eyeColor, 0.7);
    ctx.lineWidth = 0.4;
    ctx.beginPath();
    ctx.arc(irisX, irisY, ew * 0.66, 0, Math.PI * 2);
    ctx.stroke();

    // Pupil — tracks with iris, size driven by INTEREST not just expression
    // Dilated pupils = engaged/interested (autonomic response viewers feel subconsciously)
    const basePupilR = expr === "surprise" ? ew * 0.45 : ew * 0.38;
    const pupilR = basePupilR * (0.7 + dilation * 0.3); // dilation scales 0.7x to 1.3x
    ctx.fillStyle = "#0A0A14";
    ctx.beginPath();
    ctx.arc(irisX, irisY + 0.15, pupilR, 0, Math.PI * 2);
    ctx.fill();

    // Primary highlight — fixed relative to light source (top-left), NOT gaze
    ctx.fillStyle = "rgba(255,255,255,0.88)";
    ctx.beginPath();
    ctx.arc(eyeX - ew * 0.25, adjEY - eh * 0.22, ew * 0.24, 0, Math.PI * 2);
    ctx.fill();

    // Secondary highlight — smaller, opposite side, creates wet/spherical look
    ctx.fillStyle = "rgba(255,255,255,0.35)";
    ctx.beginPath();
    ctx.arc(eyeX + ew * 0.2, adjEY + eh * 0.15, ew * 0.1, 0, Math.PI * 2);
    ctx.fill();

    // Upper eyelid line
    ctx.strokeStyle = "rgba(0,0,0,0.25)";
    ctx.lineWidth = 0.9;
    ctx.beginPath();
    ctx.arc(eyeX, adjEY, ew + 0.3, Math.PI + 0.3, -0.3);
    ctx.stroke();

    // ── Eyelid follows gaze vertically — the biggest "alive" tell ──
    // When looking down, upper lid partially closes (follows iris involuntarily).
    // When looking up, lid lifts. Combined with blink droop for smooth blending.
    const partialDroop = lidCover + lidDroop + Math.max(0, lidGazeFollow);
    if (partialDroop > 0.05) {
      // Use skin color for the lid, not black — lids are skin covering the eye
      const lidAlpha = Math.min(partialDroop, 1) * 0.6;
      ctx.fillStyle = `rgba(0,0,0,${lidAlpha * 0.3})`;
      ctx.beginPath();
      ctx.ellipse(eyeX, adjEY - eh * (0.35 - partialDroop * 0.15), ew + 0.5, eh * Math.min(partialDroop, 0.9), 0, 0, Math.PI * 2);
      ctx.fill();
    }
    // Looking up: lid lifts slightly (negative lidGazeFollow) — show more sclera above iris
    if (lidGazeFollow < -0.05) {
      // Very subtle: just shift the upper lid line slightly higher
      ctx.strokeStyle = "rgba(255,255,255,0.15)";
      ctx.lineWidth = Math.abs(lidGazeFollow) * 2;
      ctx.beginPath();
      ctx.arc(eyeX, adjEY - 0.3, ew, Math.PI + 0.4, -0.4);
      ctx.stroke();
    }

    // Smile/laugh — happy squint
    if (expr === "smile" || expr === "laugh") {
      ctx.strokeStyle = "rgba(0,0,0,0.12)";
      ctx.lineWidth = 0.7;
      ctx.beginPath();
      ctx.arc(eyeX, adjEY, ew * 0.8, 0.2, Math.PI - 0.2);
      ctx.stroke();
    }

    // Style extras
    const st = eyeStyle % 8;
    if (st === 1) { // Lashes
      ctx.strokeStyle = "#1A1008";
      ctx.lineWidth = 0.9;
      ctx.beginPath(); ctx.moveTo(eyeX - ew, adjEY - 1); ctx.lineTo(eyeX - ew - 1.8, adjEY - 3.5); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(eyeX + ew, adjEY - 1); ctx.lineTo(eyeX + ew + 1.8, adjEY - 3.5); ctx.stroke();
    } else if (st === 2) { // Big/innocent — larger iris with gaze + dilation
      ctx.fillStyle = eyeColor;
      ctx.beginPath(); ctx.arc(irisX, irisY, ew * 0.82, 0, Math.PI * 2); ctx.fill();
      const bigPupilR = ew * 0.45 * (0.7 + dilation * 0.3);
      ctx.fillStyle = "#0A0A14";
      ctx.beginPath(); ctx.arc(irisX, irisY + 0.2, bigPupilR, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "rgba(255,255,255,0.9)";
      ctx.beginPath(); ctx.arc(eyeX - ew * 0.2, adjEY - eh * 0.3, ew * 0.28, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(eyeX + ew * 0.25, adjEY + eh * 0.1, ew * 0.14, 0, Math.PI * 2); ctx.fill();
    } else if (st === 3) { // Half-lidded
      ctx.fillStyle = shade(eyeColor, 0.3);
      ctx.globalAlpha = 0.35;
      ctx.beginPath();
      ctx.ellipse(eyeX, adjEY - eh * 0.3, ew + 0.5, eh * 0.45, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1.0;
    } else if (st === 4) { // Sparkle
      ctx.fillStyle = "#FFFFFF";
      ctx.beginPath(); ctx.arc(eyeX + ew * 0.15, adjEY - eh * 0.15, ew * 0.15, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.arc(eyeX - ew * 0.3, adjEY + eh * 0.2, ew * 0.1, 0, Math.PI * 2); ctx.fill();
    } else if (st === 6) { // Dot (chibi) — gaze via whole dot shift
      ctx.fillStyle = "#0A0A14";
      ctx.beginPath(); ctx.arc(irisX, irisY, ew * 0.5, 0, Math.PI * 2); ctx.fill();
    } else if (st === 7) { // Happy closed
      ctx.fillStyle = shade(eyeColor, 0.3);
      ctx.globalAlpha = 0.5;
      ctx.beginPath();
      ctx.ellipse(eyeX, adjEY, ew, eh, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1.0;
      ctx.strokeStyle = "#1A1008";
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.arc(eyeX, adjEY + 1, ew * 0.7, 0.15 * Math.PI, 0.85 * Math.PI);
      ctx.stroke();
    }
  };

  if (cat === "side") {
    // Profile: single eye on the face-front side (near = negative X in withFlip convention)
    drawSingleEye(a.cx - spread * 0.55);
  } else if (cat === "qfront") {
    // Quarter-view parallax: far eye is smaller (foreshortened),
    // near eye is slightly larger — sells the 3/4 perspective
    const savedEW = ew;
    const savedEH = eh;
    // Far eye (the one further from viewer, positioned at -spread)
    ew = savedEW * 0.82;
    eh = savedEH * 0.88;
    drawSingleEye(a.cx - spread * 0.9);
    // Near eye (closer to viewer, positioned at +spread)
    ew = savedEW * 1.04;
    eh = savedEH * 1.02;
    drawSingleEye(a.cx + spread);
    // Restore
    ew = savedEW;
    eh = savedEH;
  } else {
    // Front view — yaw creates asymmetric parallax (72+ orientation feel).
    // When head turns right (yaw>0): left eye compresses inward, right eye pushes out.
    // Far eye slightly smaller for depth cue.
    const yawShift = yaw * spread * 0.15; // asymmetric shift
    if (yawAbs > 0.1) {
      const savedEW = ew;
      const savedEH = eh;
      // Determine which eye is "far" based on yaw direction
      const farScale = 1.0 - yawAbs * 0.12;
      const nearScale = 1.0 + yawAbs * 0.04;
      // Left eye (negative x)
      ew = savedEW * (yaw > 0 ? farScale : nearScale);
      eh = savedEH * (yaw > 0 ? farScale * 0.95 + 0.05 : 1.0);
      drawSingleEye(a.cx - spread + yawShift);
      // Right eye (positive x)
      ew = savedEW * (yaw > 0 ? nearScale : farScale);
      eh = savedEH * (yaw > 0 ? 1.0 : farScale * 0.95 + 0.05);
      drawSingleEye(a.cx + spread + yawShift);
      ew = savedEW;
      eh = savedEH;
    } else {
      drawSingleEye(a.cx - spread);
      drawSingleEye(a.cx + spread);
    }
  }

  // ── Eyebrows — subtle but crucial for expression ──
  drawEyebrows(ctx, a.cx + htX, ey, spread, ew, cat, expr);

  if (flip) ctx.restore();
}

/** Draw expression + attention-driven eyebrows above the eyes */
function drawEyebrows(
  ctx: DrawCtx, cx: number, ey: number,
  spread: number, ew: number, cat: DirCat, expr: Expression
): void {
  // Eyebrows sit above the eye — position varies by expression AND attention.
  // browEngagement from the attention engine adds a living layer:
  //   positive = lifted (interest/curiosity)
  //   negative = furrowed (sustained concentration)
  const browEngage = _animPose.browEngagement;
  const browW = ew * 0.9;
  let browY = ey - 4.5;     // default position above eye
  let innerTilt = 0;         // positive = inner edge raises (surprise), negative = angry
  let outerTilt = 0;
  let thickness = 1.0;
  let alpha = 0.35;          // base visibility

  if (expr === "surprise") {
    browY = ey - 6;            // raised high
    innerTilt = -0.5;
    thickness = 0.8;
  } else if (expr === "frown") {
    browY = ey - 3.5;         // lowered
    innerTilt = 1.2;          // inner edges drop — angry/concerned
    outerTilt = -0.5;
    thickness = 1.2;
  } else if (expr === "smile" || expr === "laugh") {
    browY = ey - 4;           // slightly raised
    innerTilt = -0.3;         // gentle arch
  } else if (expr === "sleepy") {
    browY = ey - 3;           // low and flat
    outerTilt = 0.8;          // droop at outer edges
    thickness = 0.8;
  } else {
    // Neutral — brows are still there, just subtle. Attention makes them visible.
    // Without engagement, they're barely perceptible. With it, they come alive.
    alpha = 0.12 + Math.abs(browEngage) * 0.5;
    if (alpha < 0.08) return; // truly idle — skip for clean face
  }

  // Attention overlays on expression: interest lifts brows, concentration furrows them
  browY -= browEngage * 2.5;           // lift/lower from engagement
  innerTilt += browEngage * -0.4;      // interest flattens inner tilt, concentration deepens it

  ctx.strokeStyle = `rgba(30,20,10,${Math.min(alpha, 0.5).toFixed(2)})`;
  ctx.lineWidth = thickness;
  ctx.lineCap = "round";

  const drawBrow = (bx: number, flipBrow: boolean, wScale = 1.0) => {
    const sign = flipBrow ? -1 : 1;
    const bw = browW * wScale;
    const innerX = bx - sign * bw;
    const outerX = bx + sign * bw;
    const innerY = browY + innerTilt;
    const outerY = browY + outerTilt;
    ctx.beginPath();
    ctx.moveTo(innerX, innerY);
    ctx.quadraticCurveTo(bx, Math.min(innerY, outerY) - 0.5, outerX, outerY);
    ctx.stroke();
  };

  if (cat === "side") {
    // Profile brow sits above the face-front eye (near side)
    drawBrow(cx - spread * 0.55, false);
  } else if (cat === "qfront") {
    // Far brow narrower to match foreshortened far eye
    drawBrow(cx - spread * 0.9, true, 0.82);
    drawBrow(cx + spread, false, 1.04);
  } else {
    drawBrow(cx - spread, true);
    drawBrow(cx + spread, false);
  }
}

// ─── Facial Hair V2 ─────────────────────────────────────────────────────────

function drawFacialHairV2(
  ctx: DrawCtx, dir: Direction, _frame: number,
  color: string, style: number, bodyType: 0 | 1 | 2 | 3
): void {
  if (style < 0) return;
  const a = BA[bodyType];
  const bY = -_animPose.breatheY;
  const dk = shade(color, 0.75);

  withFlip(ctx, dir, (cat) => {
    const isBack = cat === "back" || cat === "qback";
    if (isBack) return; // no facial hair visible from behind

    const headRx = a.headRx * (cat === "side" ? 0.82 : 0.95);
    const eyeY = a.eyeY + bY;
    const mouthY = eyeY + 10;
    const chinY = mouthY + 4;
    const jawW = headRx * 0.7;
    const isSide = cat === "side";
    const scale = isSide ? 0.6 : 1.0;
    const cx = a.cx;

    switch (style % 6) {
      case 0: { // Stubble — dotted texture across jaw and chin
        ctx.fillStyle = color;
        ctx.globalAlpha = 0.35;
        const stubbleArea = isSide
          ? [{ x: cx - headRx * 0.3, y: mouthY - 1, w: headRx * 0.5, h: 7 }]
          : [{ x: cx - jawW, y: mouthY - 1, w: jawW * 2, h: 7 }];
        for (const area of stubbleArea) {
          for (let sy = 0; sy < area.h; sy += 2) {
            for (let sx = 0; sx < area.w; sx += 2) {
              if ((sx + sy) % 3 !== 0) continue;
              ctx.beginPath();
              ctx.arc(area.x + sx, area.y + sy, 0.5, 0, Math.PI * 2);
              ctx.fill();
            }
          }
        }
        ctx.globalAlpha = 1.0;
        break;
      }
      case 1: { // Goatee — small pointed beard below mouth
        ctx.fillStyle = color;
        ctx.beginPath();
        if (isSide) {
          ctx.moveTo(cx - headRx * 0.35, mouthY + 1);
          ctx.quadraticCurveTo(cx - headRx * 0.4, chinY + 2, cx - headRx * 0.25, chinY + 4);
          ctx.quadraticCurveTo(cx - headRx * 0.1, chinY + 2, cx - headRx * 0.15, mouthY + 1);
        } else {
          ctx.moveTo(cx - 4 * scale, mouthY + 1);
          ctx.quadraticCurveTo(cx - 5 * scale, chinY + 2, cx, chinY + 5);
          ctx.quadraticCurveTo(cx + 5 * scale, chinY + 2, cx + 4 * scale, mouthY + 1);
        }
        ctx.closePath();
        ctx.fill();
        // Texture strands
        ctx.strokeStyle = dk;
        ctx.lineWidth = 0.4;
        for (let i = -2; i <= 2; i++) {
          ctx.beginPath();
          ctx.moveTo(cx + i * 1.5 * scale, mouthY + 2);
          ctx.lineTo(cx + i * 0.8 * scale, chinY + 3);
          ctx.stroke();
        }
        break;
      }
      case 2: { // Full beard — covers jaw and chin with volume
        ctx.fillStyle = color;
        ctx.beginPath();
        if (isSide) {
          ctx.moveTo(cx + headRx * 0.2, eyeY + 6);
          ctx.quadraticCurveTo(cx - headRx * 0.5, eyeY + 8, cx - headRx * 0.5, chinY + 2);
          ctx.quadraticCurveTo(cx - headRx * 0.3, chinY + 8, cx - headRx * 0.1, chinY + 6);
          ctx.quadraticCurveTo(cx + headRx * 0.1, chinY + 4, cx + headRx * 0.2, eyeY + 8);
        } else {
          ctx.moveTo(cx - jawW - 2, eyeY + 6);
          ctx.quadraticCurveTo(cx - jawW - 3, chinY + 2, cx - jawW * 0.5, chinY + 8);
          ctx.quadraticCurveTo(cx, chinY + 10, cx + jawW * 0.5, chinY + 8);
          ctx.quadraticCurveTo(cx + jawW + 3, chinY + 2, cx + jawW + 2, eyeY + 6);
        }
        ctx.closePath();
        ctx.fill();
        // Beard texture — wavy strands
        ctx.strokeStyle = dk;
        ctx.lineWidth = 0.5;
        const strands = isSide ? 4 : 7;
        for (let i = 0; i < strands; i++) {
          const t = i / (strands - 1);
          const sx = isSide
            ? cx - headRx * 0.4 + t * headRx * 0.5
            : cx - jawW + t * jawW * 2;
          ctx.beginPath();
          ctx.moveTo(sx, mouthY + 1);
          ctx.quadraticCurveTo(sx + (Math.random() - 0.5) * 2, chinY + 3, sx, chinY + 6 + (i % 2) * 2);
          ctx.stroke();
        }
        break;
      }
      case 3: { // Mustache — handlebar style above lip
        ctx.fillStyle = color;
        if (isSide) {
          ctx.beginPath();
          ctx.moveTo(cx - headRx * 0.1, mouthY - 2);
          ctx.quadraticCurveTo(cx - headRx * 0.35, mouthY - 1, cx - headRx * 0.4, mouthY + 1);
          ctx.quadraticCurveTo(cx - headRx * 0.35, mouthY + 0.5, cx - headRx * 0.1, mouthY);
          ctx.closePath();
          ctx.fill();
        } else {
          // Left half
          ctx.beginPath();
          ctx.moveTo(cx - 1, mouthY - 2);
          ctx.quadraticCurveTo(cx - 6, mouthY - 1.5, cx - 8, mouthY + 1);
          ctx.quadraticCurveTo(cx - 7, mouthY + 0.5, cx - 1, mouthY);
          ctx.closePath();
          ctx.fill();
          // Right half
          ctx.beginPath();
          ctx.moveTo(cx + 1, mouthY - 2);
          ctx.quadraticCurveTo(cx + 6, mouthY - 1.5, cx + 8, mouthY + 1);
          ctx.quadraticCurveTo(cx + 7, mouthY + 0.5, cx + 1, mouthY);
          ctx.closePath();
          ctx.fill();
        }
        // Center part
        ctx.fillStyle = dk;
        ctx.beginPath();
        ctx.ellipse(cx - (isSide ? headRx * 0.1 : 0), mouthY - 1, 1.5 * scale, 1, 0, 0, Math.PI * 2);
        ctx.fill();
        break;
      }
      case 4: { // Soul patch — small tuft below lower lip
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.ellipse(cx - (isSide ? headRx * 0.2 : 0), mouthY + 3, 2 * scale, 2.5, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = dk;
        ctx.beginPath();
        ctx.ellipse(cx - (isSide ? headRx * 0.2 : 0), mouthY + 3, 1 * scale, 1.5, 0, 0, Math.PI * 2);
        ctx.fill();
        break;
      }
      case 5: { // Mutton chops — sideburns that widen into jaw, no chin
        ctx.fillStyle = color;
        if (isSide) {
          ctx.beginPath();
          ctx.moveTo(cx + headRx - 2, eyeY + 3);
          ctx.quadraticCurveTo(cx + headRx, eyeY + 8, cx + headRx - 1, chinY + 2);
          ctx.quadraticCurveTo(cx + headRx - 4, chinY + 3, cx + headRx - 4, eyeY + 6);
          ctx.closePath();
          ctx.fill();
        } else {
          for (let side = -1; side <= 1; side += 2) {
            ctx.beginPath();
            ctx.moveTo(cx + side * (jawW + 2), eyeY + 3);
            ctx.quadraticCurveTo(cx + side * (jawW + 4), eyeY + 8, cx + side * (jawW + 3), chinY + 3);
            ctx.quadraticCurveTo(cx + side * jawW, chinY + 4, cx + side * jawW, eyeY + 6);
            ctx.closePath();
            ctx.fill();
          }
        }
        // Texture
        ctx.strokeStyle = dk;
        ctx.lineWidth = 0.4;
        ctx.globalAlpha = 0.5;
        if (!isSide) {
          for (let side = -1; side <= 1; side += 2) {
            for (let y = 0; y < 8; y += 2) {
              ctx.beginPath();
              ctx.moveTo(cx + side * (jawW + 1), eyeY + 4 + y);
              ctx.lineTo(cx + side * (jawW + 2), eyeY + 6 + y);
              ctx.stroke();
            }
          }
        }
        ctx.globalAlpha = 1.0;
        break;
      }
    }
  });
}

// ─── Makeup V2 ───────────────────────────────────────────────────────────────

function drawMakeupV2(
  ctx: DrawCtx, dir: Direction, _frame: number,
  color: string, style: number, bodyType: 0 | 1 | 2 | 3
): void {
  if (style < 0) return;
  const a = BA[bodyType];
  const bY = -_animPose.breatheY;
  const dk = shade(color, 0.8);
  const hi = shade(color, 1.2);

  withFlip(ctx, dir, (cat) => {
    const isBack = cat === "back" || cat === "qback";
    if (isBack) return;

    const isSide = cat === "side";
    const eyeY = a.eyeY + bY;
    const headRx = a.headRx * (cat === "side" ? 0.82 : 0.95);
    const spread = headRx * 0.42;
    const cx = a.cx;

    switch (style % 6) {
      case 0: { // Natural — subtle lid tint + lash definition
        ctx.globalAlpha = 0.25;
        ctx.fillStyle = color;
        if (isSide) {
          ctx.beginPath();
          ctx.ellipse(cx - spread * 0.8, eyeY - 1, 4, 2.5, 0, Math.PI, Math.PI * 2);
          ctx.fill();
        } else {
          for (let side = -1; side <= 1; side += 2) {
            ctx.beginPath();
            ctx.ellipse(cx + side * spread, eyeY - 1, 4, 2.5, 0, Math.PI, Math.PI * 2);
            ctx.fill();
          }
        }
        ctx.globalAlpha = 1.0;
        // Subtle lash line
        ctx.strokeStyle = shade(color, 0.6);
        ctx.lineWidth = 0.6;
        if (isSide) {
          ctx.beginPath();
          ctx.moveTo(cx - spread * 0.8 - 3, eyeY);
          ctx.lineTo(cx - spread * 0.8 + 3, eyeY);
          ctx.stroke();
        } else {
          for (let side = -1; side <= 1; side += 2) {
            ctx.beginPath();
            ctx.moveTo(cx + side * spread - 3, eyeY);
            ctx.lineTo(cx + side * spread + 3, eyeY);
            ctx.stroke();
          }
        }
        break;
      }
      case 1: { // Glam — shimmer lid + defined lash + highlight
        ctx.globalAlpha = 0.35;
        ctx.fillStyle = hi;
        if (isSide) {
          ctx.beginPath();
          ctx.ellipse(cx - spread * 0.8, eyeY - 2, 4.5, 3, 0, Math.PI, Math.PI * 2);
          ctx.fill();
        } else {
          for (let side = -1; side <= 1; side += 2) {
            ctx.beginPath();
            ctx.ellipse(cx + side * spread, eyeY - 2, 4.5, 3, 0, Math.PI, Math.PI * 2);
            ctx.fill();
          }
        }
        ctx.globalAlpha = 0.5;
        ctx.fillStyle = color;
        if (!isSide) {
          for (let side = -1; side <= 1; side += 2) {
            ctx.beginPath();
            ctx.ellipse(cx + side * spread, eyeY - 1.5, 3.5, 2, 0, Math.PI, Math.PI * 2);
            ctx.fill();
          }
        }
        ctx.globalAlpha = 1.0;
        // Highlight dot on inner lid
        ctx.fillStyle = "rgba(255,255,240,0.4)";
        if (!isSide) {
          for (let side = -1; side <= 1; side += 2) {
            ctx.beginPath();
            ctx.arc(cx + side * (spread - 2), eyeY - 2, 1, 0, Math.PI * 2);
            ctx.fill();
          }
        }
        break;
      }
      case 2: { // Smoky eye — dark gradient lid + smudged lower line
        ctx.globalAlpha = 0.45;
        ctx.fillStyle = dk;
        const eyePositions = isSide
          ? [{ ex: cx - spread * 0.8, s: 0.7 }]
          : [{ ex: cx - spread, s: 1 }, { ex: cx + spread, s: 1 }];
        for (const { ex, s } of eyePositions) {
          ctx.beginPath();
          ctx.ellipse(ex, eyeY - 1, 5 * s, 3.5, 0, Math.PI, Math.PI * 2);
          ctx.fill();
        }
        // Smudged lower line
        ctx.globalAlpha = 0.2;
        ctx.fillStyle = color;
        for (const { ex, s } of eyePositions) {
          ctx.beginPath();
          ctx.ellipse(ex, eyeY + 2, 4 * s, 1.5, 0, 0, Math.PI);
          ctx.fill();
        }
        ctx.globalAlpha = 1.0;
        break;
      }
      case 3: { // Cat eye — sharp wing at outer corner
        ctx.strokeStyle = shade(color, 0.5);
        ctx.lineWidth = 0.8;
        ctx.lineCap = "round";
        const eyePositions = isSide
          ? [{ ex: cx - spread * 0.8, s: 0.7, flip: false }]
          : [{ ex: cx - spread, s: 1, flip: true }, { ex: cx + spread, s: 1, flip: false }];
        for (const { ex, s, flip } of eyePositions) {
          // Upper lid line
          ctx.beginPath();
          ctx.moveTo(ex - 3 * s, eyeY);
          ctx.lineTo(ex + 3 * s, eyeY);
          ctx.stroke();
          // Wing flick
          const wingDir = flip ? -1 : 1;
          ctx.beginPath();
          ctx.moveTo(ex + wingDir * 3 * s, eyeY);
          ctx.lineTo(ex + wingDir * 5 * s, eyeY - 3);
          ctx.stroke();
        }
        break;
      }
      case 4: { // Blush glow — rosy cheeks
        const cheekY = eyeY + 7;
        ctx.globalAlpha = 0.2;
        const blushGrad = ctx.createRadialGradient(cx, cheekY, 0, cx, cheekY, headRx);
        blushGrad.addColorStop(0, color);
        blushGrad.addColorStop(0.6, color);
        blushGrad.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = blushGrad;
        if (isSide) {
          ctx.beginPath();
          ctx.ellipse(cx - headRx * 0.3, cheekY, 6, 4, 0, 0, Math.PI * 2);
          ctx.fill();
        } else {
          for (let side = -1; side <= 1; side += 2) {
            ctx.beginPath();
            ctx.ellipse(cx + side * headRx * 0.5, cheekY, 6, 4, 0, 0, Math.PI * 2);
            ctx.fill();
          }
        }
        ctx.globalAlpha = 1.0;
        break;
      }
      case 5: { // Avant-garde — bold color across lid + cheekbone streak
        ctx.globalAlpha = 0.5;
        ctx.fillStyle = color;
        const eyePositions = isSide
          ? [{ ex: cx - spread * 0.8, s: 0.7 }]
          : [{ ex: cx - spread, s: 1 }, { ex: cx + spread, s: 1 }];
        for (const { ex, s } of eyePositions) {
          ctx.beginPath();
          ctx.ellipse(ex, eyeY - 1, 5.5 * s, 4, -0.15, Math.PI * 0.7, Math.PI * 2.3);
          ctx.fill();
        }
        // Cheekbone streak
        ctx.globalAlpha = 0.25;
        ctx.fillStyle = hi;
        if (!isSide) {
          for (let side = -1; side <= 1; side += 2) {
            ctx.beginPath();
            ctx.moveTo(cx + side * (spread + 3), eyeY + 2);
            ctx.lineTo(cx + side * (headRx - 1), eyeY + 6);
            ctx.lineTo(cx + side * (headRx - 1), eyeY + 7);
            ctx.lineTo(cx + side * (spread + 3), eyeY + 4);
            ctx.closePath();
            ctx.fill();
          }
        }
        ctx.globalAlpha = 1.0;
        break;
      }
    }
  });
}

// ─── Lips V2 ─────────────────────────────────────────────────────────────────

function drawLipsV2(
  ctx: DrawCtx, dir: Direction, _frame: number,
  color: string, style: number, bodyType: 0 | 1 | 2 | 3
): void {
  if (style < 0) return;
  const a = BA[bodyType];
  const bY = -_animPose.breatheY;
  const dk = shade(color, 0.8);
  const hi = shade(color, 1.25);
  const expr = _animPose.expression;

  withFlip(ctx, dir, (cat) => {
    const isBack = cat === "back" || cat === "qback";
    if (isBack) return;

    const isSide = cat === "side";
    const eyeY = a.eyeY + bY;
    const mouthY = eyeY + 10;
    const scale = isSide ? 0.6 : 1.0;
    const headRx = a.headRx * (cat === "side" ? 0.82 : 0.95);
    const cx = isSide ? a.cx - headRx * 0.2 : a.cx;

    // Lip width/shape responds to expression
    let lipW = 4 * scale;
    let upperH = 1.8;
    let lowerH = 2.2;
    if (expr === "smile" || expr === "laugh") { lipW = 5 * scale; lowerH = 1.8; }
    if (expr === "surprise") { lipW = 3 * scale; upperH = 2; lowerH = 2.5; }
    if (expr === "frown") { lipW = 3.5 * scale; }

    switch (style % 5) {
      case 0: { // Subtle tint — thin color overlay
        ctx.globalAlpha = 0.4;
        ctx.fillStyle = color;
        // Upper lip
        ctx.beginPath();
        ctx.moveTo(cx - lipW, mouthY);
        ctx.quadraticCurveTo(cx - lipW * 0.3, mouthY - upperH, cx, mouthY - upperH * 0.5);
        ctx.quadraticCurveTo(cx + lipW * 0.3, mouthY - upperH, cx + lipW, mouthY);
        ctx.quadraticCurveTo(cx, mouthY + 0.5, cx - lipW, mouthY);
        ctx.fill();
        // Lower lip
        ctx.beginPath();
        ctx.moveTo(cx - lipW, mouthY);
        ctx.quadraticCurveTo(cx, mouthY + lowerH, cx + lipW, mouthY);
        ctx.fill();
        ctx.globalAlpha = 1.0;
        break;
      }
      case 1: { // Matte — full opaque coverage
        ctx.fillStyle = color;
        // Upper lip with cupid's bow
        ctx.beginPath();
        ctx.moveTo(cx - lipW, mouthY);
        ctx.quadraticCurveTo(cx - lipW * 0.5, mouthY - upperH, cx - lipW * 0.15, mouthY - upperH * 0.7);
        ctx.lineTo(cx, mouthY - upperH * 0.4); // cupid's bow dip
        ctx.lineTo(cx + lipW * 0.15, mouthY - upperH * 0.7);
        ctx.quadraticCurveTo(cx + lipW * 0.5, mouthY - upperH, cx + lipW, mouthY);
        ctx.quadraticCurveTo(cx, mouthY + 0.3, cx - lipW, mouthY);
        ctx.fill();
        // Lower lip
        ctx.fillStyle = dk;
        ctx.beginPath();
        ctx.moveTo(cx - lipW + 0.5, mouthY + 0.3);
        ctx.quadraticCurveTo(cx, mouthY + lowerH + 0.5, cx + lipW - 0.5, mouthY + 0.3);
        ctx.fill();
        break;
      }
      case 2: { // Gloss — color with highlight reflection
        ctx.fillStyle = color;
        // Full lip shape
        ctx.beginPath();
        ctx.moveTo(cx - lipW, mouthY);
        ctx.quadraticCurveTo(cx - lipW * 0.3, mouthY - upperH, cx, mouthY - upperH * 0.5);
        ctx.quadraticCurveTo(cx + lipW * 0.3, mouthY - upperH, cx + lipW, mouthY);
        ctx.quadraticCurveTo(cx, mouthY + lowerH, cx - lipW, mouthY);
        ctx.fill();
        // Gloss shine — white highlight on lower lip
        ctx.fillStyle = "rgba(255,255,255,0.35)";
        ctx.beginPath();
        ctx.ellipse(cx + 0.5 * scale, mouthY + lowerH * 0.3, lipW * 0.5, lowerH * 0.3, 0, 0, Math.PI * 2);
        ctx.fill();
        break;
      }
      case 3: { // Bold — deep saturated, crisp edge
        ctx.fillStyle = color;
        // Upper lip — strong cupid's bow
        ctx.beginPath();
        ctx.moveTo(cx - lipW - 0.5, mouthY);
        ctx.quadraticCurveTo(cx - lipW * 0.5, mouthY - upperH - 0.5, cx - lipW * 0.12, mouthY - upperH);
        ctx.lineTo(cx, mouthY - upperH * 0.5);
        ctx.lineTo(cx + lipW * 0.12, mouthY - upperH);
        ctx.quadraticCurveTo(cx + lipW * 0.5, mouthY - upperH - 0.5, cx + lipW + 0.5, mouthY);
        ctx.quadraticCurveTo(cx, mouthY + 0.3, cx - lipW - 0.5, mouthY);
        ctx.fill();
        // Lower lip — fuller
        ctx.fillStyle = dk;
        ctx.beginPath();
        ctx.moveTo(cx - lipW, mouthY + 0.3);
        ctx.quadraticCurveTo(cx, mouthY + lowerH + 1, cx + lipW, mouthY + 0.3);
        ctx.fill();
        // Lip line
        ctx.strokeStyle = shade(color, 0.6);
        ctx.lineWidth = 0.4;
        ctx.beginPath();
        ctx.moveTo(cx - lipW, mouthY);
        ctx.lineTo(cx + lipW, mouthY);
        ctx.stroke();
        break;
      }
      case 4: { // Ombre — gradient from center to edge
        const grad = ctx.createRadialGradient(cx, mouthY, 0, cx, mouthY, lipW + 1);
        grad.addColorStop(0, hi);
        grad.addColorStop(0.5, color);
        grad.addColorStop(1, dk);
        ctx.fillStyle = grad;
        // Full lip shape
        ctx.beginPath();
        ctx.moveTo(cx - lipW, mouthY);
        ctx.quadraticCurveTo(cx - lipW * 0.3, mouthY - upperH, cx, mouthY - upperH * 0.5);
        ctx.quadraticCurveTo(cx + lipW * 0.3, mouthY - upperH, cx + lipW, mouthY);
        ctx.quadraticCurveTo(cx, mouthY + lowerH, cx - lipW, mouthY);
        ctx.fill();
        break;
      }
    }
  });
}

function drawAccessoryV2(ctx: DrawCtx, dir: Direction, _frame: number, color: string, style: number, bodyType: 0 | 1 | 2 | 3): void {
  if (style < 0) return;
  const a = BA[bodyType];
  const bY = -_animPose.breatheY;
  const dk = shade(color, 0.78);

  withFlip(ctx, dir, (cat) => {
    switch (style % 5) {
      case 0: { // Scarf — with cloth flow physics
        const sw = cat === "side" ? 0.55 : cat === "qfront" || cat === "qback" ? 0.78 : 1.0;
        const left = a.cx - a.shW * sw;
        const right = a.cx + a.shW * sw;
        const scarfSway = Math.sin(_animPose.clothPhase * 1.3) * _animPose.clothAmplitude * 3;
        const scarfSway2 = Math.sin(_animPose.clothPhase * 1.3 + 0.8) * _animPose.clothAmplitude * 2;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.moveTo(left - 1, a.neckY + 1);
        ctx.bezierCurveTo(left - 2, a.shY + 4, a.cx, a.shY + 6, right + 1, a.shY + 2);
        ctx.lineTo(right + 1, a.neckY + 1);
        ctx.closePath();
        ctx.fill();
        // Hanging tail — sways with movement
        const tailEndY = a.waistY - 6 + Math.abs(scarfSway) * 0.5;
        ctx.fillStyle = dk;
        ctx.beginPath();
        ctx.moveTo(right - 2, a.shY + 2);
        ctx.quadraticCurveTo(right + 4 + scarfSway, a.shY + 12 + scarfSway2, right + scarfSway * 0.8, tailEndY);
        ctx.lineTo(right - 4 + scarfSway * 0.6, tailEndY - 2);
        ctx.quadraticCurveTo(right + scarfSway2 * 0.5, a.shY + 8, right - 2, a.shY + 2);
        ctx.fill();
        // Tail tip fringe — subtle secondary motion
        if (_animPose.clothAmplitude > 0.3) {
          ctx.strokeStyle = shade(dk, 0.85);
          ctx.lineWidth = 0.6;
          for (let i = -1; i <= 1; i++) {
            const fx = right - 2 + i * 2 + scarfSway * 0.7;
            ctx.beginPath();
            ctx.moveTo(fx, tailEndY - 1);
            ctx.lineTo(fx + scarfSway * 0.3, tailEndY + 2);
            ctx.stroke();
          }
        }
        break;
      }
      case 1: { // Glasses
        if (cat === "back" || cat === "qback") break;
        const ey = a.eyeY + bY;
        const gSpread = a.headRx * 0.42;
        const r = 4;
        ctx.strokeStyle = color;
        ctx.lineWidth = 1.2;
        if (cat === "qfront") {
          // Far lens smaller (foreshortened), near lens normal
          const farR = r * 0.82;
          const farX = a.cx - gSpread * 0.9;
          ctx.beginPath(); ctx.arc(farX, ey, farR, 0, Math.PI * 2); ctx.stroke();
          ctx.beginPath(); ctx.arc(a.cx + gSpread, ey, r, 0, Math.PI * 2); ctx.stroke();
          ctx.beginPath(); ctx.moveTo(farX + farR, ey); ctx.lineTo(a.cx + gSpread - r, ey); ctx.stroke();
        } else if (cat !== "side") {
          ctx.beginPath(); ctx.arc(a.cx - gSpread, ey, r, 0, Math.PI * 2); ctx.stroke();
          ctx.beginPath(); ctx.arc(a.cx + gSpread, ey, r, 0, Math.PI * 2); ctx.stroke();
          ctx.beginPath(); ctx.moveTo(a.cx - gSpread + r, ey); ctx.lineTo(a.cx + gSpread - r, ey); ctx.stroke();
        } else {
          // Side: single lens on face-front side, arm extends to ear
          const sideEX = a.cx - gSpread * 0.55;
          ctx.beginPath(); ctx.arc(sideEX, ey, r, 0, Math.PI * 2); ctx.stroke();
          // Temple arm from lens to ear
          const headRx = a.headRx * 0.82;
          ctx.beginPath(); ctx.moveTo(sideEX + r, ey); ctx.lineTo(a.cx + headRx, ey); ctx.stroke();
        }
        break;
      }
      case 2: { // Earrings — not visible from behind, swing with movement
        if (cat === "back" || cat === "qback") break;
        const eRx = a.headRx * getNarrow(cat);
        // Secondary motion lag — earrings swing with movement
        const eLag = _animPose.secondaryLagX * 1.2;
        const eLagY = _animPose.secondaryLagY * 0.6;
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.arc(a.cx - eRx + 1 + eLag, a.headY + bY + 5 + eLagY, 1.5, 0, Math.PI * 2);
        ctx.fill();
        if (cat !== "side") {
          ctx.beginPath();
          ctx.arc(a.cx + eRx - 1 + eLag, a.headY + bY + 5 + eLagY, 1.5, 0, Math.PI * 2);
          ctx.fill();
        }
        break;
      }
      case 3: { // Necklace — pendant swings with movement
        const nw = a.shW * 0.6 * getNarrow(cat);
        // Pendant lags behind body
        const pLag = _animPose.secondaryLagX * 0.8;
        const pLagY = _animPose.secondaryLagY * 0.4;
        ctx.strokeStyle = color;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(a.cx - nw, a.neckY + 2);
        ctx.quadraticCurveTo(a.cx + pLag, a.shY + 6 + pLagY, a.cx + nw, a.neckY + 2);
        ctx.stroke();
        // Pendant — swings most
        ctx.fillStyle = shade(color, 1.3);
        ctx.beginPath();
        ctx.arc(a.cx + pLag, a.shY + 4 + pLagY, 2, 0, Math.PI * 2);
        ctx.fill();
        break;
      }
      case 4: { // Bandana
        const bRx = a.headRx * getNarrow(cat);
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.ellipse(a.cx, a.headY + bY - a.headRy + 3, bRx + 1, 4, 0, 0, Math.PI);
        ctx.fill();
        // Knot
        ctx.fillStyle = dk;
        ctx.beginPath();
        ctx.arc(a.cx + bRx, a.headY + bY - a.headRy + 5, 2, 0, Math.PI * 2);
        ctx.fill();
        break;
      }
    }
  });
}

function drawAuraV2(ctx: DrawCtx, dir: Direction, frame: number, style: number, bodyType: 0 | 1 | 2 | 3): void {
  if (style < 0) return;
  const a = BA[bodyType];
  const t = frame * 0.15; // slow animation
  const cy = (a.shY + a.hipY) / 2;
  const [cat] = dirCat(dir);
  const n = getNarrow(cat); // foreshorten aura spread with direction

  ctx.save();
  ctx.globalCompositeOperation = "screen";

  switch (style % 4) {
    case 0: { // Ember — warm orange particles rising
      for (let i = 0; i < 6; i++) {
        const angle = t * 0.8 + (i / 6) * Math.PI * 2;
        const r = 22 + Math.sin(t + i * 1.7) * 5;
        const px = a.cx + Math.cos(angle) * r * n;
        const py = cy + Math.sin(angle) * r * 0.5 - Math.sin(t * 1.3 + i) * 8;
        const alpha = 0.3 + Math.sin(t * 2 + i * 0.8) * 0.2;
        ctx.globalAlpha = alpha;
        ctx.fillStyle = i % 2 === 0 ? "#FF8030" : "#FFB060";
        ctx.beginPath();
        ctx.arc(px, py, 1.5, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }
    case 1: { // Bloom — soft green/pink petals
      for (let i = 0; i < 5; i++) {
        const angle = t * 0.5 + (i / 5) * Math.PI * 2;
        const r = 25 + Math.sin(t * 0.7 + i * 2) * 6;
        const px = a.cx + Math.cos(angle) * r * n;
        const py = cy + Math.sin(angle) * r * 0.4;
        ctx.globalAlpha = 0.25 + Math.sin(t + i) * 0.15;
        ctx.fillStyle = i % 2 === 0 ? "#80FF80" : "#FFB0D0";
        ctx.beginPath();
        ctx.ellipse(px, py, 2, 1.2, angle, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }
    case 2: { // Frost — icy blue sparkles
      for (let i = 0; i < 8; i++) {
        const angle = t * 0.6 + (i / 8) * Math.PI * 2;
        const r = 20 + Math.sin(t * 1.1 + i * 1.3) * 7;
        const px = a.cx + Math.cos(angle) * r * n;
        const py = cy + Math.sin(angle) * r * 0.45;
        ctx.globalAlpha = 0.35 + Math.sin(t * 2.5 + i) * 0.2;
        ctx.fillStyle = i % 3 === 0 ? "#A0E0FF" : "#D0F0FF";
        ctx.beginPath();
        ctx.arc(px, py, 1, 0, Math.PI * 2);
        ctx.fill();
      }
      break;
    }
    case 3: { // Pulse — rhythmic concentric rings
      const pulseR = 18 + Math.sin(t * 2) * 8;
      const alpha = 0.15 + Math.sin(t * 2) * 0.1;
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = "#C0A0FF";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.ellipse(a.cx, cy, pulseR * n, pulseR * 0.45, 0, 0, Math.PI * 2);
      ctx.stroke();
      ctx.globalAlpha = alpha * 0.5;
      ctx.beginPath();
      ctx.ellipse(a.cx, cy, pulseR * 1.5 * n, pulseR * 0.7, 0, 0, Math.PI * 2);
      ctx.stroke();
      break;
    }
  }

  ctx.globalAlpha = 1.0;
  ctx.globalCompositeOperation = "source-over";
  ctx.restore();
}

// ─── Weapon & Shield Rendering ──────────────────────────────────────────────
//
// Weapons are drawn at the hand anchor with rotation derived from the forearm
// angle. This means they naturally follow arm movement through every animation
// state — walk, idle, combat — without manual per-frame positioning.
//
// Direction-aware foreshortening scales weapon length, and withFlip handles
// mirroring. The weapon class determines shape; colors come from WeaponVisual.

/** Module-level weapon visual — set before compositing by room.ts */
let _weaponVisual: WeaponVisual | null = null;

/** Module-level shield visual — set before compositing by room.ts */
let _shieldVisual: ShieldVisual | null = null;

/** Stagger timer — >0 means character is flinching from a hit */
let _staggerTimer = 0;

/** Whether the current character is in a combat animation state.
 *  Used so held cosmetic items (coffee cup, etc.) are only hidden during combat,
 *  not permanently whenever a weapon is equipped. */
let _isCombatAnim = false;

/** Set the weapon rendering context before compositing a character */
export function setWeaponContext(weapon: WeaponVisual | null, shield: ShieldVisual | null): void {
  _weaponVisual = weapon;
  _shieldVisual = shield;
}

/** Set the stagger intensity before compositing a character */
export function setStaggerContext(timer: number): void {
  _staggerTimer = timer;
}

/**
 * Compute the forearm angle from elbow to wrist for weapon rotation.
 * Returns angle in radians (0 = right, π/2 = down).
 */
function forearmAngle(a: BodyAnchors, cat: DirCat): number {
  const n = _getNarrow(cat);
  const wp = _animPose;
  const bs = _bodyShape;
  const armHang = bs.shoulderW + 5 * bs.armMass;
  const elbowX = a.cx - (armHang - 2) * n + wp.nearArmDX * 0.4;
  const elbowY = a.elbowY + wp.nearArmDY * 0.5 + wp.nearElbowBend * 6;
  const wristX = a.cx - (armHang - 4) * n + wp.nearArmDX;
  const wristY = a.wristY + wp.nearArmDY;
  return Math.atan2(wristY - elbowY, wristX - elbowX);
}

/** Far arm forearm angle for shield positioning */
function farArmAngle(a: BodyAnchors, cat: DirCat): number {
  const n = _getNarrow(cat);
  const wp = _animPose;
  const bs = _bodyShape;
  const armHang = bs.shoulderW + 5 * bs.armMass;
  const elbowX = a.cx + (armHang - 2) * n - wp.farArmDX * 0.4;
  const elbowY = a.elbowY + wp.farArmDY * 0.5 + wp.farElbowBend * 6;
  const wristX = a.cx + (armHang - 4) * n - wp.farArmDX;
  const wristY = a.wristY + wp.farArmDY;
  return Math.atan2(wristY - elbowY, wristX - elbowX);
}

/** Foreshortening helper — pre-declared, defined after getNarrow */
let _getNarrow: (cat: DirCat) => number;

function drawWeaponV2(
  ctx: DrawCtx, dir: Direction, _frame: number,
  bodyType: 0 | 1 | 2 | 3
): void {
  if (!_weaponVisual) return;
  const wv = _weaponVisual;
  const a = BA[bodyType];

  withFlip(ctx, dir, (cat) => {
    const isBack = cat === "back" || cat === "qback";
    const n = _getNarrow(cat);
    const wp = _animPose;

    // Near-hand position (mirrors drawBodyV2 near arm endpoint exactly)
    // Uses un-projected armHang — n handles directional foreshortening.
    const bs = _bodyShape;
    const armHang = bs.shoulderW + 5 * bs.armMass;
    const armOffset = cat === "side" ? (armHang - 6) * n : (armHang - 4) * n;
    // In back view, withFlip already mirrors the canvas — use the same formula
    // so the weapon sits at the actual wrist position, not floating at body center.
    const handX = a.cx - armOffset + wp.nearArmDX;
    const handY = a.handY + wp.nearArmDY;

    // Forearm angle determines weapon direction.
    // Full back (N): arm endpoints overlap at narrow=1.0 giving near-zero dx,
    // so hardcode PI/2 (straight down). Quarter-back (NE/NW) has real perspective.
    const angle = cat === "back" ? Math.PI * 0.5 : forearmAngle(a, cat);
    // Weapon extends along forearm direction from hand
    // Base offset: -PI/2 rotates weapon perpendicular to arm (pointing away from body)
    const weaponAngle = angle - Math.PI * 0.65;

    // Foreshorten weapon length based on viewing direction
    const lenScale = cat === "side" ? 0.7 : (cat === "qfront" || cat === "qback") ? 0.85 : 1.0;
    const len = wv.length * lenScale;

    ctx.save();
    ctx.translate(handX, handY);
    ctx.rotate(weaponAngle);

    // In back view, weapon is less visible (behind body)
    if (isBack) ctx.globalAlpha = 0.5;

    switch (wv.class) {
      case "dagger": {
        // Short, fast blade — grip close, small crossguard
        // Handle
        ctx.fillStyle = wv.handleColor;
        ctxRoundRect(ctx, -1.5, -1, 3, 5, 1);
        // Crossguard — small but present, uses accent color
        ctx.fillStyle = wv.accentColor;
        ctx.fillRect(-3, -1.5, 6, 1.5);
        // Blade — short triangular
        const bladeGrad = ctx.createLinearGradient(0, -1, 0, -1 - len);
        bladeGrad.addColorStop(0, wv.bladeColor);
        bladeGrad.addColorStop(1, shade(wv.bladeColor, 1.3));
        ctx.fillStyle = bladeGrad;
        ctx.beginPath();
        ctx.moveTo(-2, -1);
        ctx.lineTo(2, -1);
        ctx.lineTo(0.5, -1 - len);
        ctx.lineTo(-0.5, -1 - len);
        ctx.closePath();
        ctx.fill();
        // Edge gleam
        ctx.strokeStyle = "rgba(255,255,255,0.35)";
        ctx.lineWidth = 0.4;
        ctx.beginPath();
        ctx.moveTo(-1.5, -1);
        ctx.lineTo(-0.3, -1 - len);
        ctx.stroke();
        break;
      }
      case "sword_1h": {
        // Classic sword — pommel, grip, crossguard, blade with taper
        // Pommel
        ctx.fillStyle = wv.accentColor;
        ctx.beginPath();
        ctx.arc(0, 5, 2, 0, Math.PI * 2);
        ctx.fill();
        // Handle
        ctx.fillStyle = wv.handleColor;
        ctx.fillRect(-1.5, 0, 3, 5);
        // Crossguard
        ctx.fillStyle = wv.accentColor;
        ctx.fillRect(-5, -1, 10, 2.5);
        // Blade body
        const sg = ctx.createLinearGradient(0, -1, 0, -1 - len);
        sg.addColorStop(0, wv.bladeColor);
        sg.addColorStop(0.7, shade(wv.bladeColor, 1.15));
        sg.addColorStop(1, shade(wv.bladeColor, 1.3));
        ctx.fillStyle = sg;
        ctx.beginPath();
        ctx.moveTo(-2.5, -1);
        ctx.lineTo(2.5, -1);
        ctx.lineTo(1, -1 - len);
        ctx.lineTo(-1, -1 - len);
        ctx.closePath();
        ctx.fill();
        // Center fuller (groove)
        ctx.strokeStyle = shade(wv.bladeColor, 0.8);
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.moveTo(0, -3);
        ctx.lineTo(0, -1 - len * 0.6);
        ctx.stroke();
        // Edge highlight
        ctx.strokeStyle = "rgba(255,255,255,0.3)";
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        ctx.moveTo(-2, -1);
        ctx.lineTo(-0.8, -1 - len);
        ctx.stroke();
        break;
      }
      case "sword_2h": {
        // Greatsword — longer, wider, heavier, more ornate crossguard
        // Pommel
        ctx.fillStyle = wv.accentColor;
        ctx.beginPath();
        ctx.arc(0, 7, 2.5, 0, Math.PI * 2);
        ctx.fill();
        // Long grip (two hands)
        ctx.fillStyle = wv.handleColor;
        ctx.fillRect(-2, 0, 4, 7);
        // Grip wrapping lines
        ctx.strokeStyle = shade(wv.handleColor, 0.7);
        ctx.lineWidth = 0.5;
        for (let i = 1; i < 7; i += 2) {
          ctx.beginPath();
          ctx.moveTo(-2, i);
          ctx.lineTo(2, i);
          ctx.stroke();
        }
        // Ornate crossguard with flared ends
        ctx.fillStyle = wv.accentColor;
        ctx.beginPath();
        ctx.moveTo(-7, 0);
        ctx.lineTo(-6, -2);
        ctx.lineTo(6, -2);
        ctx.lineTo(7, 0);
        ctx.lineTo(6, 1);
        ctx.lineTo(-6, 1);
        ctx.closePath();
        ctx.fill();
        // Massive blade
        const g2h = ctx.createLinearGradient(0, -2, 0, -2 - len);
        g2h.addColorStop(0, wv.bladeColor);
        g2h.addColorStop(0.5, shade(wv.bladeColor, 1.1));
        g2h.addColorStop(1, shade(wv.bladeColor, 1.25));
        ctx.fillStyle = g2h;
        ctx.beginPath();
        ctx.moveTo(-3.5, -2);
        ctx.lineTo(3.5, -2);
        ctx.lineTo(1.5, -2 - len);
        ctx.lineTo(-1.5, -2 - len);
        ctx.closePath();
        ctx.fill();
        // Center fuller
        ctx.strokeStyle = shade(wv.bladeColor, 0.75);
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.moveTo(0, -4);
        ctx.lineTo(0, -2 - len * 0.5);
        ctx.stroke();
        // Edge highlight both sides
        ctx.strokeStyle = "rgba(255,255,255,0.25)";
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        ctx.moveTo(-3, -2);
        ctx.lineTo(-1.3, -2 - len);
        ctx.moveTo(3, -2);
        ctx.lineTo(1.3, -2 - len);
        ctx.stroke();
        break;
      }
      case "staff": {
        // Long wooden/crystal staff — shaft with ornamental tip
        const shaftLen = len * 1.4; // staffs are tall
        // Shaft
        const shaftGrad = ctx.createLinearGradient(0, 6, 0, -shaftLen);
        shaftGrad.addColorStop(0, shade(wv.handleColor, 0.8));
        shaftGrad.addColorStop(0.5, wv.handleColor);
        shaftGrad.addColorStop(1, shade(wv.handleColor, 1.1));
        ctx.fillStyle = shaftGrad;
        ctx.fillRect(-1.5, -shaftLen, 3, shaftLen + 6);
        // Binding wraps near grip
        ctx.strokeStyle = wv.accentColor;
        ctx.lineWidth = 0.8;
        for (let i = -1; i < 5; i += 2) {
          ctx.beginPath();
          ctx.moveTo(-2, i);
          ctx.lineTo(2, i);
          ctx.stroke();
        }
        // Crystal/orb tip — glowing, scaled to weapon length
        ctx.save();
        ctx.globalCompositeOperation = "screen";
        const glowR = 3 + len * 0.12;  // scales with weapon length (4–6.6 for len 10–30)
        const tipGlow = ctx.createRadialGradient(0, -shaftLen, 0, 0, -shaftLen, glowR * 2);
        tipGlow.addColorStop(0, wv.bladeColor);
        tipGlow.addColorStop(0.5, shade(wv.bladeColor, 1.2) + "80");
        tipGlow.addColorStop(1, shade(wv.bladeColor, 1.5) + "00");
        ctx.fillStyle = tipGlow;
        ctx.beginPath();
        ctx.arc(0, -shaftLen, glowR * 2, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
        // Solid crystal core
        ctx.fillStyle = wv.bladeColor;
        ctx.beginPath();
        ctx.moveTo(0, -shaftLen - glowR);
        ctx.lineTo(-glowR * 0.6, -shaftLen + 1);
        ctx.lineTo(glowR * 0.6, -shaftLen + 1);
        ctx.closePath();
        ctx.fill();
        // Inner sparkle
        ctx.fillStyle = "rgba(255,255,255,0.6)";
        ctx.beginPath();
        ctx.arc(0, -shaftLen - 1, 1.2, 0, Math.PI * 2);
        ctx.fill();
        break;
      }
      case "axe": {
        // Heavy axe — handle with wide curved head
        // Handle
        const handleLen = len * 0.8;
        ctx.fillStyle = wv.handleColor;
        ctx.fillRect(-1.5, -handleLen, 3, handleLen + 4);
        // Axe head — curved blade on one side
        ctx.fillStyle = wv.bladeColor;
        ctx.beginPath();
        ctx.moveTo(-1, -handleLen + 2);
        ctx.lineTo(-1, -handleLen - 4);
        ctx.quadraticCurveTo(-10, -handleLen - 1, -8, -handleLen + 6);
        ctx.quadraticCurveTo(-6, -handleLen + 4, -1, -handleLen + 2);
        ctx.closePath();
        ctx.fill();
        // Axe edge gleam — trace along the curved blade edge
        ctx.strokeStyle = shade(wv.bladeColor, 1.4);
        ctx.lineWidth = 0.6;
        ctx.beginPath();
        ctx.moveTo(-1, -handleLen - 4);
        ctx.quadraticCurveTo(-10, -handleLen - 1, -8, -handleLen + 6);
        ctx.stroke();
        // Binding at head
        ctx.fillStyle = wv.accentColor;
        ctx.fillRect(-3, -handleLen, 6, 3);
        break;
      }
      case "mace": {
        // Flanged mace — handle with heavy spherical head and flanges
        const handleLen = len * 0.65;
        // Handle
        ctx.fillStyle = wv.handleColor;
        ctx.fillRect(-1.5, -handleLen, 3, handleLen + 3);
        // Head sphere
        const headY = -handleLen - 4;
        const headR = 5;
        const headGrad = ctx.createRadialGradient(0, headY, 0, 0, headY, headR);
        headGrad.addColorStop(0, shade(wv.bladeColor, 1.2));
        headGrad.addColorStop(0.7, wv.bladeColor);
        headGrad.addColorStop(1, shade(wv.bladeColor, 0.7));
        ctx.fillStyle = headGrad;
        ctx.beginPath();
        ctx.arc(0, headY, headR, 0, Math.PI * 2);
        ctx.fill();
        // Flanges — 4 protruding ridges
        ctx.fillStyle = shade(wv.bladeColor, 0.85);
        for (let i = 0; i < 4; i++) {
          const fa = (i / 4) * Math.PI * 2;
          const fx = Math.cos(fa) * (headR + 2);
          const fy = headY + Math.sin(fa) * (headR + 2);
          ctx.beginPath();
          ctx.moveTo(fx, fy);
          ctx.lineTo(fx - Math.sin(fa) * 1.5, fy + Math.cos(fa) * 1.5);
          ctx.lineTo(Math.cos(fa) * headR * 0.3, headY + Math.sin(fa) * headR * 0.3);
          ctx.lineTo(fx + Math.sin(fa) * 1.5, fy - Math.cos(fa) * 1.5);
          ctx.closePath();
          ctx.fill();
        }
        // Accent collar
        ctx.fillStyle = wv.accentColor;
        ctx.fillRect(-3, -handleLen - 1, 6, 2.5);
        break;
      }
    }

    ctx.restore();
  });
}

function drawShieldV2(
  ctx: DrawCtx, dir: Direction, _frame: number,
  bodyType: 0 | 1 | 2 | 3
): void {
  if (!_shieldVisual) return;
  const sv = _shieldVisual;
  const a = BA[bodyType];

  withFlip(ctx, dir, (cat) => {
    const isBack = cat === "back" || cat === "qback";
    const n = _getNarrow(cat);
    const wp = _animPose;

    // Far-hand position (shield arm) — mirrors drawBodyV2 far arm endpoint.
    // Uses un-projected armHang — n handles directional foreshortening.
    const bs = _bodyShape;
    const armHang = bs.shoulderW + 5 * bs.armMass;
    const armOffset = cat === "side" ? (armHang - 6) * n : (armHang - 4) * n;
    const handX = a.cx + armOffset - wp.farArmDX;
    const handY = a.handY + wp.farArmDY;

    const angle = cat === "back" ? Math.PI * 0.5 : farArmAngle(a, cat);
    const shieldAngle = angle - Math.PI * 0.5;

    // Foreshorten
    const lenScale = cat === "side" ? 0.65 : (cat === "qfront" || cat === "qback") ? 0.8 : 1.0;

    ctx.save();
    ctx.translate(handX, handY - 4); // shield rides slightly above hand
    ctx.rotate(shieldAngle);

    if (isBack) ctx.globalAlpha = 0.4;

    switch (sv.class) {
      case "buckler": {
        // Small round shield
        const r = 8 * lenScale;
        ctx.fillStyle = sv.faceColor;
        ctx.beginPath();
        ctx.arc(0, 0, r, 0, Math.PI * 2);
        ctx.fill();
        // Rim
        ctx.strokeStyle = sv.rimColor;
        ctx.lineWidth = 1.5;
        ctx.stroke();
        // Center boss
        ctx.fillStyle = sv.emblemColor;
        ctx.beginPath();
        ctx.arc(0, 0, r * 0.3, 0, Math.PI * 2);
        ctx.fill();
        break;
      }
      case "kite": {
        // Pointed kite shield
        const h = 16 * lenScale;
        const w = 10 * lenScale;
        ctx.fillStyle = sv.faceColor;
        ctx.beginPath();
        ctx.moveTo(-w / 2, -h * 0.3);
        ctx.quadraticCurveTo(-w / 2, -h * 0.1, 0, h * 0.7);
        ctx.quadraticCurveTo(w / 2, -h * 0.1, w / 2, -h * 0.3);
        ctx.quadraticCurveTo(0, -h * 0.55, -w / 2, -h * 0.3);
        ctx.closePath();
        ctx.fill();
        // Rim
        ctx.strokeStyle = sv.rimColor;
        ctx.lineWidth = 1.2;
        ctx.stroke();
        // Center line emblem
        ctx.strokeStyle = sv.emblemColor;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(0, -h * 0.4);
        ctx.lineTo(0, h * 0.4);
        ctx.stroke();
        break;
      }
      case "tower": {
        // Tall rectangular shield with reinforcement
        const h = 20 * lenScale;
        const w = 12 * lenScale;
        // Face fill
        ctx.fillStyle = sv.faceColor;
        ctxRoundRect(ctx, -w / 2, -h / 2, w, h, 2);
        // Reinforcement cross
        ctx.strokeStyle = sv.rimColor;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(-w / 2, 0);
        ctx.lineTo(w / 2, 0);
        ctx.moveTo(0, -h / 2);
        ctx.lineTo(0, h / 2);
        ctx.stroke();
        // Rim outline — stroke-only, no fill (preserve faceColor underneath)
        ctx.strokeStyle = sv.rimColor;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.roundRect(-w / 2, -h / 2, w, h, 2);
        ctx.stroke();
        // Center boss
        ctx.fillStyle = sv.emblemColor;
        ctx.beginPath();
        ctx.arc(0, 0, 2.5, 0, Math.PI * 2);
        ctx.fill();
        break;
      }
    }

    ctx.restore();
  });
}

// ─── V2 Clothing / Hair / Hat / Item layers ─────────────────────────────────
//
// All layers now draw directly at 96×144 using BA anchors — no more legacy
// 48×72 pixel art scaled through spriteAdapter. This eliminates the
// coordinate mismatch that caused clothing to appear misaligned with the body.
//
// Every function here uses the same directional narrowing and bezier
// body contour as drawBodyV2 so clothing wraps the body naturally.

/** Directional foreshortening factor — trigonometric projection.
 *  Uses average body depth/width ratio (0.55) for generic callers.
 *  For body-region-specific projection, use projectW() directly. */
function getNarrow(cat: DirCat): number {
  // Normalize to unit lateral width so result is a ≤1.0 multiplier
  const pw = projectW(cat, 1.0, 0.55);
  return Math.min(pw, 1.0);
}

// Wire up the deferred reference now that getNarrow is defined
_getNarrow = getNarrow;

// ─── Anatomy Micro-Detail V2 ────────────────────────────────────────────────
// Subtle anatomical highlights visible on bare skin: collarbones, deltoid caps,
// sternum line, hip catchlight. All use very low alpha — they add dimensional
// depth without looking painted-on. Drawn UNDER clothing layers.

function drawAnatomyDetailV2(
  ctx: DrawCtx, dir: Direction, _frame: number,
  skinColor: string, bodyType: 0 | 1 | 2 | 3,
  nude: boolean = false, bodyGender: BodyGender = "androgynous",
  breastSize: number = 2, buttSize: number = 2, genitalSize: number = 2
): void {
  const a = BA[bodyType];
  const bs = _bodyShape;
  const wp = _animPose;
  const bY = -wp.breatheY;

  withFlip(ctx, dir, (cat) => {
    const isFront = cat === "front" || cat === "qfront";
    const isBack  = cat === "back"  || cat === "qback";
    const n = getNarrow(cat);
    const sw = projectW(cat, bs.shoulderW, bs.shoulderD);

    if (isFront) {
      // ── Collarbones — two arcs from neck base to shoulder edge ──
      const neckBase = a.neckY + bY * 0.8 + (a.shY - a.neckY) * 0.85;
      ctx.strokeStyle = "rgba(0,0,0,0.05)";
      ctx.lineWidth = 0.7;
      // Left collarbone
      ctx.beginPath();
      ctx.moveTo(a.cx - bs.neckW * 0.25, neckBase);
      ctx.quadraticCurveTo(a.cx - sw * 0.45, neckBase + 1.5, a.cx - sw + 2, a.shY + 1);
      ctx.stroke();
      // Right collarbone
      ctx.beginPath();
      ctx.moveTo(a.cx + bs.neckW * 0.25, neckBase);
      ctx.quadraticCurveTo(a.cx + sw * 0.45, neckBase + 1.5, a.cx + sw - 2, a.shY + 1);
      ctx.stroke();

      // ── Deltoid caps — subtle shoulder roundness ──
      ctx.fillStyle = "rgba(255,255,255,0.03)";
      const deltR = bs.armMass * 3;
      ctx.beginPath(); ctx.ellipse(a.cx - sw - 1, a.shY + 3, deltR, deltR * 0.7, 0.2, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.ellipse(a.cx + sw + 1, a.shY + 3, deltR, deltR * 0.7, -0.2, 0, Math.PI * 2); ctx.fill();

      // ── Sternum hint — faint vertical line center chest ──
      const chestY = a.shY + (a.waistY - a.shY) * 0.35;
      ctx.strokeStyle = "rgba(0,0,0,0.03)";
      ctx.lineWidth = 0.5;
      ctx.beginPath();
      ctx.moveTo(a.cx, a.shY + 4);
      ctx.lineTo(a.cx, chestY);
      ctx.stroke();

      // ── Hip catchlight — soft highlight on iliac crest ──
      const hw = projectW(cat, bs.hipW, bs.hipD);
      ctx.fillStyle = "rgba(255,255,255,0.025)";
      ctx.beginPath(); ctx.ellipse(a.cx - hw + 3, a.hipY - 3, 3, 1.5, -0.3, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.ellipse(a.cx + hw - 3, a.hipY - 3, 3, 1.5, 0.3, 0, Math.PI * 2); ctx.fill();

    } else if (isBack) {
      // ── Deltoid caps from behind ──
      ctx.fillStyle = "rgba(0,0,0,0.025)";
      const deltR = bs.armMass * 2.5;
      ctx.beginPath(); ctx.ellipse(a.cx - sw - 1, a.shY + 3, deltR, deltR * 0.6, 0.15, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.ellipse(a.cx + sw + 1, a.shY + 3, deltR, deltR * 0.6, -0.15, 0, Math.PI * 2); ctx.fill();

      // ── Lower back dimples (Venus dimples) — two small shadows ──
      const hw = projectW(cat, bs.hipW, bs.hipD);
      const dimpleX = hw * 0.3;
      ctx.fillStyle = "rgba(0,0,0,0.025)";
      ctx.beginPath(); ctx.ellipse(a.cx - dimpleX, a.hipY - 4, 1.5, 1.5, 0, 0, Math.PI * 2); ctx.fill();
      ctx.beginPath(); ctx.ellipse(a.cx + dimpleX, a.hipY - 4, 1.5, 1.5, 0, 0, Math.PI * 2); ctx.fill();

      if (nude) {
        // ── Gluteal anatomy with jiggle physics ──
        const bSz = buttSize / 4; // 0-1 normalized
        const buttDepth = 6 + bSz * 6;
        const buttTop = a.hipY - 1;
        const buttBot = a.hipY + buttDepth;
        // Walk jiggle — each cheek shifts independently with step
        const jL = wp.buttJiggleL * bSz * 0.6;
        const jR = wp.buttJiggleR * bSz * 0.6;

        if (cat === "back") {
          // ── Full rear view ──
          // Gluteal cleft
          ctx.strokeStyle = `rgba(0,0,0,${0.06 + bSz * 0.03})`;
          ctx.lineWidth = 0.8 + bSz * 0.4;
          ctx.beginPath();
          ctx.moveTo(a.cx, buttTop);
          ctx.bezierCurveTo(a.cx, buttTop + buttDepth * 0.3, a.cx, buttBot - 2, a.cx, buttBot);
          ctx.stroke();
          // Cheek curves with jiggle offset
          const gluteW = hw * (0.35 + bSz * 0.15);
          const cheekBulge = 1 + bSz * 2.5;
          ctx.strokeStyle = `rgba(0,0,0,${0.035 + bSz * 0.02})`;
          ctx.lineWidth = 0.6;
          // Left cheek
          ctx.beginPath();
          ctx.moveTo(a.cx - gluteW * 0.3, buttTop);
          ctx.bezierCurveTo(
            a.cx - gluteW - cheekBulge + jL * 0.3, buttTop + buttDepth * 0.4 + jL * 0.5,
            a.cx - gluteW - cheekBulge * 0.5 + jL * 0.2, buttBot - 2,
            a.cx - 1, buttBot + 1);
          ctx.stroke();
          // Right cheek
          ctx.beginPath();
          ctx.moveTo(a.cx + gluteW * 0.3, buttTop);
          ctx.bezierCurveTo(
            a.cx + gluteW + cheekBulge + jR * 0.3, buttTop + buttDepth * 0.4 + jR * 0.5,
            a.cx + gluteW + cheekBulge * 0.5 + jR * 0.2, buttBot - 2,
            a.cx + 1, buttBot + 1);
          ctx.stroke();
          // Underfold creases
          ctx.strokeStyle = `rgba(0,0,0,${0.04 + bSz * 0.02})`;
          ctx.beginPath();
          ctx.moveTo(a.cx - gluteW, buttBot - 1);
          ctx.quadraticCurveTo(a.cx - gluteW * 0.4, buttBot + 1.5 + bSz + jL * 0.3, a.cx - 1, buttBot);
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(a.cx + gluteW, buttBot - 1);
          ctx.quadraticCurveTo(a.cx + gluteW * 0.4, buttBot + 1.5 + bSz + jR * 0.3, a.cx + 1, buttBot);
          ctx.stroke();
          // Cheek highlights
          if (bSz > 0.25) {
            ctx.fillStyle = `rgba(255,255,255,${0.02 + bSz * 0.015})`;
            ctx.beginPath();
            ctx.ellipse(a.cx - gluteW * 0.5 + jL * 0.2, buttTop + buttDepth * 0.4, gluteW * 0.4, buttDepth * 0.3, -0.15, 0, Math.PI * 2);
            ctx.fill();
            ctx.beginPath();
            ctx.ellipse(a.cx + gluteW * 0.5 + jR * 0.2, buttTop + buttDepth * 0.4, gluteW * 0.4, buttDepth * 0.3, 0.15, 0, Math.PI * 2);
            ctx.fill();
          }
        } else {
          // ── Quarter-back view: foreshortened cheeks, near cheek larger ──
          const gluteW = hw * (0.3 + bSz * 0.12);
          const cheekBulge = 0.8 + bSz * 2.0;
          // Partial cleft (shorter, foreshortened)
          ctx.strokeStyle = `rgba(0,0,0,${0.05 + bSz * 0.025})`;
          ctx.lineWidth = 0.7 + bSz * 0.3;
          ctx.beginPath();
          ctx.moveTo(a.cx + 1, buttTop + 1);
          ctx.bezierCurveTo(a.cx + 1, buttTop + buttDepth * 0.3, a.cx, buttBot - 3, a.cx, buttBot - 1);
          ctx.stroke();
          // Near cheek (larger, closer to camera)
          ctx.strokeStyle = `rgba(0,0,0,${0.035 + bSz * 0.02})`;
          ctx.lineWidth = 0.6;
          ctx.beginPath();
          ctx.moveTo(a.cx - gluteW * 0.2, buttTop);
          ctx.bezierCurveTo(
            a.cx - gluteW * 1.1 - cheekBulge + jL * 0.3, buttTop + buttDepth * 0.4,
            a.cx - gluteW * 0.8, buttBot - 2,
            a.cx - 1, buttBot);
          ctx.stroke();
          // Far cheek (smaller, foreshortened)
          ctx.beginPath();
          ctx.moveTo(a.cx + gluteW * 0.15, buttTop);
          ctx.bezierCurveTo(
            a.cx + gluteW * 0.7 + cheekBulge * 0.6 + jR * 0.2, buttTop + buttDepth * 0.35,
            a.cx + gluteW * 0.5, buttBot - 3,
            a.cx + 1, buttBot);
          ctx.stroke();
          // Underfold (near cheek only — more visible)
          ctx.strokeStyle = `rgba(0,0,0,${0.035 + bSz * 0.015})`;
          ctx.beginPath();
          ctx.moveTo(a.cx - gluteW * 0.8, buttBot - 1);
          ctx.quadraticCurveTo(a.cx - gluteW * 0.3, buttBot + 1 + bSz * 0.7, a.cx - 1, buttBot);
          ctx.stroke();
          // Near cheek highlight
          if (bSz > 0.3) {
            ctx.fillStyle = `rgba(255,255,255,${0.015 + bSz * 0.012})`;
            ctx.beginPath();
            ctx.ellipse(a.cx - gluteW * 0.5, buttTop + buttDepth * 0.38, gluteW * 0.35, buttDepth * 0.25, -0.2, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }
    }

    // ── Nude anatomy — drawn on all applicable view angles ──
    if (nude) {
      const ww = projectW(cat, bs.waistW, bs.waistD);
      const hw = projectW(cat, bs.hipW, bs.hipD);
      const nipColor = shade(skinColor, 0.72);
      const areolaColor = shade(skinColor, 0.78);

      // ── Navel — front and quarter-front views ──
      if (isFront) {
        const navelY = a.waistY + (a.hipY - a.waistY) * 0.15;
        // Navel pit
        ctx.fillStyle = "rgba(0,0,0,0.08)";
        ctx.beginPath();
        ctx.ellipse(a.cx, navelY, 1.3, 1.8, 0, 0, Math.PI * 2);
        ctx.fill();
        // Inner highlight
        ctx.fillStyle = "rgba(0,0,0,0.04)";
        ctx.beginPath();
        ctx.ellipse(a.cx, navelY - 0.5, 0.8, 1.0, 0, 0, Math.PI * 2);
        ctx.fill();
        // Linea alba — midline from sternum to pubis
        ctx.strokeStyle = "rgba(0,0,0,0.03)";
        ctx.lineWidth = 0.4;
        ctx.beginPath();
        ctx.moveTo(a.cx, a.shY + (a.waistY - a.shY) * 0.5);
        ctx.lineTo(a.cx, navelY - 1.5);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(a.cx, navelY + 2);
        ctx.lineTo(a.cx, a.hipY - 2);
        ctx.stroke();
      }

      // ── Breast / chest anatomy — gender and size driven with bounce physics ──
      const brstSz = breastSize / 4; // 0-1 normalized
      const showBreasts = (bodyGender === "feminine" || bodyGender === "androgynous") && breastSize > 0;
      const showPecs = bodyGender === "masculine";
      // Bounce offset — breasts move vertically with walk impact
      const bbY = wp.breastBounceY * (0.4 + brstSz * 0.6);
      // Velocity-driven stretch: fast downward motion elongates, fast upward compresses
      const bbStretch = 1.0 + wp.breastBounceVel * 0.04 * brstSz;

      if (showBreasts) {
        // Breast radius scales: breastSize 0=flat(2px), 1=small(3.5), 2=medium(5), 3=large(6.5), 4=very large(8)
        const baseR = 2 + brstSz * 6;
        const brR = baseR * bs.scale;
        const spacing = sw * (0.38 + brstSz * 0.06);
        const chestY = a.shY + (a.waistY - a.shY) * (0.32 + brstSz * 0.06) + bbY;
        // Sag increases with size + dynamic sag from bounce
        const sagY = brstSz * 2 + Math.max(0, bbY * 0.3);
        const nipOffset = brR * (0.2 + brstSz * 0.15); // nipple moves down with size
        // Vertical stretch from bounce velocity
        const brRY = brR * bbStretch;
        // Areola and nipple sizes — shared across all directions
        const areR = 1.5 + brstSz * 2;
        const nipR = 0.7 + brstSz * 0.5;

        if (cat === "front") {
          // ── Full frontal breasts — symmetric, with bounce ──
          if (brstSz > 0.25) {
            const brFill = shade(skinColor, 0.97);
            ctx.fillStyle = brFill;
            ctx.globalAlpha = 0.15 + brstSz * 0.2;
            ctx.beginPath();
            ctx.ellipse(a.cx - spacing, chestY + sagY, brR * 0.9, brRY * (0.85 + brstSz * 0.1), 0.05, 0, Math.PI * 2);
            ctx.fill();
            ctx.beginPath();
            ctx.ellipse(a.cx + spacing, chestY + sagY, brR * 0.9, brRY * (0.85 + brstSz * 0.1), -0.05, 0, Math.PI * 2);
            ctx.fill();
            ctx.globalAlpha = 1;
          }
          // Undercurve shadow
          ctx.strokeStyle = `rgba(0,0,0,${0.06 + brstSz * 0.04})`;
          ctx.lineWidth = 0.7 + brstSz * 0.5;
          ctx.beginPath();
          ctx.arc(a.cx - spacing, chestY + sagY, brR, 0.2 + brstSz * 0.1, Math.PI - 0.2 - brstSz * 0.1);
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(a.cx + spacing, chestY + sagY, brR, 0.2 + brstSz * 0.1, Math.PI - 0.2 - brstSz * 0.1);
          ctx.stroke();
          // Cleavage
          if (brstSz > 0.4) {
            ctx.strokeStyle = `rgba(0,0,0,${0.03 + brstSz * 0.03})`;
            ctx.lineWidth = 0.5;
            const clvTop = chestY - brR * 0.3;
            const clvBot = chestY + sagY + brR * 0.3;
            ctx.beginPath();
            ctx.moveTo(a.cx - spacing + brR * 0.7, clvTop);
            ctx.bezierCurveTo(a.cx - 1, clvTop + (clvBot - clvTop) * 0.4, a.cx - 1, clvBot - 3, a.cx - spacing * 0.3, clvBot);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(a.cx + spacing - brR * 0.7, clvTop);
            ctx.bezierCurveTo(a.cx + 1, clvTop + (clvBot - clvTop) * 0.4, a.cx + 1, clvBot - 3, a.cx + spacing * 0.3, clvBot);
            ctx.stroke();
          }
          // Upper highlight
          ctx.strokeStyle = `rgba(255,255,255,${0.03 + brstSz * 0.02})`;
          ctx.lineWidth = 0.5;
          ctx.beginPath(); ctx.arc(a.cx - spacing, chestY + sagY - 1, brR * 0.8, Math.PI + 0.4, -0.4); ctx.stroke();
          ctx.beginPath(); ctx.arc(a.cx + spacing, chestY + sagY - 1, brR * 0.8, Math.PI + 0.4, -0.4); ctx.stroke();
          // Areolae + nipples — at natural opacity, not ghostly
          const nipY = chestY + sagY + nipOffset;
          ctx.fillStyle = areolaColor;
          ctx.globalAlpha = 0.4 + brstSz * 0.15;
          ctx.beginPath(); ctx.ellipse(a.cx - spacing, nipY, areR, areR * (0.9 + brstSz * 0.05), 0, 0, Math.PI * 2); ctx.fill();
          ctx.beginPath(); ctx.ellipse(a.cx + spacing, nipY, areR, areR * (0.9 + brstSz * 0.05), 0, 0, Math.PI * 2); ctx.fill();
          ctx.fillStyle = nipColor;
          ctx.globalAlpha = 0.7 + brstSz * 0.15;
          ctx.beginPath(); ctx.arc(a.cx - spacing, nipY, nipR, 0, Math.PI * 2); ctx.fill();
          ctx.beginPath(); ctx.arc(a.cx + spacing, nipY, nipR, 0, Math.PI * 2); ctx.fill();
          ctx.globalAlpha = 1;

        } else if (cat === "qfront") {
          // ── Quarter-front: near breast foreshortened smaller, far breast larger ──
          // Perspective: far breast (right) is more frontal, near breast (left) turns away
          const nearR = brR * 0.7;  // foreshortened
          const farR = brR * 0.95;
          const nearSpc = spacing * 0.8;
          const farSpc = spacing * 1.0;
          // Near breast (partially turning away)
          if (brstSz > 0.3) {
            ctx.fillStyle = shade(skinColor, 0.96);
            ctx.globalAlpha = 0.1 + brstSz * 0.12;
            ctx.beginPath();
            ctx.ellipse(a.cx - nearSpc, chestY + sagY, nearR * 0.75, brRY * 0.8, 0.15, 0, Math.PI * 2);
            ctx.fill();
            ctx.globalAlpha = 1;
          }
          ctx.strokeStyle = `rgba(0,0,0,${0.05 + brstSz * 0.03})`;
          ctx.lineWidth = 0.6 + brstSz * 0.3;
          ctx.beginPath(); ctx.arc(a.cx - nearSpc, chestY + sagY, nearR, 0.3, Math.PI - 0.1); ctx.stroke();
          // Far breast (more visible)
          if (brstSz > 0.25) {
            ctx.fillStyle = shade(skinColor, 0.97);
            ctx.globalAlpha = 0.15 + brstSz * 0.18;
            ctx.beginPath();
            ctx.ellipse(a.cx + farSpc, chestY + sagY, farR * 0.85, brRY * (0.85 + brstSz * 0.1), -0.08, 0, Math.PI * 2);
            ctx.fill();
            ctx.globalAlpha = 1;
          }
          ctx.strokeStyle = `rgba(0,0,0,${0.06 + brstSz * 0.03})`;
          ctx.lineWidth = 0.7 + brstSz * 0.4;
          ctx.beginPath(); ctx.arc(a.cx + farSpc, chestY + sagY, farR, 0.15, Math.PI - 0.15); ctx.stroke();
          // Areolae — near foreshortened, far full
          const nearAreR = areR * 0.7;
          const farAreR = areR;
          const nearNipY = chestY + sagY + nipOffset * 0.8;
          const farNipY = chestY + sagY + nipOffset;
          ctx.fillStyle = areolaColor;
          ctx.globalAlpha = 0.3 + brstSz * 0.1;
          ctx.beginPath(); ctx.ellipse(a.cx - nearSpc + 1, nearNipY, nearAreR * 0.8, nearAreR * 0.9, 0.1, 0, Math.PI * 2); ctx.fill();
          ctx.globalAlpha = 0.4 + brstSz * 0.15;
          ctx.beginPath(); ctx.ellipse(a.cx + farSpc, farNipY, farAreR, farAreR * 0.9, 0, 0, Math.PI * 2); ctx.fill();
          ctx.fillStyle = nipColor;
          ctx.globalAlpha = 0.55 + brstSz * 0.15;
          ctx.beginPath(); ctx.arc(a.cx - nearSpc + 1, nearNipY, nipR * 0.7, 0, Math.PI * 2); ctx.fill();
          ctx.globalAlpha = 0.7 + brstSz * 0.15;
          ctx.beginPath(); ctx.arc(a.cx + farSpc, farNipY, nipR, 0, Math.PI * 2); ctx.fill();
          ctx.globalAlpha = 1;

        } else if (cat === "side") {
          // ── Side view breast — profile with bounce ──
          const profR = brR * (0.8 + brstSz * 0.15);
          const profX = a.cx + sw + profR * (0.15 + brstSz * 0.2);
          const profY = chestY + sagY;
          // Volume fill
          if (brstSz > 0.25) {
            ctx.fillStyle = shade(skinColor, 0.97);
            ctx.globalAlpha = 0.12 + brstSz * 0.15;
            ctx.beginPath();
            ctx.ellipse(profX, profY, profR * 0.7, profR * 0.85 * bbStretch, 0.1, 0, Math.PI * 2);
            ctx.fill();
            ctx.globalAlpha = 1;
          }
          // Profile curve
          ctx.strokeStyle = `rgba(0,0,0,${0.05 + brstSz * 0.03})`;
          ctx.lineWidth = 0.6 + brstSz * 0.3;
          ctx.beginPath();
          ctx.arc(profX, profY, profR, -0.6, Math.PI * 0.75);
          ctx.stroke();
          // Undercurve
          ctx.strokeStyle = `rgba(0,0,0,${0.04 + brstSz * 0.03})`;
          ctx.lineWidth = 0.5 + brstSz * 0.2;
          ctx.beginPath();
          ctx.arc(profX - profR * 0.1, profY + profR * 0.15, profR * 0.9, 0.3, Math.PI * 0.7);
          ctx.stroke();
          // Nipple — points outward in profile
          const sideNipX = profX + profR * 0.55;
          const sideNipY = profY + profR * 0.05 + bbY * 0.3;
          // Areola — profile view, foreshortened horizontally
          ctx.fillStyle = areolaColor;
          ctx.globalAlpha = 0.35 + brstSz * 0.12;
          ctx.beginPath(); ctx.ellipse(sideNipX, sideNipY, 1.2 + brstSz * 0.8, 1.5 + brstSz, 0.2, 0, Math.PI * 2); ctx.fill();
          ctx.fillStyle = nipColor;
          ctx.globalAlpha = 0.65 + brstSz * 0.1;
          ctx.beginPath(); ctx.arc(sideNipX, sideNipY, 0.7 + brstSz * 0.3, 0, Math.PI * 2); ctx.fill();
          ctx.globalAlpha = 1;

        } else if (cat === "qback") {
          // ── Quarter-back: side-peek of breast visible past the turned torso ──
          // Only the outer curve and possibly nipple of the near breast peeks around
          if (brstSz > 0.3) {
            const peekR = brR * 0.55;
            const peekX = a.cx - sw - peekR * 0.3;
            const peekY = chestY + sagY;
            // Subtle volume peek
            ctx.fillStyle = shade(skinColor, 0.96);
            ctx.globalAlpha = 0.08 + brstSz * 0.1;
            ctx.beginPath();
            ctx.ellipse(peekX, peekY, peekR * 0.5, peekR * 0.75 * bbStretch, -0.15, 0, Math.PI * 2);
            ctx.fill();
            ctx.globalAlpha = 1;
            // Outer curve
            ctx.strokeStyle = `rgba(0,0,0,${0.04 + brstSz * 0.02})`;
            ctx.lineWidth = 0.5 + brstSz * 0.2;
            ctx.beginPath();
            ctx.arc(peekX, peekY, peekR, Math.PI * 0.3, Math.PI * 1.2);
            ctx.stroke();
            // Nipple peek (only large sizes)
            if (brstSz > 0.6) {
              ctx.fillStyle = nipColor;
              ctx.globalAlpha = 0.5;
              ctx.beginPath(); ctx.arc(peekX - peekR * 0.3, peekY + peekR * 0.15, 0.5 + brstSz * 0.2, 0, Math.PI * 2); ctx.fill();
              ctx.globalAlpha = 1;
            }
          }
        }
      }

      if (showPecs) {
        // ── Masculine pectoral definition — musculature lines + nipples ──
        if (isFront) {
          const chestY = a.shY + (a.waistY - a.shY) * 0.3;
          const pecW = sw * 0.42;
          const pecH = (a.waistY - a.shY) * 0.22;
          // Pectoral mass shadow
          ctx.strokeStyle = "rgba(0,0,0,0.05)";
          ctx.lineWidth = 0.7;
          // Left pec
          ctx.beginPath();
          ctx.moveTo(a.cx - 1, chestY - pecH * 0.2);
          ctx.bezierCurveTo(a.cx - pecW * 0.5, chestY - 1, a.cx - pecW, chestY - 2, a.cx - pecW - 1, chestY + 1);
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(a.cx - pecW, chestY + 1);
          ctx.quadraticCurveTo(a.cx - pecW * 0.6, chestY + pecH * 0.6, a.cx - 2, chestY + pecH * 0.3);
          ctx.stroke();
          // Right pec
          ctx.beginPath();
          ctx.moveTo(a.cx + 1, chestY - pecH * 0.2);
          ctx.bezierCurveTo(a.cx + pecW * 0.5, chestY - 1, a.cx + pecW, chestY - 2, a.cx + pecW + 1, chestY + 1);
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(a.cx + pecW, chestY + 1);
          ctx.quadraticCurveTo(a.cx + pecW * 0.6, chestY + pecH * 0.6, a.cx + 2, chestY + pecH * 0.3);
          ctx.stroke();
          // Nipples
          const nipX = sw * 0.35;
          const nipY = chestY + pecH * 0.35;
          // Areola — masculine areolae are smaller/flatter but still visible
          ctx.fillStyle = areolaColor;
          ctx.globalAlpha = 0.3;
          ctx.beginPath(); ctx.arc(a.cx - nipX, nipY, 1.8, 0, Math.PI * 2); ctx.fill();
          ctx.beginPath(); ctx.arc(a.cx + nipX, nipY, 1.8, 0, Math.PI * 2); ctx.fill();
          // Nipple
          ctx.fillStyle = nipColor;
          ctx.globalAlpha = 0.6;
          ctx.beginPath(); ctx.arc(a.cx - nipX, nipY, 0.85, 0, Math.PI * 2); ctx.fill();
          ctx.beginPath(); ctx.arc(a.cx + nipX, nipY, 0.85, 0, Math.PI * 2); ctx.fill();
          ctx.globalAlpha = 1;
          // ── Abdominal definition (subtle) ──
          ctx.strokeStyle = "rgba(0,0,0,0.025)";
          ctx.lineWidth = 0.4;
          const absTop = chestY + pecH * 0.7;
          const absBot = a.waistY - 2;
          const absRows = 3;
          for (let r = 0; r < absRows; r++) {
            const ry = absTop + (absBot - absTop) * (r + 0.5) / absRows;
            ctx.beginPath();
            ctx.moveTo(a.cx - ww * 0.55, ry);
            ctx.quadraticCurveTo(a.cx, ry + 0.5, a.cx + ww * 0.55, ry);
            ctx.stroke();
          }
        } else if (cat === "side") {
          // Side pec shelf
          const chestY = a.shY + (a.waistY - a.shY) * 0.3;
          ctx.strokeStyle = "rgba(0,0,0,0.04)";
          ctx.lineWidth = 0.6;
          ctx.beginPath();
          ctx.moveTo(a.cx + sw - 1, chestY - 2);
          ctx.quadraticCurveTo(a.cx + sw + 2, chestY + 2, a.cx + sw, chestY + 4);
          ctx.stroke();
          // Nipple — profile
          ctx.fillStyle = nipColor;
          ctx.globalAlpha = 0.55;
          ctx.beginPath(); ctx.arc(a.cx + sw + 1.5, chestY + 1, 0.7, 0, Math.PI * 2); ctx.fill();
          ctx.globalAlpha = 1;
        }
      }

      // ── Genital anatomy — explicit, size-driven, with walk sway physics ──
      const gSz = genitalSize / 4; // 0-1 normalized
      const bSzFront = buttSize / 4;
      // Genital sway from walk physics (masculine only — pendular motion)
      const gSwayX = bodyGender === "masculine" ? wp.genitalSwayX * gSz : 0;
      const gSwayY = bodyGender === "masculine" ? wp.genitalSwayY * gSz : 0;

      if (cat === "front" || cat === "qfront") {
        const pubicY = a.hipY + 1;
        const innerThighGap = hw * 0.22;
        // qfront foreshortening
        const qScale = cat === "qfront" ? 0.8 : 1.0;
        const qShift = cat === "qfront" ? 1.5 : 0; // slight perspective shift

        if (bodyGender === "masculine") {
          // Pubic mound — soft shadow triangle at pubic bone
          ctx.fillStyle = `rgba(0,0,0,${0.04 + gSz * 0.02})`;
          const pubicW = innerThighGap * 1.4 * qScale;
          ctx.beginPath();
          ctx.moveTo(a.cx - pubicW + qShift, pubicY - 2);
          ctx.quadraticCurveTo(a.cx + qShift, pubicY + 2, a.cx + pubicW + qShift, pubicY - 2);
          ctx.closePath();
          ctx.fill();
          // ── Penis — skin-tone shaft with natural sway physics ──
          const shaftLen = (3 + gSz * 6) * qScale;
          const shaftW = (1.2 + gSz * 1.3) * qScale;
          // Shaft attaches at the pubic bone — flush with body, no gap
          const shaftTop = pubicY - 1;
          const shaftBot = shaftTop + shaftLen + gSwayY;
          const swX = gSwayX * 0.5;
          // Genital skin is slightly warmer/darker than body — NOT black
          const genSkin = shade(skinColor, 0.94);
          const genSkinDk = shade(skinColor, 0.90);
          // Shaft — filled bezier at full opacity, natural skin color
          ctx.fillStyle = genSkin;
          ctx.beginPath();
          // Root widens where it meets the body for natural merge
          ctx.moveTo(a.cx - shaftW * 0.65 + qShift, shaftTop);
          ctx.bezierCurveTo(
            a.cx - shaftW * 0.55 + swX * 0.3 + qShift, shaftTop + shaftLen * 0.3,
            a.cx - shaftW * 0.45 + swX * 0.7 + qShift, shaftBot - 1,
            a.cx + swX + qShift, shaftBot + 1);
          ctx.bezierCurveTo(
            a.cx + shaftW * 0.45 + swX * 0.7 + qShift, shaftBot - 1,
            a.cx + shaftW * 0.55 + swX * 0.3 + qShift, shaftTop + shaftLen * 0.3,
            a.cx + shaftW * 0.65 + qShift, shaftTop);
          ctx.closePath();
          ctx.fill();
          // Shaft midline shadow — subtle depth cue
          ctx.strokeStyle = `rgba(0,0,0,${0.03 + gSz * 0.015})`;
          ctx.lineWidth = 0.4;
          ctx.beginPath();
          ctx.moveTo(a.cx + qShift, shaftTop + 2);
          ctx.bezierCurveTo(
            a.cx + swX * 0.3 + qShift, shaftTop + shaftLen * 0.5,
            a.cx + swX * 0.6 + qShift, shaftBot - 2,
            a.cx + swX + qShift, shaftBot);
          ctx.stroke();
          // Glans — slightly darker, rounder head
          const glansR = shaftW * 0.65;
          ctx.fillStyle = genSkinDk;
          ctx.beginPath();
          ctx.ellipse(a.cx + swX + qShift, shaftBot + 0.5, glansR + 0.3, glansR * 0.75, 0, 0, Math.PI * 2);
          ctx.fill();
          // Corona ridge — subtle ring where glans meets shaft
          ctx.strokeStyle = `rgba(0,0,0,${0.04 + gSz * 0.02})`;
          ctx.lineWidth = 0.4;
          ctx.beginPath();
          ctx.ellipse(a.cx + swX + qShift, shaftBot - 0.5, glansR + 0.5, 0.6, 0, 0, Math.PI);
          ctx.stroke();
          // Urethral meatus — tiny dot at tip
          ctx.fillStyle = `rgba(0,0,0,${0.06 + gSz * 0.02})`;
          ctx.beginPath();
          ctx.arc(a.cx + swX + qShift, shaftBot + glansR * 0.5, 0.3 + gSz * 0.15, 0, Math.PI * 2);
          ctx.fill();
          // ── Scrotum — hangs behind shaft with pendular sway ──
          const scrotY = shaftTop + shaftLen * 0.6;
          const scrotW = shaftW * (0.9 + gSz * 0.5);
          const scrotH = (2 + gSz * 2.5) * qScale;
          ctx.fillStyle = genSkin;
          ctx.beginPath();
          ctx.ellipse(a.cx + swX * 0.7 + qShift, scrotY + scrotH * 0.5 + gSwayY * 0.3, scrotW, scrotH, gSwayX * 0.05, 0, Math.PI * 2);
          ctx.fill();
          // Scrotal raphe — midline seam
          ctx.strokeStyle = `rgba(0,0,0,${0.03 + gSz * 0.015})`;
          ctx.lineWidth = 0.3;
          ctx.beginPath();
          ctx.moveTo(a.cx + swX * 0.5 + qShift, scrotY);
          ctx.bezierCurveTo(
            a.cx + swX * 0.6 + qShift, scrotY + scrotH * 0.5,
            a.cx + swX * 0.7 + qShift, scrotY + scrotH * 0.8,
            a.cx + swX * 0.7 + qShift, scrotY + scrotH + gSwayY * 0.2);
          ctx.stroke();
          // Scrotal shadow — subtle underside darkening
          ctx.fillStyle = `rgba(0,0,0,0.03)`;
          ctx.beginPath();
          ctx.ellipse(a.cx + swX * 0.7 + qShift, scrotY + scrotH * 0.75 + gSwayY * 0.3, scrotW * 0.7, scrotH * 0.3, 0, 0, Math.PI * 2);
          ctx.fill();

        } else if (bodyGender === "feminine") {
          // Pubic mound
          const moundH = (3 + bSzFront * 2) * qScale;
          ctx.fillStyle = `rgba(0,0,0,${0.04 + bSzFront * 0.02})`;
          ctx.beginPath();
          ctx.moveTo(a.cx - innerThighGap * 1.1 * qScale + qShift, pubicY - 1);
          ctx.quadraticCurveTo(a.cx + qShift, pubicY + moundH, a.cx + innerThighGap * 1.1 * qScale + qShift, pubicY - 1);
          ctx.fill();
          // Vulva
          const vulvaTop = pubicY + 1;
          const vulvaBot = pubicY + moundH + 2;
          const vulvaLen = vulvaBot - vulvaTop;
          const labiaW = (1.0 + bSzFront * 0.5) * qScale;
          ctx.strokeStyle = `rgba(0,0,0,${0.05 + bSzFront * 0.02})`;
          ctx.lineWidth = 0.5 + bSzFront * 0.2;
          ctx.beginPath();
          ctx.moveTo(a.cx - labiaW + qShift, vulvaTop);
          ctx.bezierCurveTo(
            a.cx - labiaW - 0.5 + qShift, vulvaTop + vulvaLen * 0.3,
            a.cx - labiaW - 0.3 + qShift, vulvaTop + vulvaLen * 0.7,
            a.cx + qShift, vulvaBot);
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(a.cx + labiaW + qShift, vulvaTop);
          ctx.bezierCurveTo(
            a.cx + labiaW + 0.5 + qShift, vulvaTop + vulvaLen * 0.3,
            a.cx + labiaW + 0.3 + qShift, vulvaTop + vulvaLen * 0.7,
            a.cx + qShift, vulvaBot);
          ctx.stroke();
          // Clitoral hood — soft tissue, slightly warmer than surrounding skin
          ctx.fillStyle = shade(skinColor, 0.93);
          ctx.beginPath();
          ctx.ellipse(a.cx + qShift, vulvaTop - 0.3, labiaW * 0.6, 0.8, 0, 0, Math.PI * 2);
          ctx.fill();
          // Vaginal cleft
          ctx.strokeStyle = `rgba(0,0,0,${0.05 + bSzFront * 0.02})`;
          ctx.lineWidth = 0.4;
          ctx.beginPath();
          ctx.moveTo(a.cx + qShift, vulvaTop + 1);
          ctx.lineTo(a.cx + qShift, vulvaBot - 1);
          ctx.stroke();
          // Inner labia peek (larger sizes)
          if (bSzFront > 0.5) {
            ctx.strokeStyle = `rgba(0,0,0,${0.03 + bSzFront * 0.015})`;
            ctx.lineWidth = 0.3;
            const ilW = labiaW * 0.4;
            ctx.beginPath();
            ctx.moveTo(a.cx - ilW + qShift, vulvaTop + vulvaLen * 0.2);
            ctx.bezierCurveTo(a.cx - ilW - 0.2 + qShift, vulvaTop + vulvaLen * 0.5, a.cx + qShift, vulvaBot - 1, a.cx + qShift, vulvaBot);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(a.cx + ilW + qShift, vulvaTop + vulvaLen * 0.2);
            ctx.bezierCurveTo(a.cx + ilW + 0.2 + qShift, vulvaTop + vulvaLen * 0.5, a.cx + qShift, vulvaBot - 1, a.cx + qShift, vulvaBot);
            ctx.stroke();
          }

        } else {
          // Androgynous
          ctx.fillStyle = "rgba(0,0,0,0.035)";
          ctx.beginPath();
          ctx.ellipse(a.cx + qShift, pubicY + 1.5, innerThighGap * 1.1 * qScale, 3, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.strokeStyle = "rgba(0,0,0,0.03)";
          ctx.lineWidth = 0.3;
          ctx.beginPath();
          ctx.moveTo(a.cx + qShift, pubicY + 1);
          ctx.lineTo(a.cx + qShift, pubicY + 4);
          ctx.stroke();
        }
      } else if (cat === "side") {
        // ── Side view genitals with sway ──
        const pubicY = a.hipY;
        if (bodyGender === "masculine") {
          const shaftLen = 2.5 + gSz * 5;
          const genSkinS = shade(skinColor, 0.94);
          const genSkinDkS = shade(skinColor, 0.90);
          // Profile shaft — forward-pointing with pendular sway
          const profSwayAngle = gSwayX * 0.08;
          ctx.fillStyle = genSkinS;
          ctx.beginPath();
          ctx.ellipse(a.cx + sw * 0.1 + gSwayX * 0.3, pubicY + 2 + gSwayY * 0.4, shaftLen * 0.4, 1 + gSz * 0.6, 0.3 + profSwayAngle, 0, Math.PI * 2);
          ctx.fill();
          // Glans tip — slightly darker
          ctx.fillStyle = genSkinDkS;
          ctx.beginPath();
          ctx.arc(a.cx + sw * 0.1 + shaftLen * 0.35 + gSwayX * 0.4, pubicY + 2.5 + gSwayY * 0.3, 0.8 + gSz * 0.4, 0, Math.PI * 2);
          ctx.fill();
          // Scrotum — hangs behind with sway
          ctx.fillStyle = genSkinS;
          ctx.beginPath();
          ctx.ellipse(a.cx - 0.5 + gSwayX * 0.2, pubicY + 3 + gSz * 1.5 + gSwayY * 0.5, 1 + gSz * 0.5, 1.5 + gSz * 1, gSwayX * 0.04, 0, Math.PI * 2);
          ctx.fill();
        } else if (bodyGender === "feminine") {
          // Side mound with pubic curve
          const moundR = 2 + bSzFront * 1.5;
          ctx.fillStyle = shade(skinColor, 0.96);
          ctx.globalAlpha = 0.08 + bSzFront * 0.06;
          ctx.beginPath();
          ctx.ellipse(a.cx + sw * 0.08, pubicY + 1, moundR * 0.6, moundR, 0.15, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 1;
          // Outer curve
          ctx.strokeStyle = `rgba(0,0,0,${0.04 + bSzFront * 0.015})`;
          ctx.lineWidth = 0.5;
          ctx.beginPath();
          ctx.arc(a.cx + sw * 0.08, pubicY + 1, moundR, -0.8, 0.8);
          ctx.stroke();
          // Labia hint (profile)
          if (bSzFront > 0.3) {
            ctx.strokeStyle = `rgba(0,0,0,${0.03 + bSzFront * 0.01})`;
            ctx.lineWidth = 0.3;
            ctx.beginPath();
            ctx.moveTo(a.cx + sw * 0.08, pubicY);
            ctx.lineTo(a.cx + sw * 0.08, pubicY + moundR * 1.5);
            ctx.stroke();
          }
        }
      } else if (cat === "qback") {
        // ── Quarter-back genitals — partially visible between thighs ──
        const pubicY = a.hipY + 1;
        if (bodyGender === "masculine") {
          // Scrotum visible between legs from behind
          const scrotVis = gSz * 0.6;
          if (scrotVis > 0.1) {
            ctx.fillStyle = shade(skinColor, 0.94);
            ctx.beginPath();
            ctx.ellipse(a.cx, pubicY + 3 + gSwayY * 0.3, 1 + gSz * 0.4, 1.2 + gSz * 0.8, 0, 0, Math.PI * 2);
            ctx.fill();
          }
        } else if (bodyGender === "feminine") {
          // Perineal hint — very subtle between-thigh shadow
          ctx.strokeStyle = "rgba(0,0,0,0.03)";
          ctx.lineWidth = 0.3;
          ctx.beginPath();
          ctx.moveTo(a.cx, pubicY + 1);
          ctx.lineTo(a.cx, pubicY + 3);
          ctx.stroke();
        }
      }

      // ── Side view butt profile (nude) — with jiggle physics ──
      if (cat === "side" && nude) {
        const bSzSide = buttSize / 4;
        const buttProj = 2 + bSzSide * 5;
        const buttY = a.hipY + 2;
        const buttH = 5 + bSzSide * 4;
        const jiggle = (wp.buttJiggleL + wp.buttJiggleR) * 0.25 * bSzSide;
        // Butt curve — protrudes backward with bounce
        ctx.strokeStyle = `rgba(0,0,0,${0.04 + bSzSide * 0.02})`;
        ctx.lineWidth = 0.6 + bSzSide * 0.3;
        ctx.beginPath();
        ctx.moveTo(a.cx - sw * 0.1, buttY - 2);
        ctx.bezierCurveTo(
          a.cx - sw * 0.1 - buttProj - jiggle * 0.3, buttY + buttH * 0.3 + jiggle * 0.4,
          a.cx - sw * 0.1 - buttProj * 0.8 - jiggle * 0.15, buttY + buttH * 0.8,
          a.cx - sw * 0.1, buttY + buttH + 1);
        ctx.stroke();
        // Underfold crease
        ctx.strokeStyle = `rgba(0,0,0,${0.03 + bSzSide * 0.015})`;
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        ctx.moveTo(a.cx - sw * 0.1 - buttProj * 0.6, buttY + buttH + jiggle * 0.2);
        ctx.quadraticCurveTo(a.cx - sw * 0.05, buttY + buttH + 1.5 + bSzSide * 0.5, a.cx + sw * 0.05, buttY + buttH);
        ctx.stroke();
        // Highlight on upper curve
        if (bSzSide > 0.25) {
          ctx.strokeStyle = `rgba(255,255,255,${0.02 + bSzSide * 0.015})`;
          ctx.lineWidth = 0.4;
          ctx.beginPath();
          ctx.arc(a.cx - sw * 0.1 - buttProj * 0.5, buttY + buttH * 0.35 + jiggle * 0.2, buttProj * 0.6, Math.PI * 1.2, Math.PI * 1.8);
          ctx.stroke();
        }
        // Volume fill for large butts
        if (bSzSide > 0.5) {
          ctx.fillStyle = shade(skinColor, 0.97);
          ctx.globalAlpha = 0.06 + bSzSide * 0.04;
          ctx.beginPath();
          ctx.ellipse(a.cx - sw * 0.1 - buttProj * 0.45, buttY + buttH * 0.45, buttProj * 0.5, buttH * 0.35, -0.1, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 1;
        }
      }

      // ── Side view: additional anatomy details for side-facing deltoids ──
      if (cat === "side") {
        // Lateral deltoid highlight — visible from pure side
        ctx.fillStyle = "rgba(255,255,255,0.025)";
        const deltR = bs.armMass * 2.5;
        ctx.beginPath(); ctx.ellipse(a.cx + sw + 1, a.shY + 3, deltR * 0.6, deltR, 0, 0, Math.PI * 2); ctx.fill();
      }
    }

    // Side views: deltoids already visible via the arm/shoulder junction in drawBodyV2
  });
}

// ─── Skin Detail V2 ─────────────────────────────────────────────────────────

function drawSkinDetailV2(
  ctx: DrawCtx, dir: Direction, _frame: number,
  skinColor: string, detail: number, bodyType: 0 | 1 | 2 | 3
): void {
  if (detail < 0) return;
  const a = BA[bodyType];
  const bY = -_animPose.breatheY;
  const [cat, flip] = dirCat(dir);
  if (cat === "back" || cat === "qback") return;
  if (flip) { ctx.save(); ctx.translate(CHAR_W, 0); ctx.scale(-1, 1); }

  const dk = shade(skinColor, 0.82);

  switch (detail % 3) {
    case 0: { // Freckles across cheeks
      ctx.fillStyle = dk;
      const sp = cat === "side" ? a.headRx * 0.28 : a.headRx * 0.35;
      const fy = a.eyeY + bY + 5;
      const dots: [number, number][] = [[-2,0],[0,1],[-1,-1],[-3,1],[1,0]];
      const dotsR: [number, number][] = [[2,0],[0,1],[1,-1],[3,1],[-1,0]];
      if (cat !== "side") {
        for (const [dx, dy] of dots) {
          ctx.beginPath(); ctx.arc(a.cx - sp + dx, fy + dy, 0.7, 0, Math.PI * 2); ctx.fill();
        }
      }
      for (const [dx, dy] of dotsR) {
        ctx.beginPath(); ctx.arc(a.cx + sp + dx, fy + dy, 0.7, 0, Math.PI * 2); ctx.fill();
      }
      break;
    }
    case 1: { // Beauty mark
      ctx.fillStyle = shade(skinColor, 0.5);
      const mx = cat === "side" ? a.cx + a.headRx * 0.28 : a.cx + a.headRx * 0.25;
      ctx.beginPath(); ctx.arc(mx, a.eyeY + bY + 7, 1, 0, Math.PI * 2); ctx.fill();
      break;
    }
    case 2: { // Blush
      ctx.fillStyle = "rgba(200,100,100,0.12)";
      const sp = cat === "side" ? a.headRx * 0.28 : a.headRx * 0.38;
      if (cat !== "side") {
        ctx.beginPath(); ctx.ellipse(a.cx - sp, a.eyeY + bY + 5, 5, 3, 0, 0, Math.PI * 2); ctx.fill();
      }
      ctx.beginPath(); ctx.ellipse(a.cx + sp, a.eyeY + bY + 5, 5, 3, 0, 0, Math.PI * 2); ctx.fill();
      break;
    }
  }

  if (flip) ctx.restore();
}

// ─── Bottom (pants/shorts/skirt) V2 ─────────────────────────────────────────

function drawBottomV2(
  ctx: DrawCtx, dir: Direction, _frame: number,
  color: string, style: number, bodyType: 0 | 1 | 2 | 3
): void {
  const a = BA[bodyType];
  const dk = shade(color, 0.78);
  const hi = shade(color, 1.06);

  withFlip(ctx, dir, (cat) => {
    const n = getNarrow(cat);
    const isBack = cat === "back" || cat === "qback";
    const isSideB = cat === "side";
    const fc = isBack ? dk : color;
    const fc2 = isBack ? shade(dk, 0.92) : shade(color, 0.88); // far leg darker
    const bs = _bodyShape;
    const bFit = bottomFit(style);
    // Butt size widens hip area of pants — fabric stretches over curves
    const buttNormB = _anatomy.buttSize / 4;
    const isFemB = _anatomy.gender === "feminine";
    const buttHipExtra = buttNormB * 0.8 * (isBack ? 1.0 : isSideB ? 0.7 : 0.3);
    const hw = projectW(cat, bs.hipW + 1 * bFit + buttHipExtra, bs.hipD + 0.5 * bFit + buttHipExtra * 0.5);
    const ww = projectW(cat, bs.waistW + 1 * bFit, bs.waistD + 0.5 * bFit);
    const genderGapB = _anatomy.gender === "feminine" ? 1.3
                      : _anatomy.gender === "masculine" ? 0.7 : 1.0;
    const lgSliderB = 1.0 + ((_legGapSlider - 2) * 0.15);
    const lg = a.legGap * n * genderGapB * lgSliderB;
    const inseam = a.hipY + Math.round((a.kneeY - a.hipY) * 0.25); // crotch point
    const wp = _animPose;

    // ── Seated pants — simplified thigh coverage when sitting ──
    if (wp.isSitting) {
      const sitHipY = a.hipY + wp.torsoDY * 0.5;
      const thighLen = a.kneeY - a.hipY;
      const thighFwd = thighLen * 0.6;
      const thighDrop = thighLen * 0.35;
      const calfLen = a.ankleY - a.kneeY;
      const isSkirt = (style % 4) === 2;
      // Waistband
      ctx.fillStyle = shade(color, 0.62);
      ctx.fillRect(a.cx - hw, sitHipY - 5, hw * 2, 3);
      if (isSkirt) {
        // Skirt drapes over seated thighs
        const flare = hw + 8;
        const skirtBot = sitHipY + thighDrop + 6;
        ctx.fillStyle = fc;
        ctx.beginPath();
        ctx.moveTo(a.cx - ww, sitHipY - 3);
        ctx.bezierCurveTo(a.cx - ww - 2, (sitHipY + skirtBot) / 2, a.cx - flare, skirtBot - 2, a.cx - flare, skirtBot);
        ctx.quadraticCurveTo(a.cx, skirtBot + 2, a.cx + flare, skirtBot);
        ctx.bezierCurveTo(a.cx + flare, skirtBot - 2, a.cx + ww + 2, (sitHipY + skirtBot) / 2, a.cx + ww, sitHipY - 3);
        ctx.closePath();
        ctx.fill();
      } else {
        // Pants: seat bridge + two thigh tubes extending forward
        ctx.fillStyle = fc;
        ctx.beginPath();
        ctx.moveTo(a.cx - hw, sitHipY - 3);
        ctx.lineTo(a.cx + hw, sitHipY - 3);
        ctx.lineTo(a.cx + hw, sitHipY + 2);
        ctx.quadraticCurveTo(a.cx, inseam + wp.torsoDY * 0.3, a.cx - hw, sitHipY + 2);
        ctx.closePath();
        ctx.fill();
        // Thigh tubes follow seated leg positions
        const farLegOff = cat === "side" ? 1 : 0;
        for (const [side, c2] of [[-1, fc], [1, fc2]] as const) {
          const lx = a.cx + side * (lg + 3);
          const kx = lx + (side < 0 ? 1 : -farLegOff) + thighFwd * 0.4;
          const ky = sitHipY + thighDrop;
          ctx.fillStyle = c2;
          ctx.beginPath();
          ctx.moveTo(lx - 5.5, sitHipY);
          ctx.bezierCurveTo(lx - 5, ky - 4, kx - 4.5, ky, kx - 4.5, ky);
          ctx.lineTo(kx + 4.5, ky);
          ctx.bezierCurveTo(kx + 4.5, ky, lx + 5, ky - 4, lx + 5.5, sitHipY);
          ctx.closePath();
          ctx.fill();
          // Calf portion hanging down
          ctx.beginPath();
          ctx.moveTo(kx - 4, ky);
          ctx.lineTo(kx - 3.5, ky + calfLen * 0.7);
          ctx.lineTo(kx + 3.5, ky + calfLen * 0.7);
          ctx.lineTo(kx + 4, ky);
          ctx.closePath();
          ctx.fill();
        }
      }
      return;
    }

    // Scale pant leg widths to always cover the underlying skin limbs.
    // Skin uses thighW = 9.5 * dirScale * legMass. Pants must cover + padding.
    const isSide = cat === "side";
    const isNarrow = n < 0.9;
    const dirScale = isSide ? 0.7 : isNarrow ? 0.85 : 1.0;
    const pantScale = dirScale * bs.legMass;
    const pantThighW = 9.5 * pantScale + 1 * bFit;   // match organic thigh width + fabric
    const pantCalfW  = 7   * pantScale + 0.8 * bFit;

    /** Draw one pant leg — tapers from thigh to ankle, follows walk animation */
    /** Draw one organic pant leg — bezier contour matching body: quad swell, knee narrow, calf, ankle */
    const drawLeg = (side: number, topW: number, botW: number, endY: number, c: string, dx: number, dy: number) => {
      const lx = a.cx + side * (lg + 3);
      const kneeDX = dx * 0.3;
      const kneeDY = dy * 0.4;
      const ankDX = dx;
      const ankDY = dy;
      const kY = a.kneeY + kneeDY;
      const eY = endY + ankDY;
      // Organic widths — fabric follows the body beneath
      const midW = topW * 1.02;       // quad swell
      const knW = topW * 0.88;        // knee narrows
      const cpW = botW * 1.05;        // calf peak
      const akW = botW * 0.88;        // ankle taper
      const midY = a.hipY + (kY - a.hipY) * 0.45;
      const cpY = kY + (eY - kY) * 0.35;
      const tGrad = ctx.createLinearGradient(lx - topW, a.hipY, lx + topW + ankDX, eY);
      tGrad.addColorStop(0, c);
      tGrad.addColorStop(0.4, c);
      tGrad.addColorStop(1, shade(c, 0.9));
      ctx.fillStyle = tGrad;
      ctx.beginPath();
      // Outer edge: hip → quad swell → knee narrow
      ctx.moveTo(lx - topW, a.hipY);
      ctx.bezierCurveTo(
        lx - midW + kneeDX * 0.15, midY - 3,
        lx - knW + kneeDX * 0.3, kY - 5,
        lx - knW + kneeDX, kY);
      if (eY > kY + 5) {
        // Outer calf: knee → calf swell → ankle
        ctx.bezierCurveTo(
          lx - knW + kneeDX, kY + 3,
          lx - cpW + ankDX * 0.3, cpY,
          lx - akW + ankDX, eY);
      } else {
        ctx.lineTo(lx - knW + ankDX, eY);
      }
      // Hem
      ctx.lineTo(lx + (eY > kY + 5 ? akW : knW) + ankDX, eY);
      if (eY > kY + 5) {
        // Inner calf
        ctx.bezierCurveTo(
          lx + cpW + ankDX * 0.3, cpY,
          lx + knW + kneeDX, kY + 3,
          lx + knW + kneeDX, kY);
      } else {
        ctx.lineTo(lx + knW + kneeDX, kY);
      }
      // Inner edge: knee → quad → hip
      ctx.bezierCurveTo(
        lx + knW + kneeDX * 0.3, kY - 5,
        lx + midW + kneeDX * 0.15, midY - 3,
        lx + topW, a.hipY);
      ctx.closePath();
      ctx.fill();
      return lx;
    };

    switch (style % 4) {
      case 0: { // Straight leg pants — classic fit
        // Seat/crotch bridge — connects both legs at the top
        ctx.fillStyle = fc;
        ctx.beginPath();
        ctx.moveTo(a.cx - hw, a.hipY - 3);
        ctx.lineTo(a.cx + hw, a.hipY - 3);
        ctx.lineTo(a.cx + hw, a.hipY + 2);
        ctx.quadraticCurveTo(a.cx, inseam + 2, a.cx - hw, a.hipY + 2);
        ctx.closePath();
        ctx.fill();
        // Near leg (left) — slight taper
        drawLeg(-1, pantThighW, pantCalfW, a.ankleY + 2, fc, wp.nearLegDX, wp.nearLegDY);
        // Far leg (right)
        drawLeg(1, pantThighW, pantCalfW, a.ankleY + 2, fc2, wp.farLegDX, wp.farLegDY);
        // Waistband
        ctx.fillStyle = shade(color, 0.62);
        ctx.fillRect(a.cx - hw, a.hipY - 5, hw * 2, 3);
        // Belt buckle hint
        ctx.fillStyle = shade(color, 0.5);
        ctx.fillRect(a.cx - 2, a.hipY - 5, 4, 3);
        // Inner seam shadow — follows near leg walk offset
        ctx.strokeStyle = "rgba(0,0,0,0.08)";
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.moveTo(a.cx, inseam);
        ctx.lineTo(a.cx - lg - 3 + wp.nearLegDX, a.ankleY + wp.nearLegDY);
        ctx.stroke();
        // Butt fabric tension — back/side views show fabric stretching over curves
        if ((isBack || isSideB) && buttNormB > 0.25) {
          const jL = wp.buttJiggleL * buttNormB * 0.3;
          const jR = wp.buttJiggleR * buttNormB * 0.3;
          ctx.strokeStyle = `rgba(0,0,0,${0.04 + buttNormB * 0.02})`;
          ctx.lineWidth = 0.5;
          if (isBack) {
            // Crease lines radiating from center seam
            ctx.beginPath();
            ctx.moveTo(a.cx - 1, a.hipY + 1);
            ctx.quadraticCurveTo(a.cx - hw * 0.4 + jL, a.hipY + 4 + buttNormB * 2, a.cx - hw * 0.6, inseam - 2);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(a.cx + 1, a.hipY + 1);
            ctx.quadraticCurveTo(a.cx + hw * 0.4 + jR, a.hipY + 4 + buttNormB * 2, a.cx + hw * 0.6, inseam - 2);
            ctx.stroke();
          } else {
            // Side butt curve visible through fabric
            ctx.beginPath();
            ctx.arc(a.cx - hw * 0.15, a.hipY + 2 + jL, buttNormB * 3, Math.PI * 0.3, Math.PI * 1.2);
            ctx.stroke();
          }
        }
        break;
      }
      case 1: { // Shorts — above-knee, relaxed fit
        const shortEnd = a.kneeY - 2;
        ctx.fillStyle = fc;
        ctx.beginPath();
        ctx.moveTo(a.cx - hw, a.hipY - 3);
        ctx.lineTo(a.cx + hw, a.hipY - 3);
        ctx.lineTo(a.cx + hw, a.hipY + 2);
        ctx.quadraticCurveTo(a.cx, inseam, a.cx - hw, a.hipY + 2);
        ctx.closePath();
        ctx.fill();
        drawLeg(-1, pantThighW + 0.5, pantThighW, shortEnd, fc, wp.nearLegDX, wp.nearLegDY);
        drawLeg(1, pantThighW + 0.5, pantThighW, shortEnd, fc2, wp.farLegDX, wp.farLegDY);
        // Waistband
        ctx.fillStyle = shade(color, 0.62);
        ctx.fillRect(a.cx - hw, a.hipY - 5, hw * 2, 3);
        // Hem — slightly rolled (follows walk knee offset)
        ctx.strokeStyle = shade(color, 0.7);
        ctx.lineWidth = 1.5;
        const hemOffsets: [number, number, number][] = [[-1, wp.nearLegDX * 0.2, wp.nearLegDY * 0.3], [1, wp.farLegDX * 0.2, wp.farLegDY * 0.3]];
        hemOffsets.forEach(([side, hdx, hdy]) => {
          const lx = a.cx + side * (lg + 3) + hdx;
          const hEnd = shortEnd + hdy;
          ctx.beginPath();
          ctx.moveTo(lx - pantThighW, hEnd);
          ctx.lineTo(lx + pantThighW, hEnd);
          ctx.stroke();
        });
        break;
      }
      case 2: { // Skirt — A-line with cloth physics sway
        const skirtBot = a.kneeY + 6;
        const flare = hw + 8;
        // Cloth sway — hem shifts with movement
        const clothSway = Math.sin(wp.clothPhase) * wp.clothAmplitude * 4;
        const clothSway2 = Math.sin(wp.clothPhase + 1.2) * wp.clothAmplitude * 2.5;
        // Main skirt shape — flared trapezoid with dynamic hem
        const sGrad = ctx.createLinearGradient(a.cx, a.hipY, a.cx + clothSway * 0.5, skirtBot);
        sGrad.addColorStop(0, fc);
        sGrad.addColorStop(0.7, fc);
        sGrad.addColorStop(1, shade(fc, 0.88));
        ctx.fillStyle = sGrad;
        ctx.beginPath();
        ctx.moveTo(a.cx - ww, a.hipY - 3);
        ctx.bezierCurveTo(
          a.cx - ww - 2, (a.hipY + skirtBot) / 2,
          a.cx - flare + clothSway2 * 0.5, skirtBot - 2,
          a.cx - flare + clothSway, skirtBot
        );
        // Dynamic hem with wave
        const hemPts = 6;
        for (let i = 1; i <= hemPts; i++) {
          const t = i / hemPts;
          const hx = (a.cx - flare + clothSway) + (flare * 2) * t;
          const wave = Math.sin(wp.clothPhase + t * Math.PI * 2) * wp.clothAmplitude * 2;
          const hy = skirtBot + 3 * Math.sin(t * Math.PI) + wave;
          if (i === hemPts) {
            ctx.lineTo(a.cx + flare + clothSway * 0.8, skirtBot);
          } else {
            ctx.lineTo(hx + clothSway * (1 - t), hy);
          }
        }
        ctx.bezierCurveTo(
          a.cx + flare + clothSway2 * 0.3, skirtBot - 2,
          a.cx + ww + 2, (a.hipY + skirtBot) / 2,
          a.cx + ww, a.hipY - 3
        );
        ctx.closePath();
        ctx.fill();
        // Fabric fold lines — shift with sway for dynamic wrinkle feel
        ctx.strokeStyle = "rgba(0,0,0,0.06)";
        ctx.lineWidth = 1;
        for (let i = -2; i <= 2; i++) {
          const foldSway = Math.sin(wp.clothPhase + i * 0.7) * wp.clothAmplitude * 1.5;
          const fx = a.cx + i * (flare / 3) + foldSway;
          const topX = a.cx + i * (ww / 3);
          ctx.beginPath();
          ctx.moveTo(topX, a.hipY + 2);
          ctx.quadraticCurveTo(fx, (a.hipY + skirtBot) / 2 + 2, fx + clothSway * 0.3, skirtBot - 1);
          ctx.stroke();
        }
        // Hem line — follows the dynamic curve
        ctx.strokeStyle = dk;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(a.cx - flare + clothSway, skirtBot);
        ctx.quadraticCurveTo(a.cx + clothSway * 0.5, skirtBot + 3, a.cx + flare + clothSway * 0.8, skirtBot);
        ctx.stroke();
        // Waistband — fixed at hip (fabric hangs from here)
        ctx.fillStyle = shade(color, 0.62);
        ctx.fillRect(a.cx - ww, a.hipY - 5, ww * 2, 3);
        break;
      }
      case 3: { // Baggy / cargo pants — wide, gathered at cuffs
        const cuffW = 3;
        ctx.fillStyle = fc;
        ctx.beginPath();
        ctx.moveTo(a.cx - hw - 1, a.hipY - 3);
        ctx.lineTo(a.cx + hw + 1, a.hipY - 3);
        ctx.lineTo(a.cx + hw + 1, a.hipY + 2);
        ctx.quadraticCurveTo(a.cx, inseam + 4, a.cx - hw - 1, a.hipY + 2);
        ctx.closePath();
        ctx.fill();
        // Wider legs with fabric volume — baggy = skin width + 2px extra
        drawLeg(-1, pantThighW + 1.5, pantCalfW + 1.2, a.ankleY, fc, wp.nearLegDX, wp.nearLegDY);
        drawLeg(1, pantThighW + 1.5, pantCalfW + 1.2, a.ankleY, fc2, wp.farLegDX, wp.farLegDY);
        // Cargo pocket (near leg)
        ctx.fillStyle = shade(fc, 0.9);
        ctx.strokeStyle = shade(color, 0.65);
        ctx.lineWidth = 0.8;
        const pkX = a.cx - lg - 3 + 2 + wp.nearLegDX * 0.3;
        const pkY = a.kneeY - 10 + wp.nearLegDY * 0.4;
        ctxRoundRect(ctx, pkX - 4, pkY, 7, 8, 1);
        ctx.strokeRect(pkX - 4, pkY, 7, 8);
        // Gathered cuffs — elastic hem (follows each leg's walk offset)
        ctx.fillStyle = shade(color, 0.65);
        const cuffOffsets: [number, number, number][] = [[-1, wp.nearLegDX, wp.nearLegDY], [1, wp.farLegDX, wp.farLegDY]];
        cuffOffsets.forEach(([side, cdx, cdy]) => {
          const lx = a.cx + side * (lg + 3) + cdx;
          ctx.beginPath(); ctx.ellipse(lx, a.ankleY + cdy, 6 - cuffW + 3, 3, 0, 0, Math.PI * 2); ctx.fill();
        });
        // Waistband
        ctx.fillStyle = shade(color, 0.62);
        ctx.fillRect(a.cx - hw - 1, a.hipY - 5, (hw + 1) * 2, 3);
        break;
      }
    }
  });
}

// ─── Shoes V2 ────────────────────────────────────────────────────────────────

function drawShoesV2(
  ctx: DrawCtx, dir: Direction, _frame: number,
  color: string, style: number, bodyType: 0 | 1 | 2 | 3
): void {
  const a = BA[bodyType];
  const dk = shade(color, 0.72);
  const wp = _animPose;

  withFlip(ctx, dir, (cat) => {
    const n = getNarrow(cat);
    const genderGapS = _anatomy.gender === "feminine" ? 1.3
                      : _anatomy.gender === "masculine" ? 0.7 : 1.0;
    const lgSliderS = 1.0 + ((_legGapSlider - 2) * 0.15);
    const lg = a.legGap * n * genderGapS * lgSliderS;
    const fw = 6; // shoe half-width — covers organic foot (footRx=5.5)
    const fh = 3; // foot height

    const drawShoe = (cx: number, c: string, dx: number, dy: number) => {
      const fx = cx + dx;
      const fy = a.footY + dy;
      const ay = a.ankleY + dy;
      switch (style % 4) {
        case 0: // Sneakers
          ctx.fillStyle = c;
          ctxRoundRect(ctx, fx - fw, fy - fh, fw * 2, fh + 2, 2);
          // Toe cap
          ctx.fillStyle = shade(c, 1.15);
          ctx.beginPath(); ctx.arc(fx + fw * 0.3, fy - fh + 1, 2, 0, Math.PI * 2); ctx.fill();
          break;
        case 1: // Boots
          ctx.fillStyle = c;
          ctxRoundRect(ctx, fx - fw, ay - 4, fw * 2, fy - ay + 6, 2);
          // Strap
          ctx.fillStyle = dk;
          ctx.fillRect(fx - fw, ay - 1, fw * 2, 2);
          break;
        case 2: // Heels
          ctx.fillStyle = c;
          ctxRoundRect(ctx, fx - fw, fy - fh, fw * 2, fh, 1);
          // Heel spike
          ctx.fillStyle = dk;
          ctx.fillRect(fx - fw + 1, fy, 2, 3);
          break;
        case 3: // Sandals
          ctx.fillStyle = c;
          ctx.fillRect(fx - fw, fy - 1, fw * 2, 2);
          // Straps
          ctx.strokeStyle = dk;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(fx, fy - 3);
          ctx.lineTo(fx - fw + 2, fy - 1);
          ctx.moveTo(fx, fy - 3);
          ctx.lineTo(fx + fw - 2, fy - 1);
          ctx.stroke();
          break;
      }
    };

    if (wp.isSitting) {
      // Seated shoes — at the bottom of the seated calves
      const sitHipY = a.hipY + wp.torsoDY * 0.5;
      const thighDrop = (a.kneeY - a.hipY) * 0.35;
      const thighFwd = (a.kneeY - a.hipY) * 0.6;
      const calfLen = a.ankleY - a.kneeY;
      const farLegOff = cat === "side" ? 1 : 0;
      const nKneeX = a.cx - lg - 2 + thighFwd * 0.4;
      const fKneeX = a.cx + lg + 2 - farLegOff + thighFwd * 0.4;
      const kY = sitHipY + thighDrop;
      const footDY = kY + calfLen * 0.7 - a.footY + 2;
      drawShoe(nKneeX + 1, color, 0, footDY);
      drawShoe(fKneeX + 1, dk, 0, footDY);
    } else {
      // Standing/walking — centers match organic leg positions (lg + 2)
      drawShoe(a.cx - lg - 2, color, wp.nearLegDX, wp.nearLegDY);
      drawShoe(a.cx + lg + 2, dk, wp.farLegDX, wp.farLegDY);
    }
  });
}

// ─── Clothing Fit / Drape ────────────────────────────────────────────────────
// Fit determines how loosely fabric sits over the body silhouette.
// Returns a multiplier for the fabric padding beyond the skin contour.
//   fitted (0.6)   — hugs curves, minimal extra width
//   relaxed (1.0)  — default look
//   oversized (1.4) — extra drape, wider silhouette

function topFit(style: number): number {
  switch (style % 5) {
    case 0: return 1.0;  // t-shirt — relaxed
    case 1: return 0.6;  // tank top — fitted
    case 2: return 1.4;  // hoodie — oversized
    case 3: return 1.0;  // blouse — relaxed
    case 4: return 0.7;  // button-up — slightly fitted
    default: return 1.0;
  }
}

function bottomFit(style: number): number {
  switch (style % 4) {
    case 0: return 0.8;  // jeans — fitted
    case 1: return 1.3;  // sweats — loose
    case 2: return 1.0;  // skirt — relaxed
    case 3: return 0.7;  // shorts — fitted
    default: return 1.0;
  }
}

function jacketFit(style: number): number {
  switch (style % 4) {
    case 0: return 1.2;  // jacket — slightly loose
    case 1: return 1.5;  // overcoat — oversized
    case 2: return 1.0;  // blazer — standard
    case 3: return 0.8;  // vest — fitted
    default: return 1.0;
  }
}

// ─── Top (shirt/hoodie/dress) V2 ────────────────────────────────────────────

function drawTopV2(
  ctx: DrawCtx, dir: Direction, _frame: number,
  color: string, style: number, bodyType: 0 | 1 | 2 | 3
): void {
  const a = BA[bodyType];
  const dk = shade(color, 0.78);
  const hi = shade(color, 1.10);
  const seam = shade(color, 0.68);

  withFlip(ctx, dir, (cat) => {
    const isBack = cat === "back" || cat === "qback";
    const n = getNarrow(cat);
    const fc = isBack ? dk : color;
    const fc2 = isBack ? shade(dk, 0.93) : dk;

    const wp = _animPose;
    const bs = _bodyShape;
    const fit = topFit(style);
    const pad = fit; // fabric padding multiplier
    const sw = projectW(cat, bs.shoulderW + 2 * pad, bs.shoulderD + 1 * pad);
    const ww = projectW(cat, bs.waistW + 1 * pad, bs.waistD + 0.5 * pad);
    const hemW = projectW(cat, bs.hipW + 1 * pad, bs.hipD + 0.5 * pad);
    const topY = a.shY - 2;
    const botY = a.hipY - 3;
    // Sleeve arm centers match skin. Shoulder anchor sits BETWEEN the torso
    // edge and the arm hang — 60% of the gap, creating a natural rounded
    // shoulder contour. The inner half overlaps the torso body (invisible),
    // the outer half covers the skin arm cap. topW stays close to 8px.
    const armHang = bs.shoulderW + 5 * bs.armMass;
    const armNx = armHang * n;                          // skin arm shoulder X
    const slvGap = armNx - sw;                          // torso→arm distance
    const slvShOff = sw + slvGap * 0.6;                 // shoulder contour center
    const slvTopW = Math.max(8, slvGap + 6);            // covers gap + arm radius
    const farElbowX = a.cx + (armHang - 2) * n - wp.farArmDX * 0.4;
    const nearElbowX = a.cx - (armHang - 2) * n + wp.nearArmDX * 0.4;

    /** Draw a shaped sleeve — two-segment arm following animation */
    const slvY = a.shY;  // at shoulder line — natural, not raised
    const drawSleeve = (shX: number, elbX: number, wrstX: number, endY: number, topW: number, midW: number, botW: number, c: string, armDY: number, elbowBend = 0) => {
      const isLong = endY > a.elbowY + 4;
      const elbDY = armDY * 0.5 + elbowBend * 6;
      const eY = endY + armDY;
      const eElbY = a.elbowY + elbDY;
      ctx.fillStyle = c;
      ctx.beginPath();
      if (isLong) {
        ctx.moveTo(shX - topW / 2, slvY);
        ctx.bezierCurveTo(shX - topW / 2, eElbY - 6, elbX - midW / 2, eElbY - 1, elbX - midW / 2, eElbY);
        ctx.bezierCurveTo(elbX - midW / 2, eElbY + 2, wrstX - botW / 2, eY - 4, wrstX - botW / 2, eY);
        ctx.lineTo(wrstX + botW / 2, eY);
        ctx.bezierCurveTo(wrstX + botW / 2, eY - 4, elbX + midW / 2, eElbY + 2, elbX + midW / 2, eElbY);
        ctx.bezierCurveTo(elbX + midW / 2, eElbY - 1, shX + topW / 2, eElbY - 6, shX + topW / 2, slvY);
      } else {
        const sEndY = endY + elbDY;
        ctx.moveTo(shX - topW / 2, slvY);
        ctx.bezierCurveTo(shX - topW / 2, eElbY - 8, elbX - midW / 2, eElbY - 2, elbX - midW / 2, sEndY);
        ctx.lineTo(elbX + midW / 2, sEndY);
        ctx.bezierCurveTo(elbX + midW / 2, eElbY - 2, shX + topW / 2, eElbY - 8, shX + topW / 2, slvY);
      }
      ctx.closePath();
      ctx.fill();
      const cuffX = isLong ? wrstX : elbX;
      const cuffW = isLong ? botW : midW;
      const cuffY = isLong ? eY : endY + elbDY;
      ctx.strokeStyle = shade(c, 0.82);
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(cuffX - cuffW / 2, cuffY);
      ctx.lineTo(cuffX + cuffW / 2, cuffY);
      ctx.stroke();
    };
    // Sleeve length depends on style: short for tee/stripes/skirt, long for hoodie/button-up
    const isLongSleeve = (style % 5) === 2 || (style % 5) === 4; // hoodie, button-up
    const farWristX = a.cx + (armHang - 4) * n - wp.farArmDX;
    const nearWristX = a.cx - (armHang - 4) * n + wp.nearArmDX;
    const sleeveEndY = isLongSleeve ? a.wristY : a.elbowY + 2;

    // Far sleeve — shoulder center between torso and arm for natural contour
    drawSleeve(a.cx + slvShOff, farElbowX, farWristX, sleeveEndY, slvTopW, 6, 5, fc2, wp.farArmDY, wp.farElbowBend);

    // Torso — direction-aware organic shape. Fabric follows body curves at EVERY angle:
    // breast swell, butt tension, waist cinch, all with bounce physics dampened by fabric.
    const isFrontT = cat === "front" || cat === "qfront";
    const isBackT = cat === "back" || cat === "qback";
    const isSideT = cat === "side";
    const isFemT = _anatomy.gender === "feminine";
    const hasBreastsT = (_anatomy.gender === "feminine" || _anatomy.gender === "androgynous") && _anatomy.breastSize > 0;
    const brstNormT = _anatomy.breastSize / 4;
    const buttNormT = _anatomy.buttSize / 4;
    // Fabric dampens bounce by ~60% (compression + inertia)
    const fabricBounceY = hasBreastsT ? wp.breastBounceY * (0.2 + brstNormT * 0.2) : 0;
    // Breast pushes fabric out (slightly less than skin — compression)
    const breastExtraT = hasBreastsT ? brstNormT * 2.8 : 0;
    // Feminine waist cinch under clothing (fabric still follows curves, just slightly relaxed)
    const wCinchT = isFemT ? (0.5 + brstNormT * 0.2 + buttNormT * 0.2) * n * (1.0 / fit) : 0;
    const wwCinchT = ww - wCinchT;

    // Waist→hem bezier control ratios (matches skin layer anatomy)
    const wDepartT = isFemT ? 0.15 : _anatomy.gender === "masculine" ? 0.10 : 0.12;
    const hArriveT = isFemT ? 0.50 : _anatomy.gender === "masculine" ? 0.55 : 0.50;

    // Per-direction chest/butt projection for clothing
    let chestBT = 0;
    let sideFwdT = 0;  // forward breast bulge (side/quarter views)
    let sideBackT = 0; // backward butt bulge
    let buttBT = 0;

    if (cat === "front") {
      chestBT = ((bs.chestProjection - 1.0) * 4.0 + breastExtraT) * n;
    } else if (cat === "qfront") {
      chestBT = ((bs.chestProjection - 1.0) * 3.0 + breastExtraT * 0.65) * n;
      sideFwdT = hasBreastsT ? brstNormT * 1.5 : 0;
    } else if (cat === "side") {
      sideFwdT = hasBreastsT ? brstNormT * 3.0 : (bs.chestProjection - 0.8) * 1.2;
      sideBackT = buttNormT * 2.5 + (bs.buttProjection - 0.8) * 1.0;
    } else if (cat === "qback") {
      buttBT = ((bs.buttProjection - 1.0) * 3.0 + buttNormT * 2.0) * n;
      sideFwdT = hasBreastsT ? brstNormT * 0.8 : 0;
    } else { // back
      buttBT = ((bs.buttProjection - 1.0) * 3.5 + buttNormT * 2.5) * n;
    }

    const chestYT = a.shY + (a.waistY - a.shY) * (0.36 + (hasBreastsT ? brstNormT * 0.01 : 0)) + fabricBounceY;
    const cwT = sw + chestBT;

    const tGrad = ctx.createLinearGradient(a.cx - sw, topY, a.cx + sw, botY);
    tGrad.addColorStop(0, shade(fc, 1.03));
    tGrad.addColorStop(0.35, fc);
    tGrad.addColorStop(0.7, fc);
    tGrad.addColorStop(1, shade(fc, 0.93));
    ctx.fillStyle = tGrad;
    ctx.beginPath();

    if (isSideT) {
      // ── Side view clothing: full S-curve following body ──
      ctx.moveTo(a.cx - sw - sideBackT * 0.1, topY);
      // Back edge: shoulder → waist cinch → butt tension → hem
      ctx.bezierCurveTo(
        a.cx - sw - 0.5, topY + (botY - topY) * 0.35,
        a.cx - wwCinchT - 0.5, a.waistY - 2,
        a.cx - wwCinchT, a.waistY);
      ctx.bezierCurveTo(
        a.cx - wwCinchT - sideBackT * 0.3, a.waistY + 4,
        a.cx - hemW - sideBackT * 0.6, botY - 3,
        a.cx - hemW - sideBackT * 0.3, botY);
      ctx.lineTo(a.cx + hemW + sideFwdT * 0.2, botY);
      // Front edge: hem → waist → chest bulge → shoulder
      ctx.bezierCurveTo(
        a.cx + wwCinchT + sideFwdT * 0.15, a.waistY + 2,
        a.cx + wwCinchT, a.waistY,
        a.cx + wwCinchT, a.waistY);
      ctx.bezierCurveTo(
        a.cx + wwCinchT + sideFwdT * 0.3, chestYT + 4,
        a.cx + sw + sideFwdT * 0.6, chestYT,
        a.cx + sw + sideFwdT * 0.2, topY);
    } else if (cat === "qfront") {
      // ── Quarter-front: waist as path point ──
      const sToW = a.waistY - topY;
      const wToB = botY - a.waistY;
      ctx.moveTo(a.cx - sw, topY);
      ctx.bezierCurveTo(a.cx - sw, topY + sToW * 0.5, a.cx - wwCinchT, a.waistY - sToW * 0.3, a.cx - wwCinchT, a.waistY);
      ctx.bezierCurveTo(a.cx - wwCinchT, a.waistY + wToB * wDepartT, a.cx - hemW, botY - wToB * hArriveT, a.cx - hemW, botY);
      ctx.lineTo(a.cx + hemW, botY);
      if (chestBT > 0.5) {
        ctx.bezierCurveTo(a.cx + hemW, botY - wToB * hArriveT, a.cx + wwCinchT, a.waistY + wToB * wDepartT, a.cx + wwCinchT, a.waistY);
        ctx.bezierCurveTo(a.cx + wwCinchT, a.waistY - sToW * 0.25, a.cx + cwT, chestYT + sToW * 0.4, a.cx + cwT, chestYT);
        ctx.quadraticCurveTo(a.cx + cwT + 0.5, chestYT - 2, a.cx + sw, topY);
      } else {
        ctx.bezierCurveTo(a.cx + hemW, botY - wToB * hArriveT, a.cx + wwCinchT, a.waistY + wToB * wDepartT, a.cx + wwCinchT, a.waistY);
        ctx.bezierCurveTo(a.cx + wwCinchT, a.waistY - sToW * 0.3, a.cx + sw, topY + sToW * 0.5, a.cx + sw, topY);
      }
    } else if (cat === "qback") {
      // ── Quarter-back: waist as path point ──
      const sToW = a.waistY - topY;
      const wToB = botY - a.waistY;
      ctx.moveTo(a.cx - sw, topY);
      if (sideFwdT > 0.3) {
        ctx.bezierCurveTo(a.cx - sw - sideFwdT * 0.15, chestYT + 2, a.cx - wwCinchT, a.waistY - sToW * 0.2, a.cx - wwCinchT, a.waistY);
      } else {
        ctx.bezierCurveTo(a.cx - sw, topY + sToW * 0.5, a.cx - wwCinchT, a.waistY - sToW * 0.3, a.cx - wwCinchT, a.waistY);
      }
      ctx.bezierCurveTo(a.cx - wwCinchT, a.waistY + wToB * wDepartT, a.cx - hemW, botY - wToB * hArriveT, a.cx - hemW, botY);
      ctx.lineTo(a.cx + hemW + buttBT * 0.3, botY);
      const farBotT = a.cx + hemW + buttBT * 0.3;
      ctx.bezierCurveTo(farBotT, botY - wToB * hArriveT, a.cx + wwCinchT, a.waistY + wToB * wDepartT, a.cx + wwCinchT, a.waistY);
      ctx.bezierCurveTo(a.cx + wwCinchT, a.waistY - sToW * 0.3, a.cx + sw, topY + sToW * 0.5, a.cx + sw, topY);
    } else if (cat === "front") {
      // ── Front: waist as path point — no overshoot possible ──
      const sToW = a.waistY - topY;
      const wToB = botY - a.waistY;
      ctx.moveTo(a.cx - sw, topY);
      if (chestBT > 0.5) {
        ctx.quadraticCurveTo(a.cx - cwT - 0.5, chestYT - 2, a.cx - cwT, chestYT);
        ctx.bezierCurveTo(a.cx - cwT, chestYT + sToW * 0.4, a.cx - wwCinchT, a.waistY - sToW * 0.25, a.cx - wwCinchT, a.waistY);
      } else {
        ctx.bezierCurveTo(a.cx - sw, topY + sToW * 0.5, a.cx - wwCinchT, a.waistY - sToW * 0.3, a.cx - wwCinchT, a.waistY);
      }
      ctx.bezierCurveTo(a.cx - wwCinchT, a.waistY + wToB * wDepartT, a.cx - hemW, botY - wToB * hArriveT, a.cx - hemW, botY);
      ctx.lineTo(a.cx + hemW, botY);
      ctx.bezierCurveTo(a.cx + hemW, botY - wToB * hArriveT, a.cx + wwCinchT, a.waistY + wToB * wDepartT, a.cx + wwCinchT, a.waistY);
      if (chestBT > 0.5) {
        ctx.bezierCurveTo(a.cx + wwCinchT, a.waistY - sToW * 0.25, a.cx + cwT, chestYT + sToW * 0.4, a.cx + cwT, chestYT);
        ctx.quadraticCurveTo(a.cx + cwT + 0.5, chestYT - 2, a.cx + sw, topY);
      } else {
        ctx.bezierCurveTo(a.cx + wwCinchT, a.waistY - sToW * 0.3, a.cx + sw, topY + sToW * 0.5, a.cx + sw, topY);
      }
    } else {
      // ── Back: waist as path point ──
      const sToW = a.waistY - topY;
      const wToB = botY - a.waistY;
      ctx.moveTo(a.cx - sw, topY);
      ctx.bezierCurveTo(a.cx - sw, topY + sToW * 0.5, a.cx - wwCinchT, a.waistY - sToW * 0.3, a.cx - wwCinchT, a.waistY);
      ctx.bezierCurveTo(a.cx - wwCinchT, a.waistY + wToB * wDepartT, a.cx - hemW - buttBT * 0.2, botY - wToB * hArriveT, a.cx - hemW - buttBT * 0.2, botY);
      ctx.lineTo(a.cx + hemW + buttBT * 0.2, botY);
      ctx.bezierCurveTo(a.cx + hemW + buttBT * 0.2, botY - wToB * hArriveT, a.cx + wwCinchT, a.waistY + wToB * wDepartT, a.cx + wwCinchT, a.waistY);
      ctx.bezierCurveTo(a.cx + wwCinchT, a.waistY - sToW * 0.3, a.cx + sw, topY + sToW * 0.5, a.cx + sw, topY);
    }
    ctx.closePath();
    ctx.fill();

    // Fabric tension lines — breast stretch marks under tight tops
    if (hasBreastsT && brstNormT > 0.4 && fit < 1.2 && isFrontT) {
      ctx.strokeStyle = `rgba(0,0,0,${0.03 + brstNormT * 0.02})`;
      ctx.lineWidth = 0.4;
      const tensY = chestYT + 2;
      // Radial tension from center outward
      for (let i = -1; i <= 1; i += 2) {
        ctx.beginPath();
        ctx.moveTo(a.cx + i * 2, tensY);
        ctx.lineTo(a.cx + i * (cwT - 2), tensY + 1);
        ctx.stroke();
      }
    }

    // Side seam shadows — fabric drape hints
    ctx.strokeStyle = "rgba(0,0,0,0.06)";
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    ctx.moveTo(a.cx - sw + 2, topY + 3);
    ctx.bezierCurveTo(a.cx - sw, (topY + botY) / 2, a.cx - hemW + 1, botY - 4, a.cx - hemW + 1, botY);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(a.cx + sw - 2, topY + 3);
    ctx.bezierCurveTo(a.cx + sw, (topY + botY) / 2, a.cx + hemW - 1, botY - 4, a.cx + hemW - 1, botY);
    ctx.stroke();

    // Near sleeve — shoulder center between torso and arm for natural contour
    drawSleeve(a.cx - slvShOff, nearElbowX, nearWristX, sleeveEndY, slvTopW, 6, 5, fc, wp.nearArmDY, wp.nearElbowBend);

    // Style-specific detail
    switch (style % 5) {
      case 0: { // Plain tee — round neck + clean hem
        // Neckline scoop
        ctx.strokeStyle = seam;
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.arc(a.cx, topY + 2, 6 * n, 0.15 * Math.PI, 0.85 * Math.PI);
        ctx.stroke();
        // Hem
        ctx.strokeStyle = shade(fc, 0.85);
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.moveTo(a.cx - hemW, botY);
        ctx.lineTo(a.cx + hemW, botY);
        ctx.stroke();
        break;
      }
      case 1: { // Striped — Breton style
        ctx.save();
        // Clip to torso shape so stripes don't bleed
        ctx.beginPath();
        ctx.moveTo(a.cx - sw + 1, topY + 3);
        ctx.bezierCurveTo(a.cx - sw, (topY + botY) / 2, a.cx - hemW, botY - 2, a.cx - hemW, botY);
        ctx.lineTo(a.cx + hemW, botY);
        ctx.bezierCurveTo(a.cx + hemW, botY - 2, a.cx + sw, (topY + botY) / 2, a.cx + sw - 1, topY + 3);
        ctx.closePath();
        ctx.clip();
        ctx.globalAlpha = 0.22;
        ctx.fillStyle = dk;
        for (let y = topY + 4; y < botY; y += 5) {
          ctx.fillRect(a.cx - sw - 2, y, sw * 2 + 4, 2.5);
        }
        ctx.restore();
        break;
      }
      case 2: { // Hoodie — hood volume + kangaroo pocket + drawstrings
        // Hood (sits on shoulders, visible from front as collar bump)
        ctx.fillStyle = hi;
        ctx.beginPath();
        ctx.ellipse(a.cx, topY, 8 * n, 6, 0, 0, Math.PI);
        ctx.fill();
        // Hood inner shadow
        ctx.strokeStyle = shade(color, 0.65);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.arc(a.cx, topY + 1, 5 * n, 0.2 * Math.PI, 0.8 * Math.PI);
        ctx.stroke();
        // Kangaroo pocket — rounded rectangle
        ctx.save();
        ctx.globalAlpha = 0.18;
        ctx.fillStyle = dk;
        const pkW = ww * 1.1;
        const pkH = (botY - topY) * 0.32;
        ctxRoundRect(ctx, a.cx - pkW, botY - pkH - 4, pkW * 2, pkH, 3);
        ctx.restore();
        // Pocket opening line
        ctx.strokeStyle = shade(color, 0.7);
        ctx.lineWidth = 0.6;
        ctx.beginPath();
        ctx.moveTo(a.cx - pkW + 3, botY - pkH - 4);
        ctx.lineTo(a.cx + pkW - 3, botY - pkH - 4);
        ctx.stroke();
        // Drawstrings
        ctx.strokeStyle = shade(color, 0.6);
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.moveTo(a.cx - 2, topY + 6); ctx.lineTo(a.cx - 3, topY + 18);
        ctx.moveTo(a.cx + 2, topY + 6); ctx.lineTo(a.cx + 3, topY + 18);
        ctx.stroke();
        // String tips
        ctx.fillStyle = shade(color, 0.55);
        ctx.beginPath(); ctx.arc(a.cx - 3, topY + 18, 0.8, 0, Math.PI * 2); ctx.fill();
        ctx.beginPath(); ctx.arc(a.cx + 3, topY + 18, 0.8, 0, Math.PI * 2); ctx.fill();
        break;
      }
      case 3: { // Tunic / dress — extends below waist with cloth physics
        const dressBot = a.kneeY - 6;
        const dhw = (a.hipW + 6) * n;
        const dressSway = Math.sin(wp.clothPhase * 0.9) * wp.clothAmplitude * 3;
        const dGrad = ctx.createLinearGradient(a.cx, botY, a.cx + dressSway * 0.3, dressBot);
        dGrad.addColorStop(0, fc);
        dGrad.addColorStop(1, shade(fc, 0.88));
        ctx.fillStyle = dGrad;
        ctx.beginPath();
        ctx.moveTo(a.cx - hemW - 1, botY - 1);
        ctx.bezierCurveTo(a.cx - hemW - 2, botY + 8, a.cx - dhw + dressSway * 0.4, dressBot - 4, a.cx - dhw + dressSway, dressBot);
        ctx.quadraticCurveTo(a.cx + dressSway * 0.3, dressBot + 2, a.cx + dhw + dressSway * 0.6, dressBot);
        ctx.bezierCurveTo(a.cx + dhw + dressSway * 0.2, dressBot - 4, a.cx + hemW + 2, botY + 8, a.cx + hemW + 1, botY - 1);
        ctx.closePath();
        ctx.fill();
        // Hem — follows sway
        ctx.strokeStyle = dk;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(a.cx - dhw + dressSway, dressBot);
        ctx.quadraticCurveTo(a.cx + dressSway * 0.3, dressBot + 2, a.cx + dhw + dressSway * 0.6, dressBot);
        ctx.stroke();
        // Fold shadows — shift with sway
        ctx.strokeStyle = "rgba(0,0,0,0.05)";
        ctx.lineWidth = 0.8;
        for (let i = -1; i <= 1; i += 2) {
          const fSway = Math.sin(wp.clothPhase + i * 0.8) * wp.clothAmplitude * 1.5;
          ctx.beginPath();
          ctx.moveTo(a.cx + i * hemW * 0.5, botY);
          ctx.quadraticCurveTo(a.cx + i * dhw * 0.5 + fSway, (botY + dressBot) / 2, a.cx + i * dhw * 0.45 + dressSway * 0.3, dressBot - 1);
          ctx.stroke();
        }
        break;
      }
      case 4: { // Button-up — placket, buttons, pointed collar
        // Placket center line
        ctx.strokeStyle = seam;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(a.cx, topY + 5);
        ctx.lineTo(a.cx, botY);
        ctx.stroke();
        // Buttons
        ctx.fillStyle = "#FFFFFFCC";
        for (let y = topY + 8; y < botY - 3; y += 7) {
          ctx.beginPath(); ctx.arc(a.cx, y, 1.3, 0, Math.PI * 2); ctx.fill();
          // Button holes — tiny dark dots
          ctx.fillStyle = shade(color, 0.5);
          ctx.beginPath(); ctx.arc(a.cx, y, 0.4, 0, Math.PI * 2); ctx.fill();
          ctx.fillStyle = "#FFFFFFCC";
        }
        // Pointed collar
        ctx.fillStyle = shade(color, 0.92);
        // Left collar point
        ctx.beginPath();
        ctx.moveTo(a.cx - 2, topY + 1);
        ctx.lineTo(a.cx - 9 * n, topY + 8);
        ctx.lineTo(a.cx - 4 * n, topY + 6);
        ctx.lineTo(a.cx - 2, topY + 4);
        ctx.closePath(); ctx.fill();
        // Right collar point
        ctx.beginPath();
        ctx.moveTo(a.cx + 2, topY + 1);
        ctx.lineTo(a.cx + 9 * n, topY + 8);
        ctx.lineTo(a.cx + 4 * n, topY + 6);
        ctx.lineTo(a.cx + 2, topY + 4);
        ctx.closePath(); ctx.fill();
        // Collar edge
        ctx.strokeStyle = shade(color, 0.8);
        ctx.lineWidth = 0.6;
        ctx.beginPath();
        ctx.moveTo(a.cx - 2, topY + 1); ctx.lineTo(a.cx - 9 * n, topY + 8);
        ctx.moveTo(a.cx + 2, topY + 1); ctx.lineTo(a.cx + 9 * n, topY + 8);
        ctx.stroke();
        break;
      }
    }
  });
}

// ─── Jacket V2 ───────────────────────────────────────────────────────────────

function drawJacketV2(
  ctx: DrawCtx, dir: Direction, _frame: number,
  color: string, style: number, bodyType: 0 | 1 | 2 | 3
): void {
  if (style < 0) return;
  const a = BA[bodyType];
  const dk = shade(color, 0.75);
  const hi = shade(color, 1.15);

  withFlip(ctx, dir, (cat) => {
    const isBack = cat === "back" || cat === "qback";
    const n = getNarrow(cat);
    const fc = isBack ? dk : color;
    const fc2 = isBack ? shade(dk, 0.92) : shade(color, 0.88);

    const bs = _bodyShape;
    const jFit = jacketFit(style);
    const sw = projectW(cat, bs.shoulderW + 4 * jFit, bs.shoulderD + 2 * jFit);
    const ww = projectW(cat, bs.waistW + 3 * jFit, bs.waistD + 1.5 * jFit);
    const topY = a.shY - 3;
    const wp = _animPose;
    // Jacket sleeve centers match skin arm exactly — coverage comes from
    // wider sleeve widths (10/8/7), not from offset center-lines.
    // Shoulder anchors sit at the jacket torso edge (sw), bridging to arms.
    const armHang = bs.shoulderW + 5 * bs.armMass;
    const farElbowX = a.cx + (armHang - 2) * n - wp.farArmDX * 0.4;
    const farWristX = a.cx + (armHang - 4) * n - wp.farArmDX;
    const nearElbowX = a.cx - (armHang - 2) * n + wp.nearArmDX * 0.4;
    const nearWristX = a.cx - (armHang - 4) * n + wp.nearArmDX;

    /** Shaped jacket sleeve — starts just above shoulder line */
    const jSlvY = a.shY - 1;
    const drawJktSleeve = (shX: number, elbX: number, wrstX: number, endY: number, topW: number, midW: number, botW: number, c: string, armDY: number, elbowBend = 0) => {
      const elbDY = armDY * 0.5 + elbowBend * 6;
      const eElbY = a.elbowY + elbDY;
      const eY = endY + armDY;
      ctx.fillStyle = c;
      ctx.beginPath();
      // Outer edge: shoulder → elbow → wrist
      ctx.moveTo(shX - topW / 2, jSlvY);
      ctx.bezierCurveTo(shX - topW / 2, eElbY - 6, elbX - midW / 2, eElbY, elbX - midW / 2, eElbY);
      ctx.bezierCurveTo(elbX - midW / 2, eElbY + 2, wrstX - botW / 2, eY - 4, wrstX - botW / 2, eY);
      // Cuff
      ctx.lineTo(wrstX + botW / 2, eY);
      // Inner edge: wrist → elbow → shoulder
      ctx.bezierCurveTo(wrstX + botW / 2, eY - 4, elbX + midW / 2, eElbY + 2, elbX + midW / 2, eElbY);
      ctx.bezierCurveTo(elbX + midW / 2, eElbY, shX + topW / 2, eElbY - 6, shX + topW / 2, jSlvY);
      ctx.closePath();
      ctx.fill();
      // Cuff band
      ctx.fillStyle = shade(c, 0.82);
      ctx.beginPath();
      ctx.moveTo(wrstX - botW / 2, eY);
      ctx.lineTo(wrstX + botW / 2, eY);
      ctx.lineTo(wrstX + botW / 2, eY + 2);
      ctx.lineTo(wrstX - botW / 2, eY + 2);
      ctx.closePath();
      ctx.fill();
    };

    switch (style % 3) {
      case 0: { // Open cardigan — split front panels, 3/4 sleeves
        const botY = a.hipY - 1;
        const hemW = (a.hipW + 2) * n;
        // Far sleeve — 3/4 length to elbow+6
        drawJktSleeve(a.cx + sw, farElbowX, farElbowX, a.elbowY + 6, 10, 8, 7, fc2, wp.farArmDY, wp.farElbowBend);
        // Left panel
        ctx.fillStyle = fc;
        ctx.beginPath();
        ctx.moveTo(a.cx - sw, topY);
        ctx.bezierCurveTo(a.cx - sw, a.waistY, a.cx - ww, a.waistY + 2, a.cx - hemW, botY);
        ctx.lineTo(a.cx - 3, botY);
        ctx.lineTo(a.cx - 3, topY + 6);
        ctx.closePath();
        ctx.fill();
        // Right panel
        ctx.fillStyle = shade(fc, 0.95);
        ctx.beginPath();
        ctx.moveTo(a.cx + 3, topY + 6);
        ctx.lineTo(a.cx + 3, botY);
        ctx.lineTo(a.cx + hemW, botY);
        ctx.bezierCurveTo(a.cx + ww, a.waistY + 2, a.cx + sw, a.waistY, a.cx + sw, topY);
        ctx.closePath();
        ctx.fill();
        // Near sleeve
        drawJktSleeve(a.cx - sw, nearElbowX, nearElbowX, a.elbowY + 6, 10, 8, 7, fc, wp.nearArmDY, wp.nearElbowBend);
        // Lapel edge
        ctx.strokeStyle = hi;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(a.cx - 3, topY + 6); ctx.lineTo(a.cx - 3, botY);
        ctx.moveTo(a.cx + 3, topY + 6); ctx.lineTo(a.cx + 3, botY);
        ctx.stroke();
        // Hem
        ctx.strokeStyle = shade(fc, 0.82);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(a.cx - hemW, botY); ctx.lineTo(a.cx + hemW, botY);
        ctx.stroke();
        break;
      }
      case 1: { // Bomber — zip + collar band, full-length sleeves
        const botY = a.waistY + 4;
        // Far sleeve — full length to wristY
        drawJktSleeve(a.cx + sw, farElbowX, farWristX, a.wristY, 11, 8, 7, fc2, wp.farArmDY, wp.farElbowBend);
        // Body
        ctx.fillStyle = fc;
        ctx.beginPath();
        ctx.moveTo(a.cx - sw, topY);
        ctx.bezierCurveTo(a.cx - sw, (topY + botY) / 2, a.cx - ww, botY - 2, a.cx - ww, botY);
        ctx.lineTo(a.cx + ww, botY);
        ctx.bezierCurveTo(a.cx + ww, botY - 2, a.cx + sw, (topY + botY) / 2, a.cx + sw, topY);
        ctx.closePath();
        ctx.fill();
        // Near sleeve — full length
        drawJktSleeve(a.cx - sw, nearElbowX, nearWristX, a.wristY, 11, 8, 7, fc, wp.nearArmDY, wp.nearElbowBend);
        // Elastic waistband
        ctx.fillStyle = shade(color, 0.62);
        ctx.fillRect(a.cx - ww, botY, ww * 2, 3);
        // Zipper
        ctx.strokeStyle = dk;
        ctx.lineWidth = 1.5;
        ctx.beginPath(); ctx.moveTo(a.cx, topY + 4); ctx.lineTo(a.cx, botY); ctx.stroke();
        // Zipper pull
        ctx.fillStyle = "#888";
        ctx.fillRect(a.cx - 1, topY + 8, 2, 3);
        // Collar band
        ctx.fillStyle = dk;
        ctx.fillRect(a.cx - sw + 2, topY - 1, (sw - 2) * 2, 3);
        break;
      }
      case 2: { // Trench coat — long, belted, full sleeves
        const botY = a.kneeY - 2;
        // Far sleeve — full length to wristY
        drawJktSleeve(a.cx + sw, farElbowX, farWristX, a.wristY, 11, 8, 7, fc2, wp.farArmDY, wp.farElbowBend);
        // Body — extends to below hips
        const hw2 = (a.hipW + 6) * n;
        ctx.fillStyle = fc;
        ctx.beginPath();
        ctx.moveTo(a.cx - sw, topY);
        ctx.bezierCurveTo(a.cx - sw, a.waistY, a.cx - ww - 2, a.hipY, a.cx - hw2, botY);
        ctx.lineTo(a.cx + hw2, botY);
        ctx.bezierCurveTo(a.cx + ww + 2, a.hipY, a.cx + sw, a.waistY, a.cx + sw, topY);
        ctx.closePath();
        ctx.fill();
        // Near sleeve — full length
        drawJktSleeve(a.cx - sw, nearElbowX, nearWristX, a.wristY, 11, 8, 7, fc, wp.nearArmDY, wp.nearElbowBend);
        // Belt
        ctx.fillStyle = shade(color, 0.55);
        const beltW = (a.waistW + 4) * n;
        ctx.fillRect(a.cx - beltW, a.waistY - 1, beltW * 2, 3);
        ctx.fillStyle = "#C0A040";
        ctx.fillRect(a.cx - 2, a.waistY - 1, 4, 3);
        // Center seam
        ctx.strokeStyle = dk;
        ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(a.cx, topY + 4); ctx.lineTo(a.cx, botY); ctx.stroke();
        // Coat hem with subtle curve
        ctx.strokeStyle = shade(fc, 0.78);
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.moveTo(a.cx - hw2, botY);
        ctx.quadraticCurveTo(a.cx, botY + 2, a.cx + hw2, botY);
        ctx.stroke();
        // Lapels
        ctx.fillStyle = shade(fc, 0.92);
        ctx.beginPath();
        ctx.moveTo(a.cx - 3, topY + 2);
        ctx.lineTo(a.cx - sw * 0.5, topY + 10);
        ctx.lineTo(a.cx - 4, topY + 10);
        ctx.lineTo(a.cx - 3, topY + 5);
        ctx.closePath(); ctx.fill();
        ctx.beginPath();
        ctx.moveTo(a.cx + 3, topY + 2);
        ctx.lineTo(a.cx + sw * 0.5, topY + 10);
        ctx.lineTo(a.cx + 4, topY + 10);
        ctx.lineTo(a.cx + 3, topY + 5);
        ctx.closePath(); ctx.fill();
        break;
      }
    }
  });
}

// ─── Hair Back V2 ────────────────────────────────────────────────────────────

function drawHairBackV2(
  ctx: DrawCtx, dir: Direction, _frame: number,
  color: string, style: number, bodyType: 0 | 1 | 2 | 3,
  hatStyle: number = -1
): void {
  const a = BA[bodyType];
  const bY = -_animPose.breatheY;
  const dk = shade(color, 0.78);
  const hairStyle = style % 24;
  // Hair dynamics — pendulum-style swing. Tips move more than roots.
  const hSwing = _animPose.hairSwingX;
  // Hat awareness — covering hats suppress crown volume, compress drapes
  const hatCovers = hatStyle >= 0 && [0, 1, 2, 3, 4, 5, 7].includes(hatStyle % 8);

  withFlip(ctx, dir, (cat) => {
    const isBack = cat === "back" || cat === "qback";
    const isSide = cat === "side";
    const isQtr = cat === "qfront" || cat === "qback";
    const rx = a.headRx * (isSide ? 0.85 : isQtr ? 0.90 : 0.95);
    const ry = a.headRy;
    const headY = a.headY + bY;
    const jawY = headY + ry;
    const topY = headY - ry;
    const hasLongBack = [3, 5, 7, 8, 9, 13, 15, 17, 18, 20, 23].includes(hairStyle);

    if (hasLongBack) {
      // Under a covering hat: drape emerges from jawline (under hat brim), shortened 30%
      const rawLen = hairStyle === 7 ? 16 : hairStyle === 8 ? 6 : hairStyle === 18 ? 20 : hairStyle === 23 ? 28 : 24;
      const backLen = hatCovers ? Math.round(rawLen * 0.7) : rawLen;
      const backW = rx + 2;
      // Back-of-head volume — hat covers the crown, so skip under covering hats
      if (!hatCovers) {
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.ellipse(a.cx, headY, rx + 1.5, ry + 1, 0, 0, Math.PI * 2);
        ctx.fill();
      }
      // Drape from crown to tips — hair swing shifts tips more than roots
      // Under a hat: drape starts at jawY (emerging from under brim) instead of headY
      const drapeStartY = hatCovers ? jawY : headY - 2;
      const tipSwing = hSwing * 1.5;  // tips exaggerate the swing
      const midSwing = hSwing * 0.6;  // mid-length gets moderate swing
      ctx.fillStyle = dk;
      ctx.beginPath();
      ctx.moveTo(a.cx - backW, drapeStartY);
      ctx.lineTo(a.cx - backW + midSwing * 0.3, jawY - 2);
      ctx.bezierCurveTo(
        a.cx - backW - 1 + midSwing, jawY + backLen * 0.4,
        a.cx - backW * 0.5 + tipSwing, jawY + backLen,
        a.cx + tipSwing, jawY + backLen
      );
      ctx.bezierCurveTo(
        a.cx + backW * 0.5 + tipSwing, jawY + backLen,
        a.cx + backW + 1 + midSwing, jawY + backLen * 0.4,
        a.cx + backW + midSwing * 0.3, jawY - 2
      );
      ctx.lineTo(a.cx + backW, drapeStartY);
      ctx.closePath();
      ctx.fill();

      // Style-specific back texture
      if (hairStyle === 9) {
        // Curly — ringlet circles down the back
        for (let y = jawY; y < jawY + backLen; y += 5) {
          const t = (y - jawY) / backLen;
          const w = backW * (1 - t * 0.3);
          ctx.fillStyle = (y - jawY) % 10 < 5 ? color : dk;
          ctx.beginPath(); ctx.arc(a.cx - w + 2, y, 2, 0, Math.PI * 2); ctx.fill();
          ctx.beginPath(); ctx.arc(a.cx + w - 2, y, 2, 0, Math.PI * 2); ctx.fill();
        }
      } else if (hairStyle === 15) {
        // Layered — horizontal tier lines showing layers
        ctx.strokeStyle = shade(dk, 0.85);
        ctx.lineWidth = 0.8;
        for (let y = jawY + 4; y < jawY + backLen - 4; y += 7) {
          const t = (y - jawY) / backLen;
          const w = backW * (1 - t * 0.25);
          ctx.beginPath();
          ctx.moveTo(a.cx - w, y);
          ctx.quadraticCurveTo(a.cx, y + 2, a.cx + w, y);
          ctx.stroke();
        }
      } else if (hairStyle === 5) {
        // Wavy — S-curve wave lines
        ctx.strokeStyle = shade(dk, 0.88);
        ctx.lineWidth = 0.8;
        for (let y = jawY; y < jawY + backLen; y += 6) {
          const t = (y - jawY) / backLen;
          const w = backW * (1 - t * 0.3);
          ctx.beginPath();
          ctx.arc(a.cx - w - 1, y, 1.5, 0, Math.PI * 2); ctx.stroke();
          ctx.beginPath();
          ctx.arc(a.cx + w + 1, y, 1.5, 0, Math.PI * 2); ctx.stroke();
        }
      } else if (hairStyle === 13) {
        // Braids — rope pattern down the back
        for (let side = -1; side <= 1; side += 2) {
          const bx = a.cx + side * (rx - 2);
          for (let y = jawY - 2; y < jawY + backLen; y += 4) {
            ctx.fillStyle = (y - jawY) % 8 < 4 ? color : dk;
            ctx.beginPath();
            ctx.ellipse(bx + ((y - jawY) % 8 < 4 ? -1 : 1), y, 2, 2.5, 0, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      } else if (hairStyle === 17) {
        // Curtain bangs long — soft center part strands
        ctx.strokeStyle = shade(dk, 0.88);
        ctx.lineWidth = 0.6;
        ctx.globalAlpha = 0.3;
        for (let i = -3; i <= 3; i++) {
          ctx.beginPath();
          ctx.moveTo(a.cx + i * 2.5, headY - 2);
          ctx.bezierCurveTo(a.cx + i * 3, jawY * 0.6 + headY * 0.4, a.cx + i * 2, jawY, a.cx + i * 1.5, jawY + backLen - 2);
          ctx.stroke();
        }
        ctx.globalAlpha = 1.0;
      } else if (hairStyle === 18) {
        // Wolf cut — choppy layered texture
        ctx.strokeStyle = shade(dk, 0.85);
        ctx.lineWidth = 0.8;
        for (let y = jawY + 2; y < jawY + backLen - 4; y += 5) {
          const t = (y - jawY) / backLen;
          const w = backW * (1 - t * 0.2);
          ctx.beginPath();
          ctx.moveTo(a.cx - w + 1, y);
          // Choppy zigzag
          for (let x = -w + 3; x < w - 1; x += 4) {
            ctx.lineTo(a.cx + x, y + (x % 8 < 4 ? -1.5 : 1.5));
          }
          ctx.stroke();
        }
      } else if (hairStyle === 20) {
        // Half-up half-down — gathered top section + flowing bottom
        ctx.strokeStyle = shade(dk, 0.9);
        ctx.lineWidth = 0.5;
        ctx.globalAlpha = 0.35;
        // Tie at mid-back
        ctx.fillStyle = shade(color, 0.45);
        ctx.globalAlpha = 1.0;
        ctx.beginPath();
        ctx.ellipse(a.cx, jawY, 3.5, 2, 0, 0, Math.PI * 2);
        ctx.fill();
        // Flowing strands below
        ctx.strokeStyle = shade(dk, 0.9);
        ctx.lineWidth = 0.5;
        ctx.globalAlpha = 0.35;
        for (let i = -2; i <= 2; i++) {
          ctx.beginPath();
          ctx.moveTo(a.cx + i * 3, jawY + 2);
          ctx.quadraticCurveTo(a.cx + i * 3.5, jawY + backLen * 0.5, a.cx + i * 2, jawY + backLen - 2);
          ctx.stroke();
        }
        ctx.globalAlpha = 1.0;
      } else if (hairStyle === 23) {
        // High ponytail — smooth gathered strands
        ctx.strokeStyle = shade(dk, 0.9);
        ctx.lineWidth = 0.6;
        ctx.globalAlpha = 0.3;
        for (let i = -2; i <= 2; i++) {
          ctx.beginPath();
          ctx.moveTo(a.cx + i * 2, headY - 4);
          ctx.quadraticCurveTo(a.cx + i * 2.5, jawY, a.cx + i * 1.5, jawY + backLen - 2);
          ctx.stroke();
        }
        ctx.globalAlpha = 1.0;
      } else {
        // Straight styles (3, 7, 8) — vertical strand lines
        ctx.strokeStyle = shade(dk, 0.9);
        ctx.lineWidth = 0.5;
        ctx.globalAlpha = 0.35;
        for (let i = -2; i <= 2; i++) {
          ctx.beginPath();
          ctx.moveTo(a.cx + i * 3, headY);
          ctx.quadraticCurveTo(a.cx + i * 3.5, jawY, a.cx + i * 2, jawY + backLen - 2);
          ctx.stroke();
        }
        ctx.globalAlpha = 1.0;
      }
      // Highlight sheen down center of back hair
      const backGrad = ctx.createLinearGradient(a.cx, headY, a.cx, jawY + backLen);
      backGrad.addColorStop(0, "rgba(255,255,255,0.08)");
      backGrad.addColorStop(0.5, "rgba(255,255,255,0)");
      backGrad.addColorStop(1, "rgba(0,0,0,0.05)");
      ctx.fillStyle = backGrad;
      ctx.beginPath();
      ctx.moveTo(a.cx - backW * 0.3, headY);
      ctx.lineTo(a.cx - backW * 0.3, jawY + backLen - 4);
      ctx.lineTo(a.cx + backW * 0.3, jawY + backLen - 4);
      ctx.lineTo(a.cx + backW * 0.3, headY);
      ctx.closePath();
      ctx.fill();
    } else if (isBack && !hatCovers) {
      // ── Style-aware back cap for short styles ──
      // Covering hats fully suppress short back hair — nothing visible from behind.
      switch (hairStyle) {
        case 2: { // Afro — large sphere
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.ellipse(a.cx, headY - 2, rx + 6, ry + 4, 0, 0, Math.PI * 2);
          ctx.fill();
          // Texture
          ctx.fillStyle = dk;
          for (let i = 0; i < 10; i++) {
            const angle = (i / 10) * Math.PI * 2;
            const r = rx + 3 + Math.sin(i * 4.2) * 1.5;
            ctx.beginPath();
            ctx.arc(a.cx + Math.cos(angle) * r, headY - 2 + Math.sin(angle) * (ry + 1), 1.2, 0, Math.PI * 2);
            ctx.fill();
          }
          break;
        }
        case 12: { // Mohawk — shaved sides + central ridge
          ctx.fillStyle = dk;
          ctx.globalAlpha = 0.25;
          ctx.beginPath();
          ctx.ellipse(a.cx, headY, rx + 1, ry + 1, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 1.0;
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.moveTo(a.cx - 3, topY + 2);
          ctx.quadraticCurveTo(a.cx, topY - 6, a.cx + 3, topY + 2);
          ctx.lineTo(a.cx + 3, headY + ry - 4);
          ctx.lineTo(a.cx - 3, headY + ry - 4);
          ctx.closePath();
          ctx.fill();
          break;
        }
        case 14: { // Bun — tight cap + bun on top
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.ellipse(a.cx, headY, rx + 1, ry + 0.5, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = dk;
          ctx.beginPath();
          ctx.arc(a.cx, topY - 1, 5.5, 0, Math.PI * 2);
          ctx.fill();
          // Wrap texture on bun
          ctx.strokeStyle = shade(dk, 0.85);
          ctx.lineWidth = 0.5;
          ctx.globalAlpha = 0.5;
          ctx.beginPath();
          ctx.ellipse(a.cx, topY - 1, 4.5, 3.5, 0.3, 0, Math.PI * 2);
          ctx.stroke();
          ctx.globalAlpha = 1.0;
          break;
        }
        case 0: { // Buzz — tight, minimal volume
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.ellipse(a.cx, headY, rx + 0.5, ry + 0.5, 0, 0, Math.PI * 2);
          ctx.fill();
          // Tiny buzz dots
          ctx.fillStyle = dk;
          ctx.globalAlpha = 0.15;
          for (let i = 0; i < 8; i++) {
            const angle = (i / 8) * Math.PI * 2;
            ctx.beginPath();
            ctx.arc(a.cx + Math.cos(angle) * rx * 0.6, headY + Math.sin(angle) * ry * 0.6, 0.5, 0, Math.PI * 2);
            ctx.fill();
          }
          ctx.globalAlpha = 1.0;
          break;
        }
        case 6: { // Undercut — shaved sides, volume on top
          ctx.fillStyle = dk;
          ctx.globalAlpha = 0.2;
          ctx.beginPath();
          ctx.ellipse(a.cx, headY + 2, rx + 1, ry - 2, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.globalAlpha = 1.0;
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.ellipse(a.cx, headY - 3, rx, ry * 0.7, 0, 0, Math.PI * 2);
          ctx.fill();
          break;
        }
        case 10: { // Spiky — spikes visible from back too
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.ellipse(a.cx, headY, rx + 1, ry + 1, 0, 0, Math.PI * 2);
          ctx.fill();
          for (let i = 0; i < 5; i++) {
            const angle = Math.PI + (i / 5) * Math.PI;
            const sLen = 5 + (i % 2) * 3;
            ctx.fillStyle = i % 2 === 0 ? color : dk;
            ctx.beginPath();
            ctx.moveTo(a.cx + Math.cos(angle) * rx, headY + Math.sin(angle) * ry);
            ctx.lineTo(a.cx + Math.cos(angle) * (rx + sLen), headY + Math.sin(angle) * (ry + sLen));
            ctx.lineTo(a.cx + Math.cos(angle + 0.25) * (rx + sLen * 0.4), headY + Math.sin(angle + 0.25) * (ry + sLen * 0.4));
            ctx.closePath();
            ctx.fill();
          }
          break;
        }
        case 16: { // Pixie cut — short asymmetric, slight side volume
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.ellipse(a.cx, headY, rx + 1, ry + 0.5, 0, 0, Math.PI * 2);
          ctx.fill();
          // Asymmetric side — heavier on one side
          ctx.fillStyle = dk;
          ctx.beginPath();
          ctx.ellipse(a.cx + rx * 0.3, headY - 2, rx * 0.6, ry * 0.5, 0.2, 0, Math.PI * 2);
          ctx.fill();
          break;
        }
        case 19: { // Space buns — two buns on top
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.ellipse(a.cx, headY, rx + 1, ry + 1, 0, 0, Math.PI * 2);
          ctx.fill();
          for (let side = -1; side <= 1; side += 2) {
            ctx.fillStyle = dk;
            ctx.beginPath();
            ctx.arc(a.cx + side * (rx - 2), topY + 1, 5, 0, Math.PI * 2);
            ctx.fill();
            // Wrap texture
            ctx.strokeStyle = shade(dk, 0.85);
            ctx.lineWidth = 0.5;
            ctx.globalAlpha = 0.5;
            ctx.beginPath();
            ctx.ellipse(a.cx + side * (rx - 2), topY + 1, 4, 3, side * 0.3, 0, Math.PI * 2);
            ctx.stroke();
            ctx.globalAlpha = 1.0;
          }
          break;
        }
        case 21: { // Cornrows — parallel braided lines across scalp
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.ellipse(a.cx, headY, rx + 1, ry + 1, 0, 0, Math.PI * 2);
          ctx.fill();
          // Braid rows
          ctx.strokeStyle = dk;
          ctx.lineWidth = 1.2;
          for (let i = -2; i <= 2; i++) {
            ctx.beginPath();
            ctx.moveTo(a.cx + i * 3.5, topY + 3);
            ctx.quadraticCurveTo(a.cx + i * 3.5, headY, a.cx + i * 3, headY + ry - 2);
            ctx.stroke();
          }
          break;
        }
        case 22: { // Shag — all-over choppy volume
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.ellipse(a.cx, headY, rx + 2, ry + 2, 0, 0, Math.PI * 2);
          ctx.fill();
          // Choppy texture
          ctx.fillStyle = dk;
          for (let i = 0; i < 12; i++) {
            const angle = (i / 12) * Math.PI * 2;
            const r = rx + 1;
            ctx.beginPath();
            ctx.moveTo(a.cx + Math.cos(angle) * (r - 1), headY + Math.sin(angle) * ry);
            ctx.lineTo(a.cx + Math.cos(angle) * (r + 2), headY + Math.sin(angle) * (ry + 2));
            ctx.lineTo(a.cx + Math.cos(angle + 0.2) * (r + 0.5), headY + Math.sin(angle + 0.2) * (ry + 0.5));
            ctx.closePath();
            ctx.fill();
          }
          break;
        }
        default: { // Standard short styles (1, 4, 11) — classic back cap
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.ellipse(a.cx, headY, rx + 1, ry + 1, 0, 0, Math.PI * 2);
          ctx.fill();
          // Depth gradient
          const capG = ctx.createRadialGradient(a.cx, headY - 3, 2, a.cx, headY, ry + 2);
          capG.addColorStop(0, shade(color, 1.12));
          capG.addColorStop(0.5, "rgba(0,0,0,0)");
          capG.addColorStop(1, dk);
          ctx.fillStyle = capG;
          ctx.beginPath();
          ctx.ellipse(a.cx, headY, rx + 1, ry + 1, 0, 0, Math.PI * 2);
          ctx.fill();
          // Strand lines
          ctx.strokeStyle = dk;
          ctx.lineWidth = 0.5;
          ctx.globalAlpha = 0.3;
          for (let i = -2; i <= 2; i++) {
            ctx.beginPath();
            ctx.moveTo(a.cx + i * 3, headY - ry + 4);
            ctx.quadraticCurveTo(a.cx + i * 3.5, headY, a.cx + i * 2, headY + ry - 2);
            ctx.stroke();
          }
          ctx.globalAlpha = 1.0;
          break;
        }
      }
    }
  });
}

// ─── Hair Front V2 ───────────────────────────────────────────────────────────

function drawHairFrontV2(
  ctx: DrawCtx, dir: Direction, _frame: number,
  color: string, style: number, bodyType: 0 | 1 | 2 | 3,
  hatStyle: number = -1
): void {
  const a = BA[bodyType];
  const bY = -_animPose.breatheY;
  const dk = shade(color, 0.78);
  const hi = shade(color, 1.18);
  // Hair dynamics — front curtains swing with movement
  const hSwing = _animPose.hairSwingX;
  const hairStyle = style % 24;

  // Hats that cover the top of the head suppress hair volume above the brim
  const hatCovers = hatStyle >= 0 && [0, 1, 2, 3, 4, 5, 7].includes(hatStyle % 8);

  withFlip(ctx, dir, (cat) => {
    const isBack = cat === "back" || cat === "qback";
    const isSide = cat === "side";
    const isQtr = cat === "qfront" || cat === "qback";
    const rx = a.headRx * (isSide ? 0.85 : isQtr ? 0.90 : 0.95);
    const ry = a.headRy;
    const headY = a.headY + bY;
    const topY = headY - ry;  // top of head

    // ── Full hair cap for back/qback — covers entire visible scalp ──
    if (isBack && !hatCovers) {
      // Full ellipse covering the whole head, slightly larger than head
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.ellipse(a.cx, headY, rx + 1.5, ry + 1, 0, 0, Math.PI * 2);
      ctx.fill();
      // Gradient overlay for depth — darker at bottom edges
      const capGrad = ctx.createRadialGradient(a.cx, headY - 3, 2, a.cx, headY, ry + 2);
      capGrad.addColorStop(0, hi);
      capGrad.addColorStop(0.5, "rgba(0,0,0,0)");
      capGrad.addColorStop(1, dk);
      ctx.fillStyle = capGrad;
      ctx.beginPath();
      ctx.ellipse(a.cx, headY, rx + 1.5, ry + 1, 0, 0, Math.PI * 2);
      ctx.fill();
      // Texture strokes to show hair direction
      ctx.strokeStyle = dk;
      ctx.lineWidth = 0.5;
      ctx.globalAlpha = 0.4;
      for (let i = -2; i <= 2; i++) {
        ctx.beginPath();
        ctx.moveTo(a.cx + i * 3, topY + 3);
        ctx.quadraticCurveTo(a.cx + i * 3.5, headY, a.cx + i * 2, headY + ry - 2);
        ctx.stroke();
      }
      ctx.globalAlpha = 1.0;
    }

    // ── Side view: draw back-of-head hair mass for long styles ──
    const hasLongHair = [3, 5, 7, 8, 9, 13, 15, 17, 18, 20, 23].includes(hairStyle);
    if (isSide && hasLongHair && !hatCovers) {
      ctx.fillStyle = dk;
      ctx.beginPath();
      // Mass that extends behind the head silhouette on the far side
      ctx.ellipse(a.cx + rx * 0.3, headY + 1, rx * 0.75, ry + 1, 0, 0, Math.PI * 2);
      ctx.fill();
    }

    // ── Shared anchor points ──
    const attachY = topY + 4;     // where side elements attach to the crown
    const earY = headY + 2;       // roughly ear level
    const jawY = headY + ry;      // bottom of head ellipse
    const curtainEnd = jawY + 12; // how far long hair drapes below jaw

    // ── Crown helper — full ellipse, NO flat edges ──
    // Centered above the forehead, bottom curves away naturally above the eye line.
    // Each style calls this with custom sizing for a seamless integrated look.
    const drawCrown = (extraW = 0, extraH = 0, yOff = 0) => {
      if (hatCovers || isBack) return;
      const cy = topY + 1 + yOff;
      const crx = rx + 2 + extraW;
      const cry = ry * 0.55 + extraH;
      const g = ctx.createRadialGradient(a.cx - 2, cy - cry * 0.3, 1, a.cx, cy, crx);
      g.addColorStop(0, hi);
      g.addColorStop(0.35, color);
      g.addColorStop(1, dk);
      ctx.fillStyle = g;
      ctx.beginPath();
      ctx.ellipse(a.cx, cy, crx, cry, 0, 0, Math.PI * 2);
      ctx.fill();
    };

    // ── Forehead fringe helper for long styles ──
    // Draws a soft arc of hair across the forehead. Gives long styles a natural
    // parting or side-swept fringe so they don't look unnaturally pulled back.
    const drawFringe = (style: "center" | "side" | "wispy" = "center") => {
      if (hatCovers || isBack || isSide) return;
      ctx.fillStyle = dk;
      ctx.globalAlpha = 0.85;
      if (style === "center") {
        // Soft center-parted curtain bangs
        for (let side = -1; side <= 1; side += 2) {
          ctx.beginPath();
          ctx.moveTo(a.cx + side * 2, topY + 2);
          ctx.quadraticCurveTo(
            a.cx + side * (rx * 0.5), topY + 1,
            a.cx + side * (rx - 1), topY + 6
          );
          ctx.lineTo(a.cx + side * (rx - 2), topY + 8);
          ctx.quadraticCurveTo(
            a.cx + side * (rx * 0.4), topY + 4,
            a.cx + side * 2, topY + 4
          );
          ctx.closePath();
          ctx.fill();
        }
      } else if (style === "side") {
        // Swept to one side
        ctx.beginPath();
        ctx.moveTo(a.cx - rx * 0.4, topY + 1);
        ctx.quadraticCurveTo(a.cx + rx * 0.2, topY - 1, a.cx + rx, topY + 5);
        ctx.lineTo(a.cx + rx - 1, topY + 8);
        ctx.quadraticCurveTo(a.cx + rx * 0.1, topY + 3, a.cx - rx * 0.3, topY + 4);
        ctx.closePath();
        ctx.fill();
      } else {
        // Wispy strands
        ctx.lineWidth = 0.7;
        ctx.strokeStyle = dk;
        for (let i = -1; i <= 1; i++) {
          ctx.beginPath();
          ctx.moveTo(a.cx + i * 4, topY + 1);
          ctx.quadraticCurveTo(a.cx + i * 5 + 2, topY + 4, a.cx + i * 4 + 3, topY + 8);
          ctx.stroke();
        }
      }
      ctx.globalAlpha = 1.0;
    };

    // ── Shine stripe helper — adds a vertical gloss highlight to hair panels ──
    const drawShine = (x: number, y0: number, y1: number, w = 2) => {
      ctx.globalAlpha = 0.12;
      ctx.fillStyle = hi;
      ctx.beginPath();
      ctx.moveTo(x - w * 0.5, y0);
      ctx.quadraticCurveTo(x, (y0 + y1) * 0.5 - 2, x + w * 0.5, y1);
      ctx.quadraticCurveTo(x, (y0 + y1) * 0.5 + 2, x - w * 0.5, y0);
      ctx.fill();
      ctx.globalAlpha = 1.0;
    };

    switch (hairStyle) {
      case 0: { // Crew cut / buzz — close-cropped, subtle volume
        if (hatCovers || isBack) break;
        drawCrown(-1, -1);
        // Buzz highlight — soft specular
        ctx.fillStyle = hi;
        ctx.globalAlpha = 0.25;
        ctx.beginPath();
        ctx.ellipse(a.cx - 2, topY + 1, 3, 2.5, -0.2, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1.0;
        // Subtle hairline at forehead
        ctx.strokeStyle = dk;
        ctx.lineWidth = 0.6;
        ctx.globalAlpha = 0.3;
        ctx.beginPath();
        ctx.ellipse(a.cx, topY + 3, rx, 2, 0, 0, Math.PI);
        ctx.stroke();
        ctx.globalAlpha = 1.0;
        break;
      }
      case 1: { // Side part — smooth volume with visible part line
        if (hatCovers || isBack) break;
        drawCrown(1, 0);
        // Part line — deeper groove with gradient shadow
        ctx.strokeStyle = dk;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(a.cx - rx * 0.3, topY - 1);
        ctx.quadraticCurveTo(a.cx - rx * 0.25, topY + 4, a.cx - rx * 0.3, topY + ry * 0.4);
        ctx.stroke();
        // Fuller side — raised volume with gradient
        const sideG = ctx.createRadialGradient(a.cx + rx * 0.3, topY + 1, 1, a.cx + rx * 0.3, topY + 3, rx * 0.55);
        sideG.addColorStop(0, hi);
        sideG.addColorStop(1, color);
        ctx.fillStyle = sideG;
        ctx.globalAlpha = 0.5;
        ctx.beginPath();
        ctx.ellipse(a.cx + rx * 0.2, topY + 2, rx * 0.55, ry * 0.4, 0.15, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1.0;
        // Subtle fringe swooping over part
        ctx.fillStyle = dk;
        ctx.globalAlpha = 0.5;
        ctx.beginPath();
        ctx.moveTo(a.cx - rx * 0.3, topY + 1);
        ctx.quadraticCurveTo(a.cx + rx * 0.2, topY - 1, a.cx + rx * 0.6, topY + 4);
        ctx.lineTo(a.cx + rx * 0.5, topY + 6);
        ctx.quadraticCurveTo(a.cx + rx * 0.1, topY + 2, a.cx - rx * 0.3, topY + 3);
        ctx.closePath();
        ctx.fill();
        ctx.globalAlpha = 1.0;
        break;
      }
      case 2: { // Afro — full volume sphere, all angles
        if (hatCovers) {
          for (let side = -1; side <= 1; side += 2) {
            if (isSide && side === -1) continue;
            ctx.fillStyle = color;
            ctx.beginPath();
            ctx.ellipse(a.cx + side * rx, headY + 2, 4, 5, 0, 0, Math.PI * 2);
            ctx.fill();
          }
          break;
        }
        if (isBack) break; // back cap already handles afro
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.ellipse(a.cx, headY - 2, rx + 6, ry + 4, 0, 0, Math.PI * 2);
        ctx.fill();
        // Texture curls — varied sizes for natural look
        ctx.fillStyle = dk;
        for (let i = 0; i < 16; i++) {
          const angle = (i / 16) * Math.PI * 2;
          const r = rx + 3 + Math.sin(i * 3.7) * 2;
          const sz = 1.2 + Math.sin(i * 2.3) * 0.5;
          ctx.beginPath();
          ctx.arc(a.cx + Math.cos(angle) * r, headY - 2 + Math.sin(angle) * (ry + 1), sz, 0, Math.PI * 2);
          ctx.fill();
        }
        // Inner highlight — crescent shape for volumetric depth
        const afroG = ctx.createRadialGradient(a.cx - 3, headY - 6, 2, a.cx, headY - 2, rx + 4);
        afroG.addColorStop(0, hi);
        afroG.addColorStop(0.4, "rgba(0,0,0,0)");
        afroG.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = afroG;
        ctx.globalAlpha = 0.2;
        ctx.beginPath();
        ctx.ellipse(a.cx, headY - 2, rx + 6, ry + 4, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1.0;
        break;
      }
      case 3: { // Long straight — crown flows into curtains as one form
        drawCrown(1, 0);
        drawFringe("center");
        // Curtains — both sides for all non-back views
        for (let side = -1; side <= 1; side += 2) {
          if (isSide && side === -1) continue; // skip far side on side view
          const outerX = a.cx + side * (rx + (isSide ? 0 : 2));
          const innerX = a.cx + side * (rx - 2);
          ctx.fillStyle = dk;
          ctx.beginPath();
          ctx.moveTo(a.cx + side * rx, attachY);
          ctx.bezierCurveTo(
            outerX + side, earY,
            outerX, jawY,
            outerX - side, curtainEnd
          );
          ctx.lineTo(innerX, curtainEnd - 2);
          ctx.bezierCurveTo(
            innerX, jawY - 2,
            a.cx + side * (rx - 1), earY,
            a.cx + side * rx, attachY
          );
          ctx.closePath();
          ctx.fill();
          // Shine on each curtain
          drawShine(a.cx + side * rx, attachY + 4, curtainEnd - 6);
        }
        break;
      }
      case 4: { // Messy / bedhead — spiky tufts from a base
        if (hatCovers || isBack) break;
        drawCrown(0, -1);
        // Spikes — wider base triangles with rounded tips via quadratic curves
        for (let i = 0; i < 9; i++) {
          const angle = Math.PI + (i / 9) * Math.PI;
          const spikeLen = 5 + (i % 3) * 3;
          const x = a.cx + Math.cos(angle) * (rx + 1);
          const y = headY - 2 + Math.sin(angle) * (ry + 1);
          const tx = x + Math.cos(angle) * spikeLen;
          const ty = y + Math.sin(angle) * spikeLen;
          ctx.fillStyle = i % 3 === 0 ? hi : i % 3 === 1 ? color : dk;
          ctx.beginPath();
          ctx.moveTo(x - Math.sin(angle) * 2, y + Math.cos(angle) * 2);
          ctx.quadraticCurveTo(tx, ty, x + Math.sin(angle) * 2, y - Math.cos(angle) * 2);
          ctx.closePath();
          ctx.fill();
        }
        break;
      }
      case 5: { // Wavy long — voluminous crown flows into wavy drapes
        drawCrown(1, 1);
        drawFringe("wispy");
        // Wavy side drapes — smooth bezier waves with volume
        for (let side = -1; side <= 1; side += 2) {
          if (isSide && side === -1) continue;
          const ox = a.cx + side * (rx + 1);
          const waveLen = ry * 2 + 10;
          // Outer wave edge (bezier for smoothness)
          ctx.fillStyle = dk;
          ctx.beginPath();
          ctx.moveTo(ox - side, attachY);
          // Build smooth wave with bezier segments instead of lineTo
          const steps = 6;
          for (let i = 0; i < steps; i++) {
            const y0 = attachY + 2 + (i / steps) * waveLen;
            const y1 = attachY + 2 + ((i + 1) / steps) * waveLen;
            const wave0 = Math.sin((i / steps) * Math.PI * 2.5) * 3;
            const wave1 = Math.sin(((i + 1) / steps) * Math.PI * 2.5) * 3;
            ctx.bezierCurveTo(
              ox + side * (2 + wave0), y0 + (y1 - y0) * 0.3,
              ox + side * (2 + wave1), y1 - (y1 - y0) * 0.3,
              ox + side * (2 + wave1), y1
            );
          }
          // Return along inner edge
          const taper = 0.6;
          for (let i = steps - 1; i >= 0; i--) {
            const y0 = attachY + 2 + ((i + 1) / steps) * waveLen;
            const y1 = attachY + 2 + (i / steps) * waveLen;
            ctx.bezierCurveTo(
              ox - side * taper, y0 - (y0 - y1) * 0.3,
              ox - side * taper, y1 + (y0 - y1) * 0.3,
              ox - side * taper, y1
            );
          }
          ctx.closePath();
          ctx.fill();
          // Shine
          drawShine(ox, attachY + 6, attachY + waveLen - 4);
        }
        break;
      }
      case 6: { // Undercut — dramatic top volume, shaved sides
        if (hatCovers || isBack) break;
        drawCrown(1, 2, -1);
        // Swept top volume detail — show direction of swept hair
        ctx.fillStyle = color;
        ctx.globalAlpha = 0.4;
        ctx.beginPath();
        ctx.moveTo(a.cx - rx * 0.5, topY - 1);
        ctx.quadraticCurveTo(a.cx, topY - 3, a.cx + rx * 0.8, topY);
        ctx.lineTo(a.cx + rx * 0.7, topY + 3);
        ctx.quadraticCurveTo(a.cx, topY, a.cx - rx * 0.4, topY + 2);
        ctx.closePath();
        ctx.fill();
        ctx.globalAlpha = 1.0;
        // Shaved sides — gradient stipple
        ctx.fillStyle = dk;
        ctx.globalAlpha = 0.2;
        if (!isSide) {
          for (let side = -1; side <= 1; side += 2) {
            for (let y = 0; y < ry - 4; y += 2) {
              for (let xi = 0; xi < 3; xi += 2) {
                ctx.beginPath();
                ctx.arc(a.cx + side * (rx - 1 + xi), headY - 2 + y, 0.5, 0, Math.PI * 2);
                ctx.fill();
              }
            }
          }
        } else {
          // Side view: shaved side visible below the volume
          for (let y = 0; y < ry - 3; y += 2) {
            for (let xi = 0; xi < 4; xi += 2) {
              ctx.beginPath();
              ctx.arc(a.cx - rx + 2 + xi, headY - 1 + y, 0.5, 0, Math.PI * 2);
              ctx.fill();
            }
          }
        }
        ctx.globalAlpha = 1.0;
        break;
      }
      case 7: { // Ponytail — smooth pulled-back crown + tail
        drawCrown(0, -1);
        // Side/qfront: tail curves behind the head (toward far side)
        if (isSide || cat === "qfront") {
          ctx.fillStyle = dk;
          ctx.beginPath();
          // Tail goes toward the far/back side (+rx in unflipped space = behind head for side view)
          ctx.moveTo(a.cx + rx - 1, headY - 4);
          ctx.bezierCurveTo(
            a.cx + rx + 5, headY - 2,
            a.cx + rx + 5, headY + 8,
            a.cx + rx + 2, headY + 18
          );
          ctx.bezierCurveTo(
            a.cx + rx + 1, headY + 8,
            a.cx + rx - 1, headY + 2,
            a.cx + rx - 2, headY - 2
          );
          ctx.closePath();
          ctx.fill();
          // Tail tip strand detail
          ctx.strokeStyle = color;
          ctx.lineWidth = 0.6;
          ctx.globalAlpha = 0.3;
          ctx.beginPath();
          ctx.moveTo(a.cx + rx + 1, headY + 12);
          ctx.quadraticCurveTo(a.cx + rx + 3, headY + 15, a.cx + rx + 2, headY + 18);
          ctx.stroke();
          ctx.globalAlpha = 1.0;
          // Hair tie
          ctx.fillStyle = shade(color, 0.45);
          ctx.beginPath();
          ctx.ellipse(a.cx + rx - 1, headY - 2, 2, 3, 0.3, 0, Math.PI * 2);
          ctx.fill();
        }
        // Back/qback: centered ponytail
        if (isBack) {
          ctx.fillStyle = dk;
          ctx.beginPath();
          ctx.moveTo(a.cx - 3, jawY - 6);
          ctx.bezierCurveTo(a.cx - 5, jawY + 4, a.cx - 3, jawY + 14, a.cx, jawY + 18);
          ctx.bezierCurveTo(a.cx + 3, jawY + 14, a.cx + 5, jawY + 4, a.cx + 3, jawY - 6);
          ctx.closePath();
          ctx.fill();
          // Shine stripe on tail
          drawShine(a.cx, jawY - 4, jawY + 14, 1.5);
          // Hair tie
          ctx.fillStyle = shade(color, 0.45);
          ctx.beginPath();
          ctx.ellipse(a.cx, jawY - 4, 3, 2, 0, 0, Math.PI * 2);
          ctx.fill();
        }
        // Forehead: baby hairs / edge wisps
        if (!isBack && !isSide) {
          ctx.strokeStyle = dk;
          ctx.lineWidth = 0.6;
          ctx.globalAlpha = 0.4;
          ctx.beginPath();
          ctx.moveTo(a.cx - rx * 0.6, topY + 3);
          ctx.quadraticCurveTo(a.cx - rx * 0.3, topY + 1, a.cx, topY + 3);
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(a.cx, topY + 3);
          ctx.quadraticCurveTo(a.cx + rx * 0.3, topY + 1, a.cx + rx * 0.6, topY + 3);
          ctx.stroke();
          ctx.globalAlpha = 1.0;
        }
        break;
      }
      case 8: { // Bob — crown flows into jaw-length panels
        drawCrown(1, 0);
        drawFringe("side");
        // Bob panels — smooth inward curve at jaw tips
        for (let side = -1; side <= 1; side += 2) {
          if (isSide && side === -1) continue;
          ctx.fillStyle = dk;
          ctx.beginPath();
          ctx.moveTo(a.cx + side * rx, attachY);
          ctx.bezierCurveTo(
            a.cx + side * (rx + 3), earY - 2,
            a.cx + side * (rx + 3), jawY - 2,
            a.cx + side * (rx + 1), jawY + 3
          );
          // Inward curl at tips — signature bob shape
          ctx.quadraticCurveTo(
            a.cx + side * (rx - 1), jawY + 5,
            a.cx + side * (rx - 2), jawY + 2
          );
          ctx.lineTo(a.cx + side * (rx - 1), attachY + 2);
          ctx.closePath();
          ctx.fill();
          // Shine
          drawShine(a.cx + side * (rx + 0.5), earY, jawY);
        }
        break;
      }
      case 9: { // Long curly — extra-wide crown into ringlet columns
        drawCrown(2, 1);
        drawFringe("wispy");
        // Volumetric ringlet curtains
        for (let side = -1; side <= 1; side += 2) {
          if (isSide && side === -1) continue;
          // Background mass — wider bezier for volume
          ctx.fillStyle = dk;
          ctx.beginPath();
          ctx.moveTo(a.cx + side * rx, attachY);
          ctx.bezierCurveTo(
            a.cx + side * (rx + 3), earY,
            a.cx + side * (rx + 4), jawY,
            a.cx + side * (rx + 2), curtainEnd
          );
          ctx.lineTo(a.cx + side * (rx - 1), curtainEnd - 2);
          ctx.lineTo(a.cx + side * (rx - 1), attachY + 2);
          ctx.closePath();
          ctx.fill();
          // Ringlet curls — alternating sizes for natural look
          for (let y = 0; y < ry * 2 + 8; y += 3.5) {
            const curl = Math.sin(y * 0.55) * 3;
            const sz = 2 + Math.sin(y * 0.3) * 0.8;
            ctx.fillStyle = y % 7 < 3.5 ? color : dk;
            ctx.beginPath();
            ctx.arc(a.cx + side * (rx + 1 + curl), attachY + 4 + y, sz, 0, Math.PI * 2);
            ctx.fill();
          }
          // Inner highlight on curl column
          drawShine(a.cx + side * rx, attachY + 6, curtainEnd - 6);
        }
        break;
      }
      case 10: { // Spiky / punk — dramatic upward spikes from base
        if (hatCovers || isBack) break;
        drawCrown(0, -2);
        // Gradient spikes — varied heights for energy
        for (let i = 0; i < 7; i++) {
          const angle = Math.PI + (i / 7) * Math.PI;
          const spikeLen = 7 + (i % 2 === 0 ? 5 : 0) + (i === 3 ? 3 : 0); // center spike tallest
          const x = a.cx + Math.cos(angle) * rx;
          const y = headY + Math.sin(angle) * ry;
          const sG = ctx.createLinearGradient(x, y, x + Math.cos(angle) * spikeLen, y + Math.sin(angle) * spikeLen);
          sG.addColorStop(0, dk);
          sG.addColorStop(0.5, color);
          sG.addColorStop(1, hi);
          ctx.fillStyle = sG;
          ctx.beginPath();
          ctx.moveTo(x - 2.5, y);
          ctx.lineTo(x + Math.cos(angle) * spikeLen, y + Math.sin(angle) * spikeLen);
          ctx.lineTo(x + 2.5, y);
          ctx.closePath();
          ctx.fill();
        }
        break;
      }
      case 11: { // Side sweep — asymmetric volume, heavier on one side
        if (hatCovers || isBack) break;
        // Off-center crown — shifted for dramatic asymmetry
        const cy11 = topY + 1;
        const g11 = ctx.createRadialGradient(a.cx + 2, cy11 - 2, 1, a.cx + 2, cy11, rx + 4);
        g11.addColorStop(0, hi);
        g11.addColorStop(0.35, color);
        g11.addColorStop(1, dk);
        ctx.fillStyle = g11;
        ctx.beginPath();
        ctx.ellipse(a.cx + 2, cy11, rx + 4, ry * 0.55, 0.12, 0, Math.PI * 2);
        ctx.fill();
        // Heavy sweep fringe — thicker, overlapping the eye on one side
        ctx.fillStyle = dk;
        ctx.beginPath();
        ctx.moveTo(a.cx - rx, topY + 4);
        ctx.bezierCurveTo(
          a.cx - rx * 0.3, topY - 1,
          a.cx + rx * 0.5, topY - 1,
          a.cx + rx + 4, topY + 5
        );
        ctx.lineTo(a.cx + rx + 4, topY + 10);
        ctx.bezierCurveTo(
          a.cx + rx * 0.4, topY + 3,
          a.cx - rx * 0.2, topY + 4,
          a.cx - rx, topY + 7
        );
        ctx.closePath();
        ctx.fill();
        // Shine on the heavy side
        drawShine(a.cx + rx * 0.5, topY + 2, topY + 9, 3);
        break;
      }
      case 12: { // Mohawk — bold central crest
        if (hatCovers || isBack) break;
        // Shaved base
        ctx.fillStyle = dk;
        ctx.globalAlpha = 0.2;
        ctx.beginPath();
        ctx.ellipse(a.cx, headY - 2, rx + 1, ry * 0.5, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1.0;
        // Central crest — gradient-filled with rounded profile
        const mohG = ctx.createLinearGradient(a.cx, topY - 12, a.cx, topY + 5);
        mohG.addColorStop(0, hi);
        mohG.addColorStop(0.5, color);
        mohG.addColorStop(1, dk);
        ctx.fillStyle = mohG;
        ctx.beginPath();
        ctx.moveTo(a.cx - 4, topY + 5);
        ctx.quadraticCurveTo(a.cx - 3, topY - 6, a.cx, topY - 12);
        ctx.quadraticCurveTo(a.cx + 3, topY - 6, a.cx + 4, topY + 5);
        ctx.closePath();
        ctx.fill();
        // Crest base blending into scalp
        ctx.fillStyle = dk;
        ctx.beginPath();
        ctx.ellipse(a.cx, topY + 5, 5, 3, 0, 0, Math.PI * 2);
        ctx.fill();
        // Edge highlight
        ctx.strokeStyle = hi;
        ctx.lineWidth = 0.7;
        ctx.globalAlpha = 0.35;
        ctx.beginPath();
        ctx.moveTo(a.cx - 1.5, topY - 10);
        ctx.quadraticCurveTo(a.cx - 2.5, topY - 4, a.cx - 3.5, topY + 4);
        ctx.stroke();
        ctx.globalAlpha = 1.0;
        break;
      }
      case 13: { // Braids — crown with center part, braids from temples
        drawCrown(0, 0);
        drawFringe("center");
        // Center part line
        if (!isBack && !hatCovers) {
          ctx.strokeStyle = dk;
          ctx.lineWidth = 0.8;
          ctx.beginPath();
          ctx.moveTo(a.cx, topY - 1);
          ctx.lineTo(a.cx, topY + ry * 0.35);
          ctx.stroke();
        }
        // Braid columns — smoother connector, rope pattern, tapered end
        for (let side = -1; side <= 1; side += 2) {
          if (isSide && side === -1) continue;
          const bx = a.cx + side * (rx - 1);
          // Smooth connector from crown into braid start
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.moveTo(bx - side * 2, attachY - 2);
          ctx.bezierCurveTo(
            bx - side, attachY + 1,
            bx, attachY + 3,
            bx, attachY + 6
          );
          ctx.lineTo(bx + side * 2, attachY + 6);
          ctx.lineTo(bx + side * 2, attachY - 2);
          ctx.closePath();
          ctx.fill();
          // Alternating braid segments — ellipses with slight taper
          const braidLen = ry * 2 + 10;
          for (let y = 0; y < braidLen; y += 4) {
            const taper = 1 - (y / braidLen) * 0.3;
            ctx.fillStyle = y % 8 < 4 ? color : dk;
            ctx.beginPath();
            ctx.ellipse(bx + (y % 8 < 4 ? -1 : 1), attachY + 4 + y, 2 * taper, 2.5, 0, 0, Math.PI * 2);
            ctx.fill();
          }
          // Braid tie + tassel
          ctx.fillStyle = shade(color, 0.4);
          ctx.beginPath();
          ctx.arc(bx, attachY + braidLen + 4, 1.8, 0, Math.PI * 2);
          ctx.fill();
        }
        break;
      }
      case 14: { // Top knot / bun — sleek pulled-back + bun
        if (hatCovers) break;
        drawCrown(-1, -2);
        // Bun — gradient sphere with wrap texture
        const bunG = ctx.createRadialGradient(a.cx - 2, topY - 4, 1, a.cx, topY - 2, 6);
        bunG.addColorStop(0, hi);
        bunG.addColorStop(0.4, color);
        bunG.addColorStop(1, dk);
        ctx.fillStyle = bunG;
        ctx.beginPath();
        ctx.arc(a.cx, topY - 2, 6, 0, Math.PI * 2);
        ctx.fill();
        // Wrap texture — spiral lines
        ctx.strokeStyle = shade(dk, 0.85);
        ctx.lineWidth = 0.5;
        ctx.globalAlpha = 0.5;
        ctx.beginPath();
        ctx.ellipse(a.cx, topY - 2, 5, 4, 0.3, 0, Math.PI * 2);
        ctx.stroke();
        ctx.beginPath();
        ctx.ellipse(a.cx, topY - 2, 3, 2.5, -0.4, 0, Math.PI * 2);
        ctx.stroke();
        ctx.globalAlpha = 1.0;
        // Forehead wisps for softness
        if (!isBack) {
          ctx.strokeStyle = dk;
          ctx.lineWidth = 0.5;
          ctx.globalAlpha = 0.35;
          ctx.beginPath();
          ctx.moveTo(a.cx - rx * 0.5, topY + 3);
          ctx.quadraticCurveTo(a.cx - rx * 0.2, topY + 1, a.cx + rx * 0.1, topY + 4);
          ctx.stroke();
          ctx.globalAlpha = 1.0;
        }
        break;
      }
      case 15: { // Long layered — crown into tiered drapes
        drawCrown(1, 0);
        drawFringe("side");
        // Two-tier layered panels with depth
        for (let side = -1; side <= 1; side += 2) {
          if (isSide && side === -1) continue;
          // Inner layer (shorter, lighter) — face-framing
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.moveTo(a.cx + side * (rx - 1), attachY + 2);
          ctx.bezierCurveTo(
            a.cx + side * rx, earY,
            a.cx + side * rx, jawY - 4,
            a.cx + side * (rx - 1), jawY + 2
          );
          ctx.lineTo(a.cx + side * (rx - 3), jawY);
          ctx.lineTo(a.cx + side * (rx - 3), attachY + 4);
          ctx.closePath();
          ctx.fill();
          // Outer layer (longer, darker) — main body
          ctx.fillStyle = dk;
          ctx.beginPath();
          ctx.moveTo(a.cx + side * rx, attachY);
          ctx.bezierCurveTo(
            a.cx + side * (rx + 3), earY,
            a.cx + side * (rx + 2), jawY,
            a.cx + side * (rx + 1), curtainEnd - 2
          );
          ctx.lineTo(a.cx + side * (rx - 1), curtainEnd - 4);
          ctx.bezierCurveTo(
            a.cx + side * (rx - 1), jawY - 2,
            a.cx + side * rx, earY,
            a.cx + side * rx, attachY
          );
          ctx.closePath();
          ctx.fill();
          // Shimmer highlight
          drawShine(a.cx + side * (rx + 0.5), attachY + 4, curtainEnd - 8);
        }
        break;
      }
      case 16: { // Pixie cut — short, choppy, asymmetric with side-swept fringe
        if (hatCovers || isBack) break;
        drawCrown(0, 0);
        // Heavy side-swept fringe covering one eye
        ctx.fillStyle = dk;
        ctx.beginPath();
        ctx.moveTo(a.cx - rx * 0.6, topY + 1);
        ctx.bezierCurveTo(
          a.cx - rx * 0.2, topY - 2,
          a.cx + rx * 0.5, topY - 1,
          a.cx + rx + 3, topY + 7
        );
        ctx.lineTo(a.cx + rx + 2, topY + 11);
        ctx.bezierCurveTo(
          a.cx + rx * 0.3, topY + 5,
          a.cx - rx * 0.1, topY + 3,
          a.cx - rx * 0.5, topY + 4
        );
        ctx.closePath();
        ctx.fill();
        // Choppy texture wisps at ear level
        ctx.fillStyle = color;
        ctx.globalAlpha = 0.5;
        for (let i = 0; i < 4; i++) {
          const xi = a.cx + rx * 0.3 + i * 3;
          ctx.beginPath();
          ctx.moveTo(xi, topY + 8);
          ctx.quadraticCurveTo(xi + 2, topY + 11, xi + 1, topY + 14);
          ctx.lineTo(xi - 1, topY + 12);
          ctx.closePath();
          ctx.fill();
        }
        ctx.globalAlpha = 1.0;
        // Shine
        drawShine(a.cx + rx * 0.3, topY + 2, topY + 10, 3);
        break;
      }
      case 17: { // Curtain bangs — center-parted bangs framing face, long body
        drawCrown(1, 0);
        // Signature curtain bangs — two symmetric swept wings
        if (!isBack && !hatCovers) {
          for (let side = -1; side <= 1; side += 2) {
            ctx.fillStyle = color;
            ctx.beginPath();
            ctx.moveTo(a.cx + side * 1, topY + 1);
            ctx.bezierCurveTo(
              a.cx + side * (rx * 0.4), topY - 1,
              a.cx + side * (rx * 0.7), topY + 2,
              a.cx + side * (rx + 1), topY + 10
            );
            ctx.lineTo(a.cx + side * rx, topY + 12);
            ctx.bezierCurveTo(
              a.cx + side * (rx * 0.6), topY + 5,
              a.cx + side * (rx * 0.3), topY + 2,
              a.cx + side * 1, topY + 3
            );
            ctx.closePath();
            ctx.fill();
            // Dark edge
            ctx.strokeStyle = dk;
            ctx.lineWidth = 0.5;
            ctx.globalAlpha = 0.4;
            ctx.beginPath();
            ctx.moveTo(a.cx + side * 2, topY + 2);
            ctx.quadraticCurveTo(a.cx + side * (rx * 0.5), topY + 1, a.cx + side * (rx + 1), topY + 10);
            ctx.stroke();
            ctx.globalAlpha = 1.0;
          }
        }
        // Long body curtains below
        for (let side = -1; side <= 1; side += 2) {
          if (isSide && side === -1) continue;
          ctx.fillStyle = dk;
          ctx.beginPath();
          ctx.moveTo(a.cx + side * rx, attachY);
          ctx.bezierCurveTo(
            a.cx + side * (rx + 2), earY,
            a.cx + side * (rx + 2), jawY,
            a.cx + side * (rx + 1), curtainEnd
          );
          ctx.lineTo(a.cx + side * (rx - 1), curtainEnd - 2);
          ctx.lineTo(a.cx + side * (rx - 1), attachY + 2);
          ctx.closePath();
          ctx.fill();
          drawShine(a.cx + side * rx, attachY + 4, curtainEnd - 6);
        }
        break;
      }
      case 18: { // Wolf cut — shaggy mullet: short choppy top, long back
        drawCrown(1, 1);
        // Choppy top layers — short feathered pieces
        if (!isBack && !hatCovers) {
          for (let i = 0; i < 6; i++) {
            const angle = Math.PI + (i / 6) * Math.PI;
            const len = 4 + (i % 2) * 2;
            const x = a.cx + Math.cos(angle) * (rx + 1);
            const y = topY + 2 + Math.abs(Math.sin(angle)) * 4;
            ctx.fillStyle = i % 2 === 0 ? color : dk;
            ctx.beginPath();
            ctx.moveTo(x, y);
            ctx.quadraticCurveTo(
              x + Math.cos(angle) * len * 0.7, y + Math.sin(angle) * len,
              x + Math.cos(angle + 0.15) * len, y + Math.sin(angle + 0.15) * len
            );
            ctx.lineTo(x + Math.cos(angle + 0.1) * 1, y + Math.sin(angle + 0.1) * 1);
            ctx.closePath();
            ctx.fill();
          }
        }
        // Longer back/side curtains — "mullet" drape
        for (let side = -1; side <= 1; side += 2) {
          if (isSide && side === -1) continue;
          ctx.fillStyle = dk;
          ctx.beginPath();
          ctx.moveTo(a.cx + side * rx, attachY + 2);
          ctx.bezierCurveTo(
            a.cx + side * (rx + 2), earY + 2,
            a.cx + side * (rx + 2), jawY,
            a.cx + side * (rx + 1), curtainEnd + 2
          );
          ctx.lineTo(a.cx + side * (rx - 1), curtainEnd);
          ctx.lineTo(a.cx + side * (rx - 1), attachY + 4);
          ctx.closePath();
          ctx.fill();
        }
        break;
      }
      case 19: { // Space buns — two buns on top with optional pigtails
        if (hatCovers || isBack) break;
        drawCrown(0, -1);
        // Two buns
        for (let side = -1; side <= 1; side += 2) {
          if (isSide && side === -1) continue;
          // Bun
          const bunG19 = ctx.createRadialGradient(
            a.cx + side * (rx - 2) - 1, topY - 1, 1,
            a.cx + side * (rx - 2), topY + 1, 5.5
          );
          bunG19.addColorStop(0, hi);
          bunG19.addColorStop(0.4, color);
          bunG19.addColorStop(1, dk);
          ctx.fillStyle = bunG19;
          ctx.beginPath();
          ctx.arc(a.cx + side * (rx - 2), topY + 1, 5.5, 0, Math.PI * 2);
          ctx.fill();
          // Wrap texture
          ctx.strokeStyle = dk;
          ctx.lineWidth = 0.5;
          ctx.globalAlpha = 0.4;
          ctx.beginPath();
          ctx.ellipse(a.cx + side * (rx - 2), topY + 1, 4.5, 3.5, side * 0.3, 0, Math.PI * 2);
          ctx.stroke();
          ctx.globalAlpha = 1.0;
          // Short drape beneath each bun
          ctx.fillStyle = dk;
          ctx.beginPath();
          ctx.moveTo(a.cx + side * (rx - 3), topY + 5);
          ctx.quadraticCurveTo(
            a.cx + side * (rx - 1), earY + 2,
            a.cx + side * (rx - 2), earY + 6
          );
          ctx.lineTo(a.cx + side * (rx - 4), earY + 4);
          ctx.closePath();
          ctx.fill();
        }
        break;
      }
      case 20: { // Half-up half-down — top pulled back, bottom flowing
        drawCrown(0, -1);
        // Pulled-back top section — smooth gathered look
        if (!isBack && !hatCovers) {
          ctx.strokeStyle = dk;
          ctx.lineWidth = 0.6;
          ctx.globalAlpha = 0.3;
          for (let i = -1; i <= 1; i++) {
            ctx.beginPath();
            ctx.moveTo(a.cx + i * rx * 0.3, topY + 1);
            ctx.quadraticCurveTo(a.cx + i * rx * 0.4, topY + 3, a.cx + i * rx * 0.2, topY + 6);
            ctx.stroke();
          }
          ctx.globalAlpha = 1.0;
        }
        // Flowing bottom curtains
        for (let side = -1; side <= 1; side += 2) {
          if (isSide && side === -1) continue;
          ctx.fillStyle = dk;
          ctx.beginPath();
          ctx.moveTo(a.cx + side * rx, attachY + 2);
          ctx.bezierCurveTo(
            a.cx + side * (rx + 2), earY,
            a.cx + side * (rx + 2), jawY,
            a.cx + side * (rx + 1), curtainEnd
          );
          ctx.lineTo(a.cx + side * (rx - 1), curtainEnd - 2);
          ctx.lineTo(a.cx + side * (rx - 1), attachY + 4);
          ctx.closePath();
          ctx.fill();
          // Upper panel (shorter, lighter — pulled-up portion)
          ctx.fillStyle = color;
          ctx.globalAlpha = 0.5;
          ctx.beginPath();
          ctx.moveTo(a.cx + side * (rx - 1), attachY);
          ctx.quadraticCurveTo(a.cx + side * rx, earY - 2, a.cx + side * (rx - 1), earY + 2);
          ctx.lineTo(a.cx + side * (rx - 3), earY);
          ctx.lineTo(a.cx + side * (rx - 3), attachY + 2);
          ctx.closePath();
          ctx.fill();
          ctx.globalAlpha = 1.0;
          drawShine(a.cx + side * rx, attachY + 6, curtainEnd - 6);
        }
        // Back clip/tie visible from front
        if (!isBack && !isSide) {
          ctx.fillStyle = shade(color, 0.45);
          ctx.beginPath();
          ctx.ellipse(a.cx, topY + 6, 2.5, 1.5, 0, 0, Math.PI * 2);
          ctx.fill();
        }
        break;
      }
      case 21: { // Cornrows — tight braided rows across scalp
        if (hatCovers || isBack) break;
        // Base scalp — slightly darker
        ctx.fillStyle = dk;
        ctx.globalAlpha = 0.15;
        ctx.beginPath();
        ctx.ellipse(a.cx, headY - 2, rx + 1, ry * 0.65, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1.0;
        // Braid rows — raised ridges with alternating pattern
        for (let i = -2; i <= 2; i++) {
          const rowX = a.cx + i * 3.5;
          // Ridge
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.moveTo(rowX - 1, topY + 1);
          ctx.quadraticCurveTo(rowX - 1, headY - 2, rowX - 0.5, headY + ry * 0.3);
          ctx.lineTo(rowX + 1.5, headY + ry * 0.3);
          ctx.quadraticCurveTo(rowX + 1, headY - 2, rowX + 1, topY + 1);
          ctx.closePath();
          ctx.fill();
          // Braid texture — tiny alternating bumps
          ctx.fillStyle = dk;
          for (let y = topY + 3; y < headY + ry * 0.25; y += 3) {
            ctx.beginPath();
            ctx.arc(rowX + (y % 6 < 3 ? -0.5 : 0.5), y, 0.8, 0, Math.PI * 2);
            ctx.fill();
          }
        }
        break;
      }
      case 22: { // Shag — all-over choppy medium volume
        if (hatCovers || isBack) break;
        drawCrown(1, 1);
        // Choppy layered fringe covering forehead
        ctx.fillStyle = dk;
        ctx.beginPath();
        ctx.moveTo(a.cx - rx, topY + 3);
        for (let x = -rx; x <= rx; x += 3) {
          ctx.lineTo(a.cx + x, topY + 5 + Math.sin(x * 0.8) * 2 + (Math.abs(x) < rx * 0.3 ? 2 : 0));
        }
        ctx.lineTo(a.cx + rx, topY + 3);
        ctx.closePath();
        ctx.fill();
        // Side volume — feathered out at cheeks
        for (let side = -1; side <= 1; side += 2) {
          if (isSide && side === -1) continue;
          ctx.fillStyle = dk;
          for (let y = 0; y < ry; y += 4) {
            const feather = 2 + Math.sin(y * 0.5) * 1.5;
            ctx.beginPath();
            ctx.moveTo(a.cx + side * rx, attachY + y);
            ctx.lineTo(a.cx + side * (rx + feather), attachY + y + 3);
            ctx.lineTo(a.cx + side * (rx - 1), attachY + y + 5);
            ctx.closePath();
            ctx.fill();
          }
        }
        break;
      }
      case 23: { // High ponytail — crown gathered up high + long tail
        drawCrown(0, -1);
        // High gathering point with puff — skip when hat covers crown
        const puffY = topY - 3;
        if (!hatCovers && !isBack) {
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.ellipse(a.cx, puffY, 5, 4, 0, 0, Math.PI * 2);
          ctx.fill();
          // Gradient for depth
          const puffG = ctx.createRadialGradient(a.cx - 1, puffY - 2, 1, a.cx, puffY, 5);
          puffG.addColorStop(0, hi);
          puffG.addColorStop(0.5, color);
          puffG.addColorStop(1, dk);
          ctx.fillStyle = puffG;
          ctx.beginPath();
          ctx.ellipse(a.cx, puffY, 5, 4, 0, 0, Math.PI * 2);
          ctx.fill();
          // Hair tie
          ctx.fillStyle = shade(color, 0.4);
          ctx.beginPath();
          ctx.ellipse(a.cx, puffY + 3, 3, 1.5, 0, 0, Math.PI * 2);
          ctx.fill();
        }
        // Tail flowing down — behind head for side/qfront
        if (isSide || cat === "qfront") {
          ctx.fillStyle = dk;
          ctx.beginPath();
          ctx.moveTo(a.cx + rx * 0.5, puffY + 3);
          ctx.bezierCurveTo(
            a.cx + rx + 3, puffY + 6,
            a.cx + rx + 3, headY + 8,
            a.cx + rx, headY + 22
          );
          ctx.bezierCurveTo(
            a.cx + rx - 1, headY + 8,
            a.cx + rx - 2, puffY + 6,
            a.cx + rx * 0.3, puffY + 3
          );
          ctx.closePath();
          ctx.fill();
        }
        // Tail visible from back
        if (isBack) {
          ctx.fillStyle = dk;
          ctx.beginPath();
          ctx.moveTo(a.cx - 3, puffY + 4);
          ctx.bezierCurveTo(a.cx - 5, headY + 6, a.cx - 3, jawY + 10, a.cx, jawY + 20);
          ctx.bezierCurveTo(a.cx + 3, jawY + 10, a.cx + 5, headY + 6, a.cx + 3, puffY + 4);
          ctx.closePath();
          ctx.fill();
          drawShine(a.cx, puffY + 6, jawY + 16, 1.5);
        }
        // Front: baby hairs
        if (!isBack && !isSide) {
          ctx.strokeStyle = dk;
          ctx.lineWidth = 0.5;
          ctx.globalAlpha = 0.35;
          ctx.beginPath();
          ctx.moveTo(a.cx - rx * 0.5, topY + 2);
          ctx.quadraticCurveTo(a.cx, topY, a.cx + rx * 0.5, topY + 2);
          ctx.stroke();
          ctx.globalAlpha = 1.0;
        }
        break;
      }
    }
  });
}

// ─── Hat V2 ──────────────────────────────────────────────────────────────────

function drawHatV2(
  ctx: DrawCtx, dir: Direction, _frame: number,
  color: string, style: number, bodyType: 0 | 1 | 2 | 3
): void {
  if (style < 0) return;
  const a = BA[bodyType];
  const bY = -_animPose.breatheY;
  const dk = shade(color, 0.75);
  const hi = shade(color, 1.15);

  withFlip(ctx, dir, (cat) => {
    const isQtr = cat === "qfront" || cat === "qback";
    const rx = a.headRx * (cat === "side" ? 0.85 : isQtr ? 0.90 : 0.95);
    const ry = a.headRy;
    const top = (a.headY + bY) - ry;

    switch (style % 8) {
      case 0: { // Beanie
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.ellipse(a.cx, top + 4, rx + 2, 8, 0, Math.PI, 0);
        ctx.fill();
        ctx.strokeStyle = dk;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.ellipse(a.cx, top + 8, rx + 2, 3, 0, 0, Math.PI);
        ctx.stroke();
        break;
      }
      case 1: { // Baseball cap
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.ellipse(a.cx, top + 4, rx + 2, 7, 0, Math.PI, 0);
        ctx.fill();
        ctx.fillStyle = dk;
        if (cat === "side") {
          ctx.beginPath();
          ctx.ellipse(a.cx + rx + 4, top + 6, 8, 3, 0.2, 0, Math.PI * 2);
          ctx.fill();
        } else {
          ctx.beginPath();
          ctx.ellipse(a.cx, top + 8, rx + 6, 4, 0, 0, Math.PI);
          ctx.fill();
        }
        break;
      }
      case 2: { // Top hat
        ctx.fillStyle = color;
        ctxRoundRect(ctx, a.cx - rx * 0.7, top - 14, rx * 1.4, 14, 2);
        ctx.fillStyle = dk;
        ctx.beginPath();
        ctx.ellipse(a.cx, top + 1, rx + 4, 3, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = shade(color, 0.6);
        ctx.fillRect(a.cx - rx * 0.7, top - 3, rx * 1.4, 3);
        break;
      }
      case 3: { // Crown
        ctx.fillStyle = "#FFD700";
        const cw = rx + 2;
        ctx.beginPath();
        ctx.moveTo(a.cx - cw, top + 2);
        ctx.lineTo(a.cx - cw, top - 6);
        ctx.lineTo(a.cx - cw * 0.5, top - 2);
        ctx.lineTo(a.cx, top - 8);
        ctx.lineTo(a.cx + cw * 0.5, top - 2);
        ctx.lineTo(a.cx + cw, top - 6);
        ctx.lineTo(a.cx + cw, top + 2);
        ctx.closePath();
        ctx.fill();
        ctx.fillStyle = "#FF4444";
        ctx.beginPath(); ctx.arc(a.cx - cw * 0.5, top - 1, 1.5, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "#44FFFF";
        ctx.beginPath(); ctx.arc(a.cx, top - 4, 1.5, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "#44FF44";
        ctx.beginPath(); ctx.arc(a.cx + cw * 0.5, top - 1, 1.5, 0, Math.PI * 2); ctx.fill();
        break;
      }
      case 4: { // Witch hat
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.moveTo(a.cx, top - 20);
        ctx.quadraticCurveTo(a.cx + 4, top - 8, a.cx + rx + 2, top + 2);
        ctx.lineTo(a.cx - rx - 2, top + 2);
        ctx.quadraticCurveTo(a.cx - 4, top - 8, a.cx, top - 20);
        ctx.fill();
        ctx.fillStyle = dk;
        ctx.beginPath();
        ctx.ellipse(a.cx, top + 3, rx + 8, 4, 0, 0, Math.PI * 2);
        ctx.fill();
        break;
      }
      case 5: { // Beret
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.ellipse(a.cx + 2, top + 2, rx + 4, 7, 0.1, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = hi;
        ctx.beginPath(); ctx.arc(a.cx - 2, top - 1, 2, 0, Math.PI * 2); ctx.fill();
        break;
      }
      case 6: { // Halo
        ctx.save();
        ctx.globalAlpha = 0.7;
        ctx.strokeStyle = "#FFE070";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.ellipse(a.cx, top - 6, rx + 3, 3, 0, 0, Math.PI * 2);
        ctx.stroke();
        ctx.strokeStyle = "#FFD040";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.ellipse(a.cx, top - 6, rx + 3, 3, 0, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
        break;
      }
      case 7: { // Bucket hat
        ctx.fillStyle = color;
        ctx.beginPath();
        ctx.ellipse(a.cx, top + 3, rx + 1, 8, 0, Math.PI, 0);
        ctx.fill();
        ctx.fillStyle = dk;
        ctx.beginPath();
        ctx.ellipse(a.cx, top + 5, rx + 5, 4, 0, 0, Math.PI);
        ctx.fill();
        break;
      }
    }
  });
}

// ─── Device Case Pattern Overlay ─────────────────────────────────────────────

/**
 * Draw the cosmetic case pattern over the device shell area.
 * Called after the shell fill + stroke, before the screen is drawn on top.
 * Clips to the shell rectangle so nothing bleeds outside the device edge.
 *
 * @param ctx  Drawing context
 * @param x    Shell rect left edge
 * @param y    Shell rect top edge
 * @param w    Shell rect width
 * @param h    Shell rect height
 * @param r    Corner radius (used to re-clip)
 */
function drawDeviceCasePattern(
  ctx: DrawCtx,
  x: number, y: number, w: number, h: number, r: number
): void {
  const cs = _deviceCase;
  if (!cs || cs.pattern === "none" || cs.patternAlpha <= 0) return;

  ctx.save();

  // Clip to shell rounded-rect so pattern never bleeds outside
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, r);
  ctx.clip();

  ctx.globalAlpha = cs.patternAlpha;

  const pc = cs.patternColor;
  const cx = x + w / 2;
  const cy = y + h / 2;

  switch (cs.pattern) {
    case "stripe": {
      // 3 diagonal lines at ~45°, evenly spaced across the shell
      ctx.strokeStyle = pc;
      ctx.lineWidth = 0.8;
      const spacing = Math.max(w, h) * 0.38;
      for (let i = -1; i <= 1; i++) {
        const ox = cx + i * spacing;
        ctx.beginPath();
        ctx.moveTo(ox - h, y);
        ctx.lineTo(ox + h, y + h);
        ctx.stroke();
      }
      break;
    }
    case "dots": {
      // Small dot grid — 1px dots on a 2px grid, contrast with shell
      ctx.fillStyle = pc;
      const stepX = 2;
      const stepY = 2;
      for (let px = x + 1; px < x + w; px += stepX) {
        for (let py = y + 1; py < y + h; py += stepY) {
          ctx.fillRect(px, py, 0.7, 0.7);
        }
      }
      break;
    }
    case "gradient": {
      // Two-color diagonal gradient across the shell
      const grad = ctx.createLinearGradient(x, y, x + w, y + h);
      grad.addColorStop(0, _deviceColor);
      grad.addColorStop(1, pc);
      ctx.globalAlpha = cs.patternAlpha;
      ctx.fillStyle = grad;
      ctx.fillRect(x, y, w, h);
      break;
    }
    case "carbon": {
      // Fine crosshatch — 1px lines every 2px in both diagonals
      ctx.strokeStyle = pc;
      ctx.lineWidth = 0.4;
      const d = 2;
      for (let k = -Math.max(w, h); k < Math.max(w, h) * 2; k += d) {
        ctx.beginPath();
        ctx.moveTo(x + k, y);
        ctx.lineTo(x + k - h, y + h);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(x + k, y);
        ctx.lineTo(x + k + h, y + h);
        ctx.stroke();
      }
      break;
    }
    case "marble": {
      // Wavy veins — a few sinusoidal strokes across the surface
      ctx.strokeStyle = pc;
      ctx.lineWidth = 0.5;
      const veins = 3;
      for (let v = 0; v < veins; v++) {
        const t = (v + 0.5) / veins;
        const baseX = x + w * t;
        const amp = w * 0.18;
        const freq = Math.PI * 2 / h;
        ctx.beginPath();
        for (let py = y; py <= y + h; py += 0.5) {
          const vx = baseX + Math.sin((py - y) * freq + v * 1.3) * amp;
          if (py === y) ctx.moveTo(vx, py);
          else ctx.lineTo(vx, py);
        }
        ctx.stroke();
      }
      break;
    }
    case "wood": {
      // Parallel grain lines — horizontal rows slightly varying in color
      ctx.strokeStyle = pc;
      const grainStep = 1.5;
      for (let py = y; py < y + h; py += grainStep) {
        const t = (py - y) / h;
        // Alternate slightly lighter/darker grain bands
        const alpha = 0.5 + 0.5 * Math.abs(Math.sin(t * Math.PI * (h / grainStep)));
        ctx.globalAlpha = cs.patternAlpha * alpha;
        ctx.lineWidth = grainStep * 0.7;
        ctx.beginPath();
        ctx.moveTo(x, py);
        ctx.lineTo(x + w, py);
        ctx.stroke();
      }
      break;
    }
    case "custom": {
      // Solid secondary color block — placeholder for future user drawings
      ctx.fillStyle = pc;
      ctx.fillRect(x, y, w, h);
      break;
    }
  }

  ctx.restore();
}

// ─── Held Item V2 ────────────────────────────────────────────────────────────

function drawHeldItemV2(
  ctx: DrawCtx, dir: Direction, _frame: number,
  _color: string, style: number, bodyType: 0 | 1 | 2 | 3
): void {
  if (style < 0) return;
  const a = BA[bodyType];

  withFlip(ctx, dir, (cat) => {
    const isBack = cat === "back" || cat === "qback";
    // Standard held items only visible from front/qfront/side — arm hidden from back
    // Devices (6, 7) show the device back when facing away
    if (isBack && style < 6) return;
    const n = getNarrow(cat);
    // Hand position — near arm wrist anchor + walk offset
    // Uses un-projected armHang — n handles directional foreshortening.
    const bs = _bodyShape;
    const armHang = bs.shoulderW + 5 * bs.armMass;
    const armOffset = cat === "side" ? (armHang - 6) * n : (armHang - 4) * n;
    const handX = isBack ? a.cx + _animPose.nearArmDX : a.cx - armOffset + _animPose.nearArmDX;
    const handY = a.handY + _animPose.nearArmDY;

    switch (style < 6 ? style % 6 : style) {
      case 0: { // Coffee cup
        ctx.fillStyle = "#FFFFFF";
        ctxRoundRect(ctx, handX - 4, handY - 6, 8, 10, 2);
        ctx.fillStyle = "#C07840";
        ctx.fillRect(handX - 3, handY - 4, 6, 6);
        // Steam
        ctx.strokeStyle = "rgba(255,255,255,0.4)";
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.moveTo(handX - 1, handY - 7);
        ctx.quadraticCurveTo(handX - 2, handY - 11, handX, handY - 13);
        ctx.moveTo(handX + 1, handY - 7);
        ctx.quadraticCurveTo(handX + 2, handY - 11, handX, handY - 14);
        ctx.stroke();
        break;
      }
      case 1: { // Phone
        ctx.fillStyle = "#222222";
        ctxRoundRect(ctx, handX - 3, handY - 6, 6, 10, 1);
        ctx.fillStyle = "#4488FF";
        ctx.fillRect(handX - 2, handY - 4, 4, 5);
        break;
      }
      case 2: { // Book
        ctx.fillStyle = "#8B4513";
        ctxRoundRect(ctx, handX - 5, handY - 5, 10, 8, 1);
        ctx.fillStyle = "#F5DEB3";
        ctx.fillRect(handX - 3, handY - 3, 6, 4);
        ctx.fillStyle = "#5C2A0A";
        ctx.fillRect(handX - 5, handY - 5, 2, 8);
        break;
      }
      case 3: { // Flower
        ctx.strokeStyle = "#3A8C3A";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(handX, handY + 2); ctx.lineTo(handX, handY - 6);
        ctx.stroke();
        ctx.fillStyle = "#FF9999";
        for (let i = 0; i < 5; i++) {
          const angle = (i / 5) * Math.PI * 2;
          ctx.beginPath();
          ctx.ellipse(handX + Math.cos(angle) * 3, handY - 8 + Math.sin(angle) * 3, 2, 1.5, angle, 0, Math.PI * 2);
          ctx.fill();
        }
        ctx.fillStyle = "#FFE4E1";
        ctx.beginPath(); ctx.arc(handX, handY - 8, 1.5, 0, Math.PI * 2); ctx.fill();
        break;
      }
      case 4: { // Lantern
        ctx.fillStyle = "#888800";
        ctxRoundRect(ctx, handX - 4, handY - 5, 8, 8, 1);
        ctx.save();
        ctx.globalAlpha = 0.8;
        ctx.fillStyle = "#FFFF80";
        ctx.fillRect(handX - 2, handY - 3, 4, 4);
        ctx.restore();
        ctx.strokeStyle = "#888800";
        ctx.lineWidth = 1;
        ctx.beginPath(); ctx.arc(handX, handY - 7, 3, Math.PI, 0); ctx.stroke();
        // Glow
        ctx.fillStyle = "rgba(255,255,128,0.15)";
        ctx.beginPath(); ctx.arc(handX, handY - 1, 12, 0, Math.PI * 2); ctx.fill();
        break;
      }
      case 5: { // Gift box
        ctx.fillStyle = "#CC2222";
        ctxRoundRect(ctx, handX - 5, handY - 5, 10, 8, 1);
        ctx.fillStyle = "#FFD700";
        ctx.fillRect(handX - 1, handY - 5, 2, 8);
        ctx.fillRect(handX - 5, handY - 2, 10, 2);
        // Bow
        ctx.beginPath();
        ctx.ellipse(handX - 2, handY - 6, 3, 2, -0.3, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.ellipse(handX + 2, handY - 6, 3, 2, 0.3, 0, Math.PI * 2);
        ctx.fill();
        break;
      }
      case 6: { // hPhone — player's custom phone
        const dc = _deviceColor;
        const dkC = shade(dc, 0.7);
        const isSide = cat === "side";
        if (isBack) {
          // Back view — device shell only (no screen), slightly narrower (foreshortened)
          ctx.fillStyle = dc;
          ctxRoundRect(ctx, handX - 2, handY - 6, 4, 9, 1.2);
          ctx.strokeStyle = dkC;
          ctx.lineWidth = 0.6;
          ctx.stroke();
          drawDeviceCasePattern(ctx, handX - 2, handY - 6, 4, 9, 1.2);
          // Camera dot
          ctx.fillStyle = "#1a1a1a";
          ctx.beginPath(); ctx.arc(handX, handY - 4, 0.8, 0, Math.PI * 2); ctx.fill();
          // Logo mark
          ctx.fillStyle = shade(dc, 0.85);
          ctx.beginPath(); ctx.arc(handX, handY - 1, 1.2, 0, Math.PI * 2); ctx.fill();
        } else if (isSide) {
          // Side view — phone edge-on: thin sliver showing device thickness
          ctx.fillStyle = dc;
          ctxRoundRect(ctx, handX - 0.5, handY - 6, 1.5, 9, 0.5);
          ctx.strokeStyle = dkC;
          ctx.lineWidth = 0.4;
          ctx.stroke();
          // Thin screen edge glow
          ctx.fillStyle = "rgba(180,200,255,0.3)";
          ctx.fillRect(handX - 0.3, handY - 5, 0.8, 7);
        } else {
          // Front/qfront view — full screen visible (qfront slightly narrower)
          const fw = cat === "qfront" ? 5 : 6; // foreshorten width in quarter view
          const sx = handX - Math.floor(fw / 2);
          ctx.fillStyle = dc;
          ctxRoundRect(ctx, sx, handY - 7, fw, 10, 1.5);
          ctx.strokeStyle = dkC;
          ctx.lineWidth = 0.8;
          ctx.stroke();
          drawDeviceCasePattern(ctx, sx, handY - 7, fw, 10, 1.5);
          // Screen
          const scrW = fw - 2;
          const screenGrad = ctx.createLinearGradient(sx + 1, handY - 5, sx + scrW, handY + 1);
          screenGrad.addColorStop(0, "rgba(180,200,240,0.85)");
          screenGrad.addColorStop(0.5, "rgba(220,230,250,0.9)");
          screenGrad.addColorStop(1, "rgba(160,190,230,0.8)");
          ctx.fillStyle = screenGrad;
          ctx.fillRect(sx + 1, handY - 5, scrW, 6);
          ctx.strokeStyle = "rgba(255,255,255,0.4)";
          ctx.lineWidth = 0.3;
          ctx.strokeRect(sx + 1, handY - 5, scrW, 6);
          // Screen glow halo
          ctx.fillStyle = "rgba(180,200,255,0.08)";
          ctx.beginPath(); ctx.arc(handX, handY - 2, 8, 0, Math.PI * 2); ctx.fill();
        }
        break;
      }
      case 7: { // hPad — player's custom tablet (landscape)
        const dc = _deviceColor;
        const dkC = shade(dc, 0.7);
        const padX = a.cx;
        const padY = handY - 2;
        const isSide = cat === "side";
        if (isBack) {
          // Back view — tablet back, narrower for foreshortening
          ctx.fillStyle = dc;
          ctxRoundRect(ctx, padX - 4, padY - 3, 8, 6, 1.2);
          ctx.strokeStyle = dkC;
          ctx.lineWidth = 0.6;
          ctx.stroke();
          drawDeviceCasePattern(ctx, padX - 4, padY - 3, 8, 6, 1.2);
          ctx.fillStyle = "#1a1a1a";
          ctx.beginPath(); ctx.arc(padX - 2, padY - 1, 0.8, 0, Math.PI * 2); ctx.fill();
          ctx.fillStyle = shade(dc, 0.85);
          ctx.beginPath(); ctx.arc(padX, padY, 1.5, 0, Math.PI * 2); ctx.fill();
        } else if (isSide) {
          // Side view — tablet edge-on: thin horizontal sliver
          ctx.fillStyle = dc;
          ctxRoundRect(ctx, padX - 0.5, padY - 3, 1.5, 6, 0.5);
          ctx.strokeStyle = dkC;
          ctx.lineWidth = 0.4;
          ctx.stroke();
          // Thin screen edge glow
          ctx.fillStyle = "rgba(180,200,255,0.25)";
          ctx.fillRect(padX - 0.3, padY - 2, 0.8, 4);
        } else {
          // Front/qfront — full screen (qfront slightly narrower)
          const fw = cat === "qfront" ? 10 : 12;
          const fh = cat === "qfront" ? 7 : 8;
          const sx = padX - Math.floor(fw / 2);
          const sy = padY - Math.floor(fh / 2);
          ctx.fillStyle = dc;
          ctxRoundRect(ctx, sx, sy, fw, fh, 1.5);
          ctx.strokeStyle = dkC;
          ctx.lineWidth = 0.8;
          ctx.stroke();
          drawDeviceCasePattern(ctx, sx, sy, fw, fh, 1.5);
          // Screen
          const scrW = fw - 4;
          const scrH = fh - 4;
          const padGrad = ctx.createLinearGradient(sx + 2, sy + 2, sx + 2 + scrW, sy + 2 + scrH);
          padGrad.addColorStop(0, "rgba(180,200,240,0.85)");
          padGrad.addColorStop(0.5, "rgba(220,230,250,0.9)");
          padGrad.addColorStop(1, "rgba(160,190,230,0.8)");
          ctx.fillStyle = padGrad;
          ctx.fillRect(sx + 2, sy + 2, scrW, scrH);
          ctx.strokeStyle = "rgba(255,255,255,0.4)";
          ctx.lineWidth = 0.3;
          ctx.strokeRect(sx + 2, sy + 2, scrW, scrH);
          // Screen glow
          ctx.fillStyle = "rgba(180,200,255,0.06)";
          ctx.beginPath(); ctx.arc(padX, padY, 10, 0, Math.PI * 2); ctx.fill();
        }
        break;
      }
    }
  });
}

// ─── Wings V2 ──────────────────────────────────────────────────────────────

function drawWingsV2(
  ctx: DrawCtx, dir: Direction, frame: number,
  color: string, style: number, bodyType: 0 | 1 | 2 | 3
): void {
  if (style < 0) return;
  const a = BA[bodyType];
  const dk = shade(color, 0.75);
  const hi = shade(color, 1.20);
  const wp = _animPose;

  // Wing flutter — slow idle pulse, faster during walk/dance
  const flutterPhase = wp.clothPhase || (frame * 0.12) * Math.PI * 2;
  const flutterAmp = 0.03 + wp.clothAmplitude * 0.02;
  const flutter = Math.sin(flutterPhase) * flutterAmp;

  withFlip(ctx, dir, (cat) => {
    const n = getNarrow(cat);
    const isFront = cat === "front" || cat === "qfront";
    const isBack = cat === "back" || cat === "qback";
    const isSide = cat === "side";

    // Wings attach at shoulder blades
    const attachY = a.shY + 4;
    const wingSpread = isBack ? 1.0 : isSide ? 0.5 : 0.35; // visibility from front is reduced
    const wingAlpha = isFront ? 0.6 : 1.0; // partially transparent from front (behind body)

    ctx.save();
    ctx.globalAlpha = wingAlpha;

    switch (style % 4) {
      case 0: { // Angel wings — feathered, graceful arcs
        const wingW = 24 * wingSpread;
        const wingH = 32;
        const fOff = flutter * wingH;

        for (const side of [-1, 1] as const) {
          if (isSide && side === -1) continue; // far wing hidden in side view
          const wx = a.cx + side * (a.shW * n + 2);
          const fc = side === 1 ? color : dk;

          // Main wing shape — swept arc
          const wGrad = ctx.createLinearGradient(wx, attachY, wx + side * wingW, attachY - wingH * 0.6);
          wGrad.addColorStop(0, fc);
          wGrad.addColorStop(0.6, hi);
          wGrad.addColorStop(1, shade(fc, 0.9));
          ctx.fillStyle = wGrad;
          ctx.beginPath();
          ctx.moveTo(wx, attachY);
          ctx.bezierCurveTo(
            wx + side * wingW * 0.3, attachY - wingH * 0.7 + fOff,
            wx + side * wingW * 0.8, attachY - wingH * 0.6 + fOff,
            wx + side * wingW, attachY - wingH * 0.3 + fOff
          );
          ctx.bezierCurveTo(
            wx + side * wingW * 0.9, attachY + wingH * 0.1,
            wx + side * wingW * 0.4, attachY + wingH * 0.3,
            wx, attachY + 8
          );
          ctx.closePath();
          ctx.fill();

          // Feather ridges — 4-5 lines radiating from attach point
          ctx.strokeStyle = shade(fc, 0.85);
          ctx.lineWidth = 0.6;
          for (let i = 0; i < 5; i++) {
            const t = i / 5;
            const fx = wx + side * wingW * (0.2 + t * 0.6);
            const fy = attachY - wingH * 0.1 * (1 - t) + fOff * t;
            ctx.beginPath();
            ctx.moveTo(wx + side * 2, attachY + 2);
            ctx.quadraticCurveTo(fx * 0.5 + wx * 0.5, fy, fx, fy + wingH * 0.15);
            ctx.stroke();
          }
        }
        break;
      }
      case 1: { // Butterfly wings — delicate, colorful, translucent
        const wingW = 20 * wingSpread;
        const wingH = 28;
        const fOff = flutter * wingH * 1.5;

        for (const side of [-1, 1] as const) {
          if (isSide && side === -1) continue;
          const wx = a.cx + side * (a.shW * n + 1);
          ctx.save();
          ctx.globalAlpha = (wingAlpha) * 0.7; // butterflies are translucent

          // Upper wing lobe
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.moveTo(wx, attachY);
          ctx.bezierCurveTo(
            wx + side * wingW * 0.5, attachY - wingH * 0.8 + fOff,
            wx + side * wingW, attachY - wingH * 0.5 + fOff,
            wx + side * wingW * 0.7, attachY - wingH * 0.1 + fOff * 0.5
          );
          ctx.closePath();
          ctx.fill();

          // Lower wing lobe
          ctx.fillStyle = dk;
          ctx.beginPath();
          ctx.moveTo(wx, attachY + 2);
          ctx.bezierCurveTo(
            wx + side * wingW * 0.6, attachY + wingH * 0.1 + fOff * 0.3,
            wx + side * wingW * 0.8, attachY + wingH * 0.4,
            wx + side * wingW * 0.3, attachY + wingH * 0.5
          );
          ctx.closePath();
          ctx.fill();

          // Wing spot patterns
          ctx.fillStyle = hi;
          ctx.globalAlpha = 0.5;
          ctx.beginPath();
          ctx.ellipse(wx + side * wingW * 0.4, attachY - wingH * 0.25 + fOff * 0.5, 3, 4, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.beginPath();
          ctx.ellipse(wx + side * wingW * 0.35, attachY + wingH * 0.15, 2, 2.5, 0, 0, Math.PI * 2);
          ctx.fill();

          // Vein lines
          ctx.strokeStyle = shade(color, 0.6);
          ctx.lineWidth = 0.4;
          ctx.globalAlpha = 0.4;
          ctx.beginPath();
          ctx.moveTo(wx, attachY);
          ctx.lineTo(wx + side * wingW * 0.6, attachY - wingH * 0.4 + fOff * 0.5);
          ctx.stroke();

          ctx.restore();
        }
        break;
      }
      case 2: { // Bat / demon wings — angular, membrane
        const wingW = 22 * wingSpread;
        const wingH = 26;
        const fOff = flutter * wingH;

        for (const side of [-1, 1] as const) {
          if (isSide && side === -1) continue;
          const wx = a.cx + side * (a.shW * n + 2);
          const fc = side === 1 ? color : dk;

          // Membrane — angular polygon with finger bones
          ctx.fillStyle = fc;
          ctx.beginPath();
          ctx.moveTo(wx, attachY);
          // Bone 1 — top
          ctx.lineTo(wx + side * wingW * 0.4, attachY - wingH * 0.7 + fOff);
          // Bone 2 — middle
          ctx.lineTo(wx + side * wingW * 0.8, attachY - wingH * 0.4 + fOff * 0.8);
          // Bone 3 — outer
          ctx.lineTo(wx + side * wingW, attachY - wingH * 0.1 + fOff * 0.5);
          // Scalloped bottom edge
          ctx.quadraticCurveTo(wx + side * wingW * 0.8, attachY + wingH * 0.2, wx + side * wingW * 0.5, attachY + wingH * 0.15);
          ctx.quadraticCurveTo(wx + side * wingW * 0.3, attachY + wingH * 0.25, wx, attachY + 6);
          ctx.closePath();
          ctx.fill();

          // Bone lines
          ctx.strokeStyle = shade(fc, 0.65);
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.moveTo(wx, attachY);
          ctx.lineTo(wx + side * wingW * 0.4, attachY - wingH * 0.7 + fOff);
          ctx.moveTo(wx, attachY);
          ctx.lineTo(wx + side * wingW * 0.8, attachY - wingH * 0.4 + fOff * 0.8);
          ctx.moveTo(wx, attachY);
          ctx.lineTo(wx + side * wingW, attachY - wingH * 0.1 + fOff * 0.5);
          ctx.stroke();
        }
        break;
      }
      case 3: { // Fairy wings — small, iridescent, rapid flutter
        const wingW = 14 * wingSpread;
        const wingH = 18;
        const rapidFlutter = Math.sin(flutterPhase * 3) * 0.06 * wingH; // faster vibration

        for (const side of [-1, 1] as const) {
          if (isSide && side === -1) continue;
          const wx = a.cx + side * (a.shW * n + 1);

          ctx.save();
          ctx.globalAlpha = wingAlpha * 0.55;

          // Upper wing — rounded teardrop
          const wGrad = ctx.createRadialGradient(
            wx + side * wingW * 0.3, attachY - wingH * 0.2, 1,
            wx + side * wingW * 0.3, attachY - wingH * 0.2, wingW * 0.8
          );
          wGrad.addColorStop(0, hi);
          wGrad.addColorStop(0.5, color);
          wGrad.addColorStop(1, "rgba(255,255,255,0.1)");
          ctx.fillStyle = wGrad;
          ctx.beginPath();
          ctx.moveTo(wx, attachY);
          ctx.bezierCurveTo(
            wx + side * wingW * 0.2, attachY - wingH * 0.8 + rapidFlutter,
            wx + side * wingW * 0.9, attachY - wingH * 0.6 + rapidFlutter,
            wx + side * wingW * 0.5, attachY + 2
          );
          ctx.closePath();
          ctx.fill();

          // Lower wing — smaller
          ctx.fillStyle = shade(color, 0.9);
          ctx.beginPath();
          ctx.moveTo(wx, attachY + 2);
          ctx.bezierCurveTo(
            wx + side * wingW * 0.3, attachY + wingH * 0.1 + rapidFlutter * 0.5,
            wx + side * wingW * 0.6, attachY + wingH * 0.3,
            wx + side * wingW * 0.2, attachY + wingH * 0.35
          );
          ctx.closePath();
          ctx.fill();

          ctx.restore();
        }
        break;
      }
    }

    ctx.restore();
  });
}

// ─── Cape V2 ───────────────────────────────────────────────────────────────

function drawCapeV2(
  ctx: DrawCtx, dir: Direction, _frame: number,
  color: string, style: number, bodyType: 0 | 1 | 2 | 3
): void {
  if (style < 0) return;
  const a = BA[bodyType];
  const dk = shade(color, 0.72);
  const hi = shade(color, 1.12);
  const wp = _animPose;

  withFlip(ctx, dir, (cat) => {
    const n = getNarrow(cat);
    const isFront = cat === "front" || cat === "qfront";
    const isBack = cat === "back" || cat === "qback";
    const isSide = cat === "side";

    // Cape visibility — mainly visible from back/side, edges from front
    const capeWidth = a.shW * n + 4;
    const neckAttachY = a.neckY + 2;

    // Cloth sway from animation + secondary motion lag for coherent physics
    const sway = Math.sin(wp.clothPhase) * wp.clothAmplitude * 3 + wp.secondaryLagX * 0.6;

    switch (style % 3) {
      case 0: { // Short cape — shoulder to waist
        const capeBot = a.waistY + 6;
        if (isFront) {
          // From front, just see the collar/clasp and edge peeks at shoulders
          ctx.fillStyle = dk;
          // Collar band
          ctx.beginPath();
          ctx.moveTo(a.cx - capeWidth * 0.4, neckAttachY);
          ctx.lineTo(a.cx + capeWidth * 0.4, neckAttachY);
          ctx.lineTo(a.cx + capeWidth * 0.3, neckAttachY + 3);
          ctx.lineTo(a.cx - capeWidth * 0.3, neckAttachY + 3);
          ctx.closePath();
          ctx.fill();
          // Clasp
          ctx.fillStyle = "#C0A040";
          ctx.beginPath(); ctx.arc(a.cx, neckAttachY + 1.5, 2, 0, Math.PI * 2); ctx.fill();
        } else {
          // Back/side/qback — full cape drape
          const cGrad = ctx.createLinearGradient(a.cx, neckAttachY, a.cx + sway, capeBot);
          cGrad.addColorStop(0, color);
          cGrad.addColorStop(0.7, color);
          cGrad.addColorStop(1, dk);
          ctx.fillStyle = cGrad;
          ctx.beginPath();
          ctx.moveTo(a.cx - capeWidth, neckAttachY);
          ctx.bezierCurveTo(
            a.cx - capeWidth - 1, (neckAttachY + capeBot) / 2,
            a.cx - capeWidth + sway * 0.5, capeBot - 4,
            a.cx - capeWidth + 2 + sway, capeBot
          );
          ctx.quadraticCurveTo(a.cx + sway * 0.5, capeBot + 2, a.cx + capeWidth - 2 + sway, capeBot);
          ctx.bezierCurveTo(
            a.cx + capeWidth - sway * 0.5, capeBot - 4,
            a.cx + capeWidth + 1, (neckAttachY + capeBot) / 2,
            a.cx + capeWidth, neckAttachY
          );
          ctx.closePath();
          ctx.fill();
          // Center fold shadow
          ctx.strokeStyle = "rgba(0,0,0,0.08)";
          ctx.lineWidth = 0.8;
          ctx.beginPath();
          ctx.moveTo(a.cx, neckAttachY + 2);
          ctx.quadraticCurveTo(a.cx + sway * 0.3, (neckAttachY + capeBot) / 2, a.cx + sway * 0.5, capeBot - 1);
          ctx.stroke();
          // Clasp at neck
          ctx.fillStyle = "#C0A040";
          ctx.beginPath(); ctx.arc(a.cx, neckAttachY, 2, 0, Math.PI * 2); ctx.fill();
        }
        break;
      }
      case 1: { // Long cape — shoulder to ankle, dramatic
        const capeBot = a.ankleY - 2;
        if (isFront) {
          // Collar + clasp only from front
          ctx.fillStyle = dk;
          ctx.beginPath();
          ctx.moveTo(a.cx - capeWidth * 0.4, neckAttachY);
          ctx.lineTo(a.cx + capeWidth * 0.4, neckAttachY);
          ctx.lineTo(a.cx + capeWidth * 0.3, neckAttachY + 4);
          ctx.lineTo(a.cx - capeWidth * 0.3, neckAttachY + 4);
          ctx.closePath();
          ctx.fill();
          ctx.fillStyle = "#C0A040";
          ctx.beginPath(); ctx.arc(a.cx, neckAttachY + 2, 2.5, 0, Math.PI * 2); ctx.fill();
        } else {
          const flare = capeWidth + 6;
          const cGrad = ctx.createLinearGradient(a.cx, neckAttachY, a.cx, capeBot);
          cGrad.addColorStop(0, color);
          cGrad.addColorStop(0.5, color);
          cGrad.addColorStop(1, dk);
          ctx.fillStyle = cGrad;
          ctx.beginPath();
          ctx.moveTo(a.cx - capeWidth, neckAttachY);
          ctx.bezierCurveTo(
            a.cx - capeWidth - 2, a.hipY,
            a.cx - flare + sway * 0.5, capeBot - 10,
            a.cx - flare + sway, capeBot
          );
          ctx.quadraticCurveTo(a.cx + sway * 0.3, capeBot + 3, a.cx + flare + sway, capeBot);
          ctx.bezierCurveTo(
            a.cx + flare - sway * 0.5, capeBot - 10,
            a.cx + capeWidth + 2, a.hipY,
            a.cx + capeWidth, neckAttachY
          );
          ctx.closePath();
          ctx.fill();
          // Vertical fold shadows
          ctx.strokeStyle = "rgba(0,0,0,0.06)";
          ctx.lineWidth = 0.8;
          for (let i = -1; i <= 1; i++) {
            const fx = a.cx + i * capeWidth * 0.35;
            ctx.beginPath();
            ctx.moveTo(fx, neckAttachY + 4);
            ctx.quadraticCurveTo(fx + sway * 0.2 * i, (neckAttachY + capeBot) / 2, fx + sway * 0.4, capeBot - 2);
            ctx.stroke();
          }
          // Cape hem highlight
          ctx.strokeStyle = hi;
          ctx.lineWidth = 0.8;
          ctx.beginPath();
          ctx.moveTo(a.cx - flare + sway, capeBot);
          ctx.quadraticCurveTo(a.cx + sway * 0.3, capeBot + 3, a.cx + flare + sway, capeBot);
          ctx.stroke();
          // Clasp
          ctx.fillStyle = "#C0A040";
          ctx.beginPath(); ctx.arc(a.cx, neckAttachY, 2.5, 0, Math.PI * 2); ctx.fill();
        }
        break;
      }
      case 2: { // Hooded cape — like long cape but with a hood
        const capeBot = a.ankleY;
        if (isFront) {
          // Hood frame around face — visible from front as a border
          ctx.fillStyle = dk;
          ctx.beginPath();
          ctx.moveTo(a.cx - a.headRx - 3, a.headY + a.headRy - 2);
          ctx.quadraticCurveTo(a.cx - a.headRx - 5, a.headY - a.headRy, a.cx, a.headY - a.headRy - 5);
          ctx.quadraticCurveTo(a.cx + a.headRx + 5, a.headY - a.headRy, a.cx + a.headRx + 3, a.headY + a.headRy - 2);
          // Inner edge follows head
          ctx.quadraticCurveTo(a.cx + a.headRx + 1, a.headY - a.headRy + 2, a.cx, a.headY - a.headRy - 1);
          ctx.quadraticCurveTo(a.cx - a.headRx - 1, a.headY - a.headRy + 2, a.cx - a.headRx - 3, a.headY + a.headRy - 2);
          ctx.closePath();
          ctx.fill();
          // Clasp
          ctx.fillStyle = "#C0A040";
          ctx.beginPath(); ctx.arc(a.cx, neckAttachY + 2, 2, 0, Math.PI * 2); ctx.fill();
        } else {
          // Hood up — covers back of head
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.ellipse(a.cx, a.headY - 2, a.headRx + 4, a.headRy + 4, 0, 0, Math.PI * 2);
          ctx.fill();
          ctx.fillStyle = dk;
          ctx.beginPath();
          ctx.ellipse(a.cx, a.headY, a.headRx + 2, a.headRy + 2, 0, 0, Math.PI * 2);
          ctx.fill();
          // Cape body
          const flare = capeWidth + 4;
          ctx.fillStyle = color;
          ctx.beginPath();
          ctx.moveTo(a.cx - capeWidth, neckAttachY);
          ctx.bezierCurveTo(a.cx - capeWidth - 1, a.hipY, a.cx - flare + sway * 0.5, capeBot - 8, a.cx - flare + sway, capeBot);
          ctx.quadraticCurveTo(a.cx + sway * 0.3, capeBot + 2, a.cx + flare + sway, capeBot);
          ctx.bezierCurveTo(a.cx + flare - sway * 0.5, capeBot - 8, a.cx + capeWidth + 1, a.hipY, a.cx + capeWidth, neckAttachY);
          ctx.closePath();
          ctx.fill();
          // Fold shadows
          ctx.strokeStyle = "rgba(0,0,0,0.06)";
          ctx.lineWidth = 0.8;
          ctx.beginPath();
          ctx.moveTo(a.cx, neckAttachY + 2);
          ctx.quadraticCurveTo(a.cx + sway * 0.2, (neckAttachY + capeBot) / 2, a.cx + sway * 0.3, capeBot - 2);
          ctx.stroke();
        }
        break;
      }
    }
  });
}

// ─── Backpack V2 ───────────────────────────────────────────────────────────

function drawBackpackV2(
  ctx: DrawCtx, dir: Direction, _frame: number,
  color: string, style: number, bodyType: 0 | 1 | 2 | 3
): void {
  if (style < 0) return;
  const a = BA[bodyType];
  const dk = shade(color, 0.72);
  const hi = shade(color, 1.10);

  withFlip(ctx, dir, (cat) => {
    const n = getNarrow(cat);
    const isFront = cat === "front" || cat === "qfront";
    const isBack = cat === "back" || cat === "qback";

    // Backpack sits behind the character — barely visible from front
    if (isFront) {
      // Just the straps visible across shoulders from front
      ctx.strokeStyle = dk;
      ctx.lineWidth = 2;
      const strapW = a.shW * n * 0.3;
      // Left strap
      ctx.beginPath();
      ctx.moveTo(a.cx - strapW, a.shY);
      ctx.lineTo(a.cx - strapW - 1, a.waistY - 2);
      ctx.stroke();
      // Right strap
      ctx.beginPath();
      ctx.moveTo(a.cx + strapW, a.shY);
      ctx.lineTo(a.cx + strapW + 1, a.waistY - 2);
      ctx.stroke();
      return;
    }

    const packW = a.shW * n * 0.7;
    const bpTop = a.shY + 2;

    switch (style % 3) {
      case 0: { // Small daypack — compact, sits high
        const bpBot = a.waistY + 4;
        const bpGrad = ctx.createLinearGradient(a.cx - packW, bpTop, a.cx + packW, bpBot);
        bpGrad.addColorStop(0, color);
        bpGrad.addColorStop(0.7, color);
        bpGrad.addColorStop(1, dk);
        ctx.fillStyle = bpGrad;
        ctxRoundRect(ctx, a.cx - packW, bpTop, packW * 2, bpBot - bpTop, 3);
        // Top flap
        ctx.fillStyle = dk;
        ctxRoundRect(ctx, a.cx - packW + 1, bpTop, packW * 2 - 2, 6, 2);
        // Zipper line
        ctx.strokeStyle = shade(color, 0.55);
        ctx.lineWidth = 0.8;
        ctx.beginPath();
        ctx.moveTo(a.cx - packW + 3, bpTop + 10);
        ctx.lineTo(a.cx + packW - 3, bpTop + 10);
        ctx.stroke();
        // Straps — visible from back/side
        ctx.strokeStyle = shade(color, 0.65);
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(a.cx - packW + 2, bpTop + 1);
        ctx.quadraticCurveTo(a.cx - packW - 2, bpTop - 4, a.cx - packW, a.shY - 2);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(a.cx + packW - 2, bpTop + 1);
        ctx.quadraticCurveTo(a.cx + packW + 2, bpTop - 4, a.cx + packW, a.shY - 2);
        ctx.stroke();
        break;
      }
      case 1: { // Hiking backpack — tall, has bedroll on top
        const bpBot = a.hipY + 6;
        ctx.fillStyle = color;
        ctxRoundRect(ctx, a.cx - packW - 1, bpTop, (packW + 1) * 2, bpBot - bpTop, 3);
        // Side pockets
        ctx.fillStyle = dk;
        ctxRoundRect(ctx, a.cx - packW - 3, bpTop + 12, 5, 10, 1);
        ctxRoundRect(ctx, a.cx + packW - 2, bpTop + 12, 5, 10, 1);
        // Bedroll on top
        ctx.fillStyle = shade(color, 0.85);
        ctx.beginPath();
        ctx.ellipse(a.cx, bpTop - 2, packW - 1, 3, 0, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = shade(color, 0.6);
        ctx.lineWidth = 0.5;
        ctx.beginPath();
        ctx.ellipse(a.cx, bpTop - 2, packW - 1, 3, 0, 0, Math.PI * 2);
        ctx.stroke();
        // Buckle straps
        ctx.strokeStyle = shade(color, 0.5);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(a.cx - packW + 2, bpTop + 8);
        ctx.lineTo(a.cx + packW - 2, bpTop + 8);
        ctx.stroke();
        ctx.fillStyle = "#888";
        ctx.fillRect(a.cx - 1.5, bpTop + 7, 3, 3);
        break;
      }
      case 2: { // Satchel / messenger bag — diagonal strap, hangs at hip
        const bagTop = a.waistY - 2;
        const bagBot = a.hipY + 8;
        const bagW = packW + 2;
        ctx.fillStyle = color;
        ctxRoundRect(ctx, a.cx - bagW, bagTop, bagW * 2, bagBot - bagTop, 3);
        // Flap
        ctx.fillStyle = dk;
        ctx.beginPath();
        ctx.moveTo(a.cx - bagW, bagTop);
        ctx.lineTo(a.cx + bagW, bagTop);
        ctx.lineTo(a.cx + bagW, bagTop + (bagBot - bagTop) * 0.5);
        ctx.quadraticCurveTo(a.cx, bagTop + (bagBot - bagTop) * 0.55, a.cx - bagW, bagTop + (bagBot - bagTop) * 0.5);
        ctx.closePath();
        ctx.fill();
        // Buckle
        ctx.fillStyle = "#C0A040";
        ctx.fillRect(a.cx - 2, bagTop + (bagBot - bagTop) * 0.4, 4, 3);
        // Diagonal strap across body
        ctx.strokeStyle = shade(color, 0.6);
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(a.cx + bagW - 2, bagTop);
        ctx.lineTo(a.cx - a.shW * n * 0.5, a.shY - 2);
        ctx.stroke();
        break;
      }
    }
  });
}

// ─── Layer stack builder ──────────────────────────────────────────────────────

/**
 * Build the ordered layer stack for a character.
 * The return value is a sorted list of layer definitions ready to composite.
 */
function buildLayers(appearance: CharacterAppearance, dir: Direction): CharacterLayer[] {
  const bt = appearance.bodyType;
  const skin = SKIN_TONES[appearance.skinTone % SKIN_TONES.length];
  const hair = HAIR_COLORS[appearance.hairColor % HAIR_COLORS.length];
  const eye  = EYE_COLORS[appearance.eyeColor % EYE_COLORS.length];
  const top  = ITEM_COLORS[appearance.topColor % ITEM_COLORS.length];
  const bot  = ITEM_COLORS[appearance.bottomColor % ITEM_COLORS.length];
  const shoe = ITEM_COLORS[appearance.shoeColor % ITEM_COLORS.length];
  const hat  = ITEM_COLORS[appearance.hatColor % ITEM_COLORS.length];
  const jkt  = ITEM_COLORS[appearance.jacketColor % ITEM_COLORS.length];
  const acc  = ITEM_COLORS[appearance.accessoryColor % ITEM_COLORS.length];
  const wingC = ITEM_COLORS[appearance.wingsColor % ITEM_COLORS.length];
  const capeC = ITEM_COLORS[appearance.capeColor % ITEM_COLORS.length];
  const bpC   = ITEM_COLORS[appearance.backpackColor % ITEM_COLORS.length];
  const fhC   = HAIR_COLORS[appearance.facialHairColor % HAIR_COLORS.length];
  const mkC   = MAKEUP_COLORS[appearance.makeupColor % MAKEUP_COLORS.length];
  const lipC  = LIP_COLORS[appearance.lipColor % LIP_COLORS.length];

  // Direction-aware z-index: cape/wings/backpack go behind body when facing
  // front, but in front of clothing when facing away (they're on the character's back)
  const [dCat] = dirCat(dir);
  const facingAway = dCat === "back" || dCat === "qback";
  const backLayerZ = facingAway ? 7 : 0.6;   // 7 puts them between jacket(6) and accessory(8)
  const wingsZ     = facingAway ? 7.1 : 0.7;
  const backpackZ  = facingAway ? 7.2 : 0.8;

  const layers: CharacterLayer[] = [
    {
      id: "shadow",
      zIndex: 0,
      visible: true,
      draw: (ctx, dir, frame) => drawShadowV2(ctx, dir, frame, bt),
    },
    {
      id: "body",
      zIndex: 1,
      visible: true,
      draw: (ctx, dir, frame) => drawBodyV2(ctx, dir, frame, skin, bt),
    },
    {
      id: "anatomy",
      zIndex: 1.5,
      visible: true,
      draw: (ctx, dir, frame) => drawAnatomyDetailV2(ctx, dir, frame, skin, bt, appearance.nude, appearance.bodyGender, appearance.breastSize ?? 2, appearance.buttSize ?? 2, appearance.genitalSize ?? 2),
    },
    {
      id: "skin-detail",
      zIndex: 2,
      visible: appearance.skinDetail >= 0,
      draw: (ctx, dir, frame) => drawSkinDetailV2(ctx, dir, frame, skin, appearance.skinDetail, bt),
    },
    {
      id: "bottom",
      zIndex: 3,
      visible: !appearance.nude && appearance.bottomStyle >= 0,
      draw: (ctx, dir, frame) => drawBottomV2(ctx, dir, frame, bot, appearance.bottomStyle, bt),
    },
    {
      id: "shoes",
      zIndex: 4,
      visible: !appearance.nude && appearance.shoeStyle >= 0,
      draw: (ctx, dir, frame) => drawShoesV2(ctx, dir, frame, shoe, appearance.shoeStyle, bt),
    },
    {
      id: "top",
      zIndex: 5,
      visible: !appearance.nude && appearance.topStyle >= 0,
      draw: (ctx, dir, frame) => drawTopV2(ctx, dir, frame, top, appearance.topStyle, bt),
    },
    {
      id: "jacket",
      zIndex: 6,
      visible: !appearance.nude && appearance.jacketStyle >= 0,
      draw: (ctx, dir, frame) => drawJacketV2(ctx, dir, frame, jkt, appearance.jacketStyle, bt),
    },
    {
      id: "hair-back",
      zIndex: facingAway ? 6.5 : 0.5,  // Behind body from front; over clothing from back
      visible: true,
      draw: (ctx, dir, frame) => drawHairBackV2(ctx, dir, frame, hair, appearance.hairStyle, bt, appearance.hatStyle),
    },
    {
      id: "eyes",
      zIndex: 9,
      visible: true,
      draw: (ctx, dir, frame) => drawEyesV2(ctx, dir, frame % 100, eye, appearance.eyeStyle, bt, frame >= 100),
    },
    {
      id: "lips",
      zIndex: 9.05,
      visible: appearance.lipStyle >= 0,
      draw: (ctx, dir, frame) => drawLipsV2(ctx, dir, frame, lipC, appearance.lipStyle, bt),
    },
    {
      id: "makeup",
      zIndex: 9.1,
      visible: appearance.makeupStyle >= 0,
      draw: (ctx, dir, frame) => drawMakeupV2(ctx, dir, frame, mkC, appearance.makeupStyle, bt),
    },
    {
      id: "facial-hair",
      zIndex: 9.5,
      visible: appearance.facialHairStyle >= 0,
      draw: (ctx, dir, frame) => drawFacialHairV2(ctx, dir, frame, fhC, appearance.facialHairStyle, bt),
    },
    {
      id: "hair-front",
      zIndex: 10,
      visible: true,
      draw: (ctx, dir, frame) => drawHairFrontV2(ctx, dir, frame, hair, appearance.hairStyle, bt, appearance.hatStyle),
    },
    {
      id: "hat",
      zIndex: 11,
      visible: appearance.hatStyle >= 0,
      draw: (ctx, dir, frame) => drawHatV2(ctx, dir, frame, hat, appearance.hatStyle, bt),
    },
    {
      id: "held-item",
      zIndex: 12,
      visible: appearance.heldItem >= 0 && !(_weaponVisual && _isCombatAnim),
      draw: (ctx, dir, frame) => drawHeldItemV2(ctx, dir, frame, "#FFFFFF", appearance.heldItem, bt),
    },
    {
      id: "weapon",
      zIndex: facingAway ? 0.9 : dCat === "side" ? 5 : 13,  // Behind body in back; between layers in side profile; above all in front
      visible: _weaponVisual !== null,
      draw: (ctx, dir, frame) => drawWeaponV2(ctx, dir, frame, bt),
    },
    {
      id: "shield",
      zIndex: facingAway ? 7.3 : 2,  // Behind body from front (far arm); over clothing from back (near arm to viewer)
      visible: _shieldVisual !== null && _weaponVisual?.class !== "sword_2h" && _weaponVisual?.class !== "staff",
      draw: (ctx, dir, frame) => drawShieldV2(ctx, dir, frame, bt),
    },
    {
      id: "accessory",
      zIndex: 8,
      visible: appearance.accessoryStyle >= 0,
      draw: (ctx, dir, frame) => drawAccessoryV2(ctx, dir, frame, acc, appearance.accessoryStyle, bt),
    },
    {
      id: "aura",
      zIndex: 14,
      visible: appearance.auraStyle >= 0,
      draw: (ctx, dir, frame) => drawAuraV2(ctx, dir, frame, appearance.auraStyle, bt),
    },
    {
      id: "cape",
      zIndex: backLayerZ,
      visible: appearance.capeStyle >= 0,
      draw: (ctx, dir, frame) => drawCapeV2(ctx, dir, frame, capeC, appearance.capeStyle, bt),
    },
    {
      id: "wings",
      zIndex: wingsZ,
      visible: appearance.wingsStyle >= 0,
      draw: (ctx, dir, frame) => drawWingsV2(ctx, dir, frame, wingC, appearance.wingsStyle, bt),
    },
    {
      id: "backpack",
      zIndex: backpackZ,
      visible: appearance.backpackStyle >= 0,
      draw: (ctx, dir, frame) => drawBackpackV2(ctx, dir, frame, bpC, appearance.backpackStyle, bt),
    },
  ];

  return layers.sort((a, b) => a.zIndex - b.zIndex);
}

// ─── Character state factory ──────────────────────────────────────────────────

let nextCharId = 1;

/**
 * Create a new character from an appearance definition.
 * This sets up the state object but doesn't render anything yet.
 * Rendering happens lazily on the first call to renderCharacter.
 */
export function createCharacter(appearance: CharacterAppearance): CharacterState {
  return {
    id: `char_${nextCharId++}`,
    appearance: { ...appearance },
    cache: new Map(),
    dirty: true,
    animState: "idle",
    direction: "s",
    animFrame: 0,
    animTimer: 0,
    bobOffset: 0,
    offscreen: new OffscreenCanvas(CHAR_W, CHAR_H),
  };
}

/**
 * Mark a character's cache as dirty so it gets re-composited.
 * Call this after changing appearance properties.
 */
export function invalidateCharacter(char: CharacterState): void {
  char.cache.clear();
  char.dirty = true;
}

// ─── Composite renderer ───────────────────────────────────────────────────────

/**
 * Compose all visible layers for a character via Canvas2D drawing.
 * Each layer draws directly onto a shared OffscreenCanvas — proper alpha
 * blending, gradient support, and sub-pixel rendering for free.
 * Cached by direction + frame key.
 */
let _compositeCanvas: OffscreenCanvas | null = null;

/** Module-level animation pose — set before compositing, read by ALL draw functions */
let _animPose: AnimPose = { ...ZERO_POSE };

// ─── Body Shape Context — Archetype Blending ────────────────────────────────
// Two archetypes lerped by blend ratio produce anatomically coherent proportions.
// Ratios are relative — shoulder/waist/hip maintain human relationships.

interface BodyArchetype {
  shoulderToHead: number;    // shoulder width as multiple of head width
  waistToShoulder: number;   // waist as ratio of shoulder width
  hipToShoulder: number;     // hip width as ratio of shoulder width
  chestProjection: number;   // how far chest protrudes from torso plane (px at scale=1)
  buttProjection: number;    // how far glutes protrude in back/side views
  armMass: number;           // upper arm thickness multiplier
  legMass: number;           // thigh thickness multiplier
  neckToShoulder: number;    // neck width as fraction of shoulder
  shoulderSlope: number;     // 0=level shoulders, 1=strongly sloped
}

const BODY_ARCHETYPES: Record<BodyArchetypeId, BodyArchetype> = {
  athletic: {
    shoulderToHead: 2.4,  waistToShoulder: 0.70, hipToShoulder: 0.94,
    chestProjection: 1.2, buttProjection: 1.1,
    armMass: 1.2,  legMass: 1.15, neckToShoulder: 0.28, shoulderSlope: 0.25,
  },
  soft: {
    shoulderToHead: 1.9,  waistToShoulder: 0.88, hipToShoulder: 1.02,
    chestProjection: 1.4, buttProjection: 1.35,
    armMass: 1.1,  legMass: 1.2,  neckToShoulder: 0.30, shoulderSlope: 0.15,
  },
  lithe: {
    shoulderToHead: 1.75, waistToShoulder: 0.78, hipToShoulder: 0.82,
    chestProjection: 0.9, buttProjection: 0.85,
    armMass: 0.85, legMass: 0.90, neckToShoulder: 0.25, shoulderSlope: 0.10,
  },
  powerful: {
    shoulderToHead: 2.9,  waistToShoulder: 0.78, hipToShoulder: 0.97,
    chestProjection: 1.3, buttProjection: 1.0,
    armMass: 1.5,  legMass: 1.45, neckToShoulder: 0.38, shoulderSlope: 0.40,
  },
  petite: {
    shoulderToHead: 1.65, waistToShoulder: 0.84, hipToShoulder: 1.05,
    chestProjection: 1.1, buttProjection: 1.15,
    armMass: 0.90, legMass: 0.95, neckToShoulder: 0.26, shoulderSlope: 0.08,
  },
};

/** Resolved body shape — output of archetype lerp. Used by all draw functions. */
interface ResolvedShape {
  shoulderW: number;     // absolute shoulder half-width in px
  waistW: number;        // absolute waist half-width in px
  hipW: number;          // absolute hip half-width in px
  shoulderD: number;     // shoulder forward half-depth in px
  waistD: number;        // waist forward half-depth in px
  hipD: number;          // hip forward half-depth in px
  chestProjection: number; // chest protrusion multiplier
  buttProjection: number;  // glute protrusion multiplier
  armMass: number;       // arm thickness multiplier
  legMass: number;       // leg thickness multiplier
  neckW: number;         // neck width in px
  shoulderSlope: number; // 0-1 slope factor
  scale: number;         // overall body scale multiplier (from bodyScale slider)
}

const NEUTRAL_RESOLVED: ResolvedShape = {
  shoulderW: 21, waistW: 16.4, hipW: 17.2,
  shoulderD: 11.55, waistD: 9.02, hipD: 10.32,
  chestProjection: 1.0, buttProjection: 1.0,
  armMass: 1.0, legMass: 1.0, neckW: 10.5, shoulderSlope: 0.15, scale: 1.0,
};
let _bodyShape: ResolvedShape = { ...NEUTRAL_RESOLVED };

/** Module-level anatomy state — set before compositing, read by drawBodyV2 and drawAnatomyDetailV2 */
interface AnatomyState {
  nude: boolean;
  gender: BodyGender;
  breastSize: number; // 0-4
  buttSize: number;   // 0-4
  genitalSize: number; // 0-4
}
let _anatomy: AnatomyState = { nude: false, gender: "androgynous", breastSize: 2, buttSize: 2, genitalSize: 2 };

/** Module-level leg gap slider value (0-4, 2=neutral) — set before compositing */
let _legGapSlider = 2;

// ─── Isometric Projection Math ────────────────────────────────────────────────────────
// Camera looks from ~30° above. Facing direction determines how much of a body
// region's lateral span vs. forward depth you see.
//
// projW = lateralHW * cos²(θ) + depthHW * sin²(θ)
//
// cos²/sin² blend gives monotonic transition from frontal width (θ=0) to side
// depth (θ=π/2). Unlike |cos|+|sin| this NEVER exceeds lateralHW at any angle.
// θ = 0° (front), 45° (qfront), 90° (side), 135° (qback), 180° (back)
// Camera elevation compresses frontal views by ~cos(30°) ≈ 0.87.

const DIR_THETA: Record<DirCat, number> = {
  front: 0, qfront: Math.PI / 4, side: Math.PI / 2,
  qback: 3 * Math.PI / 4, back: Math.PI,
};
const CAM_ELEV_COS = Math.cos(Math.PI / 6); // cos(30°) ≈ 0.866

/** Trigonometric projected half-width for a body region at a given direction.
 *  lateralHW = side-to-side half-width, depthHW = front-to-back half-depth.
 *  Uses cos²/sin² blend for monotonic front→side narrowing. */
function projectW(cat: DirCat, lateralHW: number, depthHW: number): number {
  const θ = DIR_THETA[cat];
  const c2 = Math.cos(θ) ** 2;
  const s2 = Math.sin(θ) ** 2;
  const blend = lateralHW * c2 + depthHW * s2;
  // Camera elevation compresses frontal/back views but not pure side
  const elevFactor = 1.0 - (1.0 - CAM_ELEV_COS) * c2;
  return blend * elevFactor;
}

function resolveBodyShape(app: CharacterAppearance): ResolvedShape {
  const a1 = BODY_ARCHETYPES[app.bodyArchetype1 ?? "lithe"];
  const a2 = BODY_ARCHETYPES[app.bodyArchetype2 ?? "lithe"];
  const t  = Math.max(0, Math.min(1, app.archetypeBlend ?? 0));
  // bodyScale 0→0.75, 2→1.0, 4→1.25
  const sc = 0.75 + (app.bodyScale ?? 2) * 0.125;
  const lerp = (v1: number, v2: number) => v1 + (v2 - v1) * t;

  // Head width from body type (legacy anchor system)
  const headW = BA[app.bodyType].headRx * 2;

  const sth = lerp(a1.shoulderToHead, a2.shoulderToHead);
  const wts = lerp(a1.waistToShoulder, a2.waistToShoulder);
  const hts = lerp(a1.hipToShoulder, a2.hipToShoulder);
  const nts = lerp(a1.neckToShoulder, a2.neckToShoulder);

  // ── Gender-driven proportion modifiers ──
  // Feminine: narrower shoulders, cinched waist, wider hips → hourglass
  // Masculine: broader shoulders, narrower hips → V-taper
  // These stack ON TOP of archetype, so a "soft feminine" is ultra-curvy,
  // while a "powerful feminine" is strong but still shapely.
  const gender = app.bodyGender ?? "androgynous";
  const brstNorm = (app.breastSize ?? 2) / 4; // 0-1
  const buttNorm = (app.buttSize ?? 2) / 4;   // 0-1
  const isFem = gender === "feminine";
  const isMasc = gender === "masculine";

  // Shoulder modifier: feminine narrows 6%, masculine widens 8%
  const shoulderMod = isFem ? 0.94 : isMasc ? 1.08 : 1.0;
  // Waist modifier: feminine cinches by 8% + breast influence, masculine slightly wider
  const waistMod = isFem ? (0.92 - brstNorm * 0.03) : isMasc ? 1.04 : 1.0;
  // Hip modifier: feminine widens by 3% + subtle butt influence, masculine narrows 10%
  const hipMod = isFem ? (1.03 + buttNorm * 0.03) : isMasc ? 0.90 : (1.0 + buttNorm * 0.01);
  // Chest projection: breast size drives forward projection for feminine/andro
  const chestMod = (isFem || gender === "androgynous") ? (1.0 + brstNorm * 0.3) : 1.0;
  // Butt projection: butt size drives rear projection
  const buttMod = 1.0 + buttNorm * 0.15;

  // ── Fine-tune sliders (0-4, 2=neutral) → multiplicative adjustment ──
  // Each unit away from center = ±4% width change
  const shoulderFine = 1.0 + ((app.shoulderAdjust ?? 2) - 2) * 0.04;
  const waistFine    = 1.0 + ((app.waistAdjust ?? 2) - 2) * 0.04;
  const hipFine      = 1.0 + ((app.hipAdjust ?? 2) - 2) * 0.04;

  const shoulderAbsolute = (sth * headW * 0.5) * sc * shoulderMod * shoulderFine;

  // Forward depths: shoulders are shallow, hips deeper (seated width).
  // Chest/butt projection adds to base depth.
  const chP = lerp(a1.chestProjection, a2.chestProjection) * chestMod;
  const buP = lerp(a1.buttProjection,  a2.buttProjection)  * buttMod;
  const waistAbs = shoulderAbsolute * wts * waistMod * waistFine;
  const hipAbs   = shoulderAbsolute * hts * hipMod * hipFine;

  // Feminine neck: slightly more slender
  const neckMod = isFem ? 0.92 : isMasc ? 1.05 : 1.0;
  // Feminine shoulder slope: softer, more rounded
  const slopeMod = isFem ? 0.7 : isMasc ? 1.15 : 1.0;

  return {
    shoulderW:       shoulderAbsolute,
    waistW:          waistAbs,
    hipW:            hipAbs,
    shoulderD:       shoulderAbsolute * 0.55,         // side profile ~ 55% of frontal
    waistD:          waistAbs * 0.55,                  // consistent ratio
    hipD:            hipAbs * (0.60 + (buP - 1.0) * 0.15), // hips deeper, butt adds profile
    chestProjection: chP,
    buttProjection:  buP,
    armMass:         lerp(a1.armMass,          a2.armMass) * sc * (isFem ? 0.92 : 1.0),
    legMass:         lerp(a1.legMass,          a2.legMass) * sc * (isFem ? 0.95 : 1.0),
    neckW:           shoulderAbsolute * nts * 2 * neckMod, // full neck width
    shoulderSlope:   lerp(a1.shoulderSlope,    a2.shoulderSlope) * slopeMod,
    scale:           sc,
  };
}

/** Module-level device color — set before compositing, read by drawHeldItemV2 */
let _deviceColor: string = "#3a3a3a";

/** Module-level device type — "phone" or "pad", drives pose variant in computeAnimPose */
let _activeDeviceType: "phone" | "pad" | null = null;

/** Module-level active emote ID — drives per-emote pose variations in "emote" state */
let _activeEmoteId: string | null = null;

/** Module-level buff aura color — drives glow ring around character */
let _buffAuraColor: string | null = null;
let _buffAuraIntensity: number = 0;

/** Module-level device case — set before compositing, read by drawHeldItemV2 */
let _deviceCase: import("../device/state.js").DeviceCaseStyle | null = null;

// ─── Attention & Dynamics State ─────────────────────────────────────────────
// These are set by the caller before renderCharacter and drive gaze, hair, and
// secondary motion. They live outside the cache key (applied as post-compute
// overrides) because they change smoothly every frame.

/** Gaze attention — where the character's eyes should look (-2..2 px offsets) */
let _gazeX = 0;
let _gazeY = 0;

/** Head micro-tilt following gaze direction */
let _headTiltX = 0;
let _headTiltY = 0;

/** Continuous head orientation — wider range for 72+ apparent orientations */
let _headYaw = 0;   // -1..1 horizontal rotation
let _headPitch = 0;  // -0.5..0.5 vertical tilt

/** Blink phase — 0=open, 0→0.5=closing, 0.5=closed, 0.5→1=opening */
let _blinkPhase = 0;

/** Hair swing — horizontal offset from velocity/turning */
let _hairSwingX = 0;
let _hairSettlePhase = 0;

/** Secondary motion lag for accessories */
let _secondaryLagX = 0;
let _secondaryLagY = 0;

/** Body response to attention — shoulder orientation and breathing */
let _shoulderTurnX = 0;
let _breathIntensity = 1.0;
let _interestLevel = 0;

/** Deep eye life — pupil, eyelid, micro-tremor, eyebrow */
let _pupilDilation = 1.0;
let _lidFollowY = 0;
let _microTremorX = 0;
let _microTremorY = 0;
let _browEngagement = 0;

/** Idle micro-fidgets */
let _fidgetArmDY = 0;
let _fidgetTorsoX = 0;
let _weightShiftPhase = 0;

/**
 * Set the gaze and dynamics context before calling renderCharacter().
 *
 * All values are computed by the attention engine and drive the character's
 * living, breathing presence. The system layers:
 *   - gazeX/gazeY: pupil offset with saccadic dynamics + micro-tremor
 *   - headTiltX/Y: head physical displacement (shifts entire head, not just eyes)
 *   - blinkPhase: multi-stage eyelid with anticipatory shift-blinks
 *   - hairSwingX: spring-damped hair from body movement + head turns
 *   - shoulderTurnX: subtle torso orientation toward attention target
 *   - breathIntensity: engagement-responsive breathing amplitude
 *   - pupilDilation: interest-driven pupil size (autonomic response)
 *   - lidFollowY: upper eyelid tracks vertical gaze (involuntary)
 *   - microTremorX/Y: physiological nystagmus (eyes never perfectly still)
 *   - browEngagement: attention-driven brow lift/furrow
 *   - fidgetArmDY/fidgetTorsoX: idle micro-movements
 */
export function setAttentionContext(
  gazeX: number, gazeY: number,
  headTiltX: number, headTiltY: number,
  blinkPhase: number,
  hairSwingX: number, hairSettlePhase: number,
  secondaryLagX: number, secondaryLagY: number,
  shoulderTurnX: number = 0,
  breathIntensity: number = 1.0,
  interestLevel: number = 0,
  pupilDilation: number = 1.0,
  lidFollowY: number = 0,
  microTremorX: number = 0,
  microTremorY: number = 0,
  browEngagement: number = 0,
  fidgetArmDY: number = 0,
  fidgetTorsoX: number = 0,
  weightShiftPhase: number = 0,
  headYaw: number = 0,
  headPitch: number = 0,
): void {
  _gazeX = gazeX;
  _gazeY = gazeY;
  _headTiltX = headTiltX;
  _headTiltY = headTiltY;
  _blinkPhase = blinkPhase;
  _hairSwingX = hairSwingX;
  _hairSettlePhase = hairSettlePhase;
  _secondaryLagX = secondaryLagX;
  _secondaryLagY = secondaryLagY;
  _shoulderTurnX = shoulderTurnX;
  _breathIntensity = breathIntensity;
  _interestLevel = interestLevel;
  _pupilDilation = pupilDilation;
  _lidFollowY = lidFollowY;
  _microTremorX = microTremorX;
  _microTremorY = microTremorY;
  _browEngagement = browEngagement;
  _fidgetArmDY = fidgetArmDY;
  _fidgetTorsoX = fidgetTorsoX;
  _weightShiftPhase = weightShiftPhase;
  _headYaw = headYaw;
  _headPitch = headPitch;
}

/** Set the active device info before compositing a character holding a device */
export function setDeviceContext(color: string, deviceType: "phone" | "pad" | null): void {
  _deviceColor = color;
  _activeDeviceType = deviceType;
}

/** Set the active device case before compositing — null = bare device */
export function setDeviceCaseContext(cs: import("../device/state.js").DeviceCaseStyle | null): void {
  _deviceCase = cs;
}

/** Set the active emote context before compositing — drives per-emote pose variation */
export function setEmoteContext(emoteId: string | null): void {
  _activeEmoteId = emoteId;
}

/** Set the buff aura context before compositing — drives glow ring around character */
export function setBuffAuraContext(color: string | null, intensity: number): void {
  _buffAuraColor = color;
  _buffAuraIntensity = intensity;
}

function compositeCharacter(
  char: CharacterState,
  dir: Direction,
  frame: number,
  isBlinking: boolean
): ImageData {
  const expr = char.appearance.expression ?? "neutral";
  const caseId = _deviceCase?.pattern ?? "none";
  // Quantize gaze/dynamics to discrete steps for cache key stability.
  // gaze at ~0.33px steps, headTilt at 0.5px steps for smooth transitions.
  // blinkPhase to 4 phases. This keeps cache reasonable while looking smooth.
  const gxQ = Math.round(_gazeX * 3);
  const gyQ = Math.round(_gazeY * 3);
  const htQ = Math.round(_headTiltX * 2);
  const htYQ = Math.round(_headTiltY * 3);
  const bpQ = Math.round(_blinkPhase * 4);
  const hsQ = Math.round(_hairSwingX);
  const hspQ = Math.round(_hairSettlePhase * 4);
  const slXQ = Math.round(_secondaryLagX * 2);
  const slYQ = Math.round(_secondaryLagY * 2);
  const wpnId = _weaponVisual ? `${_weaponVisual.class}.${_weaponVisual.bladeColor}.${_weaponVisual.handleColor}.${_weaponVisual.accentColor}.${_weaponVisual.length}` : "n";
  const shdId = _shieldVisual ? `${_shieldVisual.class}.${_shieldVisual.faceColor}.${_shieldVisual.rimColor}.${_shieldVisual.emblemColor}` : "n";
  const stgQ = Math.round(_staggerTimer * 4);
  const pdQ = Math.round(_pupilDilation * 3);
  const lfQ = Math.round(_lidFollowY * 4);
  // Micro-tremor intentionally excluded from cache key — sub-pixel drift
  // should NOT trigger full sprite re-renders (that causes visible vibration).
  const brQ = Math.round(_browEngagement * 3);
  const faQ = Math.round(_fidgetArmDY * 2);
  const ftQ = Math.round(_fidgetTorsoX * 3);
  const wsQ = Math.round(_weightShiftPhase * 2);
  // Head yaw/pitch: 6 steps per unit → 12 yaw × 6 pitch = 72 orientations
  const hyQ = Math.round(_headYaw * 6);
  const hpQ = Math.round(_headPitch * 6);
  const shapeKey = `${char.appearance.bodyArchetype1 ?? "lithe"}:${char.appearance.bodyArchetype2 ?? "lithe"}:${Math.round((char.appearance.archetypeBlend ?? 0) * 20)}:${char.appearance.bodyScale ?? 2}:${char.appearance.shoulderAdjust ?? 2}:${char.appearance.waistAdjust ?? 2}:${char.appearance.hipAdjust ?? 2}:${char.appearance.legGap ?? 2}:${char.appearance.breastSize ?? 2}:${char.appearance.buttSize ?? 2}:${char.appearance.genitalSize ?? 2}:${char.appearance.bodyGender ?? "a"}`;
  const cacheKey = `${dir}:${frame}:${isBlinking ? 1 : 0}:${char.animState}:${expr}:${_deviceColor}:${caseId}:${gxQ}:${gyQ}:${htQ}:${htYQ}:${bpQ}:${hsQ}:${hspQ}:${slXQ}:${slYQ}:${wpnId}:${shdId}:${stgQ}:${pdQ}:${lfQ}:${brQ}:${faQ}:${ftQ}:${wsQ}:${hyQ}:${hpQ}:${shapeKey}`;

  if (!char.dirty && char.cache.has(cacheKey)) {
    return char.cache.get(cacheKey)!;
  }

  // Resolve body shape from appearance sliders — set module-level for all draw fns
  _bodyShape = resolveBodyShape(char.appearance);
  _legGapSlider = char.appearance.legGap ?? 2;
  _anatomy = {
    nude: char.appearance.nude ?? false,
    gender: char.appearance.bodyGender ?? "androgynous",
    breastSize: char.appearance.breastSize ?? 2,
    buttSize: char.appearance.buttSize ?? 2,
    genitalSize: char.appearance.genitalSize ?? 2,
  };

  // Compute unified animation pose — walk, idle breathing, sit, dance
  _animPose = computeAnimPose(char.animState, frame, expr);

  // ── Apply attention & dynamics overlays ──
  // These are continuous values set by the game loop's attention engine.
  // They ride on top of the base animation pose.
  // Attention values are computed in screen space, but drawing happens inside
  // withFlip() which mirrors the canvas for flipped directions. Negate X
  // components so the mirror produces the correct screen-space result.
  const [, dirFlipped] = dirCat(dir as Direction);
  const flipSign = dirFlipped ? -1 : 1;
  _animPose.gazeX = _gazeX * flipSign;
  _animPose.gazeY = _gazeY;
  _animPose.headTiltX = _headTiltX * flipSign;
  _animPose.headTiltY = _headTiltY;
  _animPose.blinkPhase = _blinkPhase;
  _animPose.hairSwingX = _hairSwingX * flipSign;
  _animPose.hairSettlePhase = _hairSettlePhase;
  _animPose.secondaryLagX = _secondaryLagX * flipSign;
  _animPose.secondaryLagY = _secondaryLagY;
  // Body response to attention — shoulder subtly orients toward gaze
  _animPose.shoulderTurnX = _shoulderTurnX * flipSign;
  _animPose.breathIntensity = _breathIntensity;
  _animPose.interestLevel = _interestLevel;
  // Apply shoulder turn to torso sway (additive with existing animation sway)
  _animPose.torsoSwayX += _animPose.shoulderTurnX;
  // Apply breath intensity to breathing amplitude
  _animPose.breatheY *= _animPose.breathIntensity;

  // ── Continuous head orientation ──
  _animPose.headYaw = _headYaw * flipSign;
  _animPose.headPitch = _headPitch;

  // ── Deep eye life overlays ──
  _animPose.pupilDilation = _pupilDilation;
  _animPose.lidFollowY = _lidFollowY;
  _animPose.microTremorX = _microTremorX * flipSign; // tremor is screen-space
  _animPose.microTremorY = _microTremorY;
  _animPose.browEngagement = _browEngagement;

  // ── Idle micro-fidgets — additive on base pose ──
  _animPose.fidgetArmDY = _fidgetArmDY;
  _animPose.fidgetTorsoX = _fidgetTorsoX * flipSign;
  _animPose.weightShiftPhase = _weightShiftPhase;
  // Apply fidgets: subtle arm drift and weight shift
  _animPose.nearArmDY += _fidgetArmDY;
  _animPose.torsoSwayX += _fidgetTorsoX + Math.sin(_weightShiftPhase) * 0.2;

  _isCombatAnim = char.animState === "combat_idle" || char.animState === "combat_attack";

  if (!_compositeCanvas) _compositeCanvas = new OffscreenCanvas(CHAR_W, CHAR_H);
  const ctx = _compositeCanvas.getContext("2d")!;
  ctx.clearRect(0, 0, CHAR_W, CHAR_H);

  const layers = buildLayers(char.appearance, dir);

  for (const layer of layers) {
    if (!layer.visible) continue;
    const frameArg = layer.id === "eyes" ? (isBlinking ? frame + 100 : frame) : frame;
    layer.draw(ctx, dir, frameArg);
  }

  const composite = ctx.getImageData(0, 0, CHAR_W, CHAR_H);
  char.cache.set(cacheKey, composite);

  if (char.cache.size > 128) {
    const firstKey = char.cache.keys().next().value;
    if (firstKey !== undefined) char.cache.delete(firstKey);
  }

  return composite;
}

// ─── Public render function ───────────────────────────────────────────────────

/**
 * Draw a character at the given screen position.
 *
 * screenX, screenY is the character's foot point on screen.
 * The 96×144 canvas is centered horizontally and extends upward.
 *
 * bobOffset is the vertical nudge applied during walking — it comes from
 * the AnimationController and gives characters that satisfying bounce.
 */
export function renderCharacter(
  ctx: CanvasRenderingContext2D,
  char: CharacterState,
  screenX: number,
  screenY: number,
  direction: Direction,
  animFrame: number,
  isBlinking = false,
  bobOffset = 0
): void {
  const imageData = compositeCharacter(char, direction, animFrame, isBlinking);

  const offCtx = char.offscreen.getContext("2d");
  if (!offCtx) return;

  offCtx.putImageData(imageData, 0, 0);

  // Characters are centered horizontally at screenX, feet at screenY.
  // Ground-contact row is at sprite y = CHAR_H - 8 (shadow center),
  // so we anchor on that row rather than the sprite bottom.
  const drawX = Math.round(screenX - CHAR_W / 2);
  const drawY = Math.round(screenY - (CHAR_H - 8) + bobOffset);

  ctx.drawImage(char.offscreen, drawX, drawY);

  char.dirty = false;
}

/**
 * Advance character animation by dt seconds.
 * When moveProgress is supplied (0–1 across the current tile), walk/device_walk
 * frames are driven directly by movement — feet plant in perfect sync with
 * actual tile traversal. No more skating.
 */
export function updateAnimation(char: CharacterState, dt: number, moveProgress?: number): void {
  const isWalking = char.animState === "walk" || char.animState === "device_walk";

  const FRAME_COUNTS_EARLY: Record<AnimState, number> = {
    idle: 4, walk: 8, sit: 4, interact: 4, emote: 6, dance: 8,
    device: 4, device_walk: 8, device_sit: 4,
    combat_idle: 4, combat_attack: 3,
  };

  // Guard: if animState changed externally (e.g. combat system set combat_attack),
  // the stale animFrame from the prior state may exceed the new frame count.
  // Reset to frame 0 so the new animation starts cleanly from the beginning.
  if (char.animFrame >= FRAME_COUNTS_EARLY[char.animState]) {
    char.animFrame = 0;
    char.animTimer = 0;
  }

  const FPS: Record<AnimState, number> = {
    idle:        0.5,    // matches AnimationController — slow breath cycle
    walk:        8,      // fallback if no moveProgress
    sit:         0.3,    // matches AnimationController — relaxed seated
    interact:    4,
    emote:       6,
    dance:       4,
    device:      2.5,
    device_walk:   5,
    device_sit:    2,
    combat_idle:   6,
    combat_attack: 10,
  };

  const FRAME_COUNTS: Record<AnimState, number> = {
    idle:          4,
    walk:          8,
    sit:           4,
    interact:      4,
    emote:         6,
    dance:         8,
    device:        4,
    device_walk:   8,
    device_sit:    4,
    combat_idle:   4,
    combat_attack: 3,
  };

  if (isWalking && moveProgress !== undefined) {
    // Movement-driven walk: 1 full gait cycle per tile traversal.
    // At 1.8 tiles/sec → 0.55s per cycle ≈ 109 BPM — natural walk cadence.
    // The leg amplitudes and speed work together: visible strides that match
    // the distance covered, with enough time to read the arm/leg swing.
    const frames = FRAME_COUNTS[char.animState];
    char.animFrame = Math.floor(moveProgress * frames) % frames;
    // Keep timer advancing for breathing sub-cycles in the pose
    char.animTimer += dt;
  } else {
    // Timer-driven animation for non-walk states
    char.animTimer += dt;
    const frameDuration = 1 / FPS[char.animState];
    if (char.animTimer >= frameDuration) {
      char.animTimer -= frameDuration;
      const nextFrame = (char.animFrame + 1) % FRAME_COUNTS[char.animState];

      // combat_attack auto-returns to combat_idle after playing all 3 frames
      if (char.animState === "combat_attack" && nextFrame === 0) {
        char.animState = "combat_idle";
        char.animFrame = 0;
        char.animTimer = 0;
      } else {
        char.animFrame = nextFrame;
      }
    }
  }

  // Note: bobOffset for rendering is computed by AnimationController.getSnapshot(),
  // not here. The controller's setMoveProgress() syncs bob to tile traversal.
  // char.bobOffset is kept as a fallback for any non-room-renderer consumers.
  if (isWalking) {
    char.bobOffset = Math.round(Math.sin(char.animFrame * Math.PI / 4) * 3);
  } else if (char.animState === "combat_idle") {
    // Alert ready bounce for combat stance
    char.bobOffset = Math.round(Math.sin(char.animFrame * Math.PI / 2) * 2);
  } else {
    char.bobOffset = 0;
  }
}

// ─── Default appearance ───────────────────────────────────────────────────────

/**
 * A friendly default appearance — ready to be customized in the character editor.
 * Every new character starts here before becoming themselves.
 */
export function defaultAppearance(): CharacterAppearance {
  return {
    bodyType:       1,
    skinTone:       1,
    hairStyle:      1,
    hairColor:      3,
    eyeStyle:       0,
    eyeColor:       2,
    topStyle:       0,
    topColor:       16,  // a soft blue
    bottomStyle:    0,
    bottomColor:    4,   // dark grey
    shoeStyle:      0,
    shoeColor:      4,
    hatStyle:       -1,
    hatColor:       0,
    heldItem:       -1,
    jacketStyle:    -1,
    jacketColor:    0,
    skinDetail:     -1,
    accessoryStyle: -1,
    accessoryColor: 0,
    auraStyle:      -1,
    wingsStyle:     -1,
    wingsColor:     0,
    capeStyle:      -1,
    capeColor:      0,
    backpackStyle:  -1,
    backpackColor:  0,
    facialHairStyle: -1,
    facialHairColor: 3,
    makeupStyle:    -1,
    makeupColor:    0,
    lipStyle:       -1,
    lipColor:       0,
    expression:     "neutral",
    bodyArchetype1: "soft",
    bodyArchetype2: "lithe",
    archetypeBlend: 0.3,
    bodyScale:      2,
    shoulderAdjust: 2,
    waistAdjust:    2,
    hipAdjust:      2,
    legGap:         2,
    nude:           false,
    bodyGender:     "feminine",
    breastSize:     2,
    buttSize:       2,
    genitalSize:    2,
  };
}

// ─── Layer Preview Rendering ────────────────────────────────────────────────

/**
 * Render a single character layer (or a small set of layers) to a canvas context.
 * Used by wardrobe/catalog/inventory UIs to show actual item visuals.
 *
 * Draws at the composer's native 96×144 resolution, caller scales as needed.
 * Pass layerIds to select which layers to draw (e.g. ["body","top"] for a
 * top preview with body underneath for context).
 */
export function renderLayerPreview(
  ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
  appearance: CharacterAppearance,
  layerIds: string[],
  direction: Direction = "s",
  frame: number = 0,
): void {
  // Set up shape + neutral idle pose
  _bodyShape = resolveBodyShape(appearance);
  _legGapSlider = appearance.legGap ?? 2;
  _anatomy = {
    nude: appearance.nude ?? false,
    gender: appearance.bodyGender ?? "androgynous",
    breastSize: appearance.breastSize ?? 2,
    buttSize: appearance.buttSize ?? 2,
    genitalSize: appearance.genitalSize ?? 2,
  };
  _animPose = computeAnimPose("idle", frame, appearance.expression ?? "neutral");

  // Build all layers from this appearance
  const layers = buildLayers(appearance, direction);

  // Draw only the requested layers in z-order
  const selected = layers.filter(l => l.visible && layerIds.includes(l.id));
  for (const layer of selected) {
    layer.draw(ctx as any, direction, frame);
  }
}
